import { motion } from 'motion/react';
import { ArrowLeft, Layers, ChevronDown, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BetaStockRestrictionNotice } from './BetaStockRestrictionNotice';
import { BETA_DEMO_TICKER } from '@/utils/betaAccess';
import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { IntradayPublisherVolumeDashboard } from './IntradayPublisherVolumeDashboard';
import { TimeAndSalesFeed } from './TimeAndSalesFeed';
import { TradeSessionProfile } from './TradeSessionProfile';
import { PriceIntelligenceFeed } from './PriceIntelligenceFeed';
import { StockAnalogAnalysisPanel } from './StockAnalogAnalysisPanel';
import { StockStorylineTabsPanel } from './StockStorylineTabsPanel';
import { StockHeaderMiniChart } from './StockHeaderMiniChart';
import { TodayMoveSummaryCard } from './TodayMoveSummaryCard';
import { StockAnalogueTeaserCard } from './StockAnalogueTeaserCard';
import { STOCK_CHART_UI_ENABLED, PRICE_INTELLIGENCE_FEED_ENABLED, DASHBOARD_STORYLINES_ENABLED } from '@/config/features';
import {
  peakVolumePriceFromRegularHours,
  type PeakVolumePriceLevel,
  type PeakVolumePricePoint,
} from '@/utils/peakVolumePriceLayer';
import {
  PeakVolumePriceGuideSvg,
  PeakVolumePriceGuidePill,
  peakVolumeGuideLabel,
  peakVolumeGuideOverlayPosition,
  peakVolumeGuidePlotY,
} from './PeakVolumePriceChartOverlay';
import { VolumeByPriceChartOverlay } from './VolumeByPriceChartOverlay';
import {
  readVolumeByPriceDisplayMode,
  VolumeByPriceLayerSettings,
} from './VolumeByPriceLayerSettings';
import {
  volumeByPriceBarsFromYearlyPayload,
  volumeByPriceDateOrderFromPayload,
  volumeByPriceHasDateAttribution,
  volumeByPriceHasSideAttribution,
  volumeByPriceRangeLabel,
  VOLUME_BY_PRICE_CHART_RIGHT_MARGIN_PX,
  resolveYearlyVolumeByPriceBucketCents,
  type VolumeByPriceColorMode,
} from '@/utils/volumeByPriceLayer';
import { capDenseBlockTradesByRollingWindow } from '@/utils/capDenseBlockTradesWindow';
import { HISTORICAL_PRICES_API_EXTRA_DAYS } from '@/utils/historicalPriceApiLookback';
import {
  historicalPriceCutoffForTimeframe,
  historicalPriceFetchDaysForTimeframe,
  HISTORICAL_CHART_DEMO_LOCK_1Y,
  type HistoricalPriceChartTimeframe,
} from '@/utils/historicalChartTimeframe';
import {
  buildWholeDollarYAxisTicks,
  formatPriceAxisTick,
} from '@/utils/historicalChartWholeDollarTicks';
import { intradayTapeBuySellFromSummary } from '@/utils/intradayTapeBuySell';
import { tapeBuySellFromRegularHours, fullTradingDaySessionFromStats } from '@/utils/tradeSessionStatsDisplay';
import { tapeImbalanceDotFill, tapeImbalanceSignedRatio } from '@/utils/tapeImbalanceColor';
import {
  blockTradeDiamondRadiusByKey,
  blockTradeDiamondRadiusFallback,
  blockTradeNotionalUsd,
} from '@/utils/blockTradeDiamondSize';
import { tapeImbalanceDotRadiusByDate, tapeImbalanceTotalVolume } from '@/utils/tapeImbalanceDotSize';
import { localTodayIsoDate } from '@/utils/inclusiveCalendarDateRange';
import { getStockTickerStorylines, hasStockStorylineEvents } from '@/mocks/stockStorylineEventsMock';
/** Set to true to show Fundamentals, Analysts, and Momentum score cards */
const SHOW_METRICS = false;
/** Set to true to show VWAP overlay and Cumulative Delta chart on intraday */
const SHOW_VWAP_AND_DELTA = false;
/** Set to true to show Buy pressure and Net delta below intraday chart (block trades sit with Volume/Trades above chart) */
const SHOW_TRADE_ANALYTICS_STATS = false;
/** Set to true to show venue Price source dropdown on intraday chart */
const SHOW_INTRADAY_PRICE_SOURCE = false;
/** Set to true to show Block trades count / volume / notional above intraday chart */
const SHOW_INTRADAY_BLOCK_TRADE_STATS = false;
/** Set to true to show Buy/Sell block detail banner when a diamond is selected */
const SHOW_INTRADAY_BLOCK_TRADE_DETAIL = false;
/** Set to true to show Prediction layer toggle in the Layers bar */
const SHOW_LAYER_PREDICTION = false;
/** Set to true to show Volume layer toggle in the Layers bar */
const SHOW_LAYER_VOLUME = true;
/** Set to true to show Peak Volume layer toggle in the Layers bar */
const SHOW_LAYER_PEAK_VOLUME = false;
/** Set to true to show Volume by Price layer toggle in the Layers bar */
const SHOW_LAYER_VOLUME_BY_PRICE = true;
/** Set to true to show Time & Sales feed below the chart */
const SHOW_TIME_AND_SALES = false;
/** Set to true to show Options Positioning accordion below the chart */
const SHOW_OPTIONS_POSITIONING = false;
/** Set to true to show Prediction Markets (Polymarket) section below the chart */
const SHOW_PREDICTION_MARKETS = false;
// Institutional holders are now toggled via the "Institutional Holders" layer button.
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, ComposedChart, Bar, ReferenceLine, ReferenceDot, Cell, Customized } from 'recharts';

const INTRADAY_MARKET_OPEN_MIN = 9 * 60 + 30;
const INTRADAY_MARKET_CLOSE_MIN = 16 * 60;

/** Recharts margin for intraday — extra right/bottom keeps last time label and line off the card edge. */
const INTRADAY_COMPOSED_CHART_MARGIN = { top: 6, right: 34, left: 8, bottom: 18 } as const;

/** Matches `ResponsiveContainer` height for the historical chart. */
const HISTORICAL_CHART_VIEW_HEIGHT_PX = 500;
/** Headroom above plot for notable-event labels that lift above local price highs. */
const HISTORICAL_CHART_NOTABLE_EVENTS_TOP_MARGIN_PX = 56;
/** Left margin when origin-event labels are shown — keeps Y-axis price ticks visible. */
const HISTORICAL_CHART_NOTABLE_EVENTS_LEFT_MARGIN_PX = 56;
/** Reserved strip above the SVG so OHLC text clears the plot + Y-axis ticks (TradingView-style). */
const HISTORICAL_CHART_TOP_CAPTION_PX = 26;
const HISTORICAL_CHART_BOTTOM_AXIS_PX = 28;
/** Max height of daily volume bars as a fraction of the plot band (Customized overlay, not price scale). */
const HISTORICAL_VOLUME_OVERLAY_MAX_HEIGHT_FRAC = 0.52;
/** Click-popover for largest-trade diamonds (clamp keeps it inside the chart wrap). */
const HISTORICAL_BLOCK_TRADE_POPOVER_W = 164;
const HISTORICAL_BLOCK_TRADE_POPOVER_EST_H = 168;

/** Click-popover for tape-imbalance dots (same clamp helper as block trades). */
const TAPE_IMBALANCE_POPOVER_W = 200;
const TAPE_IMBALANCE_POPOVER_EST_H = 192;
const OPTION_IMBALANCE_POPOVER_W = 220;
const OPTION_IMBALANCE_POPOVER_EST_H = 192;

/** Horizontal gap between diamond center and popover (screen px, approximate). */
const HISTORICAL_BLOCK_TRADE_POPOVER_SIDE_GAP = 10;
/** Approx. distance from diamond center to its outer edge for layout (rotated rect + stroke). */
const HISTORICAL_BLOCK_TRADE_MARKER_RADIUS_LAYOUT_PX = 13;

const INSIDER_TRADING_POPOVER_W = 300;
const INSIDER_TRADING_POPOVER_EST_H = 280;
const SENATE_TRADING_POPOVER_W = 300;
const SENATE_TRADING_POPOVER_EST_H = 260;
const NOTABLE_OPTION_TRADE_POPOVER_W = 268;
const NOTABLE_OPTION_TRADE_POPOVER_EST_H = 220;

type PopoverClampOpts = {
  popW?: number;
  popH?: number;
  outwardPx?: number;
};

type BlockTradeTooltipTrade = {
  date: string;
  time: string;
  price: number;
  size: number;
  dollar_value: number;
  side: string;
  fullDate?: string;
};

function historicalBlockTradeInteractionKey(
  t: Pick<BlockTradeTooltipTrade, 'date' | 'time' | 'size' | 'price'>,
): string {
  return `${t.date}|${t.time}|${Number(t.size)}|${Number(t.price)}`;
}

/** Payload passed when clicking an intraday block-trade diamond. */
type IntradayBlockDiamondPayload = {
  time: string;
  price: number;
  size: number;
  side: string;
  dollar_value: number;
  date: string;
  fullDate?: string;
};

/** `topCaptionPx`: reserved strip above the plot inside `wrap` (0 for intraday price slot). */
function clampBlockTradePopoverBesideDiamond(
  wrap: HTMLElement,
  plotEl: HTMLElement | null,
  svgCx: number,
  svgCy: number,
  topCaptionPx: number,
  opts?: PopoverClampOpts,
): { left: number; top: number } {
  const rect = wrap.getBoundingClientRect();
  const pad = 8;
  const topMin = topCaptionPx + pad;
  const popW = opts?.popW ?? HISTORICAL_BLOCK_TRADE_POPOVER_W;
  const popH = opts?.popH ?? HISTORICAL_BLOCK_TRADE_POPOVER_EST_H;
  const gap = HISTORICAL_BLOCK_TRADE_POPOVER_SIDE_GAP;
  const outward = (opts?.outwardPx ?? HISTORICAL_BLOCK_TRADE_MARKER_RADIUS_LAYOUT_PX) + gap;

  const svg = (plotEl ?? wrap).querySelector('svg');
  let centerX = rect.width / 2;
  let centerY = topMin + popH / 2;
  if (svg) {
    const pt = svg.createSVGPoint();
    pt.x = svgCx;
    pt.y = svgCy;
    const ctm = svg.getScreenCTM();
    if (ctm) {
      const screen = pt.matrixTransform(ctm);
      centerX = screen.x - rect.left;
      centerY = screen.y - rect.top;
    }
  }

  // Prefer placing to the right of the marker; flip left if it overflows.
  let left = centerX + outward;
  if (left + popW > rect.width - pad) {
    left = centerX - outward - popW;
  }
  left = Math.max(pad, Math.min(left, rect.width - popW - pad));

  let top = centerY - popH / 2;
  top = Math.max(topMin, Math.min(top, rect.height - popH - pad));
  return { left, top };
}

function clampHistoricalBlockTradePopoverBesideDiamond(
  wrap: HTMLElement,
  plotEl: HTMLElement | null,
  svgCx: number,
  svgCy: number,
  opts?: PopoverClampOpts,
): { left: number; top: number } {
  return clampBlockTradePopoverBesideDiamond(
    wrap,
    plotEl,
    svgCx,
    svgCy,
    HISTORICAL_CHART_TOP_CAPTION_PX,
    opts,
  );
}

/** Nudge OHLC caption slightly higher within the reserved strip. */
const HISTORICAL_OHLC_CAPTION_TRANSLATE_Y_PX = -14;
/** Horizontal space between each OHLC cluster (O…, L…, H…, C…) and before the change segment (px). */
const HISTORICAL_OHLC_SEGMENT_GAP_PX = 6;

/** Left padding for OHLC strip so "O" lines up with "$" on Y-axis price ticks. */
function measureHistoricalYAxisTickLeftPad(wrap: HTMLElement): number | null {
  const ticks = wrap.querySelectorAll<SVGGraphicsElement>(
    'svg .recharts-cartesian-axis-tick-value',
  );
  const wrapRect = wrap.getBoundingClientRect();
  let minLeft = Infinity;
  for (let i = 0; i < ticks.length; i++) {
    const el = ticks[i];
    const t = (el.textContent ?? '').trim();
    if (!t.startsWith('$')) continue;
    const b = el.getBoundingClientRect();
    const left = b.left - wrapRect.left;
    if (left < minLeft) minLeft = left;
  }
  return minLeft === Infinity ? null : minLeft;
}

function timeMinutesFromHHMM(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Recharts ReferenceDot on a categorical `time` axis only draws when `x` matches a bar in `chartData`.
 * Polygon/Massive sometimes omit the first minute bar; snap to the nearest existing category so the
 * marker is still visible (prefer same or next minute, else previous).
 */
function resolveIntradayChartCategoryTime(wanted: string, chartData: Array<{ time: string }>): string {
  if (!chartData.length) return wanted;
  if (chartData.some((r) => r.time === wanted)) return wanted;
  const wantedM = timeMinutesFromHHMM(wanted);
  const withM = chartData.map((r) => ({ t: r.time, m: timeMinutesFromHHMM(r.time) }));
  const atOrAfter = withM.filter((o) => o.m >= wantedM).sort((a, b) => a.m - b.m);
  if (atOrAfter.length) return atOrAfter[0].t;
  const before = withM.filter((o) => o.m <= wantedM).sort((a, b) => b.m - a.m);
  return before.length ? before[0].t : wanted;
}

/** Match a yearly-chart / largest-block print to one row in intraday `block_trades` (same logic as highlight effect). */
function findIntradayBlockTradeTapeRow(
  blockTrades: Array<{ time: string; size: number; price: number; side: string }>,
  bd: { time: string; size: number; price: number; dollar_value?: number },
): (typeof blockTrades)[0] | undefined {
  const clickedSize = Number(bd.size);
  const clickedPrice = Number(bd.price);
  const clickedNotional =
    Number(bd.dollar_value) ||
    (Number.isFinite(clickedSize) && Number.isFinite(clickedPrice) ? clickedSize * clickedPrice : NaN);
  const priceTol = 0.005;
  let match = blockTrades.find(
    (b) =>
      b.time === bd.time &&
      Number(b.size) === clickedSize &&
      Math.abs(Number(b.price) - clickedPrice) <= priceTol,
  );
  if (!match) {
    const sameMinute = blockTrades.filter((b) => b.time === bd.time);
    if (sameMinute.length === 0) return undefined;
    if (sameMinute.length === 1) {
      match = sameMinute[0];
    } else if (Number.isFinite(clickedNotional)) {
      match = sameMinute.reduce((best, b) => {
        const bn = Number(b.size) * Number(b.price);
        const bestN = Number(best.size) * Number(best.price);
        return Math.abs(bn - clickedNotional) < Math.abs(bestN - clickedNotional) ? b : best;
      });
    } else {
      match = sameMinute.reduce((best, b) =>
        Number(b.size) * Number(b.price) > Number(best.size) * Number(best.price) ? b : best,
      );
    }
  }
  return match;
}

/** Build intraday line series from 1m OHLC bars (Polygon or Databento venue aggregates). */
function barsToIntradayChartPoints(
  bars: Array<{
    time: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  }>,
): Array<{
  time: string;
  price: number;
  percentFromOpen: number;
  priceChange: number;
  isSignificantMove: boolean;
  isExtendedHours?: boolean;
}> {
  const MARKET_OPEN = '09:30';
  const MARKET_CLOSE = '16:00';
  if (!bars || bars.length === 0) return [];
  const sortedBars = [...bars].sort((a, b) => a.time.localeCompare(b.time));
  const openPrice = sortedBars[0].open;
  const chartData = sortedBars.map((bar, i) => {
    const isExtended = bar.time < MARKET_OPEN || bar.time > MARKET_CLOSE;
    return {
      time: bar.time,
      price: bar.close,
      percentFromOpen: openPrice > 0 ? ((bar.close - openPrice) / openPrice) * 100 : 0,
      priceChange: i > 0 ? Math.abs(bar.close - sortedBars[i - 1].close) : 0,
      isSignificantMove: false,
      isExtendedHours: isExtended,
    };
  });
  const sortedByChange = [...chartData].sort((a, b) => b.priceChange - a.priceChange);
  sortedByChange.slice(0, 3).forEach((point) => {
    const p = chartData.find((d) => d.time === point.time);
    if (p) p.isSignificantMove = true;
  });
  return chartData;
}

/** Keep only U.S. regular session minutes (9:30–16:00 ET) for HH:MM keys from the API. */
function intradayRegularSessionOnly<T extends { time: string }>(rows: T[]): T[] {
  return rows.filter((d) => d.time >= '09:30' && d.time <= '16:00');
}

/**
 * One marker per ET clock minute (HH:MM): keep the largest notional print; ties keep the first seen.
 * Guards against duplicate minute keys in API payloads.
 */
function collapseToLargestTradePerEtMinute(
  rows: Array<{ time: string; price: number; size: number; side: string; venue?: number }>,
): Array<{ time: string; price: number; size: number; side: string; venue?: number }> {
  const best = new Map<
    string,
    { time: string; price: number; size: number; side: string; venue?: number; notional: number }
  >();
  for (const r of rows) {
    const t = (r.time ?? '').trim();
    if (!/^\d{2}:\d{2}$/.test(t)) continue;
    const notional = (Number(r.price) || 0) * (Number(r.size) || 0);
    const prev = best.get(t);
    if (!prev || notional > prev.notional) {
      best.set(t, { ...r, time: t, notional });
    }
  }
  return [...best.values()]
    .map(({ notional: _n, ...rest }) => rest)
    .sort((a, b) => a.time.localeCompare(b.time));
}

/**
 * Adaptive spacing window (minutes) for intraday block-trade diamonds.
 * Wider buckets on busier sessions keep the chart readable (~half as many markers
 * as 1/2/3/5/10-min tiers would show on a full tape).
 */
function adaptiveDiamondWindowMinutes(activeMinuteCount: number): number {
  if (activeMinuteCount <= 15) return 1;
  if (activeMinuteCount <= 30) return 2;
  if (activeMinuteCount <= 60) return 4;
  if (activeMinuteCount <= 120) return 6;
  if (activeMinuteCount <= 200) return 10;
  return 20;
}

/**
 * Reduces a per-minute diamond list to at most one diamond per `windowMinutes`-wide bucket,
 * keeping the highest-notional trade in each bucket.  This prevents the chart from being
 * overwhelmed when a stock has activity in nearly every minute of the session.
 */
function selectSpacedDiamonds<
  T extends { time: string; price: number; size: number },
>(trades: T[]): T[] {
  if (!trades.length) return trades;

  const windowMinutes = adaptiveDiamondWindowMinutes(trades.length);

  const buckets = new Map<number, { best: T; notional: number }>();
  for (const t of trades) {
    const parts = t.time.split(':').map(Number);
    const hh = parts[0];
    const mm = parts[1];
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) continue;
    const totalMin = hh * 60 + mm;
    const bucket = Math.floor(totalMin / windowMinutes);
    const notional = (Number(t.price) || 0) * (Number(t.size) || 0);
    const prev = buckets.get(bucket);
    if (!prev || notional > prev.notional) {
      buckets.set(bucket, { best: t, notional });
    }
  }

  return [...buckets.values()]
    .map(({ best }) => best)
    .sort((a, b) => a.time.localeCompare(b.time));
}

/**
 * Returns the set of HH:MM times that correspond to meaningful intraday price extremes.
 * Includes:
 *   1. Minutes already flagged `isSignificantMove` (top-3 bar-to-bar changes from barsToIntradayChartPoints).
 *   2. Local peaks and troughs within a ±WINDOW minute neighbourhood that are at least
 *      THRESHOLD × (day high − day low) away from their worst neighbour in that window.
 *
 * These times are used by ensureSignificantMoveDiamonds to guarantee a diamond marker even when
 * selectSpacedDiamonds would otherwise drop a trade from that window.
 */
function getSignificantMoveMinutes(
  chartRows: Array<{ time: string; price: number; isSignificantMove?: boolean }>,
  options?: { window?: number; thresholdPct?: number },
): Set<string> {
  const result = new Set<string>();
  if (!chartRows.length) return result;

  chartRows.forEach((r) => { if (r.isSignificantMove) result.add(r.time); });

  const prices = chartRows.map((r) => r.price);
  const dayHigh = Math.max(...prices);
  const dayLow = Math.min(...prices);
  const dayRange = dayHigh - dayLow;
  if (dayRange <= 0) return result;

  const WINDOW = options?.window ?? 3;
  const THRESHOLD = options?.thresholdPct ?? 0.15;

  for (let i = WINDOW; i < chartRows.length - WINDOW; i++) {
    const curr = prices[i];
    const slice = prices.slice(i - WINDOW, i + WINDOW + 1);
    const sliceMax = Math.max(...slice);
    const sliceMin = Math.min(...slice);
    const isPeak = curr === sliceMax;
    const isTrough = curr === sliceMin;
    if (!isPeak && !isTrough) continue;
    const significance = isPeak
      ? (curr - sliceMin) / dayRange
      : (sliceMax - curr) / dayRange;
    if (significance >= THRESHOLD) result.add(chartRows[i].time);
  }

  return result;
}

/**
 * After selectSpacedDiamonds thins the per-minute tape by notional/window, this function
 * re-inserts the best-notional trade for any significant price-move minute that is not already
 * covered by a diamond within ±windowMinutes of an existing marker.
 *
 * This prevents large rises or troughs from appearing on the chart without any diamond.
 */
function ensureSignificantMoveDiamonds<T extends { time: string; price: number; size: number }>(
  spaced: T[],
  allTrades: T[],
  chartRows: Array<{ time: string; price: number; isSignificantMove?: boolean }>,
): T[] {
  if (!chartRows.length || !allTrades.length) return spaced;

  const significantTimes = getSignificantMoveMinutes(chartRows);
  if (!significantTimes.size) return spaced;

  // Best-notional trade per minute across the full tape
  const tradeByMinute = new Map<string, T>();
  for (const t of allTrades) {
    const prev = tradeByMinute.get(t.time);
    const notional = (Number(t.price) || 0) * (Number(t.size) || 0);
    const prevNotional = prev ? (Number(prev.price) || 0) * (Number(prev.size) || 0) : -1;
    if (notional > prevNotional) tradeByMinute.set(t.time, t);
  }

  const windowMinutes = adaptiveDiamondWindowMinutes(allTrades.length);

  // Minutes already covered by an existing diamond (within ±windowMinutes)
  const coveredMinutes = new Set<number>();
  for (const d of spaced) {
    const parts = d.time.split(':').map(Number);
    const totalMin = (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
    for (let offset = -windowMinutes; offset <= windowMinutes; offset++) {
      coveredMinutes.add(totalMin + offset);
    }
  }

  const extras: T[] = [];
  for (const sigTime of significantTimes) {
    const parts = sigTime.split(':').map(Number);
    if (!Number.isFinite(parts[0]) || !Number.isFinite(parts[1])) continue;
    const totalMin = (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
    if (coveredMinutes.has(totalMin)) continue;
    const trade = tradeByMinute.get(sigTime);
    if (trade) extras.push(trade);
  }

  if (!extras.length) return spaced;

  // Merge: existing spaced diamonds take precedence on time collision
  const merged = new Map<string, T>();
  for (const t of [...spaced, ...extras]) {
    if (!merged.has(t.time)) merged.set(t.time, t);
  }
  return [...merged.values()].sort((a, b) => a.time.localeCompare(b.time));
}

/**
 * Insert 1m chart rows at HH:MM keys where a block trade exists but Polygon/venue bars omitted that minute
 * (e.g. close cross or late extended session). Preserves sort order and recomputes `priceChange`.
 */
function mergeIntradayChartPointsForBlockTradeMinutes<
  T extends {
    time: string;
    price: number;
    percentFromOpen: number;
    priceChange: number;
    isSignificantMove: boolean;
    isExtendedHours?: boolean;
    vwap?: number | null;
    venuePrice?: number | null;
  },
>(rows: T[], blockTrades: Array<{ time: string; price: number }> | undefined | null): T[] {
  if (!rows.length || !blockTrades?.length) return rows;
  const times = new Set(rows.map((r) => r.time));
  const tmpl = rows[0];
  const openBasis =
    tmpl.percentFromOpen !== undefined && Number.isFinite(tmpl.percentFromOpen)
      ? tmpl.price / (1 + tmpl.percentFromOpen / 100)
      : tmpl.price;
  const extras: T[] = [];
  for (const b of blockTrades) {
    if (!b?.time || times.has(b.time)) continue;
    const price = Number(b.price);
    if (!Number.isFinite(price)) continue;
    times.add(b.time);
    const pct = openBasis > 0 ? ((price - openBasis) / openBasis) * 100 : 0;
    const ext = b.time < '09:30' || b.time > '16:00';
    extras.push({
      ...tmpl,
      time: b.time,
      price,
      percentFromOpen: pct,
      priceChange: 0,
      isSignificantMove: false,
      isExtendedHours: ext,
      vwap: null,
      ...(Object.prototype.hasOwnProperty.call(tmpl, 'venuePrice') ? { venuePrice: null as number | null } : {}),
    } as T);
  }
  if (!extras.length) return rows;
  const merged = [...rows, ...extras].sort((a, b) => a.time.localeCompare(b.time));
  return merged.map((p, i) => ({
    ...p,
    priceChange: i > 0 ? Math.abs(p.price - merged[i - 1].price) : 0,
  }));
}

/** Pixel x for a session boundary (e.g. 9:30 / 16:00) when that time may not exist as a bar — Recharts ReferenceLine drops those on categorical axes. */
function intradaySessionBoundaryX(
  xAxis: { scale: { (v: string): number | undefined; domain?: () => string[]; bandwidth?: () => number } },
  boundaryMin: number,
  fallbackDomain?: string[],
): number | null {
  const scale = xAxis.scale;
  const fromScale = scale.domain?.();
  const domain = fromScale && fromScale.length > 0 ? fromScale : fallbackDomain;
  if (!domain?.length) return null;
  const bw = typeof scale.bandwidth === 'function' ? scale.bandwidth() : 0;
  const hh = Math.floor(boundaryMin / 60);
  const mm = boundaryMin % 60;
  const boundaryStr = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;

  const centerOf = (cat: string): number | null => {
    const p = scale(cat);
    if (p == null || Number.isNaN(Number(p))) return null;
    return Number(p) + bw / 2;
  };

  if (domain.includes(boundaryStr)) {
    return centerOf(boundaryStr);
  }

  const sorted = [...domain].sort((a, b) => timeMinutesFromHHMM(a) - timeMinutesFromHHMM(b));
  for (let i = 0; i < sorted.length - 1; i++) {
    const t0 = timeMinutesFromHHMM(sorted[i]);
    const t1 = timeMinutesFromHHMM(sorted[i + 1]);
    if (t0 <= boundaryMin && boundaryMin <= t1) {
      const x0 = centerOf(sorted[i]);
      const x1 = centerOf(sorted[i + 1]);
      if (x0 == null || x1 == null) return null;
      if (t1 === t0) return x0;
      const ratio = (boundaryMin - t0) / (t1 - t0);
      return x0 + ratio * (x1 - x0);
    }
  }
  return null;
}

function IntradaySessionBoundaryLines(props: {
  xAxisMap?: Record<string, { scale: { (v: string): number | undefined; domain?: () => string[]; bandwidth?: () => number } }>;
  offset?: { top: number; height: number };
  showPremarketEnd: boolean;
  showAfterHoursStart: boolean;
  timeDomainFallback?: string[];
}) {
  const { xAxisMap, offset, showPremarketEnd, showAfterHoursStart, timeDomainFallback } = props;
  if (!xAxisMap || !offset) return null;
  const xAxis = xAxisMap[0] ?? Object.values(xAxisMap)[0];
  if (!xAxis?.scale) return null;

  const y1 = offset.top;
  const y2 = offset.top + offset.height;

  const line = (key: string, x: number | null) =>
    x == null ? null : (
      <line
        key={key}
        x1={x}
        x2={x}
        y1={y1}
        y2={y2}
        stroke="#94a3b8"
        strokeDasharray="2 2"
        strokeOpacity={0.6}
      />
    );

  return (
    <g className="recharts-layer intraday-session-boundaries">
      {showPremarketEnd ? line('930', intradaySessionBoundaryX(xAxis, INTRADAY_MARKET_OPEN_MIN, timeDomainFallback)) : null}
      {showAfterHoursStart ? line('1600', intradaySessionBoundaryX(xAxis, INTRADAY_MARKET_CLOSE_MIN, timeDomainFallback)) : null}
    </g>
  );
}

type BandScale = { (v: string): number | undefined; domain?: () => string[]; bandwidth?: () => number };
type LinearScale = { (v: number): number | undefined };

/**
 * Renders intraday block-trade diamonds using the real Recharts band-scale so each dot is
 * centered in its time column (`scale(time) + bandwidth/2`) instead of at the column's left
 * edge (which is what ReferenceDot does, causing the 09:30 diamond to be clipped off-screen).
 */
function IntradayBlockTradeDots(props: {
  xAxisMap?: Record<string, { scale: BandScale }>;
  yAxisMap?: Record<string, { scale: LinearScale }>;
  offset?: { top: number; height: number };
  trades: Array<{
    time: string;
    price: number;
    size: number;
    side: string;
    dollar_value: number;
    date: string;
    fullDate?: string;
    pulsate?: boolean;
    radiusPx?: number;
  }>;
  onDotClick: (bt: IntradayBlockDiamondPayload, cx: number, cy: number) => void;
}) {
  const { xAxisMap, yAxisMap, offset, trades, onDotClick } = props;
  if (!xAxisMap || !yAxisMap || !offset || !trades.length) return null;
  const xAxis = xAxisMap[0] ?? Object.values(xAxisMap)[0];
  const yAxis = yAxisMap['price'] ?? yAxisMap[0] ?? Object.values(yAxisMap)[0];
  if (!xAxis?.scale || !yAxis?.scale) return null;

  const xScale = xAxis.scale;
  const yScale = yAxis.scale;
  const bw = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;

  return (
    <g className="recharts-layer intraday-block-trade-dots" style={{ overflow: 'visible', pointerEvents: 'all' }}>
      {trades.map((bt, i) => {
        const x0 = xScale(bt.time);
        const y0 = yScale(bt.price);
        if (x0 == null || y0 == null || !Number.isFinite(Number(x0)) || !Number.isFinite(Number(y0))) {
          return null;
        }
        const cx = Number(x0) + bw / 2;
        const cy = Number(y0);
        return (
          <HistoricalBlockTradeDot
            key={`ibd-${bt.time}-${i}-${bt.size}`}
            cx={cx}
            cy={cy}
            payload={{ date: bt.date, time: bt.time, price: bt.price, size: bt.size, dollar_value: bt.dollar_value, side: bt.side, fullDate: bt.fullDate }}
            pulsate={bt.pulsate ?? false}
            radiusPx={bt.radiusPx}
            onClick={() => onDotClick(bt, cx, cy)}
          />
        );
      })}
    </g>
  );
}

/**
 * Stable named component for the intraday diamond Customized layer.
 * MUST be defined at module level (not inline) so React preserves component identity
 * across re-renders — otherwise Recharts' renderCustomized calls createElement with
 * a new function each render, causing React to unmount/remount and reset isHovered.
 *
 * Recharts forwards all chart internals (xAxisMap, yAxisMap, offset) plus any extra
 * props placed on <Customized> via cloneElement({...chartProps, ...yourProps}).
 */
function IntradayDiamondsCustomized(props: {
  xAxisMap?: Record<string, { scale: BandScale }>;
  yAxisMap?: Record<string, { scale: LinearScale }>;
  offset?: { top: number; height: number };
  // custom props forwarded from <Customized>
  markers: Array<{
    time: string;
    price: number;
    size: number;
    side: string;
    dollar_value: number;
    date: string;
    fullDate?: string;
    pulsate?: boolean;
    radiusPx?: number;
  }>;
  onDotClick: (bt: IntradayBlockDiamondPayload, cx: number, cy: number) => void;
  [key: string]: unknown; // absorb extra Recharts-injected state props
}) {
  const { xAxisMap, yAxisMap, offset, markers, onDotClick } = props;
  return (
    <IntradayBlockTradeDots
      xAxisMap={xAxisMap}
      yAxisMap={yAxisMap}
      offset={offset}
      trades={markers}
      onDotClick={onDotClick}
    />
  );
}

/** TradingView-style axis date label, e.g. "Mon 06 Apr '26". */
function formatHistoricalCrosshairDate(dateStr: string): string {
  const parts = dateStr.split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return dateStr;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return dateStr;
  const weekday = dt.toLocaleDateString('en-US', { weekday: 'short' });
  const dayPadded = String(d).padStart(2, '0');
  const monthShort = dt.toLocaleDateString('en-US', { month: 'short' });
  const yy = String(y).slice(-2);
  return `${weekday} ${dayPadded} ${monthShort} '${yy}`;
}

type HistoricalChartHoverPayload = {
  date: string;
  dateObj?: Date;
  price?: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  fullDate?: string;
  size?: number;
  dollar_value?: number;
  /** Close-to-close vs prior session (full series), from `generateHistoricalData`. */
  percentChange?: number;
};

type HistoricalChartMetrics = {
  xAxisMap?: Record<string, { scale: any }>;
  yAxisMap?: Record<string, { scale: any }>;
  offset?: { top: number; left: number; width: number; height: number };
};

function finiteHistoricalPrice(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/** OHLC for the hovered session; falls back to close-only when intraday OHLC is missing. */
function historicalOhlcFromRow(row: HistoricalChartHoverPayload): {
  o: number;
  h: number;
  l: number;
  c: number;
} | null {
  const c = finiteHistoricalPrice(row.close, finiteHistoricalPrice(row.price, NaN));
  if (!Number.isFinite(c)) return null;
  const o = finiteHistoricalPrice(row.open, c);
  const hRaw = finiteHistoricalPrice(row.high, NaN);
  const lRaw = finiteHistoricalPrice(row.low, NaN);
  const h = Number.isFinite(hRaw) ? hRaw : Math.max(o, c);
  const l = Number.isFinite(lRaw) ? lRaw : Math.min(o, c);
  return { o, h, l, c };
}

/** Session close vs prior visible bar; first bar uses `percentChange` from full-series context. */
function historicalSessionChangeFromRow(
  chartData: HistoricalChartHoverPayload[],
  index: number,
  row: HistoricalChartHoverPayload,
): { change: number; changePct: number } {
  const ohlc = historicalOhlcFromRow(row);
  if (!ohlc) return { change: 0, changePct: 0 };
  const { c } = ohlc;

  if (index > 0) {
    const prev = chartData[index - 1];
    const p = finiteHistoricalPrice(prev.close, finiteHistoricalPrice(prev.price, NaN));
    if (!Number.isFinite(p) || p === 0) return { change: 0, changePct: 0 };
    const change = c - p;
    return { change, changePct: (change / p) * 100 };
  }

  const pct = typeof row.percentChange === 'number' && Number.isFinite(row.percentChange) ? row.percentChange : 0;
  if (pct === 0 || !Number.isFinite(c) || c === 0) return { change: 0, changePct: 0 };
  const change = (c * pct) / (100 + pct);
  return { change, changePct: pct };
}

/** Map plot-relative pointer position to nearest session bar + Y-scale price (axis trackers). */
function snapHistoricalCrosshair(
  chartProps: HistoricalChartMetrics,
  chartData: HistoricalChartHoverPayload[],
  lx: number,
  ly: number,
): {
  verticalCenterX: number;
  dateLabel: string;
  cursorPrice: number | null;
  horizontalY: number;
  plotLeft: number;
  plotRight: number;
  plotBottom: number;
  snappedRow: HistoricalChartHoverPayload;
  snappedIndex: number;
} | null {
  const xAxisMap = chartProps.xAxisMap;
  const yAxisMap = chartProps.yAxisMap;
  const offset = chartProps.offset;
  if (!xAxisMap || !yAxisMap || !offset || chartData.length === 0) return null;

  const xAxis = xAxisMap[0] ?? Object.values(xAxisMap)[0];
  const yAxis = yAxisMap['price'] ?? yAxisMap[0] ?? Object.values(yAxisMap)[0];
  const xScale = xAxis?.scale;
  const yScale = yAxis?.scale;
  if (!xScale || !yScale) return null;

  const plotLeft = offset.left;
  const plotTop = offset.top;
  const plotW = offset.width;
  const plotRight = plotLeft + plotW;
  const plotBottom = plotTop + offset.height;

  const domain: string[] =
    typeof xScale.domain === 'function' ? xScale.domain() : chartData.map((d) => d.date);
  const bw = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;

  const absX = plotLeft + lx;
  if (!domain.length) return null;
  let bestI = 0;
  let bestDist = Infinity;
  for (let i = 0; i < domain.length; i++) {
    const cat = domain[i];
    const x0 = xScale(cat);
    if (x0 == null || Number.isNaN(Number(x0))) continue;
    const center = Number(x0) + (bw > 0 ? bw / 2 : 0);
    const dist = Math.abs(absX - center);
    if (dist < bestDist) {
      bestDist = dist;
      bestI = i;
    }
  }

  const cat = domain[bestI];
  let snappedIndex = chartData.findIndex((d) => d.date === cat);
  if (snappedIndex < 0) snappedIndex = Math.max(0, Math.min(bestI, chartData.length - 1));
  const row = chartData[snappedIndex];
  const x0 = cat != null ? xScale(cat) : null;
  let verticalCenterX = plotLeft + plotW / 2;
  if (x0 != null && !Number.isNaN(Number(x0))) {
    verticalCenterX = Number(x0) + (bw > 0 ? bw / 2 : 0);
  }

  const absY = plotTop + ly;
  let cursorPrice: number | null = null;
  if (typeof yScale.invert === 'function') {
    let sampleY = absY;
    if (typeof yScale.range === 'function') {
      const rng = yScale.range() as number[];
      if (rng.length >= 2) {
        const lo = Math.min(rng[0], rng[1]);
        const hi = Math.max(rng[0], rng[1]);
        sampleY = Math.min(Math.max(absY, lo), hi);
      }
    }
    const p = yScale.invert(sampleY);
    cursorPrice =
      typeof p === 'number' && Number.isFinite(p) ? Math.max(0, p) : null;
  }

  const horizontalY = plotTop + ly;
  const dateLabel = formatHistoricalCrosshairDate(row.date);

  return {
    verticalCenterX,
    dateLabel,
    cursorPrice,
    horizontalY,
    plotLeft,
    plotRight,
    plotBottom,
    snappedRow: row,
    snappedIndex,
  };
}

const HISTORICAL_BRUSH_MIN_PLOT_WIDTH_PX = 20;
const HISTORICAL_ZOOM_MIN_VISIBLE_BARS = 3;
/** Brush-zoom: label every bar up to this cap (avoids gaps); beyond it use an even stride across bars. */
const HISTORICAL_ZOOM_X_AXIS_MAX_TICKS = 40;

/**
 * Dense x-axis ticks when zoomed: one label per trading day when there are few enough bars,
 * otherwise evenly stride across bar indices (still fills the axis; no sparse target-count gaps).
 */
function historicalZoomBarTicksDense(chartData: Array<{ date: string }>, maxTicks: number): string[] {
  const n = chartData.length;
  if (n === 0) return [];
  if (n === 1) return [chartData[0].date];

  if (n <= maxTicks) {
    return chartData.map((p) => p.date);
  }

  const stride = Math.ceil(n / maxTicks);
  const dates: string[] = [];
  for (let i = 0; i < n; i += stride) {
    dates.push(chartData[i].date);
  }
  const last = chartData[n - 1].date;
  if (dates[dates.length - 1] !== last) {
    dates.push(last);
  }
  return dates;
}

/** Client coordinates → plot-inner lx/ly (same basis as the crosshair). */
function clientPointToHistoricalPlotLxLy(
  clientX: number,
  clientY: number,
  wrap: HTMLElement,
  cp: HistoricalChartMetrics,
): { lx: number; ly: number } | null {
  const svg = wrap.querySelector('svg');
  if (!svg) return null;
  const offset = cp.offset;
  if (!offset) return null;
  const pt = (svg as SVGSVGElement).createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = (svg as SVGSVGElement).getScreenCTM();
  if (!ctm) return null;
  const loc = pt.matrixTransform(ctm.inverse());
  return {
    lx: loc.x - offset.left,
    ly: loc.y - offset.top,
  };
}

function historicalPlotLxToBarIndex(
  cp: HistoricalChartMetrics,
  chartData: HistoricalChartHoverPayload[],
  lx: number,
  lyHint: number,
): number | null {
  if (chartData.length === 0) return null;
  const plotW = cp.offset?.width ?? 0;
  const plotH = cp.offset?.height ?? 0;
  if (plotW <= 0 || plotH <= 0) return null;
  const lxClamped = Math.max(0, Math.min(plotW, lx));
  const ly = Math.max(0, Math.min(plotH, lyHint));
  const snap = snapHistoricalCrosshair(cp, chartData, lxClamped, ly);
  return snap?.snappedIndex ?? null;
}

/** Gap from plot right edge to the HTML price pill (drawn outside SVG so it is not clipped). */
const HISTORICAL_PRICE_TRACKER_GAP_PX = 8;

function historicalPriceTrackerOverlayPosition(
  svg: SVGSVGElement,
  wrap: HTMLElement,
  plotRight: number,
  horizontalY: number,
  plotTop: number,
  plotBottom: number,
  pillH: number,
): { left: number; top: number } | null {
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = plotRight + HISTORICAL_PRICE_TRACKER_GAP_PX;
  pt.y = horizontalY;
  const scr = pt.matrixTransform(ctm);
  const wrapRect = wrap.getBoundingClientRect();
  const left = scr.x - wrapRect.left;
  let top = scr.y - wrapRect.top - pillH / 2;

  const ptMin = svg.createSVGPoint();
  ptMin.x = plotRight;
  ptMin.y = plotTop;
  const scrMin = ptMin.matrixTransform(ctm);
  const ptMax = svg.createSVGPoint();
  ptMax.x = plotRight;
  ptMax.y = plotBottom;
  const scrMax = ptMax.matrixTransform(ctm);
  const minTop = scrMin.y - wrapRect.top;
  const maxTop = scrMax.y - wrapRect.top - pillH;
  top = Math.max(minTop, Math.min(top, maxTop));

  return { left, top };
}

/** Crosshair lines + bottom date pill (`pointer-events: none`). Price pill is HTML overlay — see above. */
function HistoricalChartCrosshairGraphics(props: {
  chartProps: HistoricalChartMetrics;
  chartData: HistoricalChartHoverPayload[];
  plotMouse: { lx: number; ly: number } | null;
}) {
  const { chartProps, chartData, plotMouse } = props;
  if (!plotMouse || chartData.length === 0) return null;
  const snap = snapHistoricalCrosshair(chartProps, chartData, plotMouse.lx, plotMouse.ly);
  if (!snap) return null;

  const {
    verticalCenterX,
    dateLabel,
    horizontalY,
    plotBottom,
    plotRight,
    plotLeft,
  } = snap;

  const offset = chartProps.offset!;
  const plotTop = offset.top;
  const axisPillH = 22;
  const datePillW = 118;
  let datePillX = verticalCenterX - datePillW / 2;
  datePillX = Math.max(plotLeft + 2, Math.min(datePillX, plotRight - datePillW - 2));
  const bottomPillY = plotBottom + 2;

  return (
    <g className="recharts-layer historical-chart-crosshair" pointerEvents="none">
      <line
        x1={plotLeft}
        x2={plotRight}
        y1={horizontalY}
        y2={horizontalY}
        stroke="#94a3b8"
        strokeDasharray="4 4"
        strokeOpacity={0.85}
      />
      <line
        x1={verticalCenterX}
        x2={verticalCenterX}
        y1={plotTop}
        y2={plotBottom}
        stroke="#94a3b8"
        strokeDasharray="4 4"
        strokeOpacity={0.85}
      />
      <foreignObject x={datePillX} y={bottomPillY} width={datePillW} height={axisPillH}>
        <div
          className="flex h-[22px] items-center justify-center rounded-sm bg-gray-900 px-2 text-[11px] font-medium text-white shadow-sm tabular-nums"
          style={{ fontFamily: 'system-ui, sans-serif' }}
        >
          {dateLabel}
        </div>
      </foreignObject>
    </g>
  );
}

type IntradayCrosshairRow = {
  time: string;
  price: number;
  isExtendedHours?: boolean;
};

function formatIntradayCrosshairTimeLabel(row: IntradayCrosshairRow): string {
  const t = row.time;
  if (row.isExtendedHours === true) {
    if (t < '09:30') return `${t} (Pre-market)`;
    return `${t} (After-hours)`;
  }
  return t;
}

/** Same snapping math as `snapHistoricalCrosshair`, but X category is `time` (HH:MM). */
function snapIntradayCrosshair(
  chartProps: HistoricalChartMetrics,
  chartData: IntradayCrosshairRow[],
  lx: number,
  ly: number,
): {
  verticalCenterX: number;
  timeLabel: string;
  cursorPrice: number | null;
  horizontalY: number;
  plotLeft: number;
  plotRight: number;
  plotBottom: number;
  snappedRow: IntradayCrosshairRow;
  snappedIndex: number;
} | null {
  const xAxisMap = chartProps.xAxisMap;
  const yAxisMap = chartProps.yAxisMap;
  const offset = chartProps.offset;
  if (!xAxisMap || !yAxisMap || !offset || chartData.length === 0) return null;

  const xAxis = xAxisMap[0] ?? Object.values(xAxisMap)[0];
  const yAxis = yAxisMap['price'] ?? yAxisMap[0] ?? Object.values(yAxisMap)[0];
  const xScale = xAxis?.scale;
  const yScale = yAxis?.scale;
  if (!xScale || !yScale) return null;

  const plotLeft = offset.left;
  const plotTop = offset.top;
  const plotW = offset.width;
  const plotRight = plotLeft + plotW;
  const plotBottom = plotTop + offset.height;

  const domain: string[] =
    typeof xScale.domain === 'function' ? xScale.domain() : chartData.map((d) => d.time);
  const bw = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;

  const absX = plotLeft + lx;
  if (!domain.length) return null;
  let bestI = 0;
  let bestDist = Infinity;
  for (let i = 0; i < domain.length; i++) {
    const cat = domain[i];
    const x0 = xScale(cat);
    if (x0 == null || Number.isNaN(Number(x0))) continue;
    const center = Number(x0) + (bw > 0 ? bw / 2 : 0);
    const dist = Math.abs(absX - center);
    if (dist < bestDist) {
      bestDist = dist;
      bestI = i;
    }
  }

  const cat = domain[bestI];
  let snappedIndex = chartData.findIndex((d) => d.time === cat);
  if (snappedIndex < 0) snappedIndex = Math.max(0, Math.min(bestI, chartData.length - 1));
  const row = chartData[snappedIndex];
  const x0 = cat != null ? xScale(cat) : null;
  let verticalCenterX = plotLeft + plotW / 2;
  if (x0 != null && !Number.isNaN(Number(x0))) {
    verticalCenterX = Number(x0) + (bw > 0 ? bw / 2 : 0);
  }

  const absY = plotTop + ly;
  let cursorPrice: number | null = null;
  if (typeof yScale.invert === 'function') {
    let sampleY = absY;
    if (typeof yScale.range === 'function') {
      const rng = yScale.range() as number[];
      if (rng.length >= 2) {
        const lo = Math.min(rng[0], rng[1]);
        const hi = Math.max(rng[0], rng[1]);
        sampleY = Math.min(Math.max(absY, lo), hi);
      }
    }
    const p = yScale.invert(sampleY);
    cursorPrice =
      typeof p === 'number' && Number.isFinite(p) ? Math.max(0, p) : null;
  }

  const horizontalY = plotTop + ly;
  const timeLabel = formatIntradayCrosshairTimeLabel(row);

  return {
    verticalCenterX,
    timeLabel,
    cursorPrice,
    horizontalY,
    plotLeft,
    plotRight,
    plotBottom,
    snappedRow: row,
    snappedIndex,
  };
}

function IntradayChartCrosshairGraphics(props: {
  chartProps: HistoricalChartMetrics;
  chartData: IntradayCrosshairRow[];
  plotMouse: { lx: number; ly: number } | null;
}) {
  const { chartProps, chartData, plotMouse } = props;
  if (!plotMouse || chartData.length === 0) return null;
  const snap = snapIntradayCrosshair(chartProps, chartData, plotMouse.lx, plotMouse.ly);
  if (!snap) return null;

  const { verticalCenterX, timeLabel, horizontalY, plotBottom, plotRight, plotLeft } = snap;

  const offset = chartProps.offset!;
  const plotTop = offset.top;
  const axisPillH = 22;
  const datePillW = Math.max(118, Math.min(200, 8 + timeLabel.length * 7));
  let datePillX = verticalCenterX - datePillW / 2;
  datePillX = Math.max(plotLeft + 2, Math.min(datePillX, plotRight - datePillW - 2));
  const bottomPillY = plotBottom + 2;

  return (
    <g className="recharts-layer intraday-chart-crosshair" pointerEvents="none">
      <line
        x1={plotLeft}
        x2={plotRight}
        y1={horizontalY}
        y2={horizontalY}
        stroke="#94a3b8"
        strokeDasharray="4 4"
        strokeOpacity={0.85}
      />
      <line
        x1={verticalCenterX}
        x2={verticalCenterX}
        y1={plotTop}
        y2={plotBottom}
        stroke="#94a3b8"
        strokeDasharray="4 4"
        strokeOpacity={0.85}
      />
      <foreignObject x={datePillX} y={bottomPillY} width={datePillW} height={axisPillH}>
        <div
          className="flex h-[22px] items-center justify-center rounded-sm bg-gray-900 px-2 text-[11px] font-medium text-white shadow-sm tabular-nums"
          style={{ fontFamily: 'system-ui, sans-serif' }}
        >
          {timeLabel}
        </div>
      </foreignObject>
    </g>
  );
}

import {
  MetricComponent,
  FinancialHealthResponse,
  CompositeAttractivenessResponse,
  stockService,
  type TradeSessionStatsPayload,
} from '@/services/api';
import type {
  FmpInsiderTradeRow,
  FmpInsiderTradingFileResponse,
  FmpCongressTradeRow,
  FmpCongressTradesFileResponse,
  FedRateMarket,
  FedRateDecisionResponse,
  MarketProbHistory,
  NotableOptionTradeRow,
} from '@/services/api';
import { macroService } from '@/services/api';
import {
  useInstitutionalHoldersChartOverlay,
  type InstitutionalHoldersOverlayMeta,
} from './InstitutionalHoldersChartLabels';
import { INSTITUTIONAL_HOLDERS_RIBBON_HEIGHT_PX } from './InstitutionalHoldersChartRibbon';
import { buildInstitutionalChartOverlayData } from '@/utils/institutionalHoldersChartData';
import { InstitutionalHoldersLayerSettings } from './InstitutionalHoldersLayerSettings';
import { InstitutionalHoldersLayerSearch } from './InstitutionalHoldersLayerSearch';
import { InstitutionalHoldersTopHoldersWidget } from './InstitutionalHoldersTopHoldersWidget';
import { InsiderTradingLayerSettings } from './InsiderTradingLayerSettings';
import { InsiderTradingLayerSearch } from './InsiderTradingLayerSearch';
import { CongressTradingLayerSettings } from './CongressTradingLayerSettings';
import { CongressTradingLayerSearch } from './CongressTradingLayerSearch';
import { NotableOptionTradeDiamond } from './NotableOptionTradeDiamond';
import { NotableOptionTradesTooltipContent } from './NotableOptionTradesTooltipContent';
import {
  NotableOptionTradesLayerSettings,
  persistNotableOptionMinPremiumUsd,
  readNotableOptionMinPremiumUsd,
} from './NotableOptionTradesLayerSettings';
import { notableOptionTradeDiamondRadiusByKey } from '@/utils/notableOptionTradeDiamondSize';
import { InstitutionalHolderChartTooltip } from './InstitutionalHolderChartTooltip';
import { buildInstitutionalHolderDetailPanel } from '@/utils/institutionalHolderQuarterHistory';
import {
  filterFilingPeriodsBySearch,
  filingPeriodsHaveSearchMatch,
  institutionalHolderRibbonSelector,
  type InstitutionalHolderSearchResult,
} from '@/utils/institutionalHoldersSearch';
import {
  persistInstitutionalHoldersChartDisplayMode,
  readInstitutionalHoldersChartDisplayMode,
  type InstitutionalHoldersChartDisplayMode,
} from '@/utils/institutionalHoldersChartDisplayMode';
import {
  countInsiderTradesByTypeCode,
  insiderTradeDiamondColor,
  insiderTransactionCode,
  persistInsiderTradingEnabledTypeCodes,
  readInsiderTradingEnabledTypeCodes,
  type InsiderTransactionCode,
} from '@/utils/insiderTradingTransactionTypes';
import {
  tradeMatchesInsiderLayerFilters,
  type InsiderTradeSearchResult,
} from '@/utils/insiderTradingSearch';
import {
  countCongressTradesByCategory,
  countCongressTradesByChamber,
  congressTradeDiamondColor,
  congressTradeIdentityKey,
  fmpCongressTradeMarkerKey,
  persistCongressTradingEnabledCategories,
  persistCongressTradingEnabledChambers,
  readCongressTradingEnabledCategories,
  readCongressTradingEnabledChambers,
  type CongressChamber,
  type CongressTradeCategory,
} from '@/utils/congressTradingTypes';
import {
  tradeMatchesCongressLayerFilters,
  type CongressTradeSearchResult,
} from '@/utils/congressTradingSearch';
import type { InstitutionalHoldersFileResponse } from '@/services/api';
import { PolymarketSection } from './PolymarketSection';
import { OptionsPositioningSection } from './OptionsPositioningSection';
import { OptionsPositioningChartLegend } from './OptionsPositioningChartLegend';
import {
  buildInvestorDistributionRows,
  buildNearSpotLadderRows,
  buildOptionsChartOverlaySummary,
  computeOptionsLegendScreenPosition,
  hasOptionsPositioningChartData,
  type PriceDistributionBucket,
} from '@/utils/optionsPositioningChartOverlay';
import type { OptionsAnalysisResponse } from '@/services/api';

interface StockData {
  company: string;
  ticker: string;
  currentPrice: string;
  change: string;
  changePercent: string;
  sector?: string;
  industry?: string;
}

interface StockAnalysisViewProps {
  onBack: () => void;
  showBetaRestrictionNotice?: boolean;
  stockData: StockData;
  financialHealth: FinancialHealthResponse;
  compositeScore: CompositeAttractivenessResponse;
  historicalPrices?: Array<{
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    percent_change: number | null;
  }> | null;
  significantMoves?: Array<{
    date: string;
    percent_change: number;
  }> | null;
  blockTradeDates?: string[] | null;
  largestBlockTrades?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }> | null;
  /** When set (from refreshed largest_block_trades.json), chart shows one marker per session date. */
  largestBlockTradePerDay?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }> | null;
  /** IQR upper-fence outlier subset of largestBlockTradePerDay — shown when "Largest Trades" is enabled. */
  noteworthyBlockTradesPerDay?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }> | null;
  /** noteworthy ∪ significant-price/volume days — primary source for chart diamonds. */
  diamondBlockTradesPerDay?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }> | null;
  largestBlockTradePerMonth?: Array<{ month: string; trade: { date: string; time: string; price: number; size: number; dollar_value: number; side: string } }> | null;
  /** Per-date sum of block-trade notional USD (≥$10k per trade, regular hours); drives institutional area gradient. */
  blockTradeNotionalByDate?: Record<string, number> | null;
  /** YYYY-MM-DD dates where a new origin_event starts (ohlc_database_enriched). */
  originEventDates?: string[] | null;
}

interface MetricItemProps {
  label: string;
  value: string;
  grade: string;
  trend?: 'up' | 'down' | 'neutral';
  description?: string;
  explanation?: string;
  calculationMethod?: string; // "(YoY)" or "(3 Yr Avg)" for revenue growth
  quarterlyData?: {
    quarters: Array<{
      quarter: string;
      revenue: number;
      grossProfit?: number; // For Gross Margin calculation
      operatingIncome?: number; // For Rule of 40 calculation
      fcf?: number; // For Rule of 40 calculation (mature companies)
      netIncome?: number; // For Net Profit Margin calculation
      yearLabel?: string; // Optional year label for visual grouping
      secFilingUrl?: string; // SEC filing URL for this quarter
    }>;
    currentTTM: number;
    previousTTM: number;
    growthRate: string;
    revenueGrowthYoY?: string; // For Rule of 40 to reference Revenue Growth
    useOperatingMargin?: boolean; // True for growth companies, false for mature (uses FCF margin)
  };
  epsRevisionsData?: {
    periods: Array<{
      period: string;
      period_key: string;
      up_7d: number;
      down_7d: number;
      up_30d: number;
      down_30d: number;
      score: number;
    }>;
    fiscal_context?: {
      current_quarter?: {
        fiscal_year: string;
        period: string;
      };
      next_quarter?: {
        fiscal_year: string;
        period: string;
      };
      current_year?: string;
      next_year?: string;
    };
  };
  analystRatingsData?: {
    current: {
      strong_buy: number;
      buy: number;
      hold: number;
      sell: number;
      strong_sell: number;
      total: number;
      date: string;
    };
    historical?: Array<{
      date: string;
      strong_buy: number;
      buy: number;
      hold: number;
      sell: number;
      strong_sell: number;
      total: number;
      net_bias?: number; // Calculated net bias score
    }>;
  };
  ratingMomentumData?: {
    current_rating: {
      score: number;
      date: string;
      strong_buy: number;
      buy: number;
      hold: number;
      sell: number;
      strong_sell: number;
    };
    momentum_details: {
      "1_month": {
        previous_score: number;
        current_score: number;
        change: number;
        previous_date: string;
      };
      "2_month": {
        previous_score: number;
        current_score: number;
        change: number;
        previous_date: string;
      };
    };
  };
  priceTargetData?: {
    current_price: number;
    target_price: number;
    upside_percent: number;
    target_consensus: number;
    target_median: number;
    target_used: string;
  };
  priceTargetDispersionData?: {
    target_low: number;
    target_high: number;
    target_median: number;
    target_consensus: number;
    target_mid_used: string;
    range: number;
    dispersion_ratio: number;
    dispersion_percent: number;
  };
  relativePerformanceData?: {
    window_days: number;
    stock_return: number;
    benchmark_return: number;
    relative_performance: number;
    outperformance_pct: number;
    ticker?: string; // Company ticker symbol
  };
  trendPersistenceData?: {
    close_above_50dma: boolean;
    close_above_200dma: boolean;
    "50dma_above_200dma": boolean;
    ma_alignment_score: string;
    days_above_200dma: number;
    time_above_200dma_pct: number;
    close_today: number;
    sma_50: number;
    sma_200: number;
  };
  volumeConfirmationData?: {
    net_participation: number;
    up_vol_ratio: number;
    down_vol_ratio: number;
    up_days: number;
    down_days: number;
    flat_days?: number;
    total_days?: number;
    avg_volume: number;
    signal: string;
  };
  trendQualityData?: {
    return: number;
    volatility: number;
    volatility_adjusted_momentum: number;
    window_days: number;
  };
  momentumAccelerationData?: {
    short_term_return_pct: number;
    medium_term_return_pct: number;
    acceleration: number;
    short_window_days: number;
    medium_window_days: number;
    label: string; // Signal label from backend (e.g., "Slowing momentum", "Strong acceleration")
  };
}

// Helper function to format currency values, switching to millions when values are too small
function formatCurrency(value: number): string {
  const absValue = Math.abs(value);
  // If value is less than 1 billion, display in millions with 2 decimal places
  if (absValue < 1000000000) {
    return `${(value / 1000000).toFixed(2)}M`;
  }
  // Otherwise display in billions with 2 decimal places
  return `${(value / 1000000000).toFixed(2)}B`;
}

function MetricItem({ label, value, grade, trend: _trend, description, explanation, calculationMethod, quarterlyData, epsRevisionsData, analystRatingsData, ratingMomentumData, priceTargetData, priceTargetDispersionData, relativePerformanceData, trendPersistenceData, volumeConfirmationData, trendQualityData, momentumAccelerationData }: MetricItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredHeaderIndex, setHoveredHeaderIndex] = useState<number | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showTrendQualityTooltip, setShowTrendQualityTooltip] = useState(false);
  
  // Debug logging for Relative Performance
  if (label === 'Relative Performance') {
    console.log('MetricItem rendered for Relative Performance:', {
      label,
      relativePerformanceData,
      isExpanded
    });
  }
  
  // Debug logging for Trend Persistence
  if (label === 'Trend Persistence') {
    console.log('MetricItem rendered for Trend Persistence:', {
      label,
      trendPersistenceData,
      isExpanded
    });
  }
  
  const getTrendColor = () => {
    // Color based on grade
    if (['F', 'D-', 'D', 'D+', 'C-', 'C', 'C+'].includes(grade)) return 'text-amber-500'; // Yellow for F, D, and C ranges
    if (['B-', 'B', 'B+', 'A-', 'A', 'A+'].includes(grade)) return 'text-emerald-600'; // Green for B- and above
    return 'text-gray-700'; // Default
  };

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-3 hover:bg-gray-50 -mx-4 px-4 rounded-lg transition-colors cursor-pointer"
      >
        <div className="flex flex-col">
          <span className="text-gray-500">{label}</span>
          {description && (
            <span className="text-gray-400 text-xs mt-0.5">{description}</span>
          )}
        </div>
        <span className="text-gray-425 text-sm px-3 py-1 bg-gray-50 rounded-lg min-w-[48px] text-center">
          {grade}
        </span>
      </button>
      
      {/* Explanation Dropdown */}
      <motion.div
        initial={false}
        animate={{
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ overflow: isExpanded ? 'visible' : 'hidden' }}
      >
        <div className="pb-3 pt-6 px-4 -mx-4 bg-gray-50/50 rounded-lg">
          {/* EPS Revisions Table */}
          {epsRevisionsData && (
            <div>
              {/* Table */}
              <div>
                {/* Header */}
                {(() => {
                  // Get simple header label
                  const getHeaderLabel = (periodKey: string, defaultLabel: string) => {
                    if (periodKey === '0q') return 'Current Quarter';
                    if (periodKey === '+1q') return 'Next Quarter';
                    if (periodKey === '0y') return 'Current Year';
                    if (periodKey === '+1y') return 'Next Year';
                    return defaultLabel;
                  };
                  
                  // Get tooltip text from fiscal context (matches backend fiscal_period_context structure)
                  const getTooltipText = (periodKey: string) => {
                    const fiscalContext = epsRevisionsData.fiscal_context;
                    if (!fiscalContext) return '';
                    
                    // Current Quarter: e.g., "Q3 FY26" (period first, shortened year)
                    if (periodKey === '0q' && fiscalContext.current_quarter) {
                      const { fiscal_year, period } = fiscalContext.current_quarter;
                      // Convert "2026" to "26" for shortened year format
                      const shortYear = fiscal_year.length >= 4 ? fiscal_year.slice(-2) : fiscal_year;
                      return `${period} FY${shortYear}`;
                    }
                    // Next Quarter: e.g., "Q4 FY26" (period first, shortened year)
                    if (periodKey === '+1q' && fiscalContext.next_quarter) {
                      const { fiscal_year, period } = fiscalContext.next_quarter;
                      // Convert "2026" to "26" for shortened year format
                      const shortYear = fiscal_year.length >= 4 ? fiscal_year.slice(-2) : fiscal_year;
                      return `${period} FY${shortYear}`;
                    }
                    // Current Year: e.g., "FY2026" (full year format)
                    if (periodKey === '0y' && fiscalContext.current_year) {
                      return `FY${fiscalContext.current_year}`;
                    }
                    // Next Year: e.g., "FY2027" (full year format)
                    if (periodKey === '+1y' && fiscalContext.next_year) {
                      return `FY${fiscalContext.next_year}`;
                    }
                    return '';
                  };
                  
                  // Ensure all four periods are present, even if missing from data
                  // Create a map of existing periods by period_key
                  const periodsMap = new Map(epsRevisionsData.periods.map(p => [p.period_key, p]));
                  
                  // Define the required period keys in order
                  const requiredPeriodKeys = ['0q', '+1q', '0y', '+1y'];
                  
                  // Build ordered periods array, filling in missing periods with defaults
                  const orderedPeriods = requiredPeriodKeys.map(periodKey => {
                    const existingPeriod = periodsMap.get(periodKey);
                    if (existingPeriod) {
                      return existingPeriod;
                    }
                    // Return default period with all zeros (will display as "-")
                    return {
                      period_key: periodKey,
                      period: getHeaderLabel(periodKey, ''),
                      up_7d: 0,
                      down_7d: 0,
                      up_30d: 0,
                      down_30d: 0,
                      score: 0
                    };
                  });
                  
                  return (
                    <div className="grid grid-cols-5 gap-3 px-4 py-2.5">
                      <div className="text-xs text-gray-500">Revision Type</div>
                      {orderedPeriods.map((period, idx) => {
                        const tooltipText = getTooltipText(period.period_key);
                        const isHovered = hoveredHeaderIndex === idx;
                        return (
                          <div 
                            key={period.period_key} 
                            className="text-xs text-gray-500 text-center relative z-10"
                            onMouseEnter={() => setHoveredHeaderIndex(idx)}
                            onMouseLeave={() => setHoveredHeaderIndex(null)}
                          >
                            {getHeaderLabel(period.period_key, period.period)}
                            {tooltipText && (
                              <div 
                                className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap transition-opacity pointer-events-none z-20 ${
                                  isHovered ? 'opacity-100' : 'opacity-0'
                                }`}
                              >
                                {tooltipText}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              
                {/* Up Last 30 Days */}
                {(() => {
                  // Ensure all four periods are present, even if missing from data
                  // Create a map of existing periods by period_key
                  const periodsMap = new Map(epsRevisionsData.periods.map(p => [p.period_key, p]));
                  
                  // Define the required period keys in order
                  const requiredPeriodKeys = ['0q', '+1q', '0y', '+1y'];
                  
                  // Build ordered periods array, filling in missing periods with defaults
                  const orderedPeriods = requiredPeriodKeys.map(periodKey => {
                    const existingPeriod = periodsMap.get(periodKey);
                    if (existingPeriod) {
                      return existingPeriod;
                    }
                    // Return default period with all zeros (will display as "-")
                    return {
                      period_key: periodKey,
                      period: '',
                      up_7d: 0,
                      down_7d: 0,
                      up_30d: 0,
                      down_30d: 0,
                      score: 0
                    };
                  });
                  
                  return (
                    <>
                      <div className="grid grid-cols-5 gap-3 px-4 py-2.5 border-b border-gray-100">
                        <div className="text-sm text-gray-600">Upward Revisions</div>
                        {orderedPeriods.map((period) => (
                          <div key={period.period_key} className="text-sm text-gray-700 text-center">
                            {period.up_30d || '-'}
                          </div>
                        ))}
                      </div>
                    
                      {/* Down Last 30 Days */}
                      <div className="grid grid-cols-5 gap-3 px-4 py-2.5">
                        <div className="text-sm text-gray-600">Downward Revisions</div>
                        {orderedPeriods.map((period) => (
                          <div key={period.period_key} className="text-sm text-gray-700 text-center">
                            {period.down_30d || '-'}
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          
          {/* Analyst Ratings Display */}
          {analystRatingsData && (
            <div className="isolate">
              {/* Current Rating Distribution Bars */}
              <div className="space-y-3 mb-6">
                {/* Strong Buy */}
                <div className="flex items-center gap-3 relative group/rating-row">
                  <div className="text-sm text-gray-600 w-24">Strong Buy</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${(analystRatingsData.current.strong_buy / analystRatingsData.current.total) * 100}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-700 w-8 text-right">{analystRatingsData.current.strong_buy}</div>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/rating-row:opacity-100 transition-opacity pointer-events-none z-10">
                    {((analystRatingsData.current.strong_buy / analystRatingsData.current.total) * 100).toFixed(1)}%
                  </div>
                </div>
                
                {/* Buy */}
                <div className="flex items-center gap-3 relative group/rating-row">
                  <div className="text-sm text-gray-600 w-24">Buy</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${(analystRatingsData.current.buy / analystRatingsData.current.total) * 100}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-700 w-8 text-right">{analystRatingsData.current.buy}</div>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/rating-row:opacity-100 transition-opacity pointer-events-none z-10">
                    {((analystRatingsData.current.buy / analystRatingsData.current.total) * 100).toFixed(1)}%
                  </div>
                </div>
                
                {/* Hold */}
                <div className="flex items-center gap-3 relative group/rating-row">
                  <div className="text-sm text-gray-600 w-24">Hold</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gray-400 rounded-full transition-all duration-500"
                      style={{ width: `${(analystRatingsData.current.hold / analystRatingsData.current.total) * 100}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-700 w-8 text-right">{analystRatingsData.current.hold}</div>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/rating-row:opacity-100 transition-opacity pointer-events-none z-10">
                    {((analystRatingsData.current.hold / analystRatingsData.current.total) * 100).toFixed(1)}%
                  </div>
                </div>
                
                {/* Sell */}
                <div className="flex items-center gap-3 relative group/rating-row">
                  <div className="text-sm text-gray-600 w-24">Sell</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-rose-400 rounded-full transition-all duration-500"
                      style={{ width: `${(analystRatingsData.current.sell / analystRatingsData.current.total) * 100}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-700 w-8 text-right">{analystRatingsData.current.sell}</div>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/rating-row:opacity-100 transition-opacity pointer-events-none z-10">
                    {((analystRatingsData.current.sell / analystRatingsData.current.total) * 100).toFixed(1)}%
                  </div>
                </div>
                
                {/* Strong Sell */}
                <div className="flex items-center gap-3 relative group/rating-row">
                  <div className="text-sm text-gray-600 w-24">Strong Sell</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-rose-500 rounded-full transition-all duration-500"
                      style={{ width: `${(analystRatingsData.current.strong_sell / analystRatingsData.current.total) * 100}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-700 w-8 text-right">{analystRatingsData.current.strong_sell}</div>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/rating-row:opacity-100 transition-opacity pointer-events-none z-10">
                    {((analystRatingsData.current.strong_sell / analystRatingsData.current.total) * 100).toFixed(1)}%
                  </div>
                </div>
                
                {/* Total */}
                <div className="flex items-center gap-3 pt-2 mt-2 border-t border-gray-100">
                  <div className="text-sm font-medium text-gray-700 w-24">Total Analysts</div>
                  <div className="flex-1"></div>
                  <div className="text-sm font-medium text-gray-700 w-8 text-right">{analystRatingsData.current.total}</div>
                </div>
              </div>

              {/* Historical Chart if multiple snapshots available */}
              {analystRatingsData.historical && analystRatingsData.historical.length > 1 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-4">Historical Rating Position Trend</p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={analystRatingsData.historical}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          tickLine={false}
                          axisLine={{ stroke: '#e5e7eb' }}
                          tickFormatter={(value) => {
                            // Format date: "2026-01-01" -> "Jan 2026"
                            const date = new Date(value);
                            return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                          }}
                        />
                        <YAxis 
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          domain={[-1, 1]}
                          tickFormatter={(value) => {
                            // Format as percentage: 0.5 -> "+50%"
                            const percent = (value * 100).toFixed(0);
                            return value >= 0 ? `+${percent}%` : `${percent}%`;
                          }}
                        />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              const netBias = data.net_bias ?? 0;
                              const netBiasPercent = (netBias * 100).toFixed(1);
                              return (
                                <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
                                  <p className="text-xs text-gray-500 mb-2">
                                    {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </p>
                                  <div className="space-y-1 text-xs">
                                    <div className="flex justify-between gap-4">
                                      <span className="text-gray-600">Strong Buy:</span>
                                      <span className="text-gray-700 font-medium">{data.strong_buy}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-gray-600">Buy:</span>
                                      <span className="text-gray-700 font-medium">{data.buy}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-gray-600">Hold:</span>
                                      <span className="text-gray-700 font-medium">{data.hold}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-gray-600">Sell:</span>
                                      <span className="text-gray-700 font-medium">{data.sell}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-gray-600">Strong Sell:</span>
                                      <span className="text-gray-700 font-medium">{data.strong_sell}</span>
                                    </div>
                                    <div className="pt-2 mt-2 border-t border-gray-200">
                                      <div className="flex justify-between gap-4">
                                        <span className="text-gray-700 font-medium">Net Bias:</span>
                                        <span className={`font-semibold ${netBias >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                          {netBias >= 0 ? '+' : ''}{netBiasPercent}%
                                        </span>
                                      </div>
                                      <div className="flex justify-between gap-4 mt-1">
                                        <span className="text-gray-600 text-xs">Total:</span>
                                        <span className="text-gray-700 text-xs font-medium">{data.total}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="net_bias" 
                          stroke="#374151" 
                          strokeWidth={2}
                          dot={{ fill: '#374151', r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    Net Bias = (Strong Buy + 0.5×Buy - 0.5×Sell - Strong Sell) / Total
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Rating Momentum Display */}
          {ratingMomentumData && (
            <div>
              <div className="space-y-4">
                {/* Rating Scale Context */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-xs text-gray-500 mb-3">Rating Score Scale (1-5)</div>
                  <div className="relative">
                    {/* Scale bar */}
                    <div className="h-2 bg-gradient-to-r from-rose-200 via-gray-200 to-emerald-200 rounded-full"></div>
                    
                    {/* Current position marker */}
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-0.5 h-6 bg-gray-700"
                      style={{ left: `${((ratingMomentumData.current_rating.score - 1) / 4) * 100}%` }}
                    >
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-sm font-medium text-gray-700 whitespace-nowrap">
                        {ratingMomentumData.current_rating.score.toFixed(2)}
                      </div>
                    </div>
                    
                    {/* Scale labels */}
                    <div className="flex justify-between mt-2 text-xs text-gray-400">
                      <span>1.0<br/>Strong Sell</span>
                      <span className="text-center">3.0<br/>Hold</span>
                      <span className="text-right">5.0<br/>Strong Buy</span>
                    </div>
                  </div>
                </div>
                
                {/* Momentum Timeline */}
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-3">Rating Progression</div>
                  <div className="flex items-center gap-2">
                    {/* 2-month ago */}
                    <div className="flex-1 text-center">
                      <div className="text-xs text-gray-400 mb-1">
                        {(() => {
                          // Parse date string explicitly to avoid timezone issues
                          const dateParts = ratingMomentumData.momentum_details["2_month"].previous_date.split('-');
                          const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                          return date.toLocaleDateString('en-US', { month: 'short' });
                        })()}
                      </div>
                      <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
                        {ratingMomentumData.momentum_details["2_month"].previous_score.toFixed(2)}
                      </div>
                    </div>
                    
                    <div className="text-gray-300">→</div>
                    
                    {/* 1-month ago */}
                    <div className="flex-1 text-center">
                      <div className="text-xs text-gray-400 mb-1">
                        {(() => {
                          // Parse date string explicitly to avoid timezone issues
                          const dateParts = ratingMomentumData.momentum_details["1_month"].previous_date.split('-');
                          const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                          return date.toLocaleDateString('en-US', { month: 'short' });
                        })()}
                      </div>
                      <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
                        {ratingMomentumData.momentum_details["1_month"].previous_score.toFixed(2)}
                      </div>
                    </div>
                    
                    <div className="text-gray-300">→</div>
                    
                    {/* Current */}
                    <div className="flex-1 text-center">
                      <div className="text-xs text-gray-400 mb-1">
                        {(() => {
                          // Parse date string explicitly to avoid timezone issues
                          // Format: "2026-01-01" -> parse as local date
                          const dateParts = ratingMomentumData.current_rating.date.split('-');
                          const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                          return date.toLocaleDateString('en-US', { month: 'short' });
                        })()}
                      </div>
                      <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm font-medium text-emerald-700">
                        {ratingMomentumData.current_rating.score.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Momentum Changes */}
                <div className="grid grid-cols-2 gap-3">
                  {/* 1-Month Change */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">1-Month Change</div>
                    <div className={`text-lg font-medium ${ratingMomentumData.momentum_details["1_month"].change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {ratingMomentumData.momentum_details["1_month"].change >= 0 ? '+' : ''}{ratingMomentumData.momentum_details["1_month"].change.toFixed(2)}
                    </div>
                  </div>
                  
                  {/* 2-Month Change */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">2-Month Change</div>
                    <div className={`text-lg font-medium ${ratingMomentumData.momentum_details["2_month"].change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {ratingMomentumData.momentum_details["2_month"].change >= 0 ? '+' : ''}{ratingMomentumData.momentum_details["2_month"].change.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Price Target Display */}
          {priceTargetData && (
            <div>
              <div className="grid grid-cols-4 gap-0">
                <div>
                  <div className="text-xs text-gray-500 mb-2">Current</div>
                  <div className="text-gray-900">${priceTargetData.current_price.toFixed(2)}</div>
                </div>
                
                <div className="-ml-6">
                  <div className="text-xs text-gray-500 mb-2">Target (Median)</div>
                  <div className="text-gray-900">${priceTargetData.target_price.toFixed(2)}</div>
                </div>
                
                <div className="-ml-6">
                  <div className="text-xs text-gray-500 mb-2">Consensus (Avg)</div>
                  <div className="text-gray-900">
                    {priceTargetData.target_consensus != null 
                      ? `$${priceTargetData.target_consensus.toFixed(2)}`
                      : 'N/A'}
                  </div>
                </div>
                
                <div className="-ml-4">
                  <div className="text-xs text-gray-500 mb-2">Upside</div>
                  <div className={`${priceTargetData.upside_percent >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {priceTargetData.upside_percent >= 0 ? '+' : ''}{priceTargetData.upside_percent.toFixed(2)}%
                  </div>
                </div>
              </div>
              
              {/* Calculation */}
              <div className="pt-4 mt-4">
                <div className="text-xs text-gray-400 mb-2">Calculation:</div>
                <div className="text-sm text-gray-600 mb-1">
                  (${priceTargetData.target_price.toFixed(2)} - ${priceTargetData.current_price.toFixed(2)}) / ${priceTargetData.current_price.toFixed(2)} = {priceTargetData.upside_percent.toFixed(2)}%
                </div>
                <div className="text-xs text-gray-400">
                  (Target - Current) / Current
                </div>
              </div>
            </div>
          )}
          
          {/* Price Target Dispersion Display */}
          {priceTargetDispersionData && (
            <div>
              {/* Top section with labels and bar */}
              <div className="mb-6">
                <div className="flex items-start">
                  {/* Left side: Low Target and High Target with bar between them */}
                  <div className="flex-1 max-w-[65%]">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Low Target</div>
                        <div className="text-gray-900">${priceTargetDispersionData.target_low.toFixed(2)}</div>
                      </div>
                      
                      <div>
                        <div className="text-xs text-gray-500 mb-1">High Target</div>
                        <div className="text-gray-900">${priceTargetDispersionData.target_high.toFixed(2)}</div>
                      </div>
                    </div>
                    
                    {/* Visual range line */}
                    <div className="relative pt-2 pb-6">
                      <div className="relative h-0.5 bg-gray-300">
                        {/* Median dot */}
                        <div 
                          className="absolute top-1/2 w-3 h-3 bg-gray-900 rounded-full -translate-x-1/2 -translate-y-1/2"
                          style={{ 
                            left: `${((priceTargetDispersionData.target_median - priceTargetDispersionData.target_low) / (priceTargetDispersionData.target_high - priceTargetDispersionData.target_low)) * 100}%`
                          }}
                        />
                      </div>
                      
                      {/* Median label below */}
                      <div 
                        className="absolute top-full mt-2 text-sm text-gray-600 -translate-x-1/2 whitespace-nowrap"
                        style={{ 
                          left: `${((priceTargetDispersionData.target_median - priceTargetDispersionData.target_low) / (priceTargetDispersionData.target_high - priceTargetDispersionData.target_low)) * 100}%`
                        }}
                      >
                        ${priceTargetDispersionData.target_median.toFixed(2)} (Median)
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side: Dispersion */}
                  <div className="text-right ml-auto">
                    <div className="text-xs text-gray-500 mb-1">Dispersion</div>
                    <div className="relative inline-block">
                      <div 
                        className="font-medium cursor-help group/dispersion"
                        style={{ color: getDispersionColor(priceTargetDispersionData.dispersion_percent) }}
                      >
                        {priceTargetDispersionData.dispersion_percent.toFixed(2)}%
                        {/* Tooltip */}
                        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover/dispersion:opacity-100 group-hover/dispersion:visible transition-all duration-200 whitespace-nowrap pointer-events-none z-50">
                          Higher dispersion indicates less analyst agreement
                          {/* Arrow */}
                          <div className="absolute top-full right-4 -mt-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{getDispersionInterpretation(priceTargetDispersionData.dispersion_percent)}</div>
                  </div>
                </div>
              </div>
              
              {/* Calculation - no border */}
              <div className="pt-4">
                <div className="text-xs text-gray-400 mb-2">Calculation:</div>
                <div className="text-sm text-gray-600 mb-1">
                  (${priceTargetDispersionData.target_high.toFixed(2)} - ${priceTargetDispersionData.target_low.toFixed(2)}) / ${priceTargetDispersionData.target_median.toFixed(2)} = {priceTargetDispersionData.dispersion_percent.toFixed(2)}%
                </div>
                <div className="text-xs text-gray-400">
                  (High - Low) / Median
                </div>
              </div>
            </div>
          )}
          
          {/* Relative Performance Display */}
          {relativePerformanceData && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-4">
                Performance Metrics
              </p>
              
              <div className="space-y-3">
                {/* Window */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Analysis Window</span>
                  <div className="relative">
                    <span 
                      className="text-sm text-gray-700 cursor-help"
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                    >
                      1 Year
                    </span>
                    {showTooltip && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-10">
                        {relativePerformanceData.window_days} trading days
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Stock Return */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">
                    Stock Return{relativePerformanceData.ticker ? ` (${relativePerformanceData.ticker})` : ''}
                  </span>
                  <span className={`text-sm ${
                    relativePerformanceData.stock_return >= 0 ? 'text-emerald-600' : 'text-rose-600'
                  }`}>
                    {relativePerformanceData.stock_return >= 0 ? '+' : ''}{relativePerformanceData.stock_return.toFixed(2)}%
                  </span>
                </div>
                
                {/* Benchmark Return */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Benchmark Return (S&P 500)</span>
                  <span className="text-sm text-gray-700">
                    {relativePerformanceData.benchmark_return >= 0 ? '+' : ''}{relativePerformanceData.benchmark_return.toFixed(2)}%
                  </span>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-3"></div>
                
                {/* Relative Strength */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Relative Strength</span>
                  <span className="text-sm text-gray-700 font-medium">{relativePerformanceData.relative_performance.toFixed(3)}</span>
                </div>
                
                {/* Outperformance/Underperformance */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">
                    {relativePerformanceData.outperformance_pct >= 0 ? 'Outperformance' : 'Underperformance'}
                  </span>
                  <span className={`text-sm font-medium ${
                    relativePerformanceData.outperformance_pct >= 0 ? 'text-emerald-600' : 'text-rose-600'
                  }`}>
                    {relativePerformanceData.outperformance_pct >= 0 ? '+' : ''}{relativePerformanceData.outperformance_pct.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Trend Persistence Display */}
          {trendPersistenceData && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-4">
                Moving Average Analysis
              </p>
              
              <div className="space-y-3">
                {/* MA Alignment Score */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">MA Alignment Score</span>
                  <span className={`text-sm font-medium ${
                    (() => {
                      const scoreMatch = trendPersistenceData.ma_alignment_score.match(/^(\d+)\/3$/);
                      const numerator = scoreMatch ? parseInt(scoreMatch[1], 10) : 0;
                      return numerator <= 1 ? 'text-red-600' : 'text-emerald-600';
                    })()
                  }`}>{trendPersistenceData.ma_alignment_score}</span>
                </div>
                
                {/* Current Price */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Current Price</span>
                  <span className="text-sm text-gray-700 font-medium">${trendPersistenceData.close_today.toFixed(2)}</span>
                </div>
                
                {/* 50-Day MA */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">50-Day MA</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 font-medium">${trendPersistenceData.sma_50.toFixed(2)}</span>
                    {trendPersistenceData.close_above_50dma && (
                      <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                
                {/* 200-Day MA */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">200-Day MA</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 font-medium">${trendPersistenceData.sma_200.toFixed(2)}</span>
                    {trendPersistenceData.close_above_200dma && (
                      <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-3"></div>
                
                {/* Days Above 200-Day MA */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Days Above 200-Day MA</span>
                  <span className="text-sm text-gray-700 font-medium">{trendPersistenceData.days_above_200dma} / 120 days</span>
                </div>
                
                {/* Time Above 200-Day MA */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Time Above 200-Day MA</span>
                  <span className={`text-sm font-medium ${
                    trendPersistenceData.time_above_200dma_pct < 30 
                      ? 'text-red-600' 
                      : trendPersistenceData.time_above_200dma_pct < 60 
                        ? 'text-yellow-600' 
                        : 'text-emerald-600'
                  }`}>{trendPersistenceData.time_above_200dma_pct.toFixed(1)}%</span>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-3"></div>
                
                {/* MA Alignment Indicators */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {trendPersistenceData.close_above_50dma ? (
                      <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="text-xs text-gray-500">Price above 50-day MA</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {trendPersistenceData.close_above_200dma ? (
                      <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="text-xs text-gray-500">Price above 200-day MA</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {trendPersistenceData["50dma_above_200dma"] ? (
                      <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="text-xs text-gray-500">50-day MA above 200-day MA</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Volume Confirmation Display */}
          {volumeConfirmationData && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-4">
                Volume Analysis
              </p>
              
              <div className="space-y-3">
                {/* Signal */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Signal</span>
                  <span className={`text-sm font-medium capitalize ${
                    volumeConfirmationData.signal.toLowerCase() === 'distribution' 
                      ? 'text-rose-600' 
                      : 'text-emerald-600'
                  }`}>{volumeConfirmationData.signal}</span>
                </div>
                
                {/* Volume Imbalance */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Volume Imbalance</span>
                  <span className={`text-sm font-medium ${
                    volumeConfirmationData.net_participation < 0 
                      ? 'text-rose-600' 
                      : 'text-emerald-600'
                  }`}>{(volumeConfirmationData.net_participation * 100).toFixed(1)}%</span>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-3"></div>
                
                {/* Up Days vs Down Days */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Up Days</span>
                  <span className="text-sm text-gray-700">{volumeConfirmationData.up_days} days</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Down Days</span>
                  <span className="text-sm text-gray-700">{volumeConfirmationData.down_days} days</span>
                </div>
                
                {/* Flat Days - only show if flat_days exists and is greater than 0 */}
                {volumeConfirmationData.flat_days !== undefined && volumeConfirmationData.flat_days > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Flat Days</span>
                    <span className="text-sm text-gray-700">{volumeConfirmationData.flat_days} days</span>
                  </div>
                )}
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-3"></div>
                
                {/* Volume Ratios */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Volume on Up Days</span>
                  <span className="text-sm text-gray-700 font-medium">
                    {(() => {
                      const upVolPercent = (volumeConfirmationData.up_vol_ratio - 1) * 100;
                      const formatted = upVolPercent.toFixed(1);
                      return `${upVolPercent >= 0 ? '+' : ''}${formatted}% vs average`;
                    })()}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Volume on Down Days</span>
                  <span className="text-sm text-gray-700 font-medium">{((volumeConfirmationData.down_vol_ratio - 1) * 100).toFixed(1)}% vs average</span>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-3"></div>
                
                {/* Average Volume */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Average Volume</span>
                  <span className="text-sm text-gray-700">{(volumeConfirmationData.avg_volume / 1000000).toFixed(1)}M shares</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Trend Quality Display */}
          {trendQualityData && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-4">
                Volatility-Adjusted Momentum Analysis
              </p>
              
              <div className="space-y-3">
                {/* Return */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Return</span>
                  <span className={`text-sm font-medium ${
                    trendQualityData.return >= 0 ? 'text-emerald-600' : 'text-rose-600'
                  }`}>
                    {trendQualityData.return >= 0 ? '+' : ''}{trendQualityData.return.toFixed(2)}%
                  </span>
                </div>
                
                {/* Volatility */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Volatility</span>
                  <span className="text-sm text-gray-700 font-medium">{trendQualityData.volatility.toFixed(2)}%</span>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-3"></div>
                
                {/* Volatility-Adjusted Momentum */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Volatility-Adjusted Momentum</span>
                  <span className={`text-sm font-medium ${
                    trendQualityData.volatility_adjusted_momentum < 0 
                      ? 'text-rose-600' 
                      : trendQualityData.volatility_adjusted_momentum < 0.5 
                        ? 'text-amber-500' 
                        : 'text-emerald-600'
                  }`}>
                    {trendQualityData.volatility_adjusted_momentum.toFixed(3)}
                  </span>
                </div>
                
                {/* Analysis Window */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Analysis Window</span>
                  <div className="relative">
                    <span 
                      className="text-sm text-gray-700 font-medium cursor-help"
                      onMouseEnter={() => setShowTrendQualityTooltip(true)}
                      onMouseLeave={() => setShowTrendQualityTooltip(false)}
                    >
                      6 months
                    </span>
                    {showTrendQualityTooltip && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-10">
                        {trendQualityData.window_days} trading days
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Momentum Acceleration expandable section */}
          {momentumAccelerationData && isExpanded && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-4">
                Calculation Data
              </p>
              
              <div className="space-y-2">
                {/* Short-Term Return */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Short-Term Return ({Math.round(momentumAccelerationData.short_window_days / 21)}M)</span>
                  <span className="text-sm text-gray-700 font-medium">{momentumAccelerationData.short_term_return_pct.toFixed(2)}%</span>
                </div>
                
                {/* Medium-Term Return */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Medium-Term Return ({Math.round(momentumAccelerationData.medium_window_days / 21)}M)</span>
                  <span className="text-sm text-gray-700 font-medium">{momentumAccelerationData.medium_term_return_pct.toFixed(2)}%</span>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-2"></div>
                
                {/* Acceleration */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Acceleration</span>
                  <span className={`text-sm font-medium ${
                    momentumAccelerationData.acceleration > 0 ? 'text-emerald-600' : 
                    momentumAccelerationData.acceleration < 0 ? 'text-rose-600' : 
                    'text-gray-700'
                  }`}>
                    {momentumAccelerationData.acceleration > 0 ? '+' : ''}{momentumAccelerationData.acceleration.toFixed(2)}%
                  </span>
                </div>
                
                {/* Signal */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Signal</span>
                  <span className="text-sm text-gray-700 font-medium">{momentumAccelerationData.label}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Only show label/value/explanation if there's no quarterly data, no EPS revisions, no analyst ratings, no rating momentum, no price target data, no price target dispersion data, no relative performance data, no trend persistence data, no volume confirmation data, no trend quality data, and no momentum acceleration data */}
          {!quarterlyData && !epsRevisionsData && !analystRatingsData && !ratingMomentumData && !priceTargetData && !priceTargetDispersionData && !relativePerformanceData && !trendPersistenceData && !volumeConfirmationData && !trendQualityData && !momentumAccelerationData && explanation && (
            <>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-gray-400 text-sm">
                  {label}
                  {calculationMethod && <span className="text-gray-400 ml-1">{calculationMethod}</span>}
                  :
                </span>
                <span className={`font-medium ${getTrendColor()}`}>{value}</span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed whitespace-pre-line">
                {explanation}
              </p>
            </>
          )}
          
          {/* Quarterly Revenue Breakdown */}
          {quarterlyData && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">
                {label === 'Gross Margin' 
                  ? 'Quarterly Gross Margin Breakdown' 
                  : label === 'Rule of 40'
                  ? (quarterlyData.useOperatingMargin ? 'Quarterly Operating Margin' : 'Quarterly FCF Margin')
                  : label === 'Free Cash Flow Margin'
                  ? 'Quarterly FCF Margin Breakdown'
                  : label === 'Operating Margin'
                  ? 'Quarterly Operating Margin Breakdown'
                  : label === 'Net Profit Margin'
                  ? 'Quarterly Net Profit Margin Breakdown'
                  : 'Quarterly Revenue Breakdown'}
              </p>
              
              {/* Quarters Display */}
              <div className="space-y-2 mb-4">
                {quarterlyData.quarters.map((q, idx) => (
                  <div key={idx}>
                    {q.yearLabel && (
                      <div className="text-xs text-gray-300 font-medium mt-3 mb-1">{q.yearLabel}</div>
                    )}
                    {q.secFilingUrl ? (
                      <a
                        href={q.secFilingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between py-1.5 px-6 -mx-6 rounded-lg hover:bg-gray-100 transition-colors group cursor-pointer"
                      >
                        <span className="text-sm text-gray-500 group-hover:text-gray-700 transition-colors">
                          {q.quarter}
                        </span>
                        {label === 'Gross Margin' && q.grossProfit !== undefined ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Gross Profit</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.grossProfit)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {(q.revenue > 0 ? (q.grossProfit / q.revenue * 100).toFixed(1) : '0.0')}%
                              </div>
                            </div>
                          </div>
                        ) : label === 'Rule of 40' && (q.operatingIncome !== undefined || q.fcf !== undefined) ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">
                                {quarterlyData.useOperatingMargin ? 'Op. Income' : 'FCF'}
                              </div>
                              <div className="text-sm text-gray-700 font-medium">
                                ${formatCurrency(quarterlyData.useOperatingMargin ? q.operatingIncome! : q.fcf!)}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {((quarterlyData.useOperatingMargin ? q.operatingIncome! : q.fcf!) / q.revenue * 100).toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        ) : label === 'Free Cash Flow Margin' && q.fcf !== undefined ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">FCF</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.fcf)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {(q.fcf / q.revenue * 100).toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        ) : label === 'Operating Margin' && q.operatingIncome !== undefined ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Op. Income</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.operatingIncome)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {(q.revenue > 0 ? (q.operatingIncome / q.revenue * 100).toFixed(1) : '0.0')}%
                              </div>
                            </div>
                          </div>
                        ) : label === 'Net Profit Margin' && q.netIncome !== undefined ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Net Income</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.netIncome)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {(q.revenue > 0 ? (q.netIncome / q.revenue * 100).toFixed(1) : '0.0')}%
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-700 font-medium">
                            ${formatCurrency(q.revenue)}
                          </span>
                        )}
                      </a>
                    ) : (
                      <div className="flex items-center justify-between py-1.5">
                        <span className="text-sm text-gray-500">{q.quarter}</span>
                        {label === 'Gross Margin' && q.grossProfit !== undefined ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Gross Profit</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.grossProfit)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {(q.revenue > 0 ? (q.grossProfit / q.revenue * 100).toFixed(1) : '0.0')}%
                              </div>
                            </div>
                          </div>
                        ) : label === 'Rule of 40' && (q.operatingIncome !== undefined || q.fcf !== undefined) ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">
                                {quarterlyData.useOperatingMargin ? 'Op. Income' : 'FCF'}
                              </div>
                              <div className="text-sm text-gray-700 font-medium">
                                ${formatCurrency(quarterlyData.useOperatingMargin ? q.operatingIncome! : q.fcf!)}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {((quarterlyData.useOperatingMargin ? q.operatingIncome! : q.fcf!) / q.revenue * 100).toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        ) : label === 'Free Cash Flow Margin' && q.fcf !== undefined ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">FCF</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.fcf)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {(q.fcf / q.revenue * 100).toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        ) : label === 'Operating Margin' && q.operatingIncome !== undefined ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Op. Income</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.operatingIncome)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {(q.revenue > 0 ? (q.operatingIncome / q.revenue * 100).toFixed(1) : '0.0')}%
                              </div>
                            </div>
                          </div>
                        ) : label === 'Net Profit Margin' && q.netIncome !== undefined ? (
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Net Income</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.netIncome)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Revenue</div>
                              <div className="text-sm text-gray-700 font-medium">${formatCurrency(q.revenue)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Margin</div>
                              <div className="text-sm text-gray-700 font-medium">
                                {(q.revenue > 0 ? (q.netIncome / q.revenue * 100).toFixed(1) : '0.0')}%
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-700 font-medium">
                            ${formatCurrency(q.revenue)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* TTM Calculation */}
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                {label === 'Gross Margin' ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total Gross Profit (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.previousTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total Revenue (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.currentTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600 font-medium">Gross Margin (TTM)</span>
                      <span className={`text-sm font-medium ${quarterlyData.growthRate?.startsWith('-') ? 'text-red-600' : 'text-emerald-600'}`}>
                        {quarterlyData.growthRate}
                      </span>
                    </div>
                  </>
                ) : label === 'Rule of 40' ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">
                        Total {quarterlyData.useOperatingMargin ? 'Operating Income' : 'FCF'} (Last 4 Qtrs)
                      </span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.previousTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total Revenue (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.currentTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600 font-medium">
                        {quarterlyData.useOperatingMargin ? 'Operating Margin (TTM)' : 'FCF Margin (TTM)'}
                      </span>
                      <span className="text-sm text-gray-700 font-medium">
                        {quarterlyData.growthRate}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 font-medium">Revenue Growth (YoY TTM)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        {quarterlyData.revenueGrowthYoY}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-700 font-semibold">Rule of 40 Score</span>
                      <span className={`text-sm font-semibold ${
                        value && parseFloat(value) < 0
                          ? 'text-red-600'
                          : 'text-emerald-600'
                      }`}>
                        {value}
                      </span>
                    </div>
                  </>
                ) : label === 'Free Cash Flow Margin' ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total FCF (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.previousTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total Revenue (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.currentTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600 font-medium">FCF Margin (TTM)</span>
                      <span className="text-sm text-emerald-600 font-medium">
                        {quarterlyData.growthRate}
                      </span>
                    </div>
                  </>
                ) : label === 'Operating Margin' ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total Operating Income (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.previousTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total Revenue (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.currentTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600 font-medium">Operating Margin (TTM)</span>
                      <span className={`text-sm font-medium ${
                        quarterlyData.growthRate && parseFloat(quarterlyData.growthRate.replace('%', '')) < 0
                          ? 'text-red-600'
                          : 'text-emerald-600'
                      }`}>
                        {quarterlyData.growthRate}
                      </span>
                    </div>
                  </>
                ) : label === 'Net Profit Margin' ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total Net Income (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.previousTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Total Revenue (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.currentTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600 font-medium">Net Profit Margin (TTM)</span>
                      <span className={`text-sm font-medium ${
                        quarterlyData.growthRate && parseFloat(quarterlyData.growthRate.replace('%', '')) < 0
                          ? 'text-red-600'
                          : 'text-emerald-600'
                      }`}>
                        {quarterlyData.growthRate}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Current TTM (Last 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.currentTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Previous TTM (Prior 4 Qtrs)</span>
                      <span className="text-sm text-gray-700 font-medium">
                        ${formatCurrency(quarterlyData.previousTTM)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600 font-medium">Revenue Growth (TTM)</span>
                      <span className={`text-sm font-medium ${
                        quarterlyData.growthRate && parseFloat(quarterlyData.growthRate.replace('%', '')) < 0
                          ? 'text-red-600'
                          : 'text-emerald-600'
                      }`}>
                        {quarterlyData.growthRate}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

interface ScoreCardProps {
  title: string;
  score: number;
  metrics: MetricItemProps[];
  referenceMetrics?: MetricItemProps[];
  delay: number;
  isOpen: boolean;
  onToggle: () => void;
}

function ScoreCard({ title, score, metrics, referenceMetrics, delay, isOpen, onToggle }: ScoreCardProps) {
  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-rose-600';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay }}
      className={`bg-white rounded-3xl overflow-hidden transition-colors group ${!isOpen ? 'hover:bg-gray-100' : ''}`}
    >
      {/* Header - Clickable (includes progress bar) */}
      <button
        onClick={onToggle}
        className="w-full text-left"
      >
        <div className="p-10 pb-6 flex items-center justify-between">
          <div className="flex items-baseline gap-4">
            <h3 className="text-gray-700">{title}</h3>
            <div className={`text-5xl ${getScoreColor(score)}`}>
              {score}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-10 pb-10">
          <div className={`h-1.5 rounded-full overflow-hidden transition-colors ${!isOpen ? 'group-hover:bg-gray-200 bg-gray-50' : 'bg-gray-50'}`}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${score}%` }}
              transition={{ duration: 1.2, delay: delay + 0.3, ease: "easeOut" }}
              className={`h-full rounded-full ${
                score >= 70 ? 'bg-emerald-600' : score >= 40 ? 'bg-amber-600' : 'bg-rose-600'
              }`}
            />
          </div>
        </div>
      </button>

      {/* Collapsible Metrics */}
      <motion.div
        initial={false}
        animate={{
          height: isOpen ? 'auto' : 0,
          opacity: isOpen ? 1 : 0
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <div className="px-10 pt-6 pb-10 space-y-0 divide-y divide-gray-100">
          {metrics.map((metric, index) => (
            <MetricItem key={index} {...metric} />
          ))}
          
          {/* Reference Metrics (e.g., excluded Altman Z) */}
          {referenceMetrics && referenceMetrics.length > 0 && (
            <>
              <div className="pt-6 pb-2 px-10">
                <p className="text-gray-400 text-sm italic">Reference Only (Not Weighted)</p>
              </div>
              {referenceMetrics.map((metric, index) => (
                <div key={`ref-${index}`} className="opacity-50">
                  <MetricItem {...metric} />
                </div>
              ))}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

/**
 * Split a catalyst / card title into at most two SHORT lines for chart labels.
 * Truncating aggressively keeps label boxes narrow enough for collision avoidance
 * to give clean results even on dense charts.
 */
function splitChartEventLabel(
  title: string,
  compact = false,
): { line1: string; line2: string | null } {
  const t = title.replace(/\s+/g, ' ').trim();
  if (!t) return { line1: 'Notable', line2: null };
  if (compact) {
    const MAX = 22;
    const s = t.length > MAX ? `${t.slice(0, MAX - 1)}…` : t;
    return { line1: s, line2: null };
  }
  const MAX = 28;       // max total chars before truncation
  const SPLIT_AT = 14;  // target chars for line 1
  const s = t.length > MAX ? `${t.slice(0, MAX - 1)}…` : t;
  if (s.length <= SPLIT_AT) return { line1: s, line2: null };
  // Split at a word boundary near the midpoint
  const mid = Math.floor(s.length / 2);
  let cut = s.lastIndexOf(' ', mid + 4);
  if (cut < 4) cut = s.indexOf(' ', mid - 4);
  if (cut <= 0 || cut >= s.length - 1) {
    const line1 = s.slice(0, SPLIT_AT).trim();
    const line2 = s.slice(SPLIT_AT).trim() || null;
    return line2 === '…' ? { line1: s.trim(), line2: null } : { line1, line2 };
  }
  const line1 = s.slice(0, cut).trim();
  const line2 = s.slice(cut + 1).trim() || null;
  // Word split can leave only the truncation ellipsis on line 2.
  return line2 === '…' ? { line1: s.trim(), line2: null } : { line1, line2 };
}

/** Word-wrap the full catalyst title for hover expansion (no truncation). */
function wrapFullNotableEventLabel(title: string, maxCharsPerLine = 42): string[] {
  const t = title.replace(/\s+/g, ' ').trim();
  if (!t) return ['Notable'];
  const words = t.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length <= maxCharsPerLine) {
      line = candidate;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * External hover store for notable events. Decouples hover state from the
 * parent component so hovering a label/dot does NOT re-render the whole chart;
 * only the specific leader dot and its label callout subscribe and re-render.
 */
type NotableHoverStore = {
  get: () => string | null;
  set: (d: string | null) => void;
  subscribe: (cb: () => void) => () => void;
};

function createNotableHoverStore(): NotableHoverStore {
  let current: string | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => current,
    set: (d) => {
      if (current === d) return;
      current = d;
      listeners.forEach((cb) => cb());
    },
    subscribe: (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

function useHoveredNotable(store: NotableHoverStore): string | null {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

/**
 * Tiny subscriber wrapper so a single notable-event label can re-render in
 * isolation when its hover state changes. Keeps the rest of the chart static.
 */
function NotableLabelSubscriber(props: {
  hoverStore: NotableHoverStore;
  date: string;
  render: (isHighlighted: boolean) => ReactNode;
}) {
  const hovered = useHoveredNotable(props.hoverStore);
  return <>{props.render(hovered === props.date)}</>;
}

/**
 * Notable origin-event marker: anchor dot + click/hover target.
 * Headlines and leader lines are rendered by renderHistoricalNotableEventLabels.
 */
function NotableEventLeaderDot(props: {
  cx: number;
  cy: number;
  index?: number;
  timeframe?: HistoricalPriceChartTimeframe;
  /** Kept for API compatibility — label text is rendered by the Customized overlay. */
  labelText: string;
  eventDate: string;
  hoverStore: NotableHoverStore;
  showPulseRing?: boolean;
  onClick: (e: ReactMouseEvent) => void | Promise<void>;
}) {
  const { cx, cy, eventDate, hoverStore, showPulseRing = false, onClick } = props;
  const hoveredDate = useHoveredNotable(hoverStore);
  const isHighlighted = hoveredDate === eventDate;
  const [isClicked, setIsClicked] = useState(false);

  const handleClick = async (e: ReactMouseEvent) => {
    e.stopPropagation();
    setIsClicked(true);
    setTimeout(() => setIsClicked(false), 600);
    await Promise.resolve(onClick(e));
  };

  const anchorR = isHighlighted ? 5.5 : 3.5;

  return (
    <g
      data-chart-interactive-marker="true"
      onMouseEnter={() => hoverStore.set(eventDate)}
      onMouseLeave={() => hoverStore.set(null)}
      style={{ cursor: 'pointer' }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={16}
        fill="transparent"
        strokeWidth={0}
        pointerEvents="all"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
      />
      {showPulseRing ? (
        <motion.circle
          cx={cx}
          cy={cy}
          r={6}
          fill="none"
          stroke="#111827"
          strokeWidth={1.5}
          strokeOpacity={0}
          animate={{ r: [6, 16], strokeOpacity: [0.35, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', repeatDelay: 0.2 }}
          style={{ pointerEvents: 'none' }}
        />
      ) : null}
      <motion.circle
        cx={cx}
        cy={cy}
        r={anchorR}
        fill="#111827"
        strokeWidth={0}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: isClicked ? [1, 1.5, 1] : 1 }}
        transition={{
          opacity: { duration: 0.35, ease: 'easeOut' },
          scale: { duration: isClicked ? 0.5 : 0.25, ease: 'easeOut' },
        }}
        style={{ pointerEvents: 'none', transition: 'r 0.15s ease-out' }}
      />
    </g>
  );
}

// Custom dot component that enlarges on hover and bounces on click for significant moves
/**
 * Same interaction model as Notable Events dots on the historical line chart:
 * large hit target, 5→8px radius on hover, staggered fade-in, click bounce, optional pulse ring.
 */
function HistoricalEventStyleDot(props: {
  cx: number;
  cy: number;
  index?: number;
  timeframe?: HistoricalPriceChartTimeframe;
  accentColor?: string;
  showPulseRing?: boolean;
  onClick: (e: ReactMouseEvent) => void | Promise<void>;
}) {
  const {
    cx,
    cy,
    index,
    timeframe = '12M',
    accentColor = '#000000',
    showPulseRing = false,
    onClick,
  } = props;
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const baseRadius = 5;
  const hoverRadius = 8;
  const delayMultiplier =
    timeframe === '5Y' ||
    timeframe === '3Y' ||
    timeframe === '7D' ||
    timeframe === '30D'
      ? 0.001
      : 0.0035;
  const indexDelay = index !== undefined ? index * delayMultiplier : 0;

  const handleClick = async (e: ReactMouseEvent) => {
    e.stopPropagation();
    setIsClicked(true);
    setTimeout(() => setIsClicked(false), 600);
    await Promise.resolve(onClick(e));
  };

  return (
    <g
      data-chart-interactive-marker="true"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={15}
        fill="transparent"
        strokeWidth={0}
        pointerEvents="all"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
      />
      {showPulseRing ? (
        <motion.circle
          cx={cx}
          cy={cy}
          r={6}
          fill="none"
          stroke={accentColor}
          strokeWidth={1.5}
          strokeOpacity={0}
          animate={{
            r: [6, 16],
            strokeOpacity: [0.4, 0],
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeOut',
            repeatDelay: 0.2,
          }}
          style={{ pointerEvents: 'none' }}
        />
      ) : null}
      <motion.g
        initial={{ opacity: 0, scale: 0 }}
        animate={{
          opacity: 1,
          scale: isClicked ? [1, 1.5, 1] : 1,
        }}
        transition={{
          opacity: {
            duration: 0.4,
            delay: indexDelay,
            ease: 'easeOut',
          },
          scale: {
            duration: isClicked ? 0.6 : 0.3,
            delay: isClicked ? 0 : indexDelay,
            times: isClicked ? [0, 0.3, 1] : undefined,
            ease: isClicked ? 'easeOut' : [0.34, 1.56, 0.64, 1],
          },
        }}
        style={{
          transformOrigin: 'center',
          pointerEvents: 'none',
        }}
      >
        <circle
          cx={cx}
          cy={cy}
          r={isHovered ? hoverRadius : baseRadius}
          fill={accentColor}
          strokeWidth={0}
          style={{
            transition: 'r 0.2s ease-in-out',
            pointerEvents: 'none',
          }}
        />
      </motion.g>
    </g>
  );
}

/** Tape imbalance layer: volume-sized dot that grows on hover (same interaction model as notable markers). */
function TapeImbalanceChartDot(props: {
  cx: number;
  cy: number;
  baseR: number;
  fill: string;
  strokeWidth: number;
  title: string;
  onClick: (e: ReactMouseEvent) => void;
}) {
  const { cx, cy, baseR, fill, strokeWidth, title, onClick } = props;
  const [isHovered, setIsHovered] = useState(false);

  const hoverR = Math.min(18, Math.max(baseR * 1.6, baseR + 3));
  const displayR = isHovered ? hoverR : baseR;
  const displayStroke = isHovered
    ? Math.min(2.2, Math.max(0.9, strokeWidth * 1.12))
    : strokeWidth;
  const hitR = Math.max(16, baseR * 2.2);

  return (
    <g
      data-chart-interactive-marker="true"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      <title>{title}</title>
      <circle
        cx={cx}
        cy={cy}
        r={hitR}
        fill="transparent"
        strokeWidth={0}
        pointerEvents="all"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
      />
      <circle
        cx={cx}
        cy={cy}
        r={displayR}
        fill={fill}
        stroke="#ffffff"
        strokeWidth={displayStroke}
        style={{
          pointerEvents: 'none',
          transition: 'r 0.2s ease-in-out, stroke-width 0.2s ease-in-out',
        }}
      />
    </g>
  );
}

function CustomDot(props: any) {
  const isSignificant = props.payload?.isSignificant;
  const isNotableOriginEvent = !!props.payload?.isNotableOriginEvent;
  const notableEventLabels = props.notableEventLabels as Record<string, string> | undefined;
  const onDotClick = props.onDotClick;
  const selectedPoint = props.selectedPoint;

  const isSelected = selectedPoint && props.payload?.date === selectedPoint.date;

  if (!isSignificant) {
    return (
      <circle
        cx={props.cx}
        cy={props.cy}
        r={0}
        fill="transparent"
        strokeWidth={0}
        pointerEvents="none"
      />
    );
  }

  if (isNotableOriginEvent) {
    const d = props.payload?.date as string | undefined;
    const labelText = d && notableEventLabels && d in notableEventLabels ? notableEventLabels[d] ?? '' : '';
    const hoverStore = props.notableHoverStore as NotableHoverStore | undefined;
    if (!hoverStore) return null;
    return (
      <NotableEventLeaderDot
        cx={props.cx}
        cy={props.cy}
        index={props.index}
        timeframe={props.timeframe}
        labelText={labelText}
        eventDate={d ?? ''}
        hoverStore={hoverStore}
        showPulseRing={!!isSelected}
        onClick={async () => {
          if (onDotClick && props.payload) {
            await onDotClick(props.payload, { scrollToExplanation: true });
          }
        }}
      />
    );
  }

  return (
    <HistoricalEventStyleDot
      cx={props.cx}
      cy={props.cy}
      index={props.index}
      timeframe={props.timeframe}
      accentColor="#000000"
      showPulseRing={!!isSelected}
      onClick={async () => {
        if (onDotClick && props.payload) {
          await onDotClick(props.payload);
        }
      }}
    />
  );
}

// Diamond marker for block trades on the historical chart
function HistoricalBlockTradeDot(props: {
  cx: number;
  cy: number;
  payload: { date: string; time: string; price: number; size: number; dollar_value: number; side: string; fullDate?: string };
  onClick: (e: ReactMouseEvent) => void;
  /** When true (e.g. intraday opened from this print on the yearly chart), show dissipating diamond ripples */
  pulsate?: boolean;
  /** Multiplier on marker size (historical chart uses a value below 1 for subtler diamonds). */
  sizeScale?: number;
  /** Pre-ranked radius in px (from visible-trade notional spread); scaled by `sizeScale`. */
  radiusPx?: number;
}) {
  const { cx, cy, payload, onClick, pulsate = false, sizeScale = 1, radiusPx } = props;
  const [isHovered, setIsHovered] = useState(false);

  const color = payload.side === 'B' ? '#10b981' : payload.side === 'A' ? '#ef4444' : '#6b7280';
  const baseR =
    radiusPx != null && Number.isFinite(radiusPx)
      ? radiusPx
      : blockTradeDiamondRadiusFallback(payload);
  const r = baseR * sizeScale;

  const rippleDiamond = (delay: number) => (
    <motion.g
      pointerEvents="none"
      style={{ transformOrigin: '0px 0px' }}
      initial={false}
      animate={{ scale: [1, 2.75], opacity: [0.42, 0] }}
      transition={{
        duration: 4.2,
        repeat: Infinity,
        ease: 'easeOut',
        delay,
      }}
    >
      <rect
        x={-r}
        y={-r}
        width={r * 2}
        height={r * 2}
        transform="rotate(45)"
        fill="none"
        stroke={color}
        strokeWidth={0.65 * sizeScale}
        strokeLinejoin="round"
      />
    </motion.g>
  );

  // Hover scale lives on the <rect> itself — <rect> has a real fill-box so
  // transform-box:fill-box + transform-origin:50% 50% reliably centers the scale.
  // The SVG rotate(45) attribute is replaced by CSS so both transforms compose together.
  const hoverScale = isHovered ? 1.8 : 1;
  const diamond = (
    <rect
      x={-r}
      y={-r}
      width={r * 2}
      height={r * 2}
      fill={color}
      fillOpacity={0.9}
      stroke="#fff"
      strokeWidth={1.5 * sizeScale}
      style={{
        transform: `rotate(45deg) scale(${hoverScale})`,
        transformBox: 'fill-box' as React.CSSProperties['transformBox'],
        transformOrigin: '50% 50%',
        transition: 'transform 0.14s ease-out',
      } as React.CSSProperties}
    />
  );

  return (
    <g
      data-chart-interactive-marker="true"
      transform={`translate(${cx}, ${cy})`}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      {pulsate && (
        <>
          {rippleDiamond(0)}
          {rippleDiamond(2.1)}
        </>
      )}
      {diamond}
      <circle r={Math.max(10, r * 2 + 2)} fill="transparent" />
    </g>
  );
}

/** Log-scaled like block-trade diamonds; when price > 0, notional bumps size vs. shares alone. */
function insiderTradeNotional(trade: FmpInsiderTradeRow): number {
  const shares = Math.max(Number(trade.securitiesTransacted) || 0, 0);
  const price = Number(trade.price);
  if (Number.isFinite(price) && price > 0) return shares * price;
  return shares;
}

/** Dollar notional when both shares and price are present; null otherwise. */
function insiderTradeNotionalUsd(trade: FmpInsiderTradeRow): number | null {
  const shares = Math.max(Number(trade.securitiesTransacted) || 0, 0);
  const price = Number(trade.price);
  if (shares <= 0 || !Number.isFinite(price) || price <= 0) return null;
  return shares * price;
}

function formatInsiderNotionalUsd(usd: number): string {
  return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function insiderDiamondRadiusFromTrade(trade: FmpInsiderTradeRow, sizeScale = 0.82): number {
  const shares = Math.max(Number(trade.securitiesTransacted) || 0, 1);
  const price = Number(trade.price);
  let basis = shares;
  if (Number.isFinite(price) && price > 0) {
    const notional = shares * price;
    basis = Math.max(shares, notional / 40);
  }
  return Math.min(4 + Math.log10(Math.max(basis / 500, 1)) * 2, 9) * sizeScale;
}

function pickRepresentativeInsiderTrade(trades: FmpInsiderTradeRow[]): FmpInsiderTradeRow {
  let best = trades[0];
  let bestNotional = insiderTradeNotional(best);
  for (let i = 1; i < trades.length; i++) {
    const n = insiderTradeNotional(trades[i]);
    if (n > bestNotional) {
      best = trades[i];
      bestNotional = n;
    }
  }
  return best;
}

/** Stable id for pulsate + keys when multiple rows share a calendar day + transaction code. */
function fmpInsiderTradeMarkerKey(date: string, txnCode: InsiderTransactionCode): string {
  return `${date}|${txnCode}`;
}

/** Rotated-square markers matching largest-trade diamonds; color/size from filing line. */
function HistoricalInsiderTradeDiamond(props: {
  cx: number;
  cy: number;
  trade: FmpInsiderTradeRow;
  pulsate?: boolean;
  onClick: (e: ReactMouseEvent) => void;
}) {
  const { cx, cy, trade, pulsate = false, onClick } = props;
  const [isHovered, setIsHovered] = useState(false);
  const color = insiderTradeDiamondColor(trade);
  const sizeScale = 0.82;
  const r = insiderDiamondRadiusFromTrade(trade, sizeScale);

  const rippleDiamond = (delay: number) => (
    <motion.g
      pointerEvents="none"
      style={{ transformOrigin: '0px 0px' }}
      initial={false}
      animate={{ scale: [1, 2.75], opacity: [0.42, 0] }}
      transition={{
        duration: 4.2,
        repeat: Infinity,
        ease: 'easeOut',
        delay,
      }}
    >
      <rect
        x={-r}
        y={-r}
        width={r * 2}
        height={r * 2}
        transform="rotate(45)"
        fill="none"
        stroke={color}
        strokeWidth={0.65 * sizeScale}
        strokeLinejoin="round"
      />
    </motion.g>
  );

  const hoverScale = isHovered ? 1.8 : 1;
  const diamond = (
    <rect
      x={-r}
      y={-r}
      width={r * 2}
      height={r * 2}
      fill={color}
      fillOpacity={0.9}
      stroke="#fff"
      strokeWidth={1.5 * sizeScale}
      style={{
        transform: `rotate(45deg) scale(${hoverScale})`,
        transformBox: 'fill-box' as React.CSSProperties['transformBox'],
        transformOrigin: '50% 50%',
        transition: 'transform 0.14s ease-out',
      } as React.CSSProperties}
    />
  );

  return (
    <g
      data-chart-interactive-marker="true"
      transform={`translate(${cx}, ${cy})`}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      {pulsate ? (
        <>
          {rippleDiamond(0)}
          {rippleDiamond(2.1)}
        </>
      ) : null}
      {diamond}
      <circle r={Math.max(10, r * 2 + 2)} fill="transparent" />
    </g>
  );
}

// Helper function to get color for price target dispersion
// Low dispersion (good) = green, High dispersion (bad) = red
function getDispersionColor(dispersionPercent: number): string {
  // Clamp dispersion between 0 and 50% for the color scale
  const clamped = Math.min(Math.max(dispersionPercent, 0), 50);
  // Normalize to 0-1 range
  const normalized = clamped / 50;
  
  // Interpolate between green (0) and red (1)
  // Green: rgb(34, 197, 94) - emerald-500
  // Red: rgb(239, 68, 68) - red-500
  const red = Math.round(34 + (239 - 34) * normalized);
  const green = Math.round(197 - (197 - 68) * normalized);
  const blue = Math.round(94 - (94 - 68) * normalized);
  
  return `rgb(${red}, ${green}, ${blue})`;
}

// Helper function to get interpretation text for price target dispersion
function getDispersionInterpretation(dispersionPercent: number): string {
  if (dispersionPercent < 10) {
    return 'Analyst Widely Agree';
  } else if (dispersionPercent <= 20) {
    return 'Analyst Mostly Agree';
  } else if (dispersionPercent < 30) {
    // Gap between 20% and 30% - default to Mostly Agree
    return 'Analyst Mostly Agree';
  } else if (dispersionPercent <= 45) {
    return 'Analyst Mostly Disagree';
  } else {
    return 'Analyst Widely Disagree';
  }
}

function formatUsdCompact(usd: number): string {
  if (usd >= 1_000_000_000) return `$${(usd / 1_000_000_000).toFixed(2)}B`;
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(2)}M`;
  if (usd >= 1_000) return `$${(usd / 1_000).toFixed(1)}K`;
  return `$${usd.toFixed(0)}`;
}

function formatShareVolCompact(shares: number): string {
  if (shares >= 1_000_000_000) return `${(shares / 1_000_000_000).toFixed(1)}B`;
  if (shares >= 1_000_000) return `${(shares / 1_000_000).toFixed(1)}M`;
  if (shares >= 1_000) return `${(shares / 1_000).toFixed(1)}K`;
  return shares.toLocaleString();
}

/** ~same dead band as tapeImbalanceColor for “balanced” headline */
const TAPE_IMBALANCE_TOOLTIP_BALANCE_DEAD = 0.06;

function TapeImbalanceTooltipContent({
  date,
  fullDate,
  buy_volume: buyVol,
  sell_volume: sellVol,
  neutral_volume: neutralVol,
  sessionChange,
}: {
  date: string;
  fullDate?: string;
  buy_volume: number;
  sell_volume: number;
  neutral_volume: number;
  /** Close vs prior session (same rules as historical crosshair); null when OHLC unavailable. */
  sessionChange: { change: number; changePct: number } | null;
}) {
  const dateLabel =
    fullDate ??
    (date
      ? new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '—');
  const classified = buyVol + sellVol;
  const total = classified + neutralVol;
  const signed = classified > 0 ? (buyVol - sellVol) / classified : 0;
  let aggressorHeadline: string;
  if (classified <= 0) {
    aggressorHeadline =
      neutralVol > 0 ? 'Unclassified tape only (no B/A split)' : 'No classified aggressor volume';
  } else if (Math.abs(signed) < TAPE_IMBALANCE_TOOLTIP_BALANCE_DEAD) {
    aggressorHeadline = 'Balanced buy vs sell aggressors';
  } else if (signed > 0) {
    const buyPct = (buyVol / classified) * 100;
    aggressorHeadline = `Buy aggressor–heavy (${buyPct.toFixed(1)}% of classified tape)`;
  } else {
    const sellPct = (sellVol / classified) * 100;
    aggressorHeadline = `Sell aggressor–heavy (${sellPct.toFixed(1)}% of classified tape)`;
  }

  const buyPctClassified = classified > 0 ? (buyVol / classified) * 100 : null;
  const sellPctClassified = classified > 0 ? (sellVol / classified) * 100 : null;

  const chg = sessionChange?.change;
  const chgPct = sessionChange?.changePct;
  const sessionChangeColor =
    sessionChange == null
      ? 'text-gray-400'
      : chg != null && chg > 0
        ? 'text-emerald-700'
        : chg != null && chg < 0
          ? 'text-rose-700'
          : 'text-gray-600';
  const sessionChangeLine =
    sessionChange != null && chg != null && chgPct != null
      ? (() => {
          const dollarStr = chg >= 0 ? `+$${chg.toFixed(2)}` : `-$${Math.abs(chg).toFixed(2)}`;
          const pctStr = `${chgPct >= 0 ? '+' : ''}${chgPct.toFixed(2)}%`;
          return `${dollarStr} (${pctStr})`;
        })()
      : null;

  return (
    <>
      <p className="mb-2 text-[11px] font-medium leading-none text-gray-500">{dateLabel}</p>
      {sessionChangeLine ? (
        <p className={`mb-2 text-[12px] font-semibold tabular-nums leading-snug ${sessionChangeColor}`}>
          Session change {sessionChangeLine}
        </p>
      ) : (
        <p className="mb-2 text-[11px] leading-snug text-gray-400">Session change unavailable</p>
      )}
      <p className="mb-2.5 text-[12px] font-semibold leading-snug text-gray-900">{aggressorHeadline}</p>
      <dl className="space-y-1.5 text-[11px] leading-none">
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-emerald-700/90">Buy aggressor</dt>
          <dd className="text-right font-normal text-gray-900">
            {formatShareVolCompact(buyVol)}
            {buyPctClassified != null ? (
              <span className="text-gray-400"> ({buyPctClassified.toFixed(1)}%)</span>
            ) : null}
          </dd>
        </div>
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-rose-700/90">Sell aggressor</dt>
          <dd className="text-right font-normal text-gray-900">
            {formatShareVolCompact(sellVol)}
            {sellPctClassified != null ? (
              <span className="text-gray-400"> ({sellPctClassified.toFixed(1)}%)</span>
            ) : null}
          </dd>
        </div>
        {neutralVol > 0 ? (
          <div className="flex justify-between gap-4 tabular-nums">
            <dt className="font-normal text-gray-400">Unclassified</dt>
            <dd className="text-right font-normal text-gray-900">{formatShareVolCompact(neutralVol)}</dd>
          </div>
        ) : null}
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-gray-400">Classified total</dt>
          <dd className="text-right font-normal text-gray-900">{formatShareVolCompact(classified)}</dd>
        </div>
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-gray-400">Tape total</dt>
          <dd className="text-right font-normal text-gray-900">{formatShareVolCompact(total)}</dd>
        </div>
      </dl>
    </>
  );
}

function OptionImbalanceTooltipContent({
  date,
  fullDate,
  call_premium: callPremium,
  put_premium: putPremium,
  unknown_premium: unknownPremium,
  sessionChange,
}: {
  date: string;
  fullDate?: string;
  call_premium: number;
  put_premium: number;
  unknown_premium: number;
  sessionChange: { change: number; changePct: number } | null;
}) {
  const dateLabel =
    fullDate ??
    (date
      ? new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '—');
  const classified = callPremium + putPremium;
  const total = classified + unknownPremium;
  const signed = classified > 0 ? (callPremium - putPremium) / classified : 0;
  let flowHeadline: string;
  if (classified <= 0) {
    flowHeadline =
      unknownPremium > 0 ? 'Unclassified premium only (no call/put split)' : 'No option premium data';
  } else if (Math.abs(signed) < TAPE_IMBALANCE_TOOLTIP_BALANCE_DEAD) {
    flowHeadline = 'Balanced call vs put premium';
  } else if (signed > 0) {
    const callPct = (callPremium / classified) * 100;
    flowHeadline = `Call-heavy (${callPct.toFixed(1)}% of classified premium)`;
  } else {
    const putPct = (putPremium / classified) * 100;
    flowHeadline = `Put-heavy (${putPct.toFixed(1)}% of classified premium)`;
  }

  const callPctClassified = classified > 0 ? (callPremium / classified) * 100 : null;
  const putPctClassified = classified > 0 ? (putPremium / classified) * 100 : null;

  const chg = sessionChange?.change;
  const chgPct = sessionChange?.changePct;
  const sessionChangeColor =
    sessionChange == null
      ? 'text-gray-400'
      : chg != null && chg > 0
        ? 'text-emerald-700'
        : chg != null && chg < 0
          ? 'text-rose-700'
          : 'text-gray-600';
  const sessionChangeLine =
    sessionChange != null && chg != null && chgPct != null
      ? (() => {
          const dollarStr = chg >= 0 ? `+$${chg.toFixed(2)}` : `-$${Math.abs(chg).toFixed(2)}`;
          const pctStr = `${chgPct >= 0 ? '+' : ''}${chgPct.toFixed(2)}%`;
          return `${dollarStr} (${pctStr})`;
        })()
      : null;

  return (
    <>
      <p className="mb-2 text-[11px] font-medium leading-none text-gray-500">{dateLabel}</p>
      {sessionChangeLine ? (
        <p className={`mb-2 text-[12px] font-semibold tabular-nums leading-snug ${sessionChangeColor}`}>
          Session change {sessionChangeLine}
        </p>
      ) : (
        <p className="mb-2 text-[11px] leading-snug text-gray-400">Session change unavailable</p>
      )}
      <p className="mb-2.5 text-[12px] font-semibold leading-snug text-gray-900">{flowHeadline}</p>
      <dl className="space-y-1.5 text-[11px] leading-none">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 tabular-nums">
          <dt className="whitespace-nowrap font-normal text-emerald-700/90">Call premium</dt>
          <dd className="whitespace-nowrap text-right font-normal text-gray-900">
            {formatUsdCompact(callPremium)}
            {callPctClassified != null ? (
              <span className="text-gray-400"> ({callPctClassified.toFixed(1)}%)</span>
            ) : null}
          </dd>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 tabular-nums">
          <dt className="whitespace-nowrap font-normal text-rose-700/90">Put premium</dt>
          <dd className="whitespace-nowrap text-right font-normal text-gray-900">
            {formatUsdCompact(putPremium)}
            {putPctClassified != null ? (
              <span className="text-gray-400"> ({putPctClassified.toFixed(1)}%)</span>
            ) : null}
          </dd>
        </div>
        {unknownPremium > 0 ? (
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 tabular-nums">
            <dt className="whitespace-nowrap font-normal text-gray-400">Unclassified</dt>
            <dd className="whitespace-nowrap text-right font-normal text-gray-900">
              {formatUsdCompact(unknownPremium)}
            </dd>
          </div>
        ) : null}
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 tabular-nums">
          <dt className="whitespace-nowrap font-normal text-gray-400">Classified total</dt>
          <dd className="whitespace-nowrap text-right font-normal text-gray-900">
            {formatUsdCompact(classified)}
          </dd>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 tabular-nums">
          <dt className="whitespace-nowrap font-normal text-gray-400">Session total</dt>
          <dd className="whitespace-nowrap text-right font-normal text-gray-900">
            {formatUsdCompact(total)}
          </dd>
        </div>
      </dl>
    </>
  );
}

/** Trade shares as % of that session's total volume from historical OHLC (`volume` field). */
function formatBlockTradePctOfDayVolume(
  tradeShares: number,
  dayVolume: number | null | undefined,
): string | null {
  const shares = Number(tradeShares);
  const vol = Number(dayVolume);
  if (!Number.isFinite(shares) || shares <= 0 || !Number.isFinite(vol) || vol <= 0) {
    return null;
  }
  const pct = (shares / vol) * 100;
  if (pct >= 10) return `${pct.toFixed(1)}%`;
  if (pct >= 1) return `${pct.toFixed(2)}%`;
  return `${pct.toFixed(2)}%`;
}

function BlockTradeTooltipContent({
  trade: t,
  sessionVolume,
}: {
  trade: BlockTradeTooltipTrade;
  /** Session total share volume for `trade.date` (historical_prices `volume`). */
  sessionVolume?: number | null;
}) {
  const dateLabel =
    t.fullDate ??
    (t.date
      ? new Date(`${t.date}T12:00:00`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '—');
  const pctOfDayVolume = formatBlockTradePctOfDayVolume(Number(t.size) || 0, sessionVolume);
  return (
    <>
      <p className="mb-2.5 pr-5 text-[11px] font-medium leading-none text-gray-500">{dateLabel}</p>
      <dl className="space-y-1.5 text-[11px] leading-none">
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-gray-400">Time (ET)</dt>
          <dd className="text-right font-normal text-gray-900">{t.time || '—'}</dd>
        </div>
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-gray-400">Side</dt>
          <dd className="text-right font-normal text-gray-900">
            {t.side === 'B' ? 'Buy' : t.side === 'A' ? 'Sell' : t.side || '—'}
          </dd>
        </div>
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-gray-400">Price</dt>
          <dd className="text-right font-normal text-gray-900">${Number(t.price).toFixed(2)}</dd>
        </div>
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-gray-400">Shares</dt>
          <dd className="text-right font-normal text-gray-900">
            {formatShareVolCompact(Number(t.size) || 0)}
          </dd>
        </div>
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-gray-400">Total</dt>
          <dd className="text-right font-normal text-gray-900">
            {formatUsdCompact(Number(t.dollar_value) || 0)}
          </dd>
        </div>
        <div className="flex justify-between gap-4 tabular-nums">
          <dt className="font-normal text-gray-400">% of total volume</dt>
          <dd className="text-right font-normal text-gray-900">{pctOfDayVolume ?? '—'}</dd>
        </div>
      </dl>
    </>
  );
}

function fmtInsiderField(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'number' && Number.isFinite(v)) {
    if (Number.isInteger(v) || Math.abs(v) >= 1000) return v.toLocaleString();
    return String(v);
  }
  const s = String(v).trim();
  return s;
}

function InsiderDlRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-3 text-[11px] leading-snug">
      <dt className="shrink-0 font-normal text-gray-400">{label}</dt>
      <dd className="max-w-[190px] break-words text-right font-normal text-gray-900">{value}</dd>
    </div>
  );
}

function InsiderTradeTooltipContent({
  date,
  fullDate,
  trades,
}: {
  date: string;
  fullDate?: string;
  trades: FmpInsiderTradeRow[];
}) {
  const dateLabel =
    fullDate ??
    (date
      ? new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '—');
  const sortedTrades = [...trades].sort(
    (a, b) => insiderTradeNotional(b) - insiderTradeNotional(a),
  );
  const multi = sortedTrades.length > 1;
  return (
    <>
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-[11px] font-medium leading-tight text-gray-500">{dateLabel}</p>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ${
            multi ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {sortedTrades.length} transaction{sortedTrades.length === 1 ? '' : 's'}
        </span>
      </div>
      {!multi ? (
        <p className="mb-2 text-[10px] leading-snug text-gray-400">Transaction date {date}</p>
      ) : null}
      <div className="max-h-[min(58vh,380px)] space-y-3 overflow-y-auto pr-0.5">
        {sortedTrades.map((t, idx) => {
          const notionalUsd = insiderTradeNotionalUsd(t);
          return (
          <div
            key={`${t.reportingCik ?? ''}-${t.filingDate ?? ''}-${t.transactionType ?? ''}-${idx}`}
            className={idx > 0 ? 'border-t border-gray-100 pt-3' : ''}
          >
            {multi ? (
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-gray-700">
                  #{idx + 1} of {sortedTrades.length}
                </span>
                {notionalUsd != null ? (
                  <span className="text-[10px] font-medium tabular-nums text-gray-500">
                    {formatUsdCompact(notionalUsd)}
                  </span>
                ) : null}
              </div>
            ) : null}
            <dl className="space-y-1.5">
              <InsiderDlRow label="Reporting name" value={fmtInsiderField(t.reportingName)} />
              <InsiderDlRow label="Transaction type" value={fmtInsiderField(t.transactionType)} />
              <InsiderDlRow label="Owner type" value={fmtInsiderField(t.typeOfOwner)} />
              <InsiderDlRow label="Security" value={fmtInsiderField(t.securityName)} />
              <InsiderDlRow label="Shares (line)" value={fmtInsiderField(t.securitiesTransacted)} />
              <InsiderDlRow
                label="Price"
                value={
                  t.price != null && Number.isFinite(Number(t.price))
                    ? `$${Number(t.price).toFixed(4)}`
                    : ''
                }
              />
              <InsiderDlRow
                label="Notional value"
                value={notionalUsd != null ? formatInsiderNotionalUsd(notionalUsd) : ''}
              />
              <InsiderDlRow label="Shares owned after" value={fmtInsiderField(t.securitiesOwned)} />
              <InsiderDlRow label="Acq / Disp" value={fmtInsiderField(t.acquisitionOrDisposition)} />
              <InsiderDlRow label="Direct / Indirect" value={fmtInsiderField(t.directOrIndirect)} />
              <InsiderDlRow label="Form" value={fmtInsiderField(t.formType)} />
              <InsiderDlRow label="Filing date" value={fmtInsiderField(t.filingDate)} />
              <InsiderDlRow label="Transaction date" value={fmtInsiderField(t.transactionDate)} />
              <InsiderDlRow label="Reporting CIK" value={fmtInsiderField(t.reportingCik)} />
              <InsiderDlRow label="Company CIK" value={fmtInsiderField(t.companyCik)} />
              {t.url ? (
                <div className="pt-1">
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] font-medium text-blue-600 hover:underline"
                  >
                    Open SEC filing
                  </a>
                </div>
              ) : null}
            </dl>
          </div>
          );
        })}
      </div>
    </>
  );
}

// ─── House / Senate Trading layer ────────────────────────────────────────────

/** Map amount range strings to approximate notional for log-scaling. */
function houseTradeNotionalFromAmount(amount: string): number {
  const s = (amount ?? '').toLowerCase();
  if (s.includes('1,000,001') || s.includes('5,000,001')) return 2_500_000;
  if (s.includes('500,001')) return 750_000;
  if (s.includes('250,001')) return 375_000;
  if (s.includes('100,001')) return 175_000;
  if (s.includes('50,001')) return 75_000;
  if (s.includes('15,001')) return 32_500;
  return 8_000; // default: $1,001–$15,000
}

function houseTradeDiamondRadius(trade: FmpCongressTradeRow, sizeScale = 0.82): number {
  const notional = houseTradeNotionalFromAmount(trade.amount ?? '');
  return Math.min(4 + Math.log10(Math.max(notional / 500, 1)) * 2, 9) * sizeScale;
}

function HistoricalCongressTradeDiamond(props: {
  cx: number;
  cy: number;
  trade: FmpCongressTradeRow;
  pulsate?: boolean;
  stackDyPx?: number;
  stackCount?: number;
  onClick: (e: ReactMouseEvent) => void;
}) {
  const { cx, cy, trade, pulsate = false, stackDyPx = 0, stackCount = 1, onClick } = props;
  const [isHovered, setIsHovered] = useState(false);
  const color = congressTradeDiamondColor(trade);
  const sizeScale = 0.82;
  const r = houseTradeDiamondRadius(trade, sizeScale);

  const rippleDiamond = (delay: number) => (
    <motion.g
      pointerEvents="none"
      style={{ transformOrigin: '0px 0px' }}
      initial={false}
      animate={{ scale: [1, 2.75], opacity: [0.42, 0] }}
      transition={{ duration: 4.2, repeat: Infinity, ease: 'easeOut', delay }}
    >
      <rect
        x={-r} y={-r} width={r * 2} height={r * 2}
        transform="rotate(45)"
        fill="none" stroke={color}
        strokeWidth={0.65 * sizeScale} strokeLinejoin="round"
      />
    </motion.g>
  );

  const hoverScale = isHovered ? 1.8 : 1;
  const diamond = (
    <rect
      x={-r} y={-r} width={r * 2} height={r * 2}
      fill={color} fillOpacity={0.9}
      stroke="#fff" strokeWidth={1.5 * sizeScale}
      style={{
        transform: `rotate(45deg) scale(${hoverScale})`,
        transformBox: 'fill-box' as React.CSSProperties['transformBox'],
        transformOrigin: '50% 50%',
        transition: 'transform 0.14s ease-out',
      } as React.CSSProperties}
    />
  );

  return (
    <g
      data-chart-interactive-marker="true"
      transform={`translate(${cx}, ${cy + stackDyPx})`}
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      {pulsate ? (<>{rippleDiamond(0)}{rippleDiamond(2.1)}</>) : null}
      {diamond}
      <circle
        r={stackCount > 1 ? Math.min(5.5, Math.max(4, r * 1.15)) : Math.max(10, r * 2 + 2)}
        fill="transparent"
      />
    </g>
  );
}

function CongressTradeTooltipContent({
  date,
  fullDate,
  trades,
  sameDayOrdinal,
  sameDayTotal,
}: {
  date: string;
  fullDate?: string;
  trades: FmpCongressTradeRow[];
  sameDayOrdinal?: number;
  sameDayTotal?: number;
}) {
  const dateLabel =
    fullDate ??
    (date
      ? new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
        })
      : '—');
  const multiDay = sameDayTotal != null && sameDayOrdinal != null && sameDayTotal > 1;
  return (
    <>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-sky-600">Congress Trading</p>
      <p className="mb-1.5 text-[11px] font-medium leading-none text-gray-500">{dateLabel}</p>
      <p className="mb-2 text-[10px] leading-snug text-gray-400">
        {multiDay
          ? `Line ${sameDayOrdinal} of ${sameDayTotal} · transaction date ${date}`
          : `${trades.length} filing${trades.length === 1 ? '' : 's'} · transaction date ${date}`}
      </p>
      <div className="max-h-[min(58vh,340px)] space-y-3 overflow-y-auto pr-0.5">
        {trades.map((t, idx) => (
          <div
            key={`${t.firstName ?? ''}-${t.lastName ?? ''}-${t.transactionDate ?? ''}-${idx}`}
            className={idx > 0 ? 'border-t border-gray-100 pt-3' : ''}
          >
            <dl className="space-y-1.5">
              <InsiderDlRow
                label="Chamber"
                value={t.chamber === 'senate' ? 'Senate' : t.chamber === 'house' ? 'House' : fmtInsiderField(t.chamber)}
              />
              <InsiderDlRow label="Member" value={[t.firstName, t.lastName].filter(Boolean).join(' ')} />
              <InsiderDlRow label="District" value={fmtInsiderField(t.district)} />
              <InsiderDlRow label="Owner" value={fmtInsiderField(t.owner)} />
              <InsiderDlRow label="Action" value={fmtInsiderField(t.type)} />
              <InsiderDlRow label="Amount" value={fmtInsiderField(t.amount)} />
              <InsiderDlRow label="Asset" value={fmtInsiderField(t.assetDescription)} />
              <InsiderDlRow label="Asset type" value={fmtInsiderField(t.assetType)} />
              <InsiderDlRow label="Capital gains >$200" value={fmtInsiderField(t.capitalGainsOver200USD)} />
              <InsiderDlRow label="Transaction date" value={fmtInsiderField(t.transactionDate)} />
              <InsiderDlRow label="Disclosure date" value={fmtInsiderField(t.disclosureDate)} />
              {t.link ? (
                <div className="pt-1">
                  <a
                    href={t.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] font-medium text-sky-600 hover:underline"
                  >
                    Open disclosure PDF
                  </a>
                </div>
              ) : null}
            </dl>
          </div>
        ))}
      </div>
    </>
  );
}

/** One day from `{ticker}_decision_attribution.json` (backend decision attributer). */
type DecisionAttributionDayRow = {
  date: string;
  ticker: string;
  price_change_pct?: number | null;
  primary_driver?: string | null;
  confidence?: string | null;
  driver_scores?: Record<string, number> | null;
  reasoning?: string | null;
  signals_available?: Record<string, boolean> | null;
};

const DECISION_DRIVER_SCORE_KEYS = ['company_specific', 'market_driven', 'liquidity_dynamics'] as const;

function labelDecisionDriver(key: string): string {
  switch (key) {
    case 'company_specific':
      return 'Company-specific';
    case 'market_driven':
      return 'Market-driven';
    case 'liquidity_dynamics':
      return 'Liquidity dynamics';
    case 'mixed':
      return 'Mixed';
    default:
      return key.replace(/_/g, ' ');
  }
}

// ---------------------------------------------------------------------------
// Fed Decision in June — multi-outcome chart overlay helpers
// ---------------------------------------------------------------------------

/** Stable event title used to group all June FOMC outcome markets. */
const FED_DECISION_JUNE_EVENT_TITLE = 'Fed Decision in June?';

/** Short legend label derived from the market question text. */
function fedOutcomeShortLabel(question: string): string {
  const q = question.toLowerCase();
  if (/no change|unchanged/.test(q)) return 'Hold';
  if (/decrease.*50/.test(q)) return '−50+ bps';
  if (/decrease.*25/.test(q)) return '−25 bps';
  if (/increase.*50/.test(q)) return '+50+ bps';
  if (/increase.*25/.test(q)) return '+25 bps';
  const highStrike = question.match(/\(\s*HIGH\s*\)\s*\$?\s*([\d,]+)/i);
  if (highStrike) return `↑ $${highStrike[1]} May`;
  const lowStrike = question.match(/\(\s*LOW\s*\)\s*\$?\s*([\d,]+)/i);
  if (lowStrike) return `↓ $${lowStrike[1]} May`;
  return question.slice(0, 22);
}

/** Ordered palette: violet (Hold), emerald (−25), sky (−50+), rose (+25), amber (+50+). */
const FED_OUTCOME_LINE_COLORS = ['#7c3aed', '#059669', '#0284c7', '#e11d48', '#d97706'] as const;

function fedOutcomeColor(idx: number): string {
  return FED_OUTCOME_LINE_COLORS[idx % FED_OUTCOME_LINE_COLORS.length];
}

export function StockAnalysisView({
  onBack,
  showBetaRestrictionNotice = false,
  stockData,
  financialHealth,
  compositeScore,
  historicalPrices,
  significantMoves: _significantMoves,
  blockTradeDates,
  blockTradeNotionalByDate,
  largestBlockTrades,
  largestBlockTradePerDay,
  noteworthyBlockTradesPerDay,
  diamondBlockTradesPerDay,
  largestBlockTradePerMonth: _largestBlockTradePerMonth,
  originEventDates: originEventDatesProp,
}: StockAnalysisViewProps) {
  const navigate = useNavigate();
  const tickerStorylines = useMemo(
    () => (DASHBOARD_STORYLINES_ENABLED ? getStockTickerStorylines(stockData.ticker) : []),
    [stockData.ticker],
  );
  const useStorylineTabs =
    DASHBOARD_STORYLINES_ENABLED && hasStockStorylineEvents(stockData.ticker) && tickerStorylines.length > 0;
  const sectorLabel = useMemo(() => {
    const sector =
      financialHealth.metadata?.sector_name?.trim() || stockData.sector?.trim();
    const industry =
      financialHealth.metadata?.industry_name?.trim() || stockData.industry?.trim();
    if (sector && industry) return `${sector} • ${industry}`;
    return sector || industry || null;
  }, [
    financialHealth.metadata?.sector_name,
    financialHealth.metadata?.industry_name,
    stockData.sector,
    stockData.industry,
  ]);
  const originEventDates = originEventDatesProp ?? [];
  const [betaNoticeOpen, setBetaNoticeOpen] = useState(showBetaRestrictionNotice);
  const [openSections, setOpenSections] = useState({
    fundamentals: false,
    sentiment: false,
    momentum: false
  });
  const [selectedPoint, setSelectedPoint] = useState<any>(null);
  const [executiveSummary, setExecutiveSummary] = useState<string | null>(null);
  const [highSignificanceThemes, setHighSignificanceThemes] = useState<Array<{ theme: string; key_evidence: string }> | null>(null);
  const [todayMoveStory, setTodayMoveStory] = useState<{
    moveHeader: string | null;
    executiveSummary: string;
  } | null>(null);
  const [todayMoveLoading, setTodayMoveLoading] = useState(false);
  const [loadingStory, setLoadingStory] = useState(false);
  const [storyError, setStoryError] = useState<string | null>(null);
  /** From Stock_Move_Analysis.json when the clicked day is an origin_event date */
  const [originEventCatalysts, setOriginEventCatalysts] = useState<
    Array<{ rank: number; catalyst: string; mechanism: string }> | null
  >(null);
  const [decisionAttribution, setDecisionAttribution] = useState<DecisionAttributionDayRow | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Array<{
    title: string;
    source: string;
    url: string;
  }>>([]);
  /** From stock-story API: *_trades_minute_metrics_storyline_*.json (overall_arc only in UI) */
  const [tapeStoryline, setTapeStoryline] = useState<{ overall_arc: string } | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState<{
    status: string;
    progress: number;
    completed_moves: number;
    total_moves: number;
  } | null>(null);
  const processingStartedRef = useRef(false);
  const [timeframe, setTimeframe] = useState<HistoricalPriceChartTimeframe>('12M');
  const [historicalChartStyle, setHistoricalChartStyle] = useState<'line' | 'candle'>('line');
  // Layer visibility state (matches figma mockup – all can be enabled independently)
  const [layers, setLayers] = useState({
    notableEvents: {
      enabled: false,
    },
    largestTrades: {
      enabled: false,
    },
    imbalance: {
      enabled: false,
    },
    volume: {
      enabled: false,
    },
    peakVolumePrice: {
      enabled: false,
    },
    volumeByPrice: {
      enabled: false,
    },
    insiderTrading: {
      enabled: false,
    },
    senateTrading: {
      enabled: false,
    },
    prediction: {
      enabled: false,
    },
    institutionalHolders: {
      enabled: false,
    },
    optionsPositioning: {
      enabled: false,
    },
    notableOptionTrades: {
      enabled: false,
    },
    optionImbalance: {
      enabled: false,
    },
  });
  const [optionsChartData, setOptionsChartData] = useState<OptionsAnalysisResponse | null>(null);
  const [optionsChartLoading, setOptionsChartLoading] = useState(false);
  const [optionsLegendPosition, setOptionsLegendPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [optionsLegendDismissed, setOptionsLegendDismissed] = useState(false);
  const [tapeImbalanceByDate, setTapeImbalanceByDate] = useState<Record<
    string,
    { buy_volume: number; sell_volume: number; neutral_volume: number }
  > | null>(null);
  const [peakVolumePriceByDate, setPeakVolumePriceByDate] = useState<Record<
    string,
    PeakVolumePricePoint
  > | null>(null);
  const [intradayPeakVolumeLevel, setIntradayPeakVolumeLevel] =
    useState<PeakVolumePriceLevel | null>(null);
  const [dynamicHistoricalPrices, setDynamicHistoricalPrices] = useState<typeof historicalPrices>(historicalPrices);
  const headerChartPrices = useMemo(
    () => (dynamicHistoricalPrices ?? historicalPrices ?? []).map((row) => ({ date: row.date, close: row.close })),
    [dynamicHistoricalPrices, historicalPrices],
  );
  const todayIsoDate = useMemo(() => localTodayIsoDate(), []);
  const todayMovePercentChange = useMemo(() => {
    const prices = dynamicHistoricalPrices ?? historicalPrices ?? [];
    const row = prices.find((d) => d.date === todayIsoDate);
    return row?.percent_change ?? null;
  }, [dynamicHistoricalPrices, historicalPrices, todayIsoDate]);
  const todayMoveClose = useMemo(() => {
    const prices = dynamicHistoricalPrices ?? historicalPrices ?? [];
    return prices.find((d) => d.date === todayIsoDate)?.close ?? null;
  }, [dynamicHistoricalPrices, historicalPrices, todayIsoDate]);
  const [loadingHistoricalPrices, setLoadingHistoricalPrices] = useState(false);
  /** Plot-area mouse position (SVG coords minus offset) for crosshair alignment without blocking chart clicks. */
  const [historicalPlotMouse, setHistoricalPlotMouse] = useState<{ lx: number; ly: number } | null>(null);
  /** Price tracker rendered as HTML outside the SVG clip rect (gutter to the right of the plot). */
  const [historicalPriceTrackerOverlay, setHistoricalPriceTrackerOverlay] = useState<{
    left: number;
    top: number;
    label: string;
  } | null>(null);
  const [historicalPeakVolumeGuideOverlay, setHistoricalPeakVolumeGuideOverlay] = useState<{
    left: number;
    top: number;
    label: string;
  } | null>(null);
  /** Embedded top-left OHLC + session change for the crosshair-snapped bar (no tooltip chrome). */
  const [historicalOhlcTracker, setHistoricalOhlcTracker] = useState<{
    o: number;
    h: number;
    l: number;
    c: number;
    change: number;
    changePct: number;
    /** Snapped bar — same as `HistoricalChartHoverPayload.date`. */
    date: string;
    fullDate?: string;
  } | null>(null);
  /** Horizontal inset so OHLC "O" aligns with Y-axis "$" (measured from first tick label). */
  const [historicalOhlcAlignPadPx, setHistoricalOhlcAlignPadPx] = useState(6);
  /** Absolute indices into `historicalChartData`; null = show full timeframe. */
  const [historicalZoomRange, setHistoricalZoomRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [historicalBrushSelecting, setHistoricalBrushSelecting] = useState(false);
  const [historicalBrushBox, setHistoricalBrushBox] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);
  /** Short titles for origin-event dates (from stock-move-analysis); used on-chart leader labels. */
  const [notableEventLabels, setNotableEventLabels] = useState<Record<string, string>>({});
  /** Hover link between headline, leader line, and chart dot for one notable event. */
  /**
   * External store keeps notable-event hover state out of React's render tree
   * for the parent component. Only the leader dots + label callouts subscribe,
   * so hovering doesn't re-render the chart or its 250+ dots.
   */
  const notableEventHoverStore = useMemo<NotableHoverStore>(() => createNotableHoverStore(), []);
  const notableLabelFetchInFlightRef = useRef<Set<string>>(new Set());
  /** Dates whose label fetch finished (success or fallback); avoids re-fetch loops. */
  const notableLabelFetchedRef = useRef<Set<string>>(new Set());
  /** Floating summary when clicking a largest-trade diamond on the historical chart. */
  const [historicalBlockTradePopover, setHistoricalBlockTradePopover] = useState<{
    trade: BlockTradeTooltipTrade;
    left: number;
    top: number;
  } | null>(null);
  const historicalBlockTradePopoverRef = useRef<HTMLDivElement>(null);
  /** Daily tape B/A imbalance summary beside an imbalance dot (click, same layout as block-trade popover). */
  const [tapeImbalancePopover, setTapeImbalancePopover] = useState<{
    date: string;
    fullDate?: string;
    buy_volume: number;
    sell_volume: number;
    neutral_volume: number;
    sessionChange: { change: number; changePct: number } | null;
    left: number;
    top: number;
  } | null>(null);
  const tapeImbalancePopoverRef = useRef<HTMLDivElement>(null);
  const [insiderTradingPopover, setInsiderTradingPopover] = useState<{
    date: string;
    fullDate?: string;
    trades: FmpInsiderTradeRow[];
    highlightKey: string;
    left: number;
    top: number;
  } | null>(null);
  const insiderTradingPopoverRef = useRef<HTMLDivElement>(null);
  /** Same tooltip pattern for intraday session diamonds. */
  const [intradayBlockTradePopover, setIntradayBlockTradePopover] = useState<{
    trade: BlockTradeTooltipTrade;
    left: number;
    top: number;
  } | null>(null);
  const intradayBlockTradePopoverRef = useRef<HTMLDivElement>(null);
  const intradayPriceChartWrapRef = useRef<HTMLDivElement>(null);
  const intradayPriceChartPlotRef = useRef<HTMLDivElement>(null);
  const intradayChartMetricsRef = useRef<HistoricalChartMetrics | null>(null);
  const intradayChartDataForCrosshairRef = useRef<IntradayCrosshairRow[]>([]);
  const [intradayPlotMouse, setIntradayPlotMouse] = useState<{ lx: number; ly: number } | null>(null);
  const [intradayPriceTrackerOverlay, setIntradayPriceTrackerOverlay] = useState<{
    left: number;
    top: number;
    label: string;
  } | null>(null);
  const [intradayPeakVolumeGuideOverlay, setIntradayPeakVolumeGuideOverlay] = useState<{
    left: number;
    top: number;
    label: string;
  } | null>(null);
  const historicalChartWrapRef = useRef<HTMLDivElement>(null);
  const historicalChartPlotRef = useRef<HTMLDivElement>(null);

  const historicalChartMetricsRef = useRef<HistoricalChartMetrics | null>(null);
  const historicalBrushStartClientRef = useRef<{ x: number; y: number } | null>(null);
  const historicalChartVisibleDataRef = useRef<HistoricalChartHoverPayload[]>([]);
  const historicalZoomRangeRef = useRef<{ start: number; end: number } | null>(null);
  const historicalChartFullLengthRef = useRef(0);
  const historicalChartSuppressClickRef = useRef(false);
  /** Preserve viewport when a chart day opens the intraday / explanation panel below. */
  const chartSelectionScrollYRef = useRef<number | null>(null);
  /** Scroll to the explanation panel after a notable-event headline/dot click. */
  const pendingScrollToExplanationRef = useRef(false);
  const eventExplanationPanelRef = useRef<HTMLDivElement>(null);
  /** After dismissing the historical diamond popover, next click on the same diamond opens intraday. */
  const historicalBlockTradeAwaitingDrillKeyRef = useRef<string | null>(null);
  const [fmpInsiderPayload, setFmpInsiderPayload] = useState<FmpInsiderTradingFileResponse | null>(null);
  const [insiderTradingLoading, setInsiderTradingLoading] = useState(false);
  const [institutionalHoldersPayload, setInstitutionalHoldersPayload] =
    useState<InstitutionalHoldersFileResponse | null>(null);
  const [institutionalHoldersLoading, setInstitutionalHoldersLoading] = useState(false);
  const [institutionalHoldersLoadError, setInstitutionalHoldersLoadError] = useState<string | null>(null);
  const [institutionalHoldersDenseHint, setInstitutionalHoldersDenseHint] = useState(false);
  const [institutionalHoldersChartDisplayMode, setInstitutionalHoldersChartDisplayMode] =
    useState<InstitutionalHoldersChartDisplayMode>(() => readInstitutionalHoldersChartDisplayMode());
  const [institutionalHoldersSearchQuery, setInstitutionalHoldersSearchQuery] = useState('');
  const [institutionalHoldersSearchHighlight, setInstitutionalHoldersSearchHighlight] = useState<{
    quarterKey: string;
    positionKey: string;
  } | null>(null);
  const [institutionalHoldersSearchDetail, setInstitutionalHoldersSearchDetail] = useState<{
    quarterKey: string;
    positionKey: string;
  } | null>(null);
  const [insiderTradingLoadError, setInsiderTradingLoadError] = useState<string | null>(null);
  const [insiderTradingEnabledTypeCodes, setInsiderTradingEnabledTypeCodes] = useState<
    Set<InsiderTransactionCode>
  >(() => readInsiderTradingEnabledTypeCodes());
  const [insiderTradingSearchQuery, setInsiderTradingSearchQuery] = useState('');
  const [insiderTradingSearchHighlight, setInsiderTradingSearchHighlight] = useState<{
    date: string;
    txnCode: InsiderTransactionCode;
  } | null>(null);
  const [congressTradingEnabledCategories, setCongressTradingEnabledCategories] = useState<
    Set<CongressTradeCategory>
  >(() => readCongressTradingEnabledCategories());
  const [congressTradingEnabledChambers, setCongressTradingEnabledChambers] = useState<
    Set<CongressChamber>
  >(() => readCongressTradingEnabledChambers());
  const [congressTradingSearchQuery, setCongressTradingSearchQuery] = useState('');
  const [congressTradingSearchHighlight, setCongressTradingSearchHighlight] = useState<{
    tradeIdentityKey: string;
  } | null>(null);
  const [fmpCongressPayload, setFmpCongressPayload] = useState<FmpCongressTradesFileResponse | null>(null);
  const [congressTradingLoading, setCongressTradingLoading] = useState(false);
  const [congressTradingLoadError, setCongressTradingLoadError] = useState<string | null>(null);
  const [congressTradingPopover, setCongressTradingPopover] = useState<{
    date: string;
    fullDate?: string;
    trades: FmpCongressTradeRow[];
    highlightKey: string;
    sameDayOrdinal?: number;
    sameDayTotal?: number;
    left: number;
    top: number;
  } | null>(null);
  const congressTradingPopoverRef = useRef<HTMLDivElement>(null);
  const [notableOptionTradesPerDay, setNotableOptionTradesPerDay] = useState<NotableOptionTradeRow[]>([]);
  const [notableOptionTradesLoading, setNotableOptionTradesLoading] = useState(false);
  const [notableOptionTradesLoadError, setNotableOptionTradesLoadError] = useState<string | null>(null);
  const [notableOptionMinPremiumUsd, setNotableOptionMinPremiumUsd] = useState(() =>
    readNotableOptionMinPremiumUsd(),
  );
  const [notableOptionTradePopover, setNotableOptionTradePopover] = useState<{
    trade: NotableOptionTradeRow;
    fullDate?: string;
    left: number;
    top: number;
  } | null>(null);
  const notableOptionTradePopoverRef = useRef<HTMLDivElement>(null);
  const [optionImbalanceByDate, setOptionImbalanceByDate] = useState<Record<
    string,
    { call_premium: number; put_premium: number; unknown_premium: number }
  > | null>(null);
  const [optionImbalanceLoading, setOptionImbalanceLoading] = useState(false);
  const [optionImbalanceLoadError, setOptionImbalanceLoadError] = useState<string | null>(null);
  const [optionImbalancePopover, setOptionImbalancePopover] = useState<{
    date: string;
    fullDate?: string;
    call_premium: number;
    put_premium: number;
    unknown_premium: number;
    sessionChange: { change: number; changePct: number } | null;
    left: number;
    top: number;
  } | null>(null);
  const optionImbalancePopoverRef = useRef<HTMLDivElement>(null);
  const [fedRateData, setFedRateData] = useState<FedRateDecisionResponse | null>(null);
  const [fedRateLoading, setFedRateLoading] = useState(false);
  const [fedRateError, setFedRateError] = useState<string | null>(null);
  /** Markets whose YES odds are overlaid on the price chart (multi-outcome events: toggle each leg independently). */
  const [selectedEventGroup, setSelectedEventGroup] = useState<FedRateMarket[]>([]);
  /** Probability history keyed by market.id — one entry per fetched leg. */
  const [fedProbHistories, setFedProbHistories] = useState<Record<string, MarketProbHistory>>({});
  const [fedProbHistoryLoading, setFedProbHistoryLoading] = useState(false);
  /** Polymarket card: hide on click-outside while keeping Prediction layer + chart overlay active. */
  const [predictionPanelOpen, setPredictionPanelOpen] = useState(true);
  const predictionPanelRef = useRef<HTMLDivElement>(null);
  const fedPredictionMarketsOrdered = useMemo(() => {
    const list = fedRateData?.markets;
    if (!list?.length) return [];
    const norm = (s: string) => s.trim().toLowerCase();
    const juneKey = norm(FED_DECISION_JUNE_EVENT_TITLE);
    const june = list.filter((m) => norm(m.event_title || '') === juneKey);
    const seen = new Set(june.map((m) => m.id));
    const rest = list.filter((m) => !seen.has(m.id));
    return [...june, ...rest];
  }, [fedRateData?.markets]);
  const historicalBrushListenersRef = useRef<{ move: (e: MouseEvent) => void; up: (e: MouseEvent) => void } | null>(
    null,
  );
  const [intradayData, setIntradayData] = useState<Array<{
    time: string;
    price: number;
    percentFromOpen: number;
    priceChange: number;
    isSignificantMove: boolean;
    isExtendedHours?: boolean;
    /** Databento venue close when overlaying on Polygon */
    venuePrice?: number | null;
  }> | null>(null);
  /** From intraday-prices when `minute_line=duckdb_largest_trade` (DuckDB largest print per minute for diamonds). */
  const [intradayMinuteLargestTrades, setIntradayMinuteLargestTrades] = useState<Array<{
    time: string;
    price: number;
    size: number;
    side: string;
    venue?: number;
  }> | null>(null);
  const [intradayVolumeAndTrades, setIntradayVolumeAndTrades] = useState<{
    volume: number;
    trades: number;
  } | null>(null);
  /** Precomputed RTH tape stats (`*_trade_session_stats.json`) for intraday headline row. */
  const [intradaySessionStats, setIntradaySessionStats] = useState<TradeSessionStatsPayload | null>(null);
  /** Yearly volume-at-price profile for the visible historical chart range. */
  const [yearlyPriceVolume, setYearlyPriceVolume] = useState<
    import('@/utils/volumeByPriceLayer').YearlyPriceVolumePayload | null
  >(null);
  const [yearlyPriceVolumeLoading, setYearlyPriceVolumeLoading] = useState(false);
  const [volumeByPriceDisplayMode, setVolumeByPriceDisplayMode] = useState<VolumeByPriceColorMode>(
    () => readVolumeByPriceDisplayMode(),
  );
  const [loadingIntraday, setLoadingIntraday] = useState(false);
  const [selectedIntradayPoint, setSelectedIntradayPoint] = useState<any>(null);
  /** When false, block-trade diamonds and block-trade summary above the intraday chart are hidden. */
  const [showIntradayBlockTrades, setShowIntradayBlockTrades] = useState(true);
  /** Publisher IDs in price source and chart legend; toggle removed from UI. */
  const showPublisherIds = true;
  /** Volume-by-publisher bar chart below intraday; hidden from UI. */
  const showPublisherVolumeDashboard = false;
  /**
   * null = Polygon/Massive consolidated tape; number = Databento publisher_id (venue prints).
   */
  const [intradayPricePublisherId, setIntradayPricePublisherId] = useState<number | null>(null);
  /** When true and a publisher is selected, chart shows Polygon consolidated plus venue line. */
  const [intradayOverlayVenueOnPolygon, setIntradayOverlayVenueOnPolygon] = useState(false);
  /** Stale decision-attribution responses ignored when user clicks another date quickly. */
  const decisionAttrFetchIdRef = useRef(0);
  const [tradeAnalytics, setTradeAnalytics] = useState<{
    session_et_extended?: boolean;
    summary: {
      session_vwap: number | null;
      buy_pct: number | null;
      sell_pct: number | null;
      net_delta: number;
      buy_volume: number;
      sell_volume: number;
      neutral_volume: number;
      block_trade_count: number;
      block_trade_volume: number;
      block_trade_notional_usd?: number;
      total_volume: number;
      trade_count?: number;
      session_notional_usd?: number;
    };
    per_minute: Array<{
      time: string;
      cumulative_delta: number;
      vwap: number | null;
    }>;
    block_trades: Array<{ time: string; price: number; size: number; side: string; venue?: number }>;
    venue_volume: Array<{
      publisher_id: number;
      volume: number;
      trade_count?: number;
      notional_usd?: number;
      label?: string | null;
    }>;
  } | null>(null);
  // Cache fetched data for each timeframe to avoid re-fetching
  const priceCacheRef = useRef<Partial<Record<HistoricalPriceChartTimeframe, typeof historicalPrices>>>(
    {},
  );

  // Initialize dynamic historical prices when prop changes
  useEffect(() => {
    if (historicalPrices && historicalPrices.length > 0) {
      setDynamicHistoricalPrices(historicalPrices);
      // Cache the initial 12M data
      priceCacheRef.current['12M'] = historicalPrices;
    }
  }, [historicalPrices]);

  useEffect(() => {
    setHistoricalPlotMouse(null);
    setHistoricalPriceTrackerOverlay(null);
    setHistoricalOhlcTracker(null);
    setHistoricalOhlcAlignPadPx(6);
    setHistoricalZoomRange(null);
    setHistoricalBrushSelecting(false);
    setHistoricalBrushBox(null);
    setIntradayBlockTradePopover(null);
    historicalBlockTradeAwaitingDrillKeyRef.current = null;
    const pair = historicalBrushListenersRef.current;
    if (pair) {
      window.removeEventListener('mousemove', pair.move);
      window.removeEventListener('mouseup', pair.up);
      historicalBrushListenersRef.current = null;
    }
  }, [stockData.ticker, timeframe, historicalChartStyle, loadingHistoricalPrices]);

  useLayoutEffect(() => {
    if (!selectedPoint?.date) return;

    if (pendingScrollToExplanationRef.current) {
      pendingScrollToExplanationRef.current = false;
      // Defer until the explanation panel has mounted and laid out.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          eventExplanationPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
      return;
    }

    const y = chartSelectionScrollYRef.current;
    if (y == null) return;
    chartSelectionScrollYRef.current = null;
    window.scrollTo({ top: y, left: 0, behavior: 'instant' });
  }, [selectedPoint?.date]);

  useEffect(() => {
    setIntradayPricePublisherId(null);
    setIntradayOverlayVenueOnPolygon(false);
    setIntradayBlockTradePopover(null);
  }, [selectedPoint?.date]);

  useEffect(() => {
    if (!selectedPoint) setIntradayBlockTradePopover(null);
  }, [selectedPoint]);

  // Intraday stats + tick analytics (VWAP, delta, venues) when user selects a chart point
  useEffect(() => {
    if (!selectedPoint?.date || !stockData.ticker) {
      setTradeAnalytics(null);
      setIntradayVolumeAndTrades(null);
      setIntradaySessionStats(null);
      setSelectedIntradayPoint(null);
      return;
    }
    setSelectedIntradayPoint(null);
    setIntradayVolumeAndTrades(null);
    setIntradaySessionStats(null);
    setTradeAnalytics(null);

    const ticker = stockData.ticker;
    const date = selectedPoint.date;

    Promise.all([
      stockService.getIntradayStats(ticker, date),
      stockService.getIntradayTradeAnalytics(ticker, date),
      stockService.getTradeSessionStats(ticker, date),
    ])
      .then(([stats, analytics, sessionStats]) => {
        if (analytics) {
          setTradeAnalytics(analytics);
        }
        if (
          sessionStats?.success &&
          (sessionStats.extended?.total_trades || sessionStats.regular_hours?.total_trades)
        ) {
          setIntradaySessionStats(sessionStats);
        }

        let volume: number | null = stats?.volume ?? null;
        let trades: number | null = stats?.trade_count ?? null;
        if (volume == null && analytics?.summary?.total_volume != null) {
          volume = analytics.summary.total_volume;
        }
        if (trades == null && analytics?.summary?.trade_count != null) {
          trades = analytics.summary.trade_count;
        }
        if (volume != null || trades != null) {
          setIntradayVolumeAndTrades({
            volume: volume ?? 0,
            trades: trades ?? Math.round((volume ?? 0) / 350),
          });
        }
      })
      .catch(() => {
        setTradeAnalytics(null);
        setIntradayVolumeAndTrades(null);
        setIntradaySessionStats(null);
      });
  }, [selectedPoint?.date, stockData.ticker]);

  useEffect(() => {
    if (!showIntradayBlockTrades) setIntradayBlockTradePopover(null);
  }, [showIntradayBlockTrades]);

  /** Highlight the selected block in intraday stats when date is unchanged (e.g. switching among top-5 blocks). */
  useEffect(() => {
    const pop = intradayBlockTradePopover?.trade;
    const bd = selectedPoint?.blockTradeDetail;
    const detail =
      pop?.time != null
        ? {
            time: pop.time,
            size: pop.size,
            price: pop.price,
            dollar_value: pop.dollar_value,
          }
        : bd?.time != null
          ? bd
          : null;
    if (!detail?.time) {
      setSelectedIntradayPoint(null);
      return;
    }
    const duckTape =
      intradayMinuteLargestTrades && intradayMinuteLargestTrades.length > 0
        ? collapseToLargestTradePerEtMinute(intradayMinuteLargestTrades)
        : null;
    const tape = duckTape ?? tradeAnalytics?.block_trades;
    if (!tape?.length) {
      setSelectedIntradayPoint(null);
      return;
    }
    const match = findIntradayBlockTradeTapeRow(tape, detail);
    const effectiveMatch =
      match ??
      (detail.time
        ? {
            time: detail.time,
            size: detail.size,
            price: detail.price,
            side: 'N' as const,
          }
        : undefined);
    if (!effectiveMatch) {
      setSelectedIntradayPoint(null);
      return;
    }
    setSelectedIntradayPoint({
      time: effectiveMatch.time,
      blockDetail: { size: effectiveMatch.size, side: effectiveMatch.side, price: effectiveMatch.price },
    });
  }, [
    tradeAnalytics,
    intradayMinuteLargestTrades,
    intradayBlockTradePopover?.trade?.time,
    intradayBlockTradePopover?.trade?.size,
    intradayBlockTradePopover?.trade?.price,
    intradayBlockTradePopover?.trade?.dollar_value,
    selectedPoint?.blockTradeDetail?.time,
    selectedPoint?.blockTradeDetail?.size,
    selectedPoint?.blockTradeDetail?.price,
    selectedPoint?.blockTradeDetail?.dollar_value,
  ]);

  // 1-minute price bars: Polygon consolidated, Databento venue only, or both (overlay)
  useEffect(() => {
    if (!selectedPoint?.date || !stockData.ticker) {
      setIntradayData(null);
      setIntradayMinuteLargestTrades(null);
      return;
    }
    const ticker = stockData.ticker;
    const date = selectedPoint.date;
    const pid = intradayPricePublisherId;

    /** Backend uses `{SYM}_trades.duckdb` when available; otherwise Polygon/venue bars. */
    const duckOpts = { minuteLine: 'duckdb_largest_trade' as const };

    const overlay =
      intradayOverlayVenueOnPolygon && pid != null && Number.isFinite(pid);

    setLoadingIntraday(true);
    setIntradayMinuteLargestTrades(null);

    if (overlay) {
      Promise.all([
        stockService.getIntradayPrices(ticker, date, null, duckOpts),
        stockService.getIntradayPrices(ticker, date, pid, duckOpts),
      ])
        .then(([polyRes, venueRes]) => {
          const polyBars = polyRes?.bars;
          if (!polyBars || polyBars.length === 0) {
            setIntradayData(null);
            setIntradayMinuteLargestTrades(null);
            return;
          }
          const polyPoints = barsToIntradayChartPoints(polyBars);
          const venueBars = venueRes?.bars;
          const venuePoints =
            venueBars && venueBars.length > 0 ? barsToIntradayChartPoints(venueBars) : [];
          const venueByTime = new Map(venuePoints.map((d) => [d.time, d.price]));
          const merged = polyPoints.map((d) => ({
            ...d,
            venuePrice: venueByTime.has(d.time) ? venueByTime.get(d.time)! : null,
          }));
          setIntradayData(merged);
          setIntradayMinuteLargestTrades(polyRes?.minuteLargestTrades ?? null);
        })
        .catch(() => {
          setIntradayData(null);
          setIntradayMinuteLargestTrades(null);
        })
        .finally(() => setLoadingIntraday(false));
      return;
    }

    stockService
      .getIntradayPrices(ticker, date, pid, duckOpts)
      .then((res) => {
        const bars = res?.bars;
        if (!bars || bars.length === 0) {
          setIntradayData(null);
          setIntradayMinuteLargestTrades(null);
          return;
        }
        setIntradayData(barsToIntradayChartPoints(bars));
        setIntradayMinuteLargestTrades(res?.minuteLargestTrades ?? null);
      })
      .catch(() => {
        setIntradayData(null);
        setIntradayMinuteLargestTrades(null);
      })
      .finally(() => setLoadingIntraday(false));
  }, [
    selectedPoint?.date,
    stockData.ticker,
    intradayPricePublisherId,
    intradayOverlayVenueOnPolygon,
  ]);

  // Prefetch 3Y and 5Y data in the background after initial load
  useEffect(() => {
    if (HISTORICAL_CHART_DEMO_LOCK_1Y) return;
    if (!historicalPrices || historicalPrices.length === 0) return;
    
    // Prefetch after a short delay to not block initial render
    const prefetchTimer = setTimeout(() => {
      // Prefetch 3Y data
      stockService.getHistoricalPrices(stockData.ticker, 1095 + HISTORICAL_PRICES_API_EXTRA_DAYS).then((data) => {
        if (data) {
          priceCacheRef.current['3Y'] = data;
        }
      }).catch(() => {
        // Silently fail - user can still fetch on demand
      });

      // Prefetch 5Y data
      stockService.getHistoricalPrices(stockData.ticker, 1825 + HISTORICAL_PRICES_API_EXTRA_DAYS).then((data) => {
        if (data) {
          priceCacheRef.current['5Y'] = data;
        }
      }).catch(() => {
        // Silently fail - user can still fetch on demand
      });
    }, 2000); // Wait 2 seconds after initial load

    return () => clearTimeout(prefetchTimer);
  }, [stockData.ticker, historicalPrices]);

  // Fetch historical prices when timeframe changes (with debounce)
  useEffect(() => {
    // Small debounce to prevent rapid switching from causing multiple fetches
    const timeoutId = setTimeout(() => {
      const fetchHistoricalPricesForTimeframe = async () => {
        const daysNeeded = historicalPriceFetchDaysForTimeframe(timeframe);

        // Check cache first - if we have it, use it immediately (instant switch!)
        if (priceCacheRef.current[timeframe]) {
          setDynamicHistoricalPrices(priceCacheRef.current[timeframe]!);
          return;
        }

        // 7D / 30D: initial page load already fetches ~1Y; slice from cache when it covers the window.
        if (
          (timeframe === '7D' || timeframe === '30D') &&
          historicalPrices &&
          historicalPrices.length > 0
        ) {
          const cutoffDate = historicalPriceCutoffForTimeframe(timeframe);
          const sorted = [...historicalPrices].sort((a, b) => a.date.localeCompare(b.date));
          const [oy, om, od] = sorted[0].date.split('-').map(Number);
          const oldestDate = new Date(oy, om - 1, od);
          if (oldestDate <= cutoffDate) {
            const filtered = sorted.filter((day) => {
              const [y, m, d0] = day.date.split('-').map(Number);
              return new Date(y, m - 1, d0) >= cutoffDate;
            });
            setDynamicHistoricalPrices(filtered);
            priceCacheRef.current[timeframe] = filtered;
            return;
          }
        }

        // For 12M, use prop data if available
        if (timeframe === '12M' && historicalPrices && historicalPrices.length > 0) {
          setDynamicHistoricalPrices(historicalPrices);
          priceCacheRef.current['12M'] = historicalPrices;
          return;
        }

        // Show filtered existing data immediately while fetching more (optimistic update)
        if (historicalPrices && historicalPrices.length > 0) {
          // Filter existing data to show what we have
          const cutoffDate = historicalPriceCutoffForTimeframe(timeframe);
          
          const filteredExisting = historicalPrices.filter((day) => {
            const [year, month, dayNum] = day.date.split('-').map(Number);
            const date = new Date(year, month - 1, dayNum);
            return date >= cutoffDate;
          });
          
          // Show filtered data immediately if we have some
          if (filteredExisting.length > 0) {
            setDynamicHistoricalPrices(filteredExisting);
          }
        }

        // Fetch full data in the background
        setLoadingHistoricalPrices(true);
        try {
          const fetchedPrices = await stockService.getHistoricalPrices(
            stockData.ticker,
            daysNeeded + HISTORICAL_PRICES_API_EXTRA_DAYS,
          );
          if (fetchedPrices) {
            setDynamicHistoricalPrices(fetchedPrices);
            // Cache the fetched data
            priceCacheRef.current[timeframe] = fetchedPrices;
          } else {
            // Fallback to existing data if fetch returns null
            if (historicalPrices) {
              setDynamicHistoricalPrices(historicalPrices);
            }
          }
        } catch (error) {
          console.error('Error fetching historical prices:', error);
          // Fallback to existing data if fetch fails
          if (historicalPrices) {
            setDynamicHistoricalPrices(historicalPrices);
          }
        } finally {
          setLoadingHistoricalPrices(false);
        }
      };

      fetchHistoricalPricesForTimeframe();
    }, 100); // 100ms debounce - fast enough to feel instant, slow enough to prevent spam

    return () => clearTimeout(timeoutId);
  }, [timeframe, stockData.ticker]);

  // Check analysis status and trigger if missing when component mounts
  useEffect(() => {
    const checkAndTriggerAnalysis = async () => {
      // Only check once
      if (processingStartedRef.current) return;
      processingStartedRef.current = true;

      console.log(`📊 Checking if analyses exist for ${stockData.ticker}`);
      
      try {
        // Check analysis progress/status
        const progress = await stockService.getAnalysisProgress(stockData.ticker);
        
        if (progress) {
          setAnalysisProgress(progress);
          
          if (progress.status === 'processing') {
            // Already processing
            console.log(`🔄 Processing already in progress: ${progress.completed_moves}/${progress.total_moves}`);
          } else if (progress.status === 'completed') {
            // All done
            console.log(`✅ All analyses complete: ${progress.completed_moves}/${progress.total_moves}`);
          } else if (progress.status === 'not_started' || progress.status === 'partially_complete') {
            // Missing analyses - just log, don't auto-trigger
            const missing = progress.total_moves - progress.completed_moves;
            console.log(`⚠️ ${missing} dates missing analysis. Use manual trigger if needed.`);
            
            // REMOVED: Auto-triggering processLargestMoves
            // Users can manually trigger via UI if they want to process missing analyses
            // const result = await stockService.processLargestMoves(stockData.ticker);
          }
        }
      } catch (error) {
        console.error('Error checking/triggering analysis:', error);
      }
    };

    // Only check status if we have historical prices (which means largest_moves.json exists)
    if (historicalPrices && historicalPrices.length > 0) {
      checkAndTriggerAnalysis();
    }
  }, [stockData.ticker, historicalPrices]);

  const toggleSection = (section: 'fundamentals' | 'sentiment' | 'momentum') => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Layer panel toggles
  const toggleLayer = (
    layer:
      | 'notableEvents'
      | 'largestTrades'
      | 'imbalance'
      | 'volume'
      | 'peakVolumePrice'
      | 'volumeByPrice'
      | 'insiderTrading'
      | 'senateTrading'
      | 'prediction'
      | 'institutionalHolders'
      | 'optionsPositioning'
      | 'notableOptionTrades'
      | 'optionImbalance',
  ) => {
    setLayers(prev => ({
      ...prev,
      [layer]: { ...prev[layer], enabled: !prev[layer].enabled }
    }));
  };

  useEffect(() => {
    if (
      !historicalBlockTradePopover &&
      !intradayBlockTradePopover &&
      !insiderTradingPopover &&
      !congressTradingPopover &&
      !notableOptionTradePopover &&
      !tapeImbalancePopover &&
      !optionImbalancePopover
    ) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (historicalBlockTradePopover) {
          historicalBlockTradeAwaitingDrillKeyRef.current = historicalBlockTradeInteractionKey(
            historicalBlockTradePopover.trade,
          );
        }
        setHistoricalBlockTradePopover(null);
        setIntradayBlockTradePopover(null);
        setInsiderTradingPopover(null);
        setCongressTradingPopover(null);
        setNotableOptionTradePopover(null);
        setTapeImbalancePopover(null);
        setOptionImbalancePopover(null);
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (historicalBlockTradePopoverRef.current?.contains(t)) return;
      if (intradayBlockTradePopoverRef.current?.contains(t)) return;
      if (insiderTradingPopoverRef.current?.contains(t)) return;
      if (congressTradingPopoverRef.current?.contains(t)) return;
      if (notableOptionTradePopoverRef.current?.contains(t)) return;
      if (tapeImbalancePopoverRef.current?.contains(t)) return;
      if (optionImbalancePopoverRef.current?.contains(t)) return;
      const el = t instanceof Element ? t : null;
      if (el?.closest('[data-chart-interactive-marker="true"]')) return;
      const hadHistorical = historicalBlockTradePopover != null;
      const armDrillKey =
        hadHistorical && historicalBlockTradePopover
          ? historicalBlockTradeInteractionKey(historicalBlockTradePopover.trade)
          : null;
      setHistoricalBlockTradePopover(null);
      setIntradayBlockTradePopover(null);
      setInsiderTradingPopover(null);
      setCongressTradingPopover(null);
      setNotableOptionTradePopover(null);
      setTapeImbalancePopover(null);
      setOptionImbalancePopover(null);
      if (armDrillKey) {
        historicalBlockTradeAwaitingDrillKeyRef.current = armDrillKey;
        historicalChartSuppressClickRef.current = true;
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [
    historicalBlockTradePopover,
    intradayBlockTradePopover,
    insiderTradingPopover,
    congressTradingPopover,
    notableOptionTradePopover,
    tapeImbalancePopover,
    optionImbalancePopover,
  ]);

  useEffect(() => {
    if (!layers.largestTrades.enabled) {
      setHistoricalBlockTradePopover(null);
      historicalBlockTradeAwaitingDrillKeyRef.current = null;
    }
  }, [layers.largestTrades.enabled]);

  useEffect(() => {
    if (!layers.imbalance.enabled) {
      setTapeImbalancePopover(null);
    }
  }, [layers.imbalance.enabled]);

  useEffect(() => {
    if (!layers.insiderTrading.enabled || !stockData.ticker) {
      setFmpInsiderPayload(null);
      setInsiderTradingLoadError(null);
      setInsiderTradingLoading(false);
      return;
    }
    let cancelled = false;
    setInsiderTradingLoading(true);
    setInsiderTradingLoadError(null);
    void stockService.getFmpInsiderTrading(stockData.ticker).then((res) => {
      if (cancelled) return;
      setInsiderTradingLoading(false);
      if (!res) {
        setInsiderTradingLoadError('Request failed');
        setFmpInsiderPayload(null);
        return;
      }
      if (!res.success) {
        setInsiderTradingLoadError(res.error ?? 'No insider data');
        setFmpInsiderPayload(null);
        return;
      }
      setFmpInsiderPayload(res);
    });
    return () => {
      cancelled = true;
    };
  }, [layers.insiderTrading.enabled, stockData.ticker]);

  useEffect(() => {
    if (!layers.insiderTrading.enabled) {
      setInsiderTradingPopover(null);
      setInsiderTradingSearchQuery('');
      setInsiderTradingSearchHighlight(null);
    }
  }, [layers.insiderTrading.enabled]);

  useEffect(() => {
    if (!layers.senateTrading.enabled || !stockData.ticker) {
      setFmpCongressPayload(null);
      setCongressTradingLoadError(null);
      setCongressTradingLoading(false);
      return;
    }
    let cancelled = false;
    setCongressTradingLoading(true);
    setCongressTradingLoadError(null);
    void stockService.getFmpCongressTrades(stockData.ticker).then((res) => {
      if (cancelled) return;
      setCongressTradingLoading(false);
      if (!res) {
        setCongressTradingLoadError('Request failed');
        setFmpCongressPayload(null);
        return;
      }
      if (!res.success) {
        setCongressTradingLoadError(res.error ?? 'No congress trade data');
        setFmpCongressPayload(null);
        return;
      }
      setFmpCongressPayload(res);
    });
    return () => { cancelled = true; };
  }, [layers.senateTrading.enabled, stockData.ticker]);

  useEffect(() => {
    if (!layers.senateTrading.enabled) {
      setCongressTradingPopover(null);
      setCongressTradingSearchQuery('');
      setCongressTradingSearchHighlight(null);
    }
  }, [layers.senateTrading.enabled]);

  useEffect(() => {
    if (!layers.notableOptionTrades.enabled || !stockData.ticker) {
      setNotableOptionTradesPerDay([]);
      setNotableOptionTradesLoadError(null);
      setNotableOptionTradesLoading(false);
      return;
    }
    let cancelled = false;
    setNotableOptionTradesLoading(true);
    setNotableOptionTradesLoadError(null);
    void stockService
      .getNotableOptionTrades(stockData.ticker, notableOptionMinPremiumUsd)
      .then((res) => {
        if (cancelled) return;
        setNotableOptionTradesLoading(false);
        if (!res?.success) {
          setNotableOptionTradesLoadError(res?.error ?? 'No notable option trade data');
          setNotableOptionTradesPerDay([]);
          return;
        }
        setNotableOptionTradesPerDay(res.notable_option_trades_per_day ?? []);
      });
    return () => {
      cancelled = true;
    };
  }, [layers.notableOptionTrades.enabled, stockData.ticker, notableOptionMinPremiumUsd]);

  useEffect(() => {
    if (!layers.notableOptionTrades.enabled) {
      setNotableOptionTradePopover(null);
    }
  }, [layers.notableOptionTrades.enabled]);

  useEffect(() => {
    if (!layers.optionImbalance.enabled || !stockData.ticker) {
      setOptionImbalanceByDate(null);
      setOptionImbalanceLoadError(null);
      setOptionImbalanceLoading(false);
      return;
    }
    const pricesToUse = dynamicHistoricalPrices || historicalPrices;
    if (!pricesToUse?.length) {
      setOptionImbalanceByDate(null);
      return;
    }
    const cutoffDate = historicalPriceCutoffForTimeframe(timeframe);
    const filtered = pricesToUse.filter((day) => {
      const [y, m, d] = day.date.split('-').map(Number);
      return new Date(y, m - 1, d) >= cutoffDate;
    });
    if (!filtered.length) {
      setOptionImbalanceByDate(null);
      return;
    }
    const sorted = [...filtered].sort((a, b) => a.date.localeCompare(b.date));
    const start = sorted[0].date;
    const end = sorted[sorted.length - 1].date;
    let cancelled = false;
    setOptionImbalanceLoading(true);
    setOptionImbalanceLoadError(null);
    stockService
      .getOptionImbalanceSeries(stockData.ticker, start, end)
      .then((res) => {
        if (cancelled) return;
        setOptionImbalanceLoading(false);
        if (!res?.points?.length) {
          if (!res) {
            setOptionImbalanceLoadError('No option imbalance data');
          }
          setOptionImbalanceByDate({});
          return;
        }
        const m: Record<
          string,
          { call_premium: number; put_premium: number; unknown_premium: number }
        > = {};
        for (const p of res.points) {
          m[p.date] = {
            call_premium: p.call_premium,
            put_premium: p.put_premium,
            unknown_premium: p.unknown_premium ?? 0,
          };
        }
        setOptionImbalanceByDate(m);
      })
      .catch(() => {
        if (!cancelled) {
          setOptionImbalanceLoading(false);
          setOptionImbalanceLoadError('Failed to load option imbalance');
          setOptionImbalanceByDate(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    layers.optionImbalance.enabled,
    stockData.ticker,
    dynamicHistoricalPrices,
    historicalPrices,
    timeframe,
  ]);

  useEffect(() => {
    if (!layers.optionImbalance.enabled) {
      setOptionImbalancePopover(null);
    }
  }, [layers.optionImbalance.enabled]);

  useEffect(() => {
    if (!layers.institutionalHolders.enabled || !stockData.ticker) {
      setInstitutionalHoldersPayload(null);
      setInstitutionalHoldersLoadError(null);
      setInstitutionalHoldersLoading(false);
      return;
    }
    let cancelled = false;
    setInstitutionalHoldersLoading(true);
    setInstitutionalHoldersLoadError(null);
    void stockService.getInstitutionalHolders(stockData.ticker).then((res) => {
      if (cancelled) return;
      setInstitutionalHoldersLoading(false);
      if (!res) {
        setInstitutionalHoldersLoadError('Request failed');
        setInstitutionalHoldersPayload(null);
        return;
      }
      if (!res.success) {
        setInstitutionalHoldersLoadError(res.error ?? 'No institutional holders data');
        setInstitutionalHoldersPayload(null);
        return;
      }
      setInstitutionalHoldersPayload(res);
    });
    return () => {
      cancelled = true;
    };
  }, [layers.institutionalHolders.enabled, stockData.ticker]);

  useEffect(() => {
    if (!layers.prediction.enabled) {
      setFedRateData(null);
      setFedRateError(null);
      setFedRateLoading(false);
      setSelectedEventGroup([]);
      setFedProbHistories({});
      return;
    }
    let cancelled = false;
    setFedRateLoading(true);
    setFedRateError(null);
    void macroService.getFedRateDecision().then((res) => {
      if (cancelled) return;
      setFedRateLoading(false);
      if (!res || !res.success) {
        setFedRateError(res?.error ?? 'Failed to load Fed prediction markets');
        setFedRateData(null);
        return;
      }
      setFedRateData(res);
    });
    return () => { cancelled = true; };
  }, [layers.prediction.enabled]);

  useEffect(() => {
    if (layers.prediction.enabled) {
      setPredictionPanelOpen(true);
    }
  }, [layers.prediction.enabled]);

  useEffect(() => {
    if (!layers.prediction.enabled || !predictionPanelOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (predictionPanelRef.current?.contains(t)) return;
      const el = t instanceof Element ? t : null;
      if (el?.closest('[data-prediction-dismiss-skip="true"]')) return;
      setPredictionPanelOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [layers.prediction.enabled, predictionPanelOpen]);

  useEffect(() => {
    if (!layers.optionsPositioning.enabled || !stockData.ticker) {
      setOptionsChartData(null);
      setOptionsChartLoading(false);
      return;
    }
    let cancelled = false;
    setOptionsChartLoading(true);
    void stockService.getOptionsAnalysis(stockData.ticker).then((res) => {
      if (cancelled) return;
      setOptionsChartLoading(false);
      setOptionsChartData(res);
    });
    return () => {
      cancelled = true;
    };
  }, [layers.optionsPositioning.enabled, stockData.ticker]);

  useEffect(() => {
    if (selectedEventGroup.length === 0) {
      setFedProbHistories({});
      setFedProbHistoryLoading(false);
      return;
    }
    let cancelled = false;
    setFedProbHistoryLoading(true);
    const fetches = selectedEventGroup
      .filter((m) => (m.clob_yes_token_id ?? '').trim().length > 0)
      .map((m) =>
        macroService
          .getMarketHistory(m.clob_yes_token_id.trim())
          .then((res) => ({ id: m.id, res })),
      );
    void Promise.all(fetches).then((results) => {
      if (cancelled) return;
      setFedProbHistoryLoading(false);
      const map: Record<string, MarketProbHistory> = {};
      for (const { id, res } of results) {
        if (res && res.success) map[id] = res;
      }
      setFedProbHistories(map);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventGroup.map((m) => m.id).join(',')]);

  // Tape buy/sell imbalance (DuckDB) for visible historical range — Imbalance layer dots on main chart.
  useEffect(() => {
    if (!layers.imbalance.enabled || !stockData.ticker) {
      setTapeImbalanceByDate(null);
      return;
    }
    const pricesToUse = dynamicHistoricalPrices || historicalPrices;
    if (!pricesToUse?.length) {
      setTapeImbalanceByDate(null);
      return;
    }
    const cutoffDate = historicalPriceCutoffForTimeframe(timeframe);
    const filtered = pricesToUse.filter((day) => {
      const [y, m, d] = day.date.split('-').map(Number);
      return new Date(y, m - 1, d) >= cutoffDate;
    });
    if (!filtered.length) {
      setTapeImbalanceByDate(null);
      return;
    }
    const sorted = [...filtered].sort((a, b) => a.date.localeCompare(b.date));
    const start = sorted[0].date;
    const end = sorted[sorted.length - 1].date;
    let cancelled = false;
    stockService
      .getTapeImbalanceSeries(stockData.ticker, start, end)
      .then((res) => {
        if (cancelled) return;
        if (!res?.points?.length) {
          setTapeImbalanceByDate({});
          return;
        }
        const m: Record<string, { buy_volume: number; sell_volume: number; neutral_volume: number }> = {};
        for (const p of res.points) {
          m[p.date] = {
            buy_volume: p.buy_volume,
            sell_volume: p.sell_volume,
            neutral_volume: p.neutral_volume ?? 0,
          };
        }
        setTapeImbalanceByDate(m);
      })
      .catch(() => {
        if (!cancelled) setTapeImbalanceByDate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [layers.imbalance.enabled, stockData.ticker, dynamicHistoricalPrices, historicalPrices, timeframe]);

  // Peak volume price (trade_session_stats) for visible historical range.
  useEffect(() => {
    if (!layers.peakVolumePrice.enabled || !stockData.ticker) {
      setPeakVolumePriceByDate(null);
      return;
    }
    const pricesToUse = dynamicHistoricalPrices || historicalPrices;
    if (!pricesToUse?.length) {
      setPeakVolumePriceByDate(null);
      return;
    }
    const cutoffDate = historicalPriceCutoffForTimeframe(timeframe);
    const filtered = pricesToUse.filter((day) => {
      const [y, m, d] = day.date.split('-').map(Number);
      return new Date(y, m - 1, d) >= cutoffDate;
    });
    if (!filtered.length) {
      setPeakVolumePriceByDate(null);
      return;
    }
    const sorted = [...filtered].sort((a, b) => a.date.localeCompare(b.date));
    const start = sorted[0].date;
    const end = sorted[sorted.length - 1].date;
    let cancelled = false;
    const loadPeakVolumeByDate = async () => {
      const m: Record<string, PeakVolumePricePoint> = {};
      try {
        const res = await stockService.getPeakVolumePriceSeries(
          stockData.ticker,
          start,
          end,
        );
        if (res?.points?.length) {
          for (const p of res.points) {
            m[p.date] = p;
          }
        }
        if (Object.keys(m).length === 0) {
          const recent = [...sorted].reverse().slice(0, 120);
          await Promise.all(
            recent.map(async (day) => {
              const stats = await stockService.getTradeSessionStats(
                stockData.ticker,
                day.date,
              );
              const level = peakVolumePriceFromRegularHours(stats?.regular_hours);
              if (!level) return;
              m[day.date] = {
                date: day.date,
                price: level.price,
                volume: level.volume,
                pct_of_session_volume: level.pctOfSessionVolume,
              };
            }),
          );
        }
        if (!cancelled) setPeakVolumePriceByDate(m);
      } catch {
        if (!cancelled) setPeakVolumePriceByDate(null);
      }
    };
    void loadPeakVolumeByDate();
    return () => {
      cancelled = true;
    };
  }, [
    layers.peakVolumePrice.enabled,
    stockData.ticker,
    dynamicHistoricalPrices,
    historicalPrices,
    timeframe,
  ]);

  // Peak volume price for the selected intraday session (same stats file as Tape Profile).
  useEffect(() => {
    if (!layers.peakVolumePrice.enabled || !stockData.ticker || !selectedPoint?.date) {
      setIntradayPeakVolumeLevel(null);
      return;
    }
    let cancelled = false;
    stockService.getTradeSessionStats(stockData.ticker, selectedPoint.date).then((res) => {
      if (cancelled) return;
      setIntradayPeakVolumeLevel(peakVolumePriceFromRegularHours(res?.regular_hours));
    }).catch(() => {
      if (!cancelled) setIntradayPeakVolumeLevel(null);
    });
    return () => {
      cancelled = true;
    };
  }, [layers.peakVolumePrice.enabled, stockData.ticker, selectedPoint?.date]);

  const applyTapeStorylineFromStoryResponse = (
    story: {
      tape_storyline?: { overall_arc?: string | null; signal_summary?: string | null } | null;
    } | null | undefined,
  ) => {
    const oa = story?.tape_storyline?.overall_arc;
    setTapeStoryline(typeof oa === 'string' && oa.trim() ? { overall_arc: oa } : null);
  };

  const loadDecisionAttributionForDate = useCallback(
    (date: string) => {
      const fid = ++decisionAttrFetchIdRef.current;
      setDecisionAttribution(null);
      void stockService.getDecisionAttributionDay(stockData.ticker, date).then((row) => {
        if (fid !== decisionAttrFetchIdRef.current) return;
        setDecisionAttribution((row ?? null) as DecisionAttributionDayRow | null);
      });
    },
    [stockData.ticker],
  );

  useEffect(() => {
    let cancelled = false;
    setTodayMoveLoading(true);
    setTodayMoveStory(null);

    void stockService.getStockStory(stockData.ticker, todayIsoDate).then((data) => {
      if (cancelled) return;
      if (data?.executive_summary?.trim()) {
        setTodayMoveStory({
          moveHeader: data.move_header ?? null,
          executiveSummary: data.executive_summary,
        });
      } else {
        setTodayMoveStory(null);
      }
      setTodayMoveLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setTodayMoveStory(null);
        setTodayMoveLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [stockData.ticker, todayIsoDate]);

  const todayMoveSummaryCard =
    todayMoveLoading || todayMoveStory ? (
      <TodayMoveSummaryCard
        date={todayIsoDate}
        moveHeader={todayMoveStory?.moveHeader}
        executiveSummary={todayMoveStory?.executiveSummary ?? ''}
        percentChange={todayMovePercentChange}
        loading={todayMoveLoading}
      />
    ) : null;

  const todayAnalogPanel = (
    <StockAnalogAnalysisPanel
      ticker={stockData.ticker}
      companyName={stockData.company}
      referenceDate={todayIsoDate}
      catalystSummary={todayMoveStory?.executiveSummary}
      omitHeaderSummary={Boolean(todayMoveStory?.executiveSummary)}
      dayReturnPct={todayMovePercentChange}
      close={todayMoveClose}
    />
  );

  // Handle dot click - fetch Stock_Story data and display executive_summary
  const handleDotClick = async (
    payload: any,
    options?: { scrollToExplanation?: boolean },
  ) => {
    if (options?.scrollToExplanation) {
      chartSelectionScrollYRef.current = null;
      pendingScrollToExplanationRef.current = true;
    } else {
      chartSelectionScrollYRef.current = window.scrollY;
      pendingScrollToExplanationRef.current = false;
    }
    historicalBlockTradeAwaitingDrillKeyRef.current = null;
    setHistoricalBlockTradePopover(null);
    setIntradayBlockTradePopover(null);
    setTapeImbalancePopover(null);
    setOptionImbalancePopover(null);
    setSelectedPoint(payload);
    setTapeStoryline(null);
    setExecutiveSummary(null);
    setHighSignificanceThemes(null);
    setStoryError(null);
    setRelatedArticles([]);
    setOriginEventCatalysts(null);
    setLoadingStory(true);
    
    // Get the date in YYYY-MM-DD format (now directly from payload.date)
    const originalDate = payload.interactionDate ?? payload.date;
    
    console.log('Dot clicked - payload:', payload);
    console.log('Original date:', originalDate);
    console.log('Ticker:', stockData.ticker);
    
    if (!originalDate) {
      console.warn('No date found in payload. Available keys:', Object.keys(payload));
      setStoryError('No date information available for this point');
      setLoadingStory(false);
      setDecisionAttribution(null);
      return;
    }

    loadDecisionAttributionForDate(originalDate);

    const loadRelatedArticlesForDate = async () => {
      console.log(`Fetching Stock Article Snapshot for ${stockData.ticker} on ${originalDate}...`);
      try {
        const articleSnapshotData = await stockService.getStockArticleSnapshot(stockData.ticker, originalDate);
        console.log('Stock Article Snapshot response:', articleSnapshotData);

        if (articleSnapshotData?.selected_articles) {
          const topArticles = [...articleSnapshotData.selected_articles]
            .sort((a, b) => {
              if (a.relevance_score !== undefined && b.relevance_score !== undefined) {
                return (b.relevance_score || 0) - (a.relevance_score || 0);
              }
              if (a.relevance_score !== undefined) return -1;
              if (b.relevance_score !== undefined) return 1;
              return 0;
            })
            .slice(0, 3)
            .map((article) => ({
              title: article.title,
              source: article.source_name,
              url: article.news_url,
            }));

          console.log('Top 3 related articles:', topArticles);
          setRelatedArticles(topArticles);
        }
      } catch (articleError) {
        console.warn('Error fetching Stock Article Snapshot:', articleError);
      }
    };
    
    try {
      const isOriginEventDate = originEventDates.includes(originalDate);
      console.log(`Fetching Stock Story for ${stockData.ticker} on ${originalDate}...`);
      const [storyData, catalystPack] = await Promise.all([
        stockService.getStockStory(stockData.ticker, originalDate),
        isOriginEventDate ? stockService.getStockMoveDominantCatalysts(stockData.ticker, originalDate) : Promise.resolve(null),
      ]);
      console.log('Stock Story response:', storyData);
      applyTapeStorylineFromStoryResponse(storyData);

      const originCardTitle =
        isOriginEventDate &&
        typeof catalystPack?.origin_event_card_title === 'string' &&
        catalystPack.origin_event_card_title.trim().length > 0
          ? catalystPack.origin_event_card_title.trim()
          : '';

      const catalystRows = (catalystPack?.dominant_catalysts ?? []).filter((c) => (c.catalyst?.length ?? 0) > 0);

      if (catalystRows.length > 0) {
        setOriginEventCatalysts(catalystRows);
        setExecutiveSummary(null);
        setHighSignificanceThemes(null);
        setStoryError(null);
        const cardTitle = originCardTitle || storyData?.move_header;
        if (cardTitle) {
          setSelectedPoint((prev: any) => ({
            ...prev,
            explanationTitle: cardTitle,
          }));
        }
        await loadRelatedArticlesForDate();
      } else {
        setOriginEventCatalysts(null);
        if (storyData) {
          if (storyData.executive_summary) {
            console.log('Executive summary found, length:', storyData.executive_summary.length);
            setExecutiveSummary(storyData.executive_summary);
            setHighSignificanceThemes(storyData.high_significance_themes ?? null);
            setStoryError(null);

            const cardTitleElse = originCardTitle || storyData.move_header;
            if (cardTitleElse) {
              setSelectedPoint((prev: any) => ({
                ...prev,
                explanationTitle: cardTitleElse,
              }));
            }

            await loadRelatedArticlesForDate();
          } else if (storyData.error && storyData.error.includes('not found')) {
            const currentProgress = analysisProgress;
            if (currentProgress && currentProgress.status === 'processing') {
              const percent = currentProgress.progress.toFixed(0);
              setStoryError(`Analysis in progress: ${currentProgress.completed_moves}/${currentProgress.total_moves} dates complete (${percent}%). Please wait...`);
            } else {
              setStoryError('Analysis not yet available. Processing may have just started. Please wait a moment and try again.');
            }
            setExecutiveSummary(null);
          } else if (storyData.error) {
            console.warn('Stock Story API returned error:', storyData.error);
            setStoryError(storyData.error);
            setExecutiveSummary(null);
          } else {
            console.warn('No executive_summary in response:', storyData);
            setStoryError('No analysis available for this date');
            setExecutiveSummary(null);
          }
        } else {
          setStoryError('Failed to fetch analysis');
          setExecutiveSummary(null);
        }
      }

      if (originCardTitle) {
        setSelectedPoint((prev: any) => (prev ? { ...prev, explanationTitle: originCardTitle } : prev));
      }
    } catch (error) {
      console.error('Error fetching Stock Story:', error);
      setStoryError('Error loading analysis');
      setExecutiveSummary(null);
      setOriginEventCatalysts(null);
      setTapeStoryline(null);
    } finally {
      setLoadingStory(false);
    }
  };

  /**
   * Resolve which trading day was clicked from pointer position (same SVG math as crosshair / brush).
   * Uses capture so it runs before Recharts or candle SVG geometry can swallow the event; skips
   * block-trade diamonds and significant-move dots (they use data-chart-interactive-marker).
   */
  const handleHistoricalPlotDateClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if (loadingHistoricalPrices) return;
    if (historicalChartSuppressClickRef.current) {
      historicalChartSuppressClickRef.current = false;
      return;
    }
    const raw = e.target;
    if (!(raw instanceof Element)) return;
    if (raw.closest('[data-chart-interactive-marker="true"]')) return;
    if (raw.closest('button, a, [role="button"]')) return;

    const wrap = historicalChartWrapRef.current;
    const cp = historicalChartMetricsRef.current;
    const chartData = historicalChartVisibleData;
    if (!wrap || !cp?.offset || chartData.length === 0) return;

    const o = cp.offset;
    const res = clientPointToHistoricalPlotLxLy(e.clientX, e.clientY, wrap, cp);
    if (!res) return;
    if (res.lx < 0 || res.lx > o.width || res.ly < 0 || res.ly > o.height) return;

    const idx = historicalPlotLxToBarIndex(cp, chartData, res.lx, res.ly);
    if (idx == null || idx < 0) return;
    const dataIndex = Math.max(0, Math.min(idx, chartData.length - 1));
    const payload = chartData[dataIndex];
    if (!payload) return;
    void handleDotClick(payload);
    e.stopPropagation();
  };

  // Map backend metric components to frontend display format
  const mapMetricToFrontend = (component: MetricComponent): MetricItemProps => {
    const getTrend = (): 'up' | 'down' | 'neutral' => {
      if (component.interpretation === 'very_strong' || component.interpretation === 'strong') return 'up';
      if (component.interpretation === 'very_weak' || component.interpretation === 'weak') return 'down';
      return 'neutral';
    };

    // Extract the numeric value with units from the description (e.g., "6.4%", "$8.2B", "0.42")
    // This will be shown when expanded
    let displayValue = component.raw_value?.toString() || 'N/A';
    
    // Special handling for analyst expectations metrics
    // Price target upside: display as percentage with 2 decimal places
    if (component.name === 'price_target_upside' && component.raw_value != null) {
      displayValue = `${(component.raw_value * 100).toFixed(2)}%`;
    }
    // Ratios: format to 3 decimal places (eps_revisions, analyst_rating_position, rating_momentum, price_target_dispersion)
    else if (['eps_revisions', 'analyst_rating_position', 'rating_momentum', 'price_target_dispersion'].includes(component.name) && component.raw_value != null) {
      displayValue = component.raw_value.toFixed(3);
    }
    // Special handling for Rule of 40 - format to 1 decimal place (e.g., "38.4")
    else if (component.name === 'rule_of_40' && component.raw_value != null) {
      displayValue = component.raw_value.toFixed(1);
    }
    // Special handling for Debt/EBITDA - ALWAYS show as ratio (e.g., "0.76x")
    // This metric is always a ratio, never a percentage, so we ignore description parsing
    else if (component.name === 'debt_to_ebitda' && component.raw_value != null) {
      displayValue = `${component.raw_value.toFixed(2)}x`;
    }
    // Special handling for momentum metrics - format raw values appropriately
    else if (component.raw_value != null && ['relative_strength', 'relative_performance', 'trend_persistence', 'volume_confirmation', 'volatility_adjusted_momentum', 'momentum_acceleration'].includes(component.name)) {
      if (component.name === 'relative_strength' || component.name === 'relative_performance') {
        // Show as ratio (e.g., "1.144")
        displayValue = component.raw_value.toFixed(3);
      } else if (component.name === 'trend_persistence') {
        // Show as percentage (e.g., "100.0%")
        displayValue = `${(component.raw_value * 100).toFixed(1)}%`;
      } else if (component.name === 'volume_confirmation') {
        // Show as ratio (e.g., "0.128")
        displayValue = component.raw_value.toFixed(3);
      } else if (component.name === 'volatility_adjusted_momentum') {
        // Show as ratio (e.g., "0.527")
        displayValue = component.raw_value.toFixed(3);
      } else if (component.name === 'momentum_acceleration') {
        // Show as percentage (e.g., "-16.1%")
        displayValue = `${(component.raw_value * 100).toFixed(2)}%`;
      }
    }
    // For percentage-based metrics, format with %
    else if (component.description && component.description.match(/-?\d+\.?\d*%/)) {
      const match = component.description.match(/-?\d+\.?\d*%/);
      if (match) displayValue = match[0];
    }
    // For other metrics, try to extract the value from description
    else if (displayValue === 'N/A' && component.description) {
      // Try to extract value like "(6.4%)" or "value of 6.4%" or just "6.4" (including negative signs)
      const valueMatch = component.description.match(/-?[\d.]+%?/);
      if (valueMatch) displayValue = valueMatch[0];
    }

    // Grade is displayed as the badge on the right
    const grade = component.grade || 'N/A';

    // Helper function to convert snake_case to Title Case
    const snakeToTitleCase = (str: string): string => {
      return str
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    // Get user-friendly label
    const labelMap: Record<string, string> = {
      'revenue_growth': 'Revenue Growth',
      'gross_margin': 'Gross Margin',
      'rule_of_40': 'Rule of 40',
      'fcf_margin': 'Free Cash Flow Margin',
      'net_margin': 'Net Profit Margin',
      'altman_z_score': 'Altman Z-Score',
      'piotroski_f_score': 'Piotroski F-Score',
      'return_on_equity': 'Return on Equity (ROE)',
      'return_on_assets': 'Return on Assets (ROA)',
      'roic': 'Return on Invested Capital',
      'operating_margin': 'Operating Margin',
      'operating_efficiency': 'Operating Efficiency',
      'debt_to_equity': 'Debt to Equity',
      'debt_to_ebitda': 'Debt/EBITDA',
      'cash_runway': 'Cash Runway (months)',
      'cash_to_market_cap': 'Cash/Market Cap',
      'rnd_intensity': 'R&D Intensity',
      'burn_trend': 'Burn Rate Trend',
      'dividend_yield': 'Dividend Yield',
      'cash_flow_margin': 'Cash Flow Margin (FFO)',
      'revenue_stability': 'Revenue Stability',
      'operating_cash_flow_margin': 'Operating Cash Flow Margin',
      'fcf_yield': 'Free Cash Flow Yield',
      'momentum': 'Momentum',
      'relative_strength': 'Relative Performance',
      'relative_performance': 'Relative Performance',
      'trend_persistence': 'Trend Persistence',
      'volume_confirmation': 'Volume Confirmation',
      'volatility_adjusted_momentum': 'Trend Quality',
      'momentum_acceleration': 'Momentum Acceleration',
      'breadth': 'Breadth',
      // Expectations metrics (analyst expectations)
      'eps_revisions': 'EPS Revisions',
      'analyst_rating_position': 'Analyst Position',
      'rating_momentum': 'Analyst Trend',
      'price_target_upside': 'Target Price',
      'price_target_dispersion': 'Price Target Dispersion',
      'estimate_dispersion_confidence': 'Price Target Dispersion',
    };

    // Enhanced descriptions for sentiment metrics with raw value context
    const getSentimentMetricExplanation = (metric: any, rawValue: number | null): string => {
      if (rawValue === null || rawValue === undefined) {
        // Fallback if no raw value available
        if (metric.detailed_description) return metric.detailed_description;
        if (metric.why_it_matters) return metric.why_it_matters;
        return '';
      }
      
      const value = rawValue;
      const absValue = Math.abs(value);
      
      switch (metric.name) {
        case 'eps_revisions':
          // Raw value is a ratio: positive = upgrades, negative = downgrades
          // Typical range: -1.0 to +1.0, where magnitude indicates strength
          const epsDirection = value > 0 ? 'upgraded' : value < 0 ? 'downgraded' : 'kept unchanged';
          const epsMagnitude = absValue > 0.5 ? 'significantly' : absValue > 0.2 ? 'moderately' : 'slightly';
          const epsSentiment = value > 0.2 ? 'very bullish' : value > 0 ? 'bullish' : value < -0.2 ? 'very bearish' : 'bearish';
          return `Tracks changes in forward earnings estimates over 30-day and 90-day periods. The value of ${value.toFixed(3)} means analysts have ${epsMagnitude} ${epsDirection} their forward earnings estimates, indicating ${epsSentiment} sentiment. Positive values indicate improving analyst outlook and stronger expected earnings, while negative values suggest analysts are cutting estimates due to concerns. Values above +0.2 are very bullish (strong upgrades), values between 0 and +0.2 are moderately positive, values between -0.2 and 0 are concerning (downgrades), and values below -0.2 are very bearish (significant estimate cuts).`;
        
        case 'analyst_rating_position':
          // Raw value ranges from -1.0 to +1.0, representing (Buys - Sells) / Total
          const biasPercent = (value * 100).toFixed(2);
          const biasInterpretation = value > 0.3 ? 'strongly bullish' : value > 0.1 ? 'moderately bullish' : value > -0.1 ? 'neutral/mixed' : value > -0.3 ? 'moderately bearish' : 'strongly bearish';
          return `Measures the balance between buy and sell recommendations from analysts. The value of ${value.toFixed(3)} (or ${biasPercent}% when expressed as percentage) indicates a ${biasInterpretation} consensus. This metric ranges from -1.0 (all analysts recommend selling) to +1.0 (all analysts recommend buying). Positive values mean more buy recommendations than sell recommendations. Values above +0.3 (+30%) are very bullish, values between +0.1 and +0.3 are moderately positive, values near 0 indicate mixed sentiment, and negative values suggest more sell than buy recommendations, which is bearish.`;
        
        case 'rating_momentum':
          // Raw value shows change in rating distribution over time
          // Positive = upgrades trending, negative = downgrades trending
          const momentumDirection = value > 0 ? 'improving' : value < 0 ? 'deteriorating' : 'stable';
          const momentumStrength = absValue > 0.05 ? 'significantly' : absValue > 0.02 ? 'moderately' : 'slightly';
          return `Tracks changes in analyst ratings over time, showing whether sentiment is improving or deteriorating. The value of ${value.toFixed(3)} indicates ratings are ${momentumStrength} ${momentumDirection}. Positive values mean analysts are upgrading their ratings (becoming more bullish over time), while negative values suggest downgrades (becoming more bearish). Values above +0.05 show strong positive momentum (analysts increasingly bullish), values between 0 and +0.05 indicate mild improvement, values between -0.05 and 0 show mild deterioration, and values below -0.05 indicate significant negative momentum (analysts losing confidence).`;
        
        case 'price_target_upside':
          // Raw value is already a decimal percentage: 0.10 = 10% upside
          const upsidePercent = (value * 100).toFixed(2);
          const upsideInterpretation = value > 0.3 ? 'very significant upside' : value > 0.2 ? 'significant upside' : value > 0.1 ? 'moderate upside' : value > 0 ? 'limited upside' : 'analysts expect downside';
          return `Difference between consensus analyst price target and current price. The value of ${upsidePercent}% means analysts expect ${upsideInterpretation}. Positive values indicate analysts believe the stock will rise above its current price. Values above 20% are very bullish, values between 10-20% are considered healthy upside potential, values between 0-10% show limited expected gains, and negative values suggest analysts expect the stock to fall below current price, which is bearish.`;
        
        case 'price_target_dispersion':
          // Raw value is a ratio: (High - Low) / Consensus
          // Lower = more agreement, Higher = more disagreement
          const dispersionPercent = (value * 100).toFixed(2);
          const dispersionInterpretation = value < 0.2 ? 'very strong consensus' : value < 0.3 ? 'strong consensus' : value < 0.5 ? 'moderate agreement' : value < 0.7 ? 'significant disagreement' : 'high uncertainty';
          return `Measures analyst agreement by comparing the range of estimates. The value of ${value.toFixed(3)} (or ${dispersionPercent}% spread) indicates ${dispersionInterpretation} among analysts. This metric calculates the difference between highest and lowest estimates relative to consensus. Lower values mean analysts largely agree on estimates, showing confidence and clarity. Values below 0.20 (20% spread) show very strong consensus, values 0.20-0.30 show good agreement, values 0.30-0.50 indicate moderate disagreement, values 0.50-0.70 suggest significant uncertainty, and values above 0.70 show high uncertainty and lack of consensus, which can indicate volatile or unpredictable prospects.`;
        
        default:
          // Fallback to backend description (should not reach here for sentiment metrics)
          if (metric.detailed_description) {
            return metric.detailed_description;
          }
          if (metric.why_it_matters) {
            return metric.why_it_matters;
          }
          return '';
      }
    };

    // Enhanced descriptions for fundamental metrics with raw value context
    const getFundamentalMetricExplanation = (metric: any, rawValue: number | null): string => {
      if (rawValue === null || rawValue === undefined) {
        // Fallback if no raw value available
        if (metric.detailed_description) return metric.detailed_description;
        if (metric.why_it_matters) return metric.why_it_matters;
        return localExplanationMap[metric.name] || '';
      }
      
      const value = rawValue;
      
      switch (metric.name) {
        case 'revenue_growth':
          // Value is percentage: 6.4 = 6.4% growth
          const growthInterpretation = value > 20 ? 'exceptional growth' : value > 10 ? 'strong growth' : value > 5 ? 'moderate growth' : value > 0 ? 'slow growth' : 'declining revenue';
          return `Measures sales increase over time. The value of ${value.toFixed(1)}% indicates ${growthInterpretation}. Higher growth indicates expanding business and market demand. For large companies (>$200B market cap), growth above 5% is strong, while smaller companies typically need 10%+ to be considered high-growth. Values above 20% are exceptional, values 10-20% are very strong, values 5-10% are solid for mature companies, values 0-5% show slow growth, and negative values indicate declining revenue, which is concerning.`;
        
        case 'gross_margin':
          // Value is percentage: 46.9 = 46.9% margin
          const grossMarginInterpretation = value > 60 ? 'excellent pricing power' : value > 40 ? 'strong margins' : value > 25 ? 'moderate margins' : value > 10 ? 'low margins' : 'very low margins';
          return `Revenue minus cost of goods sold, as a percentage. The value of ${value.toFixed(1)}% indicates ${grossMarginInterpretation}. Higher margins suggest better pricing power and efficiency. Software companies typically have 60-80% margins, while hardware/commodity businesses have lower margins (20-40%). Values above 60% are excellent, values 40-60% are strong, values 25-40% are moderate, values 10-25% are low (commodity-like), and values below 10% indicate very thin margins and pricing pressure.`;
        
        case 'rule_of_40':
          // Value is sum of growth% + margin%: 38.4 = 38.4
          const rule40Interpretation = value > 60 ? 'elite performance' : value > 40 ? 'excellent balance' : value > 30 ? 'good balance' : value > 20 ? 'moderate' : 'needs improvement';
          return `Growth rate plus profit margin. The value of ${value.toFixed(1)} indicates ${rule40Interpretation}. For SaaS and growth companies, a score above 40 indicates healthy balance of growth and profitability. This metric balances the tradeoff between growth and profitability - companies can grow fast with low margins, or grow slowly with high margins, but both should sum to 40+. Values above 60 are elite, values 40-60 are excellent, values 30-40 are good, values 20-30 are moderate, and values below 20 suggest the company needs to improve either growth or profitability.`;
        
        case 'fcf_margin':
          // Value is percentage: 23.7 = 23.7% margin
          const fcfInterpretation = value > 25 ? 'exceptional cash generation' : value > 15 ? 'strong cash generation' : value > 5 ? 'moderate cash generation' : value > 0 ? 'weak cash generation' : 'cash burn';
          return `Free cash flow as percentage of revenue. The value of ${value.toFixed(1)}% indicates ${fcfInterpretation}. Shows how much cash the business generates after capital expenditures. Profitable tech companies typically convert 15-30% of revenue to free cash flow. Values above 25% are exceptional, values 15-25% are strong, values 5-15% are moderate, values 0-5% show weak cash generation, and negative values indicate the company is burning cash, which requires external funding.`;
        
        case 'net_margin':
          // Value is percentage: 26.9 = 26.9% margin
          const netMarginInterpretation = value > 20 ? 'highly profitable' : value > 10 ? 'strongly profitable' : value > 5 ? 'moderately profitable' : value > 0 ? 'thinly profitable' : 'unprofitable';
          return `Net profit as percentage of revenue. The value of ${value.toFixed(1)}% indicates ${netMarginInterpretation}. Indicates how much of each dollar earned becomes actual profit. Growth companies may sacrifice margin for growth, but mature companies should be profitable. Values above 20% are highly profitable, values 10-20% are strong, values 5-10% are moderate, values 0-5% are thin margins, and negative values indicate losses, which is concerning for mature companies but may be acceptable for high-growth companies investing heavily.`;
        
        case 'altman_z_score':
          // Value is a score: 10.78 = 10.78
          const altmanInterpretation = value > 3.0 ? 'safe zone' : value > 1.8 ? 'caution zone' : 'distress zone';
          return `Bankruptcy risk predictor. The value of ${value.toFixed(2)} indicates ${altmanInterpretation}. Above 3.0 is safe (low bankruptcy risk), 1.8-3.0 is caution zone (moderate risk), below 1.8 signals distress (high bankruptcy risk). Note: This metric was designed for manufacturing companies with tangible assets. Tech and asset-light companies may score differently, so it's often shown for reference only.`;
        
        case 'piotroski_f_score':
          // Value is score 0-9: 7 = 7
          const piotroskiInterpretation = value >= 8 ? 'very strong fundamentals' : value >= 6 ? 'strong fundamentals' : value >= 4 ? 'moderate fundamentals' : value >= 2 ? 'weak fundamentals' : 'very weak fundamentals';
          return `Financial strength score (0-9). The value of ${value.toFixed(0)} indicates ${piotroskiInterpretation}. Higher scores indicate stronger fundamentals across profitability, leverage, and efficiency. Scores of 8-9 are very strong, 6-7 are strong, 4-5 are moderate, 2-3 are weak, and 0-1 are very weak. This composite score evaluates nine criteria including profitability, operating efficiency, and leverage.`;
        
        case 'return_on_equity':
          // Value is percentage: 15.2 = 15.2% ROE
          const roeInterpretation = value > 20 ? 'excellent efficiency' : value > 15 ? 'strong efficiency' : value > 10 ? 'moderate efficiency' : value > 5 ? 'weak efficiency' : 'poor efficiency';
          return `Profit generated per dollar of shareholder equity. The value of ${value.toFixed(1)}% indicates ${roeInterpretation}. Measures how efficiently management uses investor capital. Higher values mean the company generates more profit from each dollar of equity. Values above 20% are excellent, values 15-20% are strong, values 10-15% are moderate, values 5-10% are weak, and values below 5% indicate poor capital efficiency. Compare to industry averages as ROE varies by sector.`;
        
        case 'return_on_assets':
          // Value is percentage: 8.5 = 8.5% ROA
          const roaInterpretation = value > 10 ? 'excellent asset efficiency' : value > 5 ? 'strong asset efficiency' : value > 2 ? 'moderate efficiency' : value > 0 ? 'weak efficiency' : 'poor efficiency';
          return `Profit generated per dollar of total assets. The value of ${value.toFixed(1)}% indicates ${roaInterpretation}. Shows how efficiently the company uses its resources. Higher values mean better asset utilization. Values above 10% are excellent, values 5-10% are strong, values 2-5% are moderate, values 0-2% are weak, and negative values indicate poor asset efficiency. Asset-light companies (like software) typically have higher ROA than asset-heavy companies (like manufacturing).`;
        
        case 'roic':
          // Value is percentage: 8.0 = 8.0% ROIC
          const roicInterpretation = value > 15 ? 'excellent capital efficiency' : value > 10 ? 'strong capital efficiency' : value > 5 ? 'moderate efficiency' : value > 0 ? 'weak efficiency' : 'destroying value';
          return `Return on Invested Capital - measures capital efficiency. The value of ${value.toFixed(1)}% indicates ${roicInterpretation}. ROIC > WACC (weighted average cost of capital) creates value for shareholders. This is one of the most important metrics as it shows whether the company generates returns above its cost of capital. Values above 15% are excellent, values 10-15% are strong, values 5-10% are moderate, values 0-5% are weak, and negative values indicate the company is destroying value. Compare to the company's WACC (typically 8-12% for most companies).`;
        
        case 'operating_margin':
          // Value is percentage: 7.2 = 7.2% margin
          const opMarginInterpretation = value > 20 ? 'excellent profitability' : value > 15 ? 'strong profitability' : value > 10 ? 'moderate profitability' : value > 5 ? 'weak profitability' : value > 0 ? 'thin margins' : 'operating losses';
          return `Operating income as percentage of revenue. The value of ${value.toFixed(1)}% indicates ${opMarginInterpretation}. Reflects core business profitability before interest and taxes. Higher margins indicate better operational efficiency and pricing power. Values above 20% are excellent, values 15-20% are strong, values 10-15% are moderate, values 5-10% are weak, values 0-5% are thin margins, and negative values indicate operating losses. This metric excludes non-operating items, making it a good measure of core business health.`;
        
        case 'operating_efficiency':
          // Value is typically a ratio or percentage
          const efficiencyInterpretation = value > 0.8 ? 'highly efficient' : value > 0.6 ? 'efficient' : value > 0.4 ? 'moderately efficient' : value > 0.2 ? 'inefficient' : 'very inefficient';
          return `How well the company converts operating expenses into revenue. The value of ${value.toFixed(2)} indicates ${efficiencyInterpretation}. Higher values indicate better cost management. This metric measures the relationship between expenses and revenue generation. Values above 0.8 are highly efficient, values 0.6-0.8 are efficient, values 0.4-0.6 are moderate, values 0.2-0.4 are inefficient, and values below 0.2 indicate very poor expense management.`;
        
        case 'debt_to_equity':
          // Value is ratio: 0.5 = 0.5x or 50% debt to equity
          const dteInterpretation = value < 0.3 ? 'low leverage' : value < 0.7 ? 'moderate leverage' : value < 1.5 ? 'high leverage' : 'very high leverage';
          return `Total debt divided by shareholder equity. The value of ${value.toFixed(2)}x (or ${(value * 100).toFixed(0)}% when expressed as percentage) indicates ${dteInterpretation}. Lower values mean less leverage and financial risk. Values below 0.3x (30%) are low leverage, values 0.3-0.7x are moderate, values 0.7-1.5x are high leverage, and values above 1.5x indicate very high leverage and increased financial risk. Acceptable levels vary by industry - capital-intensive industries (utilities, real estate) typically have higher ratios than tech companies.`;
        
        case 'debt_to_ebitda':
          // Value is ratio: 2.5 = 2.5x
          const debtEbitdaInterpretation = value < 2 ? 'low leverage' : value < 4 ? 'moderate leverage' : value < 6 ? 'high leverage' : 'very high leverage';
          return `Net debt divided by EBITDA (as a ratio). The value of ${value.toFixed(2)}x indicates ${debtEbitdaInterpretation}. Shows how many years of earnings needed to pay off debt. Lower is better. Values below 2x are low leverage, values 2-4x are moderate, values 4-6x are high leverage, and values above 6x indicate very high leverage and potential financial stress. This metric is particularly useful for comparing companies as it normalizes for company size and profitability.`;
        
        case 'cash_runway':
          // Value is months: 18 = 18 months
          const runwayInterpretation = value > 24 ? 'excellent runway' : value > 12 ? 'adequate runway' : value > 6 ? 'limited runway' : value > 0 ? 'critical runway' : 'out of cash';
          return `Months of operations the company can fund with current cash. The value of ${value.toFixed(0)} months indicates ${runwayInterpretation}. Critical for unprofitable growth companies. This shows how long the company can operate before needing additional funding. Values above 24 months are excellent, values 12-24 months are adequate, values 6-12 months are limited (may need funding soon), values 0-6 months are critical (urgent need for funding), and values of 0 indicate the company is out of cash. Companies with less than 12 months runway face high risk if they can't raise capital.`;
        
        case 'cash_to_market_cap':
          // Value is percentage: 15.2 = 15.2%
          const cashMcInterpretation = value > 30 ? 'very high cash position' : value > 15 ? 'high cash position' : value > 5 ? 'moderate cash' : value > 0 ? 'low cash' : 'minimal cash';
          return `Cash and equivalents as percentage of market value. The value of ${value.toFixed(1)}% indicates ${cashMcInterpretation}. High values can indicate undervaluation or acquisition potential. This metric shows how much of the company's market value is backed by cash. Values above 30% are very high (may indicate undervaluation or special situations), values 15-30% are high, values 5-15% are moderate, values 0-5% are low, and values near 0% indicate minimal cash reserves. Very high values (>50%) may also indicate the company is hoarding cash inefficiently.`;
        
        case 'rnd_intensity':
          // Value is percentage: 12.5 = 12.5% of revenue
          const rndInterpretation = value > 20 ? 'very high R&D investment' : value > 10 ? 'high R&D investment' : value > 5 ? 'moderate R&D' : value > 0 ? 'low R&D' : 'minimal R&D';
          return `R&D spending as percentage of revenue. The value of ${value.toFixed(1)}% indicates ${rndInterpretation}. High values indicate investment in innovation and future products. This shows how much the company reinvests in research and development. Values above 20% are very high (typical for biotech/pharma), values 10-20% are high (typical for tech), values 5-10% are moderate, values 0-5% are low, and values near 0% indicate minimal R&D investment. High R&D intensity is positive for growth companies but may reduce current profitability.`;
        
        case 'burn_trend':
          // Value is typically positive (improving) or negative (worsening)
          const burnInterpretation = value > 0.1 ? 'significantly improving' : value > 0 ? 'improving' : value > -0.1 ? 'slightly worsening' : 'significantly worsening';
          return `Whether cash burn rate is increasing or decreasing. The value of ${value.toFixed(3)} indicates the burn trend is ${burnInterpretation}. Improving trend (positive values) is positive for growth companies as it shows they're moving toward profitability. Negative values indicate burn rate is increasing, which means the company is consuming cash faster. Values above +0.1 show significant improvement, values 0 to +0.1 show improvement, values -0.1 to 0 show slight deterioration, and values below -0.1 indicate significant worsening of cash burn.`;
        
        case 'dividend_yield':
          // Value is percentage: 2.5 = 2.5% yield
          const divYieldInterpretation = value > 5 ? 'very high yield' : value > 3 ? 'high yield' : value > 1 ? 'moderate yield' : value > 0 ? 'low yield' : 'no dividend';
          return `Annual dividends as percentage of stock price. The value of ${value.toFixed(2)}% indicates ${divYieldInterpretation}. Provides income return to shareholders. Higher yields provide more income but may indicate the stock price has fallen (dividend yield = dividend/price). Values above 5% are very high (may indicate value trap or high risk), values 3-5% are high, values 1-3% are moderate, values 0-1% are low, and 0% means the company doesn't pay dividends. Growth companies typically don't pay dividends as they reinvest profits.`;
        
        case 'cash_flow_margin':
          // Value is percentage: 15.2 = 15.2% margin
          const cfMarginInterpretation = value > 25 ? 'excellent cash generation' : value > 15 ? 'strong cash generation' : value > 5 ? 'moderate cash generation' : value > 0 ? 'weak cash generation' : 'cash burn';
          return `Funds from operations as percentage of revenue. The value of ${value.toFixed(1)}% indicates ${cfMarginInterpretation}. Shows cash generation from core business activities. This metric focuses on operating cash flow, which is more reliable than earnings. Values above 25% are excellent, values 15-25% are strong, values 5-15% are moderate, values 0-5% are weak, and negative values indicate cash burn from operations. Higher values indicate the company converts revenue to cash efficiently.`;
        
        case 'revenue_stability':
          // Value is typically a coefficient of variation or similar measure
          const stabilityInterpretation = value > 0.9 ? 'very stable' : value > 0.7 ? 'stable' : value > 0.5 ? 'moderately stable' : value > 0.3 ? 'volatile' : 'highly volatile';
          return `Consistency of revenue over time. The value of ${value.toFixed(2)} indicates ${stabilityInterpretation}. Higher stability indicates predictable business model. This metric measures how consistent revenue is across periods. Values above 0.9 are very stable (predictable revenue), values 0.7-0.9 are stable, values 0.5-0.7 are moderately stable, values 0.3-0.5 are volatile, and values below 0.3 are highly volatile. Stable revenue is valued by investors as it reduces risk and enables better planning.`;
        
        case 'operating_cash_flow_margin':
          // Value is percentage: 12.5 = 12.5% margin
          const ocfMarginInterpretation = value > 20 ? 'excellent cash generation' : value > 10 ? 'strong cash generation' : value > 5 ? 'moderate cash generation' : value > 0 ? 'weak cash generation' : 'cash burn';
          return `Operating cash flow as percentage of revenue. The value of ${value.toFixed(1)}% indicates ${ocfMarginInterpretation}. Measures cash generated from operations. This is a key metric as it shows actual cash generation, not just accounting profits. Values above 20% are excellent, values 10-20% are strong, values 5-10% are moderate, values 0-5% are weak, and negative values indicate cash burn from operations. Companies with high operating cash flow margins have strong financial flexibility.`;
        
        case 'fcf_yield':
          // Value is percentage: 5.2 = 5.2% yield
          const fcfYieldInterpretation = value > 8 ? 'very high yield' : value > 5 ? 'high yield' : value > 3 ? 'moderate yield' : value > 0 ? 'low yield' : 'negative yield';
          return `Free cash flow divided by market cap. The value of ${value.toFixed(1)}% indicates ${fcfYieldInterpretation}. Higher yields can indicate undervaluation. This metric shows the return an investor would get if all free cash flow were paid as dividends. Values above 8% are very high (may indicate undervaluation), values 5-8% are high, values 3-5% are moderate, values 0-3% are low, and negative values indicate the company is burning cash. Compare to bond yields and other investment opportunities.`;
        
        default:
          // Fallback to backend description or local map
          if (metric.detailed_description) {
            return metric.detailed_description;
          }
          if (metric.why_it_matters) {
            return metric.why_it_matters;
          }
          return localExplanationMap[metric.name] || '';
      }
    };

    // Get metric explanation
    // Prioritize enhanced descriptions for sentiment and fundamentals, then backend detailed_description, fallback to local map
    const getMetricExplanation = (metric: any): string => {
      // Check if this is a sentiment metric that needs enhanced explanation
      const sentimentMetrics = ['eps_revisions', 'analyst_rating_position', 'rating_momentum', 'price_target_upside', 'price_target_dispersion'];
      if (sentimentMetrics.includes(metric.name)) {
        return getSentimentMetricExplanation(metric, metric.raw_value);
      }
      
      // Check if this is a fundamental metric that needs enhanced explanation
      const fundamentalMetrics = [
        'revenue_growth', 'gross_margin', 'rule_of_40', 'fcf_margin', 'net_margin',
        'altman_z_score', 'piotroski_f_score', 'return_on_equity', 'return_on_assets', 'roic',
        'operating_margin', 'operating_efficiency', 'debt_to_equity', 'debt_to_ebitda',
        'cash_runway', 'cash_to_market_cap', 'rnd_intensity', 'burn_trend', 'dividend_yield',
        'cash_flow_margin', 'revenue_stability', 'operating_cash_flow_margin', 'fcf_yield'
      ];
      if (fundamentalMetrics.includes(metric.name)) {
        return getFundamentalMetricExplanation(metric, metric.raw_value);
      }
      
      // For other metrics, use backend description or fallback
      if (metric.detailed_description) {
        return metric.detailed_description;
      }
      
      // For sector models, use why_it_matters field
      if (metric.why_it_matters) {
        return metric.why_it_matters;
      }
      
      // Fallback to local map (for backwards compatibility with old cache data)
      return localExplanationMap[metric.name] || '';
    };

    // Local fallback map for backwards compatibility with old cached data
    const localExplanationMap: Record<string, string> = {
      'revenue_growth': 'Measures sales increase over time. Higher growth indicates expanding business and market demand.',
      'gross_margin': 'Revenue minus cost of goods sold, as a percentage. Higher margins suggest better pricing power and efficiency.',
      'rule_of_40': 'Growth rate plus profit margin. For SaaS/growth companies, a score above 40 indicates healthy balance of growth and profitability.',
      'fcf_margin': 'Free cash flow as percentage of revenue. Shows how much cash the business generates after capital expenditures.',
      'net_margin': 'Net profit as percentage of revenue. Indicates how much of each dollar earned becomes actual profit.',
      'altman_z_score': 'Bankruptcy risk predictor. Above 3.0 is safe, 1.8-3.0 is caution zone, below 1.8 signals distress.',
      'piotroski_f_score': 'Financial strength score (0-9). Higher scores indicate stronger fundamentals across profitability, leverage, and efficiency.',
      'return_on_equity': 'Profit generated per dollar of shareholder equity. Measures how efficiently management uses investor capital.',
      'return_on_assets': 'Profit generated per dollar of total assets. Shows how efficiently the company uses its resources.',
      'operating_margin': 'Operating income as percentage of revenue. Reflects core business profitability before interest and taxes.',
      'operating_efficiency': 'How well the company converts operating expenses into revenue. Higher values indicate better cost management.',
      'debt_to_equity': 'Total debt divided by shareholder equity. Lower values mean less leverage and financial risk.',
      'debt_to_ebitda': 'Net debt divided by EBITDA (as a ratio). Shows how many years of earnings needed to pay off debt. Lower is better.',
      'cash_runway': 'Months of operations the company can fund with current cash. Critical for unprofitable growth companies.',
      'cash_to_market_cap': 'Cash and equivalents as percentage of market value. High values can indicate undervaluation or acquisition potential.',
      'rnd_intensity': 'R&D spending as percentage of revenue. High values indicate investment in innovation and future products.',
      'burn_trend': 'Whether cash burn rate is increasing or decreasing. Improving trend is positive for growth companies.',
      'dividend_yield': 'Annual dividends as percentage of stock price. Provides income return to shareholders.',
      'cash_flow_margin': 'Funds from operations as percentage of revenue. Shows cash generation from core business activities.',
      'revenue_stability': 'Consistency of revenue over time. Higher stability indicates predictable business model.',
      'operating_cash_flow_margin': 'Operating cash flow as percentage of revenue. Measures cash generated from operations.',
      'fcf_yield': 'Free cash flow divided by market cap. Higher yields can indicate undervaluation.',
      'momentum': 'Recent price trend strength. Measures velocity and acceleration of price movements.',
      'relative_strength': 'Stock performance versus market or sector. Higher values indicate outperformance.',
      'trend_persistence': 'Evaluates trend quality through moving average alignment and time above 200 DMA.',
      'volume_confirmation': 'Measures accumulation vs distribution through volume patterns.',
      'volatility_adjusted_momentum': 'Return per unit of risk. Separates clean trends from chaotic spikes.',
      'momentum_acceleration': 'Measures whether momentum is strengthening or weakening relative to recent history. Detects inflection points and trend changes.',
      'breadth': 'Participation level across technical indicators and timeframes. Broader strength suggests more sustainable moves.',
    };

    // Get the detailed explanation
    const detailedExplanation = getMetricExplanation(component);
    
    // Don't add the 📊 prefix, just use the explanation as-is
    const fullExplanation = detailedExplanation;
    
    // Determine calculation method tag for revenue growth (only shown in expanded view)
    let calculationMethod: string | undefined = undefined;
    if (component.name === 'revenue_growth') {
      // Check description to determine if it's 3yr avg or YoY
      const desc = component.description || '';
      if (desc.includes('(3yr avg)') || desc.includes('(3yr)') || desc.includes('3yr avg')) {
        calculationMethod = '(3 Yr Avg)';
      } else {
        // Tech model uses YoY by default
        calculationMethod = '(YoY)';
      }
    }
    
    // Extract and transform quarterly revenue data for revenue_growth metric
    let quarterlyData: MetricItemProps['quarterlyData'] = undefined;
    if (component.name === 'revenue_growth' && component.calculation_details?.quarterly_revenue) {
      const quarterlyRevenue = component.calculation_details.quarterly_revenue;
      
      // Transform backend format to frontend format
      // Backend format: [{ date, period, fiscal_year, revenue }, ...]
      // Frontend format: { quarters: [{ quarter, revenue, yearLabel?, secFilingUrl? }], currentTTM, previousTTM, growthRate }
      
      // Sort by date descending (newest first) - most recent at top
      const sortedQuarters = [...quarterlyRevenue].sort((a, b) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
      
      // Transform to frontend format
      const quarters = sortedQuarters.map((q, idx) => {
        // Format quarter label: "Q1 2025"
        const quarterLabel = `${q.period} ${q.fiscal_year}`;
        
        // Add year label for first quarter of each year (checking previous quarter's year)
        let yearLabel: string | undefined = undefined;
        if (idx === 0 || sortedQuarters[idx - 1].fiscal_year !== q.fiscal_year) {
          yearLabel = q.fiscal_year;
        }
        
        // Generate SEC filing URL (10-Q for Q1-Q3, 10-K for Q4)
        // Format: https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK={CIK}&type={TYPE}&dateb=&owner=exclude&count=40
        // Note: We don't have CIK here, so we'll leave secFilingUrl undefined for now
        // It can be populated later if needed
        
        return {
          quarter: quarterLabel,
          revenue: q.revenue, // Revenue in raw dollars (e.g., 57006000000)
          yearLabel: yearLabel,
          secFilingUrl: undefined // Can be added later if CIK is available
        };
      });
      
      // Calculate TTM (Trailing Twelve Months)
      // Current TTM = sum of first 4 quarters (most recent, since sorted newest first)
      // Previous TTM = sum of quarters 4-7 (prior 4 quarters)
      const currentTTM = quarters.slice(0, 4).reduce((sum, q) => sum + q.revenue, 0);
      const previousTTM = quarters.slice(4, 8).reduce((sum, q) => sum + q.revenue, 0);
      
      // Calculate YoY growth rate
      const growthRate = previousTTM > 0 
        ? `${((currentTTM - previousTTM) / previousTTM * 100).toFixed(1)}%`
        : 'N/A';
      
      // Add + sign if positive
      const formattedGrowthRate = growthRate !== 'N/A' && !growthRate.startsWith('-')
        ? `+${growthRate}`
        : growthRate;
      
      quarterlyData = {
        quarters: quarters,
        currentTTM: currentTTM,
        previousTTM: previousTTM,
        growthRate: formattedGrowthRate
      };
    }
    
    // Extract and transform quarterly gross margin data for gross_margin metric
    if (component.name === 'gross_margin' && component.calculation_details?.quarterly_gross_profit) {
      const quarterlyGrossProfit = component.calculation_details.quarterly_gross_profit;
      
      // Get revenue data from revenue_growth component to match with gross profit
      const revenueGrowthComponent = financialHealth.components.find(c => c.name === 'revenue_growth');
      const quarterlyRevenue = revenueGrowthComponent?.calculation_details?.quarterly_revenue || [];
      
      // Create a map of revenue by date for quick lookup
      const revenueMap = new Map<string, number>();
      quarterlyRevenue.forEach((q: any) => {
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        revenueMap.set(key, q.revenue);
      });
      
      // Sort gross profit quarters by date descending (newest first)
      const sortedGrossProfitQuarters = [...quarterlyGrossProfit].sort((a, b) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
      
      // Transform to frontend format, matching with revenue data
      const quarters = sortedGrossProfitQuarters.map((q, idx) => {
        // Format quarter label: "Q1 2025"
        const quarterLabel = `${q.period} ${q.fiscal_year}`;
        
        // Add year label for first quarter of each year
        let yearLabel: string | undefined = undefined;
        if (idx === 0 || sortedGrossProfitQuarters[idx - 1].fiscal_year !== q.fiscal_year) {
          yearLabel = q.fiscal_year;
        }
        
        // Match revenue by date/period/fiscal_year
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        const revenue = revenueMap.get(key) || 0;
        
        return {
          quarter: quarterLabel,
          revenue: revenue,
          grossProfit: q.grossProfit,
          yearLabel: yearLabel,
          secFilingUrl: undefined // Can be added later if CIK is available
        };
      });
      
      // Calculate TTM (Trailing Twelve Months) Gross Margin
      // Total Gross Profit (Last 4 Qtrs) = sum of gross profit for first 4 quarters
      // Total Revenue (Last 4 Qtrs) = sum of revenue for first 4 quarters
      const totalGrossProfit = quarters.slice(0, 4).reduce((sum, q) => sum + (q.grossProfit || 0), 0);
      const totalRevenue = quarters.slice(0, 4).reduce((sum, q) => sum + q.revenue, 0);
      
      // Calculate Gross Margin (TTM) = Total Gross Profit / Total Revenue
      const grossMarginTTM = totalRevenue > 0 
        ? `${((totalGrossProfit / totalRevenue) * 100).toFixed(1)}%`
        : 'N/A';
      
      quarterlyData = {
        quarters: quarters,
        currentTTM: totalRevenue, // Total Revenue (Last 4 Qtrs)
        previousTTM: totalGrossProfit, // Total Gross Profit (Last 4 Qtrs)
        growthRate: grossMarginTTM // Gross Margin (TTM)
      };
    }
    
    // Extract and transform quarterly FCF margin data for fcf_margin metric
    if (component.name === 'fcf_margin' && (component.calculation_details as any)?.quarterly_fcf && (component.calculation_details as any)?.quarterly_revenue) {
      const quarterlyFCF = (component.calculation_details as any).quarterly_fcf;
      const quarterlyRevenue = (component.calculation_details as any).quarterly_revenue;
      
      // Create a map of revenue by date for quick lookup
      const revenueMap = new Map<string, number>();
      quarterlyRevenue.forEach((q: any) => {
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        revenueMap.set(key, q.revenue);
      });
      
      // Sort FCF quarters by date descending (newest first)
      const sortedFCFQuarters = [...quarterlyFCF].sort((a, b) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
      
      // Transform to frontend format, matching with revenue data
      const quarters = sortedFCFQuarters.map((q, idx) => {
        // Format quarter label: "Q1 2025"
        const quarterLabel = `${q.period} ${q.fiscal_year}`;
        
        // Add year label for first quarter of each year
        let yearLabel: string | undefined = undefined;
        if (idx === 0 || sortedFCFQuarters[idx - 1].fiscal_year !== q.fiscal_year) {
          yearLabel = q.fiscal_year;
        }
        
        // Match revenue by date/period/fiscal_year
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        const revenue = revenueMap.get(key) || 0;
        
        return {
          quarter: quarterLabel,
          revenue: revenue,
          fcf: q.freeCashFlow,
          yearLabel: yearLabel,
          secFilingUrl: undefined // Can be added later if CIK is available
        };
      });
      
      // Calculate TTM (Trailing Twelve Months) FCF Margin
      // Total FCF (Last 4 Qtrs) = sum of FCF for first 4 quarters
      // Total Revenue (Last 4 Qtrs) = sum of revenue for first 4 quarters
      const totalFCF = quarters.slice(0, 4).reduce((sum, q) => sum + (q.fcf || 0), 0);
      const totalRevenue = quarters.slice(0, 4).reduce((sum, q) => sum + q.revenue, 0);
      
      // Calculate FCF Margin (TTM) = Total FCF / Total Revenue
      const fcfMarginTTM = totalRevenue > 0 
        ? `${((totalFCF / totalRevenue) * 100).toFixed(1)}%`
        : 'N/A';
      
      quarterlyData = {
        quarters: quarters,
        currentTTM: totalRevenue, // Total Revenue (Last 4 Qtrs)
        previousTTM: totalFCF, // Total FCF (Last 4 Qtrs)
        growthRate: fcfMarginTTM // FCF Margin (TTM)
      };
    }
    
    // Extract and transform quarterly operating margin data for operating_margin metric
    if (component.name === 'operating_margin' && (component.calculation_details as any)?.quarterly_operating_income && (component.calculation_details as any)?.quarterly_revenue) {
      const quarterlyOperatingIncome = (component.calculation_details as any).quarterly_operating_income;
      const quarterlyRevenue = (component.calculation_details as any).quarterly_revenue;
      
      // Create a map of revenue by date for quick lookup
      const revenueMap = new Map<string, number>();
      quarterlyRevenue.forEach((q: any) => {
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        revenueMap.set(key, q.revenue);
      });
      
      // Sort operating income quarters by date descending (newest first)
      const sortedOperatingIncomeQuarters = [...quarterlyOperatingIncome].sort((a, b) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
      
      // Transform to frontend format, matching with revenue data
      const quarters = sortedOperatingIncomeQuarters.map((q, idx) => {
        // Format quarter label: "Q1 2025"
        const quarterLabel = `${q.period} ${q.fiscal_year}`;
        
        // Add year label for first quarter of each year
        let yearLabel: string | undefined = undefined;
        if (idx === 0 || sortedOperatingIncomeQuarters[idx - 1].fiscal_year !== q.fiscal_year) {
          yearLabel = q.fiscal_year;
        }
        
        // Match revenue by date/period/fiscal_year
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        const revenue = revenueMap.get(key) || 0;
        
        return {
          quarter: quarterLabel,
          revenue: revenue,
          operatingIncome: q.operatingIncome,
          yearLabel: yearLabel,
          secFilingUrl: undefined // Can be added later if CIK is available
        };
      });
      
      // Calculate TTM (Trailing Twelve Months) Operating Margin
      // Total Operating Income (Last 4 Qtrs) = sum of operating income for first 4 quarters
      // Total Revenue (Last 4 Qtrs) = sum of revenue for first 4 quarters
      const totalOperatingIncome = quarters.slice(0, 4).reduce((sum, q) => sum + (q.operatingIncome || 0), 0);
      const totalRevenue = quarters.slice(0, 4).reduce((sum, q) => sum + q.revenue, 0);
      
      // Calculate Operating Margin (TTM) = Total Operating Income / Total Revenue
      const operatingMarginTTM = totalRevenue > 0 
        ? `${((totalOperatingIncome / totalRevenue) * 100).toFixed(1)}%`
        : 'N/A';
      
      quarterlyData = {
        quarters: quarters,
        currentTTM: totalRevenue, // Total Revenue (Last 4 Qtrs)
        previousTTM: totalOperatingIncome, // Total Operating Income (Last 4 Qtrs)
        growthRate: operatingMarginTTM // Operating Margin (TTM)
      };
    }
    
    // Extract and transform quarterly net profit margin data for net_margin metric
    if (component.name === 'net_margin' && (component.calculation_details as any)?.quarterly_net_income && (component.calculation_details as any)?.quarterly_revenue) {
      const quarterlyNetIncome = (component.calculation_details as any).quarterly_net_income;
      const quarterlyRevenue = (component.calculation_details as any).quarterly_revenue;
      
      // Create a map of revenue by date for quick lookup
      const revenueMap = new Map<string, number>();
      quarterlyRevenue.forEach((q: any) => {
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        revenueMap.set(key, q.revenue);
      });
      
      // Sort net income quarters by date descending (newest first)
      const sortedNetIncomeQuarters = [...quarterlyNetIncome].sort((a, b) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
      
      // Transform to frontend format, matching with revenue data
      const quarters = sortedNetIncomeQuarters.map((q, idx) => {
        // Format quarter label: "Q1 2025"
        const quarterLabel = `${q.period} ${q.fiscal_year}`;
        
        // Add year label for first quarter of each year
        let yearLabel: string | undefined = undefined;
        if (idx === 0 || sortedNetIncomeQuarters[idx - 1].fiscal_year !== q.fiscal_year) {
          yearLabel = q.fiscal_year;
        }
        
        // Match revenue by date/period/fiscal_year
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        const revenue = revenueMap.get(key) || 0;
        
        return {
          quarter: quarterLabel,
          revenue: revenue,
          netIncome: q.netIncome,
          yearLabel: yearLabel,
          secFilingUrl: undefined // Can be added later if CIK is available
        };
      });
      
      // Calculate TTM (Trailing Twelve Months) Net Profit Margin
      // Total Net Income (Last 4 Qtrs) = sum of net income for first 4 quarters
      // Total Revenue (Last 4 Qtrs) = sum of revenue for first 4 quarters
      const totalNetIncome = quarters.slice(0, 4).reduce((sum, q) => sum + (q.netIncome || 0), 0);
      const totalRevenue = quarters.slice(0, 4).reduce((sum, q) => sum + q.revenue, 0);
      
      // Calculate Net Profit Margin (TTM) = Total Net Income / Total Revenue
      const netMarginTTM = totalRevenue > 0 
        ? `${((totalNetIncome / totalRevenue) * 100).toFixed(1)}%`
        : 'N/A';
      
      quarterlyData = {
        quarters: quarters,
        currentTTM: totalRevenue, // Total Revenue (Last 4 Qtrs)
        previousTTM: totalNetIncome, // Total Net Income (Last 4 Qtrs)
        growthRate: netMarginTTM // Net Profit Margin (TTM)
      };
    }
    
    // Extract and transform quarterly Rule of 40 data
    if (component.name === 'rule_of_40' && (component.calculation_details as any)?.margin_raw_data) {
      const marginRawData = (component.calculation_details as any).margin_raw_data;
      const revenueGrowthRawData = (component.calculation_details as any).revenue_growth_raw_data;
      const marginType = (component.calculation_details as any).margin_type; // "operating" or "fcf"
      const revenueGrowthPct = (component.calculation_details as any).revenue_growth_pct;
      
      // Determine which quarterly data to use
      const quarterlyMarginData = marginType === 'fcf' 
        ? marginRawData.quarterly_fcf 
        : marginRawData.quarterly_operating_income;
      const quarterlyRevenue = marginRawData.quarterly_revenue || revenueGrowthRawData?.quarterly_revenue || [];
      
      // Create a map of revenue by date for quick lookup
      const revenueMap = new Map<string, number>();
      quarterlyRevenue.forEach((q: any) => {
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        revenueMap.set(key, q.revenue);
      });
      
      // Sort margin quarters by date descending (newest first)
      const sortedMarginQuarters = [...(quarterlyMarginData || [])].sort((a, b) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
      
      // Transform to frontend format, matching with revenue data
      const quarters = sortedMarginQuarters.map((q, idx) => {
        // Format quarter label: "Q1 2025"
        const quarterLabel = `${q.period} ${q.fiscal_year}`;
        
        // Add year label for first quarter of each year
        let yearLabel: string | undefined = undefined;
        if (idx === 0 || sortedMarginQuarters[idx - 1].fiscal_year !== q.fiscal_year) {
          yearLabel = q.fiscal_year;
        }
        
        // Match revenue by date/period/fiscal_year
        const key = `${q.date}_${q.period}_${q.fiscal_year}`;
        const revenue = revenueMap.get(key) || 0;
        
        return {
          quarter: quarterLabel,
          revenue: revenue,
          operatingIncome: marginType === 'operating' ? (q.operatingIncome || 0) : undefined,
          fcf: marginType === 'fcf' ? (q.freeCashFlow || 0) : undefined,
          yearLabel: yearLabel,
          secFilingUrl: undefined // Can be added later if CIK is available
        };
      });
      
      // Calculate TTM (Trailing Twelve Months)
      // Total Operating Income/FCF (Last 4 Qtrs) = sum of margin for first 4 quarters
      // Total Revenue (Last 4 Qtrs) = sum of revenue for first 4 quarters
      const totalMargin = quarters.slice(0, 4).reduce((sum, q) => {
        return sum + (marginType === 'operating' ? (q.operatingIncome || 0) : (q.fcf || 0));
      }, 0);
      const totalRevenue = quarters.slice(0, 4).reduce((sum, q) => sum + q.revenue, 0);
      
      // Calculate Margin (TTM) = Total Margin / Total Revenue
      const marginTTM = totalRevenue > 0 
        ? `${((totalMargin / totalRevenue) * 100).toFixed(1)}%`
        : 'N/A';
      
      // Format revenue growth YoY
      const revenueGrowthYoY = revenueGrowthPct !== undefined && revenueGrowthPct !== null
        ? `${revenueGrowthPct >= 0 ? '+' : ''}${revenueGrowthPct.toFixed(1)}%`
        : undefined;
      
      quarterlyData = {
        quarters: quarters,
        currentTTM: totalRevenue, // Total Revenue (Last 4 Qtrs)
        previousTTM: totalMargin, // Total Operating Income/FCF (Last 4 Qtrs)
        growthRate: marginTTM, // Operating Margin/FCF Margin (TTM)
        revenueGrowthYoY: revenueGrowthYoY, // Revenue Growth (YoY TTM)
        useOperatingMargin: marginType === 'operating' // True for growth companies, false for mature
      };
    }
    
    // Extract EPS revisions data for eps_revisions metric
    let epsRevisionsData: MetricItemProps['epsRevisionsData'] = undefined;
    if (component.name === 'eps_revisions') {
      const calcDetails = component.calculation_details as any;
      if (calcDetails?.revision_details?.periods) {
        // Fiscal context can be in revision_details or at top level
        const fiscalContext = calcDetails.revision_details.fiscal_period_context || calcDetails.fiscal_period_context;
        
        epsRevisionsData = {
          periods: calcDetails.revision_details.periods.map((period: any) => ({
            period: period.period,
            period_key: period.period_key,
            up_7d: period.up_7d,
            down_7d: period.down_7d,
            up_30d: period.up_30d,
            down_30d: period.down_30d,
            score: period.score
          })),
          fiscal_context: fiscalContext
        };
      }
    }

    // Extract analyst ratings data for analyst_rating_position metric
    let analystRatingsData: MetricItemProps['analystRatingsData'] = undefined;
    if (component.name === 'analyst_rating_position') {
      const calcDetails = component.calculation_details as any;
      const ratingDistribution = calcDetails?.rating_distribution;
      const rawRatings = calcDetails?.raw_analyst_ratings;
      
      if (ratingDistribution?.rating_counts) {
        const current = ratingDistribution.rating_counts;
        const currentDate = ratingDistribution.date || rawRatings?.[0]?.date;
        
        // Prepare current snapshot
        const currentSnapshot = {
          strong_buy: current.strong_buy || 0,
          buy: current.buy || 0,
          hold: current.hold || 0,
          sell: current.sell || 0,
          strong_sell: current.strong_sell || 0,
          total: current.total || 0,
          date: currentDate || ''
        };

        // Prepare historical data if multiple snapshots available
        let historical: Array<{
          date: string;
          strong_buy: number;
          buy: number;
          hold: number;
          sell: number;
          strong_sell: number;
          total: number;
          net_bias: number;
        }> | undefined = undefined;

        if (rawRatings && Array.isArray(rawRatings) && rawRatings.length > 1) {
          // Sort by date (newest first, then reverse for chart)
          const sortedRatings = [...rawRatings].sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA; // Newest first
          }).reverse(); // Reverse to show oldest to newest on chart

          historical = sortedRatings.map((snapshot: any) => {
            const strongBuy = snapshot.analystRatingsStrongBuy || 0;
            const buy = snapshot.analystRatingsBuy || 0;
            const hold = snapshot.analystRatingsHold || 0;
            const sell = snapshot.analystRatingsSell || 0;
            const strongSell = snapshot.analystRatingsStrongSell || 0;
            const total = strongBuy + buy + hold + sell + strongSell;
            
            // Calculate net bias: (strong_buy + 0.5*buy - 0.5*sell - strong_sell) / total
            const netBias = total > 0 
              ? (strongBuy + 0.5 * buy - 0.5 * sell - strongSell) / total
              : 0;

            return {
              date: snapshot.date,
              strong_buy: strongBuy,
              buy: buy,
              hold: hold,
              sell: sell,
              strong_sell: strongSell,
              total: total,
              net_bias: netBias
            };
          });
        }

        analystRatingsData = {
          current: currentSnapshot,
          historical: historical
        };
      }
    }

    // Extract rating momentum data for rating_momentum metric
    let ratingMomentumData: MetricItemProps['ratingMomentumData'] = undefined;
    if (component.name === 'rating_momentum') {
      const calcDetails = component.calculation_details as any;
      const rawSnapshots = calcDetails?.raw_snapshots;
      const momentumDetails = calcDetails?.momentum_details;
      
      if (rawSnapshots?.t0 && momentumDetails) {
        const currentSnapshot = rawSnapshots.t0;
        
        // Use current_score from momentum_details (both 1_month and 2_month should have the same current_score)
        // Fallback to calculating it if not available
        const currentScore = momentumDetails["1_month"]?.current_score || 
          momentumDetails["2_month"]?.current_score ||
          (() => {
            const total = (currentSnapshot.analystRatingsStrongBuy || 0) + 
                          (currentSnapshot.analystRatingsBuy || 0) + 
                          (currentSnapshot.analystRatingsHold || 0) + 
                          (currentSnapshot.analystRatingsSell || 0) + 
                          (currentSnapshot.analystRatingsStrongSell || 0);
            // Formula: (5*strong_buy + 4*buy + 3*hold + 2*sell + 1*strong_sell) / total
            return total > 0
              ? ((5 * (currentSnapshot.analystRatingsStrongBuy || 0)) +
                 (4 * (currentSnapshot.analystRatingsBuy || 0)) +
                 (3 * (currentSnapshot.analystRatingsHold || 0)) +
                 (2 * (currentSnapshot.analystRatingsSell || 0)) +
                 (1 * (currentSnapshot.analystRatingsStrongSell || 0))) / total
              : 3.0; // Default to neutral if no ratings
          })();
        
        ratingMomentumData = {
          current_rating: {
            score: currentScore,
            date: currentSnapshot.date || '',
            strong_buy: currentSnapshot.analystRatingsStrongBuy || 0,
            buy: currentSnapshot.analystRatingsBuy || 0,
            hold: currentSnapshot.analystRatingsHold || 0,
            sell: currentSnapshot.analystRatingsSell || 0,
            strong_sell: currentSnapshot.analystRatingsStrongSell || 0
          },
          momentum_details: {
            "1_month": {
              previous_score: momentumDetails["1_month"]?.previous_score || 0,
              current_score: momentumDetails["1_month"]?.current_score || currentScore,
              change: momentumDetails["1_month"]?.change || 0,
              previous_date: momentumDetails["1_month"]?.previous_date || ''
            },
            "2_month": {
              previous_score: momentumDetails["2_month"]?.previous_score || 0,
              current_score: momentumDetails["2_month"]?.current_score || currentScore,
              change: momentumDetails["2_month"]?.change || 0,
              previous_date: momentumDetails["2_month"]?.previous_date || ''
            }
          }
        };
      }
    }
    
    // Extract price target data for price_target_upside metric
    let priceTargetData: MetricItemProps['priceTargetData'] = undefined;
    if (component.name === 'price_target_upside') {
      const calcDetails = component.calculation_details as any;
      
      // Try multiple locations for price_target_details:
      // 1. calculation_details.price_target_details (new format after backend update)
      // 2. component.metadata.price_target_details (if metadata is on component)
      let priceTargetDetails = calcDetails?.price_target_details || 
                               (component as any).metadata?.price_target_details;
      
      // Debug: log if we don't find it
      if (!priceTargetDetails) {
        console.log('Price target details not found in component, checking compositeScore...', {
          hasCalcDetails: !!calcDetails,
          calcDetailsKeys: calcDetails ? Object.keys(calcDetails) : [],
          componentKeys: Object.keys(component),
          hasCompositeMetadata: !!(compositeScore as any).metadata,
          hasAEMetadata: !!compositeScore?.layer_breakdown?.analyst_expectations?.metadata
        });
      }
      
      // If not found, check compositeScore metadata for analyst expectations (for cached data)
      if (!priceTargetDetails && (compositeScore as any).metadata) {
        const compositeMetadata = (compositeScore as any).metadata;
        if (compositeMetadata?.composite_result?.metrics?.price_target_upside?.metadata?.price_target_details) {
          priceTargetDetails = compositeMetadata.composite_result.metrics.price_target_upside.metadata.price_target_details;
        }
      }
      
      // Also check analyst_expectations layer metadata
      if (!priceTargetDetails && compositeScore?.layer_breakdown?.analyst_expectations?.metadata) {
        const aeMetadata = compositeScore.layer_breakdown.analyst_expectations.metadata as any;
        if (aeMetadata?.composite_result?.metrics?.price_target_upside?.metadata?.price_target_details) {
          priceTargetDetails = aeMetadata.composite_result.metrics.price_target_upside.metadata.price_target_details;
        }
      }
      
      // Final fallback: check if the entire compositeScore has the metadata structure
      if (!priceTargetDetails) {
        const fullComposite = compositeScore as any;
        if (fullComposite?.metadata?.composite_result?.metrics?.price_target_upside?.metadata?.price_target_details) {
          priceTargetDetails = fullComposite.metadata.composite_result.metrics.price_target_upside.metadata.price_target_details;
        }
      }
      
      if (priceTargetDetails && 
          typeof priceTargetDetails.current_price === 'number' &&
          typeof priceTargetDetails.target_price === 'number') {
        priceTargetData = {
          current_price: priceTargetDetails.current_price,
          target_price: priceTargetDetails.target_price,
          upside_percent: priceTargetDetails.upside_percent || 
                          ((priceTargetDetails.target_price - priceTargetDetails.current_price) / priceTargetDetails.current_price * 100),
          target_consensus: priceTargetDetails.target_consensus,
          target_median: priceTargetDetails.target_median,
          target_used: priceTargetDetails.target_used || 'median'
        };
        console.log('Price target data extracted:', priceTargetData);
      } else {
        console.log('Price target details not found or invalid:', priceTargetDetails);
      }
    }
    
    // Extract price target dispersion data for price_target_dispersion or estimate_dispersion_confidence metric
    let priceTargetDispersionData: MetricItemProps['priceTargetDispersionData'] = undefined;
    if (component.name === 'price_target_dispersion' || component.name === 'estimate_dispersion_confidence') {
      const calcDetails = component.calculation_details as any;
      
      // Try multiple locations for dispersion_details:
      // 1. calculation_details.dispersion_details (new format)
      // 2. component.metadata.dispersion_details (if metadata is on component)
      let dispersionDetails = calcDetails?.dispersion_details || 
                              (component as any).metadata?.dispersion_details;
      
      // If not found, check compositeScore metadata for analyst expectations (for cached data)
      if (!dispersionDetails && (compositeScore as any).metadata) {
        const compositeMetadata = (compositeScore as any).metadata;
        // Check both possible component names
        if (compositeMetadata?.composite_result?.metrics?.price_target_dispersion?.metadata?.dispersion_details) {
          dispersionDetails = compositeMetadata.composite_result.metrics.price_target_dispersion.metadata.dispersion_details;
        } else if (compositeMetadata?.composite_result?.metrics?.estimate_dispersion_confidence?.metadata?.dispersion_details) {
          dispersionDetails = compositeMetadata.composite_result.metrics.estimate_dispersion_confidence.metadata.dispersion_details;
        }
      }
      
      // Also check analyst_expectations layer metadata
      if (!dispersionDetails && compositeScore?.layer_breakdown?.analyst_expectations?.metadata) {
        const aeMetadata = compositeScore.layer_breakdown.analyst_expectations.metadata as any;
        if (aeMetadata?.composite_result?.metrics?.price_target_dispersion?.metadata?.dispersion_details) {
          dispersionDetails = aeMetadata.composite_result.metrics.price_target_dispersion.metadata.dispersion_details;
        } else if (aeMetadata?.composite_result?.metrics?.estimate_dispersion_confidence?.metadata?.dispersion_details) {
          dispersionDetails = aeMetadata.composite_result.metrics.estimate_dispersion_confidence.metadata.dispersion_details;
        }
      }
      
      // Final fallback: check if the entire compositeScore has the metadata structure
      if (!dispersionDetails) {
        const fullComposite = compositeScore as any;
        if (fullComposite?.metadata?.composite_result?.metrics?.price_target_dispersion?.metadata?.dispersion_details) {
          dispersionDetails = fullComposite.metadata.composite_result.metrics.price_target_dispersion.metadata.dispersion_details;
        } else if (fullComposite?.metadata?.composite_result?.metrics?.estimate_dispersion_confidence?.metadata?.dispersion_details) {
          dispersionDetails = fullComposite.metadata.composite_result.metrics.estimate_dispersion_confidence.metadata.dispersion_details;
        }
      }
      
      if (dispersionDetails && 
          typeof dispersionDetails.target_low === 'number' &&
          typeof dispersionDetails.target_high === 'number' &&
          typeof dispersionDetails.target_median === 'number') {
        priceTargetDispersionData = {
          target_low: dispersionDetails.target_low,
          target_high: dispersionDetails.target_high,
          target_median: dispersionDetails.target_median,
          target_consensus: dispersionDetails.target_consensus,
          target_mid_used: dispersionDetails.target_mid_used || 'median',
          range: dispersionDetails.range || (dispersionDetails.target_high - dispersionDetails.target_low),
          dispersion_ratio: dispersionDetails.dispersion_ratio,
          dispersion_percent: dispersionDetails.dispersion_percent || (dispersionDetails.dispersion_ratio * 100)
        };
      }
    }
    
    // Extract relative performance data for relative_strength or relative_performance metric
    let relativePerformanceData: MetricItemProps['relativePerformanceData'] = undefined;
    if (component.name === 'relative_strength' || component.name === 'relative_performance') {
      console.log('Processing relative performance component:', component.name, 'Full component:', component);
      const calcDetails = component.calculation_details as any;
      console.log('Calculation details:', calcDetails);
      
      // Handle both field names: relative_performance or relative_strength in calculation_details
      const relativePerfValue = calcDetails?.relative_performance ?? calcDetails?.relative_strength;
      
      if (calcDetails && 
          typeof calcDetails.window_days === 'number' &&
          typeof calcDetails.stock_return === 'number' &&
          typeof calcDetails.benchmark_return === 'number' &&
          typeof relativePerfValue === 'number' &&
          typeof calcDetails.outperformance_pct === 'number') {
        relativePerformanceData = {
          window_days: calcDetails.window_days,
          stock_return: calcDetails.stock_return,
          benchmark_return: calcDetails.benchmark_return,
          relative_performance: relativePerfValue,
          outperformance_pct: calcDetails.outperformance_pct,
          ticker: stockData.ticker
        };
        console.log('✅ Relative Performance data extracted successfully:', relativePerformanceData);
      } else {
        console.warn('❌ Relative Performance calculation_details missing or invalid:', {
          calcDetails,
          componentName: component.name,
          hasCalcDetails: !!calcDetails,
          window_days: calcDetails?.window_days,
          stock_return: calcDetails?.stock_return,
          benchmark_return: calcDetails?.benchmark_return,
          relative_performance: calcDetails?.relative_performance,
          relative_strength: calcDetails?.relative_strength,
          relativePerfValue,
          outperformance_pct: calcDetails?.outperformance_pct
        });
      }
    }
    
    // Extract trend persistence data for trend_persistence metric
    let trendPersistenceData: MetricItemProps['trendPersistenceData'] = undefined;
    if (component.name === 'trend_persistence') {
      console.log('Processing trend persistence component:', component.name, 'Full component:', component);
      const calcDetails = component.calculation_details as any;
      console.log('Calculation details:', calcDetails);
      
      if (calcDetails && 
          typeof calcDetails.close_above_50dma === 'boolean' &&
          typeof calcDetails.close_above_200dma === 'boolean' &&
          typeof calcDetails["50dma_above_200dma"] === 'boolean' &&
          typeof calcDetails.ma_alignment_score === 'string' &&
          typeof calcDetails.days_above_200dma === 'number' &&
          typeof calcDetails.time_above_200dma_pct === 'number' &&
          typeof calcDetails.close_today === 'number' &&
          typeof calcDetails.sma_50 === 'number' &&
          typeof calcDetails.sma_200 === 'number') {
        trendPersistenceData = {
          close_above_50dma: calcDetails.close_above_50dma,
          close_above_200dma: calcDetails.close_above_200dma,
          "50dma_above_200dma": calcDetails["50dma_above_200dma"],
          ma_alignment_score: calcDetails.ma_alignment_score,
          days_above_200dma: calcDetails.days_above_200dma,
          time_above_200dma_pct: calcDetails.time_above_200dma_pct,
          close_today: calcDetails.close_today,
          sma_50: calcDetails.sma_50,
          sma_200: calcDetails.sma_200
        };
        console.log('✅ Trend Persistence data extracted successfully:', trendPersistenceData);
      } else {
        console.warn('❌ Trend Persistence calculation_details missing or invalid:', {
          calcDetails,
          componentName: component.name,
          hasCalcDetails: !!calcDetails,
          close_above_50dma: calcDetails?.close_above_50dma,
          close_above_200dma: calcDetails?.close_above_200dma,
          "50dma_above_200dma": calcDetails?.["50dma_above_200dma"],
          ma_alignment_score: calcDetails?.ma_alignment_score,
          days_above_200dma: calcDetails?.days_above_200dma,
          time_above_200dma_pct: calcDetails?.time_above_200dma_pct,
          close_today: calcDetails?.close_today,
          sma_50: calcDetails?.sma_50,
          sma_200: calcDetails?.sma_200
        });
      }
    }
    
    // Extract volume confirmation data for volume_confirmation metric
    let volumeConfirmationData: MetricItemProps['volumeConfirmationData'] = undefined;
    if (component.name === 'volume_confirmation') {
      const calcDetails = component.calculation_details as any;
      
      if (calcDetails && 
          typeof calcDetails.net_participation === 'number' &&
          typeof calcDetails.up_vol_ratio === 'number' &&
          typeof calcDetails.down_vol_ratio === 'number' &&
          typeof calcDetails.up_days === 'number' &&
          typeof calcDetails.down_days === 'number' &&
          typeof calcDetails.avg_volume === 'number' &&
          typeof calcDetails.signal === 'string') {
        volumeConfirmationData = {
          net_participation: calcDetails.net_participation,
          up_vol_ratio: calcDetails.up_vol_ratio,
          down_vol_ratio: calcDetails.down_vol_ratio,
          up_days: calcDetails.up_days,
          down_days: calcDetails.down_days,
          avg_volume: calcDetails.avg_volume,
          signal: calcDetails.signal,
          ...(typeof calcDetails.flat_days === 'number' && { flat_days: calcDetails.flat_days }),
          ...(typeof calcDetails.total_days === 'number' && { total_days: calcDetails.total_days })
        };
      }
    }
    
    // Extract trend quality data for volatility_adjusted_momentum metric
    let trendQualityData: MetricItemProps['trendQualityData'] = undefined;
    if (component.name === 'volatility_adjusted_momentum') {
      const calcDetails = component.calculation_details as any;
      
      if (calcDetails && 
          typeof calcDetails.return === 'number' &&
          typeof calcDetails.volatility === 'number' &&
          typeof calcDetails.vam === 'number' &&
          typeof calcDetails.window_days === 'number') {
        trendQualityData = {
          return: calcDetails.return,
          volatility: calcDetails.volatility,
          volatility_adjusted_momentum: calcDetails.vam,
          window_days: calcDetails.window_days
        };
      }
    }

    let momentumAccelerationData: MetricItemProps['momentumAccelerationData'] = undefined;
    if (component.name === 'momentum_acceleration') {
      const calcDetails = component.calculation_details as any;
      
      if (calcDetails && 
          typeof calcDetails.short_term_return_pct === 'number' &&
          typeof calcDetails.medium_term_return_pct === 'number' &&
          typeof calcDetails.acceleration === 'number' &&
          typeof calcDetails.short_window_days === 'number' &&
          typeof calcDetails.medium_window_days === 'number') {
        momentumAccelerationData = {
          short_term_return_pct: calcDetails.short_term_return_pct,
          medium_term_return_pct: calcDetails.medium_term_return_pct,
          acceleration: calcDetails.acceleration,
          short_window_days: calcDetails.short_window_days,
          medium_window_days: calcDetails.medium_window_days,
          label: calcDetails.label || 'Unknown' // Use backend label for signal
        };
      }
    }
    
    return {
      label: labelMap[component.name] || snakeToTitleCase(component.name),
      value: displayValue,
      grade: grade,
      trend: getTrend(),
      description: !component.is_applicable ? component.description : undefined,
      explanation: fullExplanation,
      calculationMethod: calculationMethod,
      quarterlyData: quarterlyData,
      epsRevisionsData: epsRevisionsData,
      analystRatingsData: analystRatingsData,
      ratingMomentumData: ratingMomentumData,
      priceTargetData: priceTargetData,
      priceTargetDispersionData: priceTargetDispersionData,
      relativePerformanceData: relativePerformanceData,
      trendPersistenceData: trendPersistenceData,
      volumeConfirmationData: volumeConfirmationData,
      trendQualityData: trendQualityData,
      momentumAccelerationData: momentumAccelerationData
    };
  };

  // Get applicable metrics for fundamentals (financial health)
  const fundamentalsMetrics: MetricItemProps[] = financialHealth.components
    .filter(c => c.is_applicable)
    .map(mapMetricToFrontend);
  
  // Add non-applicable metrics at the end with special styling
  // Exclude Altman Z-Score if not weighted
  const referenceMetrics: MetricItemProps[] = financialHealth.components
    .filter(c => !c.is_applicable && c.name !== 'altman_z_score')
    .map(mapMetricToFrontend);

  // Use academic score for fundamentals to show transformed 1-100 scale
  // The backend converts percentile (e.g., 60) to academic score (e.g., 81)
  // This provides a more intuitive scale where ~78 is average
  const fundamentalsScore = Math.round(
    compositeScore.layer_breakdown.financial_health?.academic_score ??
    compositeScore.layer_breakdown.financial_health?.score ??
    0
  );
  const sentimentScore = Math.round(compositeScore.layer_breakdown.analyst_expectations.score);
  const momentumScore = Math.round(compositeScore.layer_breakdown.market_dynamics.score);

  // Create sentiment metrics from analyst expectations layer
  const analystExpectationsLayer = compositeScore.layer_breakdown.analyst_expectations;
  const sentimentMetrics: MetricItemProps[] = analystExpectationsLayer.components
    ? analystExpectationsLayer.components
        .filter(c => c.is_applicable)
        .map(mapMetricToFrontend)
    : [{ 
        label: 'Analyst Expectations', 
        value: `${sentimentScore}/100`, 
        grade: 'N/A',
        trend: sentimentScore >= 60 ? 'up' : sentimentScore >= 40 ? 'neutral' : 'down' 
      }];
  
  // Add non-applicable metrics for sentiment as reference
  const sentimentReferenceMetrics: MetricItemProps[] = analystExpectationsLayer.components
    ? analystExpectationsLayer.components
        .filter(c => !c.is_applicable)
        .map(mapMetricToFrontend)
    : [];

  // Create momentum metrics from market dynamics layer
  // The backend now returns 5 separate components instead of 1 composite component
  const marketDynamicsLayer = compositeScore.layer_breakdown.market_dynamics;
  let momentumMetrics: MetricItemProps[] = [];
  
  if (marketDynamicsLayer.components && marketDynamicsLayer.components.length > 0) {
    // Map of component names to display labels
    const componentLabelMap: Record<string, string> = {
      'relative_strength': 'Relative Performance',
      'relative_performance': 'Relative Performance',
      'trend_persistence': 'Trend Persistence',
      'volume_confirmation': 'Volume Confirmation',
      'volatility_adjusted_momentum': 'Trend Quality',
      'momentum_acceleration': 'Momentum Acceleration'
    };
    
    // Filter for applicable momentum components (the 5 metrics)
    const momentumComponentNames = Object.keys(componentLabelMap);
    const applicableMomentumComponents = marketDynamicsLayer.components.filter(
      c => c.is_applicable && momentumComponentNames.includes(c.name)
    );
    
    if (applicableMomentumComponents.length > 0) {
      // Map each component directly to MetricItemProps
      momentumMetrics = applicableMomentumComponents.map(component => {
        const mapped = mapMetricToFrontend(component);
        // For momentum metrics, don't show normalized score in collapsed view
        // Raw value will show when expanded (in value field), just like expectations and fundamentals
        // Override label with our label map to ensure consistent naming
        return {
          ...mapped,
          label: componentLabelMap[component.name] || mapped.label,
          // value already contains formatted raw_value from mapMetricToFrontend
          // description is optional and shows in collapsed view (don't add normalized score here)
        };
      });
    } else {
      // Fallback if no momentum components found (shouldn't happen with new backend)
      momentumMetrics = [{
        label: 'Market Momentum',
        value: `${momentumScore}/100`,
        grade: 'N/A',
        trend: momentumScore >= 60 ? 'up' : momentumScore >= 40 ? 'neutral' : 'down'
      }];
    }
  } else {
    // Fallback if no components
    momentumMetrics = [{
      label: 'Market Momentum',
      value: `${momentumScore}/100`,
      grade: 'N/A',
      trend: momentumScore >= 60 ? 'up' : momentumScore >= 40 ? 'neutral' : 'down'
    }];
  }
  
  // Add non-applicable metrics for momentum as reference
  const momentumReferenceMetrics: MetricItemProps[] = marketDynamicsLayer.components
    ? marketDynamicsLayer.components
        .filter(c => !c.is_applicable)
        .map(mapMetricToFrontend)
    : [];

  const isPositiveChange = !stockData.changePercent.startsWith('-');

  // Largest block trades (diamond markers) when "Largest Trades" layer is on.
  // Source priority: diamond (noteworthy ∪ significant-day) → noteworthy → per-day → top_block_trades.
  const blockTradesForChart = useMemo(() => {
    if (!layers.largestTrades.enabled) return [];
    const useDiamond = (diamondBlockTradesPerDay?.length ?? 0) > 0;
    const useNoteworthy = !useDiamond && (noteworthyBlockTradesPerDay?.length ?? 0) > 0;
    const usePerDay = !useDiamond && !useNoteworthy && (largestBlockTradePerDay?.length ?? 0) > 0;
    const sourceTrades = useDiamond
      ? (diamondBlockTradesPerDay ?? [])
      : useNoteworthy
        ? (noteworthyBlockTradesPerDay ?? [])
        : usePerDay
          ? (largestBlockTradePerDay ?? [])
          : (largestBlockTrades ?? []);
    if (!sourceTrades.length) return [];
    const pricesToUse = dynamicHistoricalPrices || historicalPrices;
    if (!pricesToUse?.length) return [];
    const cutoffDate = historicalPriceCutoffForTimeframe(timeframe);
    const chartDates = new Set(
      pricesToUse
        .filter((day) => {
          const [y, m, d] = day.date.split('-').map(Number);
          return new Date(y, m - 1, d) >= cutoffDate;
        })
        .map((day) => day.date)
    );
    const pricesList = pricesToUse.filter((day) => {
      const [y, m, d] = day.date.split('-').map(Number);
      return new Date(y, m - 1, d) >= cutoffDate;
    });
    const sortedFull = [...pricesToUse].sort((a, b) => a.date.localeCompare(b.date));
    const fullIndexByDate = new Map(sortedFull.map((d, i) => [d.date, i]));
    const dateToPercentChange = new Map<string, number>();
    pricesList.forEach((day) => {
      const fi = fullIndexByDate.get(day.date);
      let pct = 0;
      if (fi !== undefined && fi > 0) {
        const prevClose = sortedFull[fi - 1].close;
        pct = prevClose > 0 ? ((day.close - prevClose) / prevClose) * 100 : 0;
      } else {
        pct = day.percent_change ?? 0;
      }
      dateToPercentChange.set(day.date, pct);
    });
    const inRange = sourceTrades.filter((bt) => chartDates.has(bt.date));
    // Curated one-per-day lists (diamond / noteworthy / per-day) skip density cap — the backend
    // already selects dates (including significant price/volume days like Apr 15). Capping here
    // was dropping low-notional significant-day diamonds during busy stretches.
    const afterDensity = (useDiamond || useNoteworthy || usePerDay)
      ? inRange
      : capDenseBlockTradesByRollingWindow(inRange, { windowDays: 30, maxInWindow: 15 });
    return afterDensity.map((bt) => ({
      ...bt,
      fullDate: new Date(bt.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      percentChange: dateToPercentChange.get(bt.date) ?? 0,
    }));
  }, [
    largestBlockTrades,
    largestBlockTradePerDay,
    noteworthyBlockTradesPerDay,
    diamondBlockTradesPerDay,
    layers.largestTrades.enabled,
    dynamicHistoricalPrices,
    historicalPrices,
    timeframe,
  ]);

  // Generate historical stock data from real price data or fallback to mock data
  const generateHistoricalData = () => {
    // Use dynamic historical prices if available, otherwise fall back to prop
    const pricesToUse = dynamicHistoricalPrices || historicalPrices;
    
    if (pricesToUse && pricesToUse.length > 0) {
      // Filter data based on selected timeframe
      const cutoffDate = historicalPriceCutoffForTimeframe(timeframe);
      
      // Filter historical prices to only include data within the selected timeframe
      const filteredPrices = pricesToUse.filter((day) => {
        const [year, month, dayNum] = day.date.split('-').map(Number);
        const date = new Date(year, month - 1, dayNum);
        return date >= cutoffDate;
      });
      
      // Origin event starts (chart dots when Notable Events is on)
      const chartDateSet = new Set(filteredPrices.map((d) => d.date));
      const originEventDatesInRange = new Set<string>();
      for (const od of originEventDates) {
        if (chartDateSet.has(od)) originEventDatesInRange.add(od);
      }

      // Dots on the Line: "Notable Events" shows origin_event start dates only.
      const blockTradeDatesSet = blockTradeDates?.length ? new Set(blockTradeDates) : new Set<string>();
      const useBlockTradeDatesOnLine =
        layers.largestTrades.enabled &&
        !(largestBlockTrades?.length) &&
        !(largestBlockTradePerDay?.length) &&
        blockTradeDatesSet.size > 0;
      const notableEventsOn = layers.notableEvents.enabled;
      const significantMoveDotDates: string[] = [];
      if (notableEventsOn) {
        significantMoveDotDates.push(...originEventDatesInRange);
      }
      const dotDatesSet = new Set<string>([
        ...significantMoveDotDates,
        ...(useBlockTradeDatesOnLine ? blockTradeDatesSet : []),
      ]);

      // Full series (sorted) so the first *visible* day after the timeframe cutoff still gets
      // prior-session close-to-close % (filtered list index 0 would otherwise show 0% when
      // percent_change is missing).
      const sortedFull = [...pricesToUse].sort((a, b) => a.date.localeCompare(b.date));
      const fullIndexByDate = new Map(sortedFull.map((d, i) => [d.date, i]));

      // Use filtered historical price data
      return filteredPrices.map((day) => {
        // Parse date string manually to avoid timezone issues
        // Format: "2025-11-13" -> parse as local date, not UTC
        const [year, month, dayNum] = day.date.split('-').map(Number);
        const date = new Date(year, month - 1, dayNum); // month is 0-indexed

        const fi = fullIndexByDate.get(day.date);
        let percentChange: number;
        if (fi !== undefined && fi > 0) {
          const prevClose = sortedFull[fi - 1].close;
          percentChange = prevClose > 0 ? ((day.close - prevClose) / prevClose) * 100 : 0;
        } else {
          percentChange = day.percent_change ?? 0;
        }

        return {
          date: day.date, // Use original YYYY-MM-DD format as the data key
          dateObj: date, // Store Date object for formatting
          price: day.close,
          open: day.open,
          high: day.high,
          low: day.low,
          close: day.close,
          volume: day.volume ?? 0,
          fullDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          isSignificant: dotDatesSet.has(day.date),
          isNotableOriginEvent: notableEventsOn && originEventDatesInRange.has(day.date),
          percentChange: percentChange,
          blockTradeNotionalUsd: blockTradeNotionalByDate?.[day.date] ?? 0,
          explanationTitle: null,
          explanation: null // Will be populated when data is integrated
        };
      });
    }
    
    // Fallback to mock data if no historical prices available
    const data = [];
    const now = new Date();
    const startPrice = 115; // Starting price
    const endPrice = parseFloat(stockData.currentPrice.replace('$', '')) || 142.50; // Current price
    
    // Determine number of months based on timeframe
    let monthsToGenerate: number;
    switch (timeframe) {
      case '7D':
        monthsToGenerate = 1;
        break;
      case '30D':
        monthsToGenerate = 2;
        break;
      case '12M':
        monthsToGenerate = 12;
        break;
      case '3Y':
        monthsToGenerate = 36;
        break;
      case '5Y':
        monthsToGenerate = 60;
        break;
      default:
        monthsToGenerate = 12;
    }
    
    // Generate monthly data points for the selected timeframe
    let prevCloseForMock = startPrice;
    for (let i = monthsToGenerate; i >= 0; i--) {
      const date = new Date(now);
      date.setMonth(date.getMonth() - i);
      
      // Create a somewhat realistic price progression with volatility
      const progress = (monthsToGenerate - i) / monthsToGenerate;
      const trendPrice = startPrice + (endPrice - startPrice) * progress;
      
      // Add some volatility
      const volatility = Math.sin(i * 0.8) * 8 + Math.random() * 6 - 3;
      const close = Math.round((trendPrice + volatility) * 100) / 100;
      const open = Math.round(prevCloseForMock * 100) / 100;
      prevCloseForMock = close;
      const bodyHigh = Math.max(open, close);
      const bodyLow = Math.min(open, close);
      const high = Math.round((bodyHigh + Math.random() * 2 + 0.25) * 100) / 100;
      const low = Math.round((bodyLow - Math.random() * 2 - 0.25) * 100) / 100;
      
      // Generate date in YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      data.push({
        date: dateString, // Use YYYY-MM-DD format as the data key
        dateObj: new Date(date), // Store Date object for formatting
        price: close,
        open,
        high,
        low,
        close,
        fullDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        isSignificant: false, // Mock data doesn't mark significant moves
        isNotableOriginEvent: false,
        percentChange: 0,
        blockTradeNotionalUsd: 0,
        explanationTitle: null,
        explanation: null
      });
    }
    
    return data;
  };

  const historicalChartData = useMemo(() => generateHistoricalData(), [
    dynamicHistoricalPrices,
    historicalPrices,
    timeframe,
    layers,
    blockTradeDates,
    largestBlockTrades,
    largestBlockTradePerDay,
    blockTradeNotionalByDate,
    stockData.currentPrice,
    originEventDates,
  ]);

  const historicalChartVisibleData = useMemo(() => {
    const full = historicalChartData;
    if (full.length === 0) return full;
    if (!historicalZoomRange) return full;
    const s = Math.max(0, Math.min(historicalZoomRange.start, full.length - 1));
    const e = Math.max(s, Math.min(historicalZoomRange.end, full.length - 1));
    return full.slice(s, e + 1);
  }, [historicalChartData, historicalZoomRange]);

  const historicalVisibleDates = useMemo(
    () => historicalChartVisibleData.map((row) => row.date).filter(Boolean),
    [historicalChartVisibleData],
  );

  const priceIntelligenceContext = useMemo(() => {
    const prices = dynamicHistoricalPrices ?? historicalPrices;
    if (!prices?.length) {
      return { date: null as string | null, ohlc: null, recentBars: [] as Array<{ date: string; close: number; pct_change?: number }> };
    }
    const focusDate =
      selectedPoint?.date ??
      historicalChartVisibleData[historicalChartVisibleData.length - 1]?.date ??
      [...prices].sort((a, b) => a.date.localeCompare(b.date)).at(-1)?.date;
    if (!focusDate) {
      return { date: null, ohlc: null, recentBars: [] };
    }
    const focusDay = prices.find((d) => d.date === focusDate);
    if (!focusDay) {
      return { date: focusDate, ohlc: null, recentBars: [] };
    }
    const sorted = [...prices].sort((a, b) => a.date.localeCompare(b.date));
    const idx = sorted.findIndex((d) => d.date === focusDate);
    const window = sorted.slice(Math.max(0, idx - 19), idx + 1);
    return {
      date: focusDate,
      ohlc: {
        date: focusDate,
        open: focusDay.open,
        high: focusDay.high,
        low: focusDay.low,
        close: focusDay.close,
        volume: focusDay.volume,
        pct_change: focusDay.percent_change ?? undefined,
      },
      recentBars: window.map((d) => ({
        date: d.date,
        close: d.close,
        pct_change: d.percent_change ?? undefined,
      })),
    };
  }, [
    dynamicHistoricalPrices,
    historicalPrices,
    selectedPoint?.date,
    historicalChartVisibleData,
  ]);

  const volumeByPriceVisibleRange = useMemo(() => {
    if (historicalVisibleDates.length < 1) return null;
    return {
      start: historicalVisibleDates[0]!,
      end: historicalVisibleDates[historicalVisibleDates.length - 1]!,
    };
  }, [historicalVisibleDates]);

  const yearlyVolumeByPriceBucketCents = useMemo(() => {
    const parsed = parseFloat(String(stockData.currentPrice ?? '').replace(/[$,]/g, ''));
    return resolveYearlyVolumeByPriceBucketCents(Number.isFinite(parsed) ? parsed : null);
  }, [stockData.currentPrice]);

  useEffect(() => {
    if (!layers.volumeByPrice.enabled || !stockData.ticker || !volumeByPriceVisibleRange) {
      setYearlyPriceVolume(null);
      setYearlyPriceVolumeLoading(false);
      return;
    }
    const { start, end } = volumeByPriceVisibleRange;
    let cancelled = false;
    setYearlyPriceVolumeLoading(true);
    stockService
      .getYearlyPriceVolume(
        stockData.ticker,
        start,
        end,
        'extended',
        yearlyVolumeByPriceBucketCents,
      )
      .then((res) => {
        if (cancelled) return;
        setYearlyPriceVolume(res);
        setYearlyPriceVolumeLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setYearlyPriceVolume(null);
          setYearlyPriceVolumeLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    layers.volumeByPrice.enabled,
    stockData.ticker,
    volumeByPriceVisibleRange,
    yearlyVolumeByPriceBucketCents,
  ]);

  /** Visible chart data with per-market fedProb_{id} fields (0–100) merged in for each active outcome leg. */
  const historicalChartVisibleDataWithProb = useMemo(() => {
    if (selectedEventGroup.length === 0 || Object.keys(fedProbHistories).length === 0) {
      return historicalChartVisibleData;
    }
    return historicalChartVisibleData.map((row) => {
      const extras: Record<string, number> = {};
      for (const m of selectedEventGroup) {
        const hist = fedProbHistories[m.id];
        if (hist) {
          const p = hist.history[row.date];
          if (p != null) extras[`fedProb_${m.id}`] = p * 100;
        }
      }
      return Object.keys(extras).length > 0 ? { ...row, ...extras } : row;
    });
  }, [historicalChartVisibleData, selectedEventGroup, fedProbHistories]);

  useEffect(() => {
    setNotableEventLabels({});
    notableEventHoverStore.set(null);
    notableLabelFetchInFlightRef.current.clear();
    notableLabelFetchedRef.current.clear();
  }, [stockData.ticker, notableEventHoverStore]);

  useEffect(() => {
    if (!layers.notableEvents.enabled) {
      notableEventHoverStore.set(null);
    }
  }, [layers.notableEvents.enabled, notableEventHoverStore]);

  useEffect(() => {
    if (!layers.notableEvents.enabled) return;
    const dates = [
      ...new Set(
        historicalChartVisibleData
          .filter((d) => (d as { isNotableOriginEvent?: boolean }).isNotableOriginEvent)
          .map((d) => d.date),
      ),
    ];
    if (dates.length === 0) return;

    const toFetch = dates.filter(
      (d) =>
        !notableLabelFetchedRef.current.has(d) && !notableLabelFetchInFlightRef.current.has(d),
    );
    if (toFetch.length === 0) return;

    toFetch.forEach((d) => notableLabelFetchInFlightRef.current.add(d));
    let cancelled = false;

    const run = async () => {
      const batchSize = 5;
      for (let i = 0; i < toFetch.length; i += batchSize) {
        if (cancelled) break;
        const slice = toFetch.slice(i, i + batchSize);
        const updates: Record<string, string> = {};
        await Promise.all(
          slice.map(async (date) => {
            try {
              const pack = await stockService.getStockMoveDominantCatalysts(stockData.ticker, date);
              const title =
                (pack?.origin_event_card_title && pack.origin_event_card_title.trim()) ||
                (pack?.dominant_catalysts?.[0]?.catalyst && pack.dominant_catalysts[0].catalyst.trim()) ||
                '';
              updates[date] = title || 'Notable move';
            } catch {
              updates[date] = 'Notable move';
            } finally {
              notableLabelFetchInFlightRef.current.delete(date);
              notableLabelFetchedRef.current.add(date);
            }
          }),
        );
        if (!cancelled && Object.keys(updates).length > 0) {
          setNotableEventLabels((prev) => ({ ...prev, ...updates }));
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [layers.notableEvents.enabled, stockData.ticker, historicalChartVisibleData]);

  const institutionalChartOverlayData = useMemo(() => {
    if (!layers.institutionalHolders.enabled || !institutionalHoldersPayload) {
      return { filingPeriods: [], quarterEndReferenceLines: [] };
    }
    const chartDates = historicalChartVisibleData.map((d) => d.date);
    return buildInstitutionalChartOverlayData(
      institutionalHoldersPayload,
      chartDates,
      institutionalHoldersChartDisplayMode
    );
  }, [
    layers.institutionalHolders.enabled,
    institutionalHoldersPayload,
    historicalChartVisibleData,
    institutionalHoldersChartDisplayMode,
  ]);

  const institutionalFilingPeriodsForChart = institutionalChartOverlayData.filingPeriods;
  const institutionalQuarterEndReferenceLines =
    institutionalChartOverlayData.quarterEndReferenceLines;

  const institutionalChartQuarterKeys = useMemo(
    () => new Set(institutionalFilingPeriodsForChart.map((p) => p.quarterKey)),
    [institutionalFilingPeriodsForChart]
  );

  const institutionalFilingPeriodsFiltered = useMemo(
    () =>
      filterFilingPeriodsBySearch(
        institutionalFilingPeriodsForChart,
        institutionalHoldersSearchQuery
      ),
    [institutionalFilingPeriodsForChart, institutionalHoldersSearchQuery]
  );

  const institutionalSearchDetailPanel = useMemo(() => {
    if (!institutionalHoldersSearchDetail || !institutionalHoldersPayload) return null;
    return buildInstitutionalHolderDetailPanel(
      institutionalHoldersPayload,
      stockData.ticker,
      institutionalHoldersSearchDetail.positionKey,
      institutionalHoldersSearchDetail.quarterKey
    );
  }, [institutionalHoldersSearchDetail, institutionalHoldersPayload, stockData.ticker]);

  const institutionalHoldersSearchActive =
    institutionalHoldersSearchQuery.trim().length >= 2;

  const showInstitutionalHoldersRibbon =
    layers.institutionalHolders.enabled &&
    (institutionalHoldersLoading || institutionalFilingPeriodsForChart.length > 0);

  const onInstitutionalOverlayMeta = useCallback((meta: InstitutionalHoldersOverlayMeta) => {
    queueMicrotask(() => {
      setInstitutionalHoldersDenseHint(meta.usesMinimalDensity);
    });
  }, []);

  useEffect(() => {
    if (!layers.institutionalHolders.enabled) {
      setInstitutionalHoldersDenseHint(false);
      setInstitutionalHoldersSearchQuery('');
      setInstitutionalHoldersSearchHighlight(null);
      setInstitutionalHoldersSearchDetail(null);
    }
  }, [layers.institutionalHolders.enabled]);

  useEffect(() => {
    if (!institutionalHoldersSearchHighlight) return;
    const el = document.querySelector(
      institutionalHolderRibbonSelector(
        institutionalHoldersSearchHighlight.quarterKey,
        institutionalHoldersSearchHighlight.positionKey
      )
    );
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [institutionalHoldersSearchHighlight, institutionalFilingPeriodsFiltered]);

  const handleInstitutionalSearchSelect = useCallback(
    (result: InstitutionalHolderSearchResult) => {
      setInstitutionalHoldersSearchQuery(result.displayName);
      setInstitutionalHoldersSearchHighlight({
        quarterKey: result.quarterKey,
        positionKey: result.positionKey,
      });
      setInstitutionalHoldersSearchDetail({
        quarterKey: result.quarterKey,
        positionKey: result.positionKey,
      });
    },
    []
  );

  const handleInstitutionalTopHolderSelect = useCallback(
    (quarterKey: string, positionKey: string, displayName: string) => {
      setInstitutionalHoldersSearchQuery(displayName);
      setInstitutionalHoldersSearchHighlight({ quarterKey, positionKey });
      setInstitutionalHoldersSearchDetail({ quarterKey, positionKey });
    },
    []
  );

  const optionsChartDistribution = useMemo((): PriceDistributionBucket[] => {
    return optionsChartData?.options_analysis?.price_distribution ?? [];
  }, [optionsChartData]);

  const optionsChartOverlaySummary = useMemo(() => {
    const oa = optionsChartData?.options_analysis;
    if (!hasOptionsPositioningChartData(oa)) return null;
    return buildOptionsChartOverlaySummary({
      current_price: oa.current_price,
      primary_expiration: oa.primary_expiration,
      dte: oa.dte,
      expected_move: oa.expected_move,
      volume_and_open_interest: oa.volume_and_open_interest,
      implied_volatility: oa.implied_volatility,
      iv_skew: oa.iv_skew,
      delta_positioning: oa.delta_positioning,
      max_pain: oa.max_pain,
      gamma_exposure: oa.gamma_exposure,
      strike_walls: oa.strike_walls,
      price_distribution: oa.price_distribution,
      probability_targets: oa.probability_targets,
      directional_bias: optionsChartData?.interpretation?.directional_bias ?? null,
      interpretation: optionsChartData?.interpretation,
      analysis_timestamp: optionsChartData?.analysis_metadata?.analysis_timestamp,
    });
  }, [optionsChartData]);

  const optionsChartLegendRows = useMemo(() => {
    if (!optionsChartOverlaySummary || optionsChartDistribution.length === 0) {
      return [];
    }
    return buildInvestorDistributionRows(
      optionsChartDistribution,
      optionsChartOverlaySummary.spot,
    );
  }, [optionsChartDistribution, optionsChartOverlaySummary]);

  const optionsChartLadderRows = useMemo(() => {
    const targets = optionsChartData?.options_analysis?.probability_targets;
    if (!targets?.length || !optionsChartOverlaySummary) return [];
    return buildNearSpotLadderRows(targets, optionsChartOverlaySummary.spot);
  }, [optionsChartData, optionsChartOverlaySummary]);

  const syncOptionsLegendPosition = useCallback(() => {
    if (
      !layers.optionsPositioning.enabled ||
      optionsChartLoading ||
      !optionsChartOverlaySummary
    ) {
      setOptionsLegendPosition(null);
      return;
    }
    const wrap = historicalChartWrapRef.current;
    const cp = historicalChartMetricsRef.current;
    const data = historicalChartVisibleData;
    if (!wrap || !cp?.offset || data.length === 0) {
      setOptionsLegendPosition(null);
      return;
    }
    const svg = wrap.querySelector('svg');
    if (!svg) {
      setOptionsLegendPosition(null);
      return;
    }
    const pos = computeOptionsLegendScreenPosition(
      svg as SVGSVGElement,
      wrap,
      cp,
      data,
      optionsChartOverlaySummary.spot,
    );
    setOptionsLegendPosition((prev) => {
      if (pos == null) return null;
      if (
        prev != null &&
        Math.abs(prev.left - pos.left) < 0.5 &&
        Math.abs(prev.top - pos.top) < 0.5
      ) {
        return prev;
      }
      return pos;
    });
  }, [
    layers.optionsPositioning.enabled,
    optionsChartLoading,
    optionsChartOverlaySummary,
    historicalChartVisibleData,
  ]);

  useEffect(() => {
    if (!layers.optionsPositioning.enabled) {
      setOptionsLegendPosition(null);
      setOptionsLegendDismissed(false);
      return;
    }
    syncOptionsLegendPosition();
    const wrap = historicalChartWrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => syncOptionsLegendPosition());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [layers.optionsPositioning.enabled, syncOptionsLegendPosition]);

  useEffect(() => {
    if (!layers.optionsPositioning.enabled) return;
    syncOptionsLegendPosition();
  }, [historicalChartVisibleData, syncOptionsLegendPosition, layers.optionsPositioning.enabled]);

  const renderInstitutionalHoldersChartOverlay = useInstitutionalHoldersChartOverlay(
    layers.institutionalHolders.enabled,
    institutionalFilingPeriodsFiltered,
    institutionalQuarterEndReferenceLines,
    institutionalHoldersPayload,
    stockData.ticker,
    historicalChartVisibleData[0]?.date,
    institutionalHoldersLoading,
    onInstitutionalOverlayMeta,
    institutionalHoldersChartDisplayMode,
    institutionalHoldersSearchHighlight,
    institutionalHoldersSearchActive
  );

  const historicalChartPlotTopMargin = useMemo(() => {
    let top = 5;
    if (layers.notableEvents.enabled) {
      const anyOrigin = historicalChartVisibleData.some(
        (d) => (d as { isNotableOriginEvent?: boolean }).isNotableOriginEvent,
      );
      if (anyOrigin) top = HISTORICAL_CHART_NOTABLE_EVENTS_TOP_MARGIN_PX;
    }
    if (showInstitutionalHoldersRibbon) {
      top = Math.max(top, INSTITUTIONAL_HOLDERS_RIBBON_HEIGHT_PX);
    }
    return top;
  }, [
    layers.notableEvents.enabled,
    historicalChartVisibleData,
    showInstitutionalHoldersRibbon,
  ]);

  const historicalChartLeftMargin = useMemo(() => {
    if (!layers.notableEvents.enabled) return 5;
    const anyOrigin = historicalChartVisibleData.some(
      (d) => (d as { isNotableOriginEvent?: boolean }).isNotableOriginEvent,
    );
    return anyOrigin ? HISTORICAL_CHART_NOTABLE_EVENTS_LEFT_MARGIN_PX : 5;
  }, [layers.notableEvents.enabled, historicalChartVisibleData]);

  const blockTradesForVisibleChart = useMemo(() => {
    if (historicalChartVisibleData.length === 0) return [];
    const dates = new Set(historicalChartVisibleData.map((d) => d.date));
    return blockTradesForChart.filter((bt) => dates.has(bt.date));
  }, [blockTradesForChart, historicalChartVisibleData]);

  const blockTradeDiamondRadiusByDate = useMemo(
    () =>
      blockTradeDiamondRadiusByKey(
        blockTradesForVisibleChart.map((bt) => ({
          key: bt.date,
          notional: blockTradeNotionalUsd(bt),
        })),
      ),
    [blockTradesForVisibleChart],
  );

  const historicalSessionVolumeByDate = useMemo(() => {
    const prices = dynamicHistoricalPrices ?? historicalPrices;
    const map = new Map<string, number>();
    if (!prices?.length) return map;
    for (const day of prices) {
      const vol = Number(day.volume);
      if (day.date && Number.isFinite(vol) && vol > 0) {
        map.set(day.date, vol);
      }
    }
    return map;
  }, [dynamicHistoricalPrices, historicalPrices]);

  const imbalanceDotsForVisibleChart = useMemo(() => {
    if (!layers.imbalance.enabled || !tapeImbalanceByDate) return [];
    type Bar = (typeof historicalChartVisibleData)[number];
    const out: Array<
      Bar & {
        buy_volume: number;
        sell_volume: number;
        neutral_volume: number;
        sessionChange: { change: number; changePct: number } | null;
      }
    > = [];
    for (let i = 0; i < historicalChartVisibleData.length; i++) {
      const row = historicalChartVisibleData[i];
      const agg = tapeImbalanceByDate[row.date];
      if (!agg) continue;
      const ohlc = historicalOhlcFromRow(row);
      const sessionChange =
        ohlc != null ? historicalSessionChangeFromRow(historicalChartVisibleData, i, row) : null;
      out.push({ ...row, ...agg, sessionChange });
    }
    return out;
  }, [layers.imbalance.enabled, tapeImbalanceByDate, historicalChartVisibleData]);

  const imbalanceDotRadiusByDate = useMemo(() => {
    const rows = imbalanceDotsForVisibleChart.map((row) => ({
      date: row.date,
      totalVolume: tapeImbalanceTotalVolume(row.buy_volume, row.sell_volume, row.neutral_volume),
    }));
    return tapeImbalanceDotRadiusByDate(rows);
  }, [imbalanceDotsForVisibleChart]);

  const optionImbalanceDotsForVisibleChart = useMemo(() => {
    if (!layers.optionImbalance.enabled || !optionImbalanceByDate) return [];
    type Bar = (typeof historicalChartVisibleData)[number];
    const out: Array<
      Bar & {
        call_premium: number;
        put_premium: number;
        unknown_premium: number;
        sessionChange: { change: number; changePct: number } | null;
      }
    > = [];
    for (let i = 0; i < historicalChartVisibleData.length; i++) {
      const row = historicalChartVisibleData[i];
      const agg = optionImbalanceByDate[row.date];
      if (!agg) continue;
      const ohlc = historicalOhlcFromRow(row);
      const sessionChange =
        ohlc != null ? historicalSessionChangeFromRow(historicalChartVisibleData, i, row) : null;
      out.push({ ...row, ...agg, sessionChange });
    }
    return out;
  }, [layers.optionImbalance.enabled, optionImbalanceByDate, historicalChartVisibleData]);

  const optionImbalanceDotRadiusByDate = useMemo(() => {
    const rows = optionImbalanceDotsForVisibleChart.map((row) => ({
      date: row.date,
      totalVolume: tapeImbalanceTotalVolume(
        row.call_premium,
        row.put_premium,
        row.unknown_premium,
      ),
    }));
    return tapeImbalanceDotRadiusByDate(rows);
  }, [optionImbalanceDotsForVisibleChart]);

  /** One guide on the historical chart: latest/selected session peak volume price. */
  const historicalPeakVolumeGuide = useMemo(() => {
    if (!layers.peakVolumePrice.enabled || peakVolumePriceByDate === null) return null;
    const visibleDatesWithPeak = historicalChartVisibleData
      .map((d) => d.date)
      .filter((d) => peakVolumePriceByDate[d]);
    let date: string | undefined =
      selectedPoint?.date && peakVolumePriceByDate[selectedPoint.date]
        ? selectedPoint.date
        : undefined;
    if (!date) {
      date = visibleDatesWithPeak.length
        ? visibleDatesWithPeak[visibleDatesWithPeak.length - 1]
        : undefined;
    }
    if (!date) {
      const allPeakDates = Object.keys(peakVolumePriceByDate).sort();
      date = allPeakDates.length ? allPeakDates[allPeakDates.length - 1] : undefined;
    }
    if (!date) return null;
    const peak = peakVolumePriceByDate[date];
    return {
      date,
      price: peak.price,
    };
  }, [
    layers.peakVolumePrice.enabled,
    peakVolumePriceByDate,
    historicalChartVisibleData,
    selectedPoint?.date,
  ]);

  /** Yearly volume-at-price bars for the visible chart range (horizontal profile on Y-axis). */
  const volumeByPriceProfile = useMemo(() => {
    if (!layers.volumeByPrice.enabled || !volumeByPriceVisibleRange) return null;
    const bars = volumeByPriceBarsFromYearlyPayload(yearlyPriceVolume);
    if (!bars?.length) return null;
    const bucketCents =
      typeof yearlyPriceVolume?.bucket_cents === 'number' && yearlyPriceVolume.bucket_cents > 0
        ? yearlyPriceVolume.bucket_cents
        : 1;
    return {
      bars,
      bucketCents,
      startDate: yearlyPriceVolume?.start_date ?? volumeByPriceVisibleRange.start,
      endDate: yearlyPriceVolume?.end_date ?? volumeByPriceVisibleRange.end,
      symbol: yearlyPriceVolume?.symbol ?? stockData.ticker,
      hasSideAttribution: volumeByPriceHasSideAttribution(yearlyPriceVolume, bars),
      hasDateAttribution: volumeByPriceHasDateAttribution(yearlyPriceVolume, bars),
      dateOrder: volumeByPriceDateOrderFromPayload(yearlyPriceVolume, bars),
    };
  }, [
    layers.volumeByPrice.enabled,
    volumeByPriceVisibleRange,
    yearlyPriceVolume,
    stockData.ticker,
  ]);

  const volumeByPriceColorMode = useMemo((): VolumeByPriceColorMode => {
    if (volumeByPriceDisplayMode === 'bySide' && volumeByPriceProfile?.hasSideAttribution) {
      return 'bySide';
    }
    if (volumeByPriceDisplayMode === 'byDate' && volumeByPriceProfile?.hasDateAttribution) {
      return 'byDate';
    }
    return 'total';
  }, [
    volumeByPriceDisplayMode,
    volumeByPriceProfile?.hasSideAttribution,
    volumeByPriceProfile?.hasDateAttribution,
  ]);

  useEffect(() => {
    if (!volumeByPriceProfile) return;
    if (volumeByPriceDisplayMode === 'bySide' && !volumeByPriceProfile.hasSideAttribution) {
      setVolumeByPriceDisplayMode('total');
    }
    if (volumeByPriceDisplayMode === 'byDate' && !volumeByPriceProfile.hasDateAttribution) {
      setVolumeByPriceDisplayMode('total');
    }
  }, [volumeByPriceDisplayMode, volumeByPriceProfile]);

  const syncHistoricalPeakVolumeGuideOverlay = useCallback(() => {
    if (!layers.peakVolumePrice.enabled || !historicalPeakVolumeGuide) {
      setHistoricalPeakVolumeGuideOverlay(null);
      return;
    }
    const wrap = historicalChartWrapRef.current;
    const cp = historicalChartMetricsRef.current;
    const svg = wrap?.querySelector('svg');
    if (!wrap || !cp || !svg) {
      setHistoricalPeakVolumeGuideOverlay(null);
      return;
    }
    const plot = peakVolumeGuidePlotY(cp, historicalPeakVolumeGuide.price);
    if (!plot) {
      setHistoricalPeakVolumeGuideOverlay(null);
      return;
    }
    const pos = peakVolumeGuideOverlayPosition(
      svg as SVGSVGElement,
      wrap,
      plot.plotRight,
      plot.y,
      plot.plotTop,
      plot.plotBottom,
    );
    if (!pos) {
      setHistoricalPeakVolumeGuideOverlay(null);
      return;
    }
    setHistoricalPeakVolumeGuideOverlay({
      left: pos.left,
      top: pos.top,
      label: peakVolumeGuideLabel(historicalPeakVolumeGuide.price),
    });
  }, [layers.peakVolumePrice.enabled, historicalPeakVolumeGuide]);

  const syncIntradayPeakVolumeGuideOverlay = useCallback(
    (price: number) => {
      if (!layers.peakVolumePrice.enabled) {
        setIntradayPeakVolumeGuideOverlay(null);
        return;
      }
      const wrap = intradayPriceChartWrapRef.current;
      const cp = intradayChartMetricsRef.current;
      const svg = wrap?.querySelector('svg');
      if (!wrap || !cp || !svg) {
        setIntradayPeakVolumeGuideOverlay(null);
        return;
      }
      const plot = peakVolumeGuidePlotY(cp, price);
      if (!plot) {
        setIntradayPeakVolumeGuideOverlay(null);
        return;
      }
      const pos = peakVolumeGuideOverlayPosition(
        svg as SVGSVGElement,
        wrap,
        plot.plotRight,
        plot.y,
        plot.plotTop,
        plot.plotBottom,
      );
      if (!pos) {
        setIntradayPeakVolumeGuideOverlay(null);
        return;
      }
      setIntradayPeakVolumeGuideOverlay({
        left: pos.left,
        top: pos.top,
        label: peakVolumeGuideLabel(price),
      });
    },
    [layers.peakVolumePrice.enabled],
  );

  useLayoutEffect(() => {
    syncHistoricalPeakVolumeGuideOverlay();
    const wrap = historicalChartWrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => syncHistoricalPeakVolumeGuideOverlay());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [syncHistoricalPeakVolumeGuideOverlay, historicalChartVisibleData, historicalChartStyle]);

  useLayoutEffect(() => {
    if (!layers.peakVolumePrice.enabled || !intradayPeakVolumeLevel) {
      setIntradayPeakVolumeGuideOverlay(null);
      return;
    }
    syncIntradayPeakVolumeGuideOverlay(intradayPeakVolumeLevel.price);
    const wrap = intradayPriceChartWrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => {
      if (intradayPeakVolumeLevel) {
        syncIntradayPeakVolumeGuideOverlay(intradayPeakVolumeLevel.price);
      }
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [layers.peakVolumePrice.enabled, intradayPeakVolumeLevel, syncIntradayPeakVolumeGuideOverlay, selectedPoint?.date]);

  const renderHistoricalPeakVolumeGuide = useCallback(
    (chartProps: HistoricalChartMetrics & Record<string, unknown>) => {
      if (!historicalPeakVolumeGuide) return null;
      queueMicrotask(() => syncHistoricalPeakVolumeGuideOverlay());
      return (
        <PeakVolumePriceGuideSvg
          price={historicalPeakVolumeGuide.price}
          chartData={historicalChartVisibleData}
          categoryKey="date"
          chartProps={chartProps}
        />
      );
    },
    [historicalPeakVolumeGuide, historicalChartVisibleData, syncHistoricalPeakVolumeGuideOverlay],
  );

  const renderHistoricalVolumeByPrice = useCallback(
    (chartProps: HistoricalChartMetrics & Record<string, unknown>) => {
      if (!volumeByPriceProfile) return null;
      return (
        <VolumeByPriceChartOverlay
          bars={volumeByPriceProfile.bars}
          bucketCents={volumeByPriceProfile.bucketCents}
          chartProps={chartProps}
          yAxisId="price"
          colorMode={volumeByPriceColorMode}
          dateOrder={volumeByPriceProfile.dateOrder}
          highlightDate={selectedPoint?.date ?? null}
        />
      );
    },
    [volumeByPriceProfile, volumeByPriceColorMode, selectedPoint?.date],
  );

  const renderIntradayVolumeByPrice = useCallback(
    (chartProps: HistoricalChartMetrics & Record<string, unknown>) => {
      if (!volumeByPriceProfile) return null;
      return (
        <VolumeByPriceChartOverlay
          bars={volumeByPriceProfile.bars}
          bucketCents={volumeByPriceProfile.bucketCents}
          chartProps={chartProps}
          colorMode={volumeByPriceColorMode}
          dateOrder={volumeByPriceProfile.dateOrder}
          highlightDate={selectedPoint?.date ?? null}
        />
      );
    },
    [volumeByPriceProfile, volumeByPriceColorMode, selectedPoint?.date],
  );

  const insiderTradesByTransactionDate = useMemo(() => {
    const rows = fmpInsiderPayload?.trades;
    if (!rows?.length) return new Map<string, FmpInsiderTradeRow[]>();
    const m = new Map<string, FmpInsiderTradeRow[]>();
    for (const r of rows) {
      const d = (r.transactionDate ?? '').trim();
      if (!d) continue;
      if (
        !tradeMatchesInsiderLayerFilters(
          r,
          insiderTradingEnabledTypeCodes,
          insiderTradingSearchQuery
        )
      ) {
        continue;
      }
      const arr = m.get(d) ?? [];
      arr.push(r);
      m.set(d, arr);
    }
    return m;
  }, [
    fmpInsiderPayload?.trades,
    insiderTradingEnabledTypeCodes,
    insiderTradingSearchQuery,
  ]);

  const insiderTradeTypeCounts = useMemo(
    () => countInsiderTradesByTypeCode(fmpInsiderPayload?.trades),
    [fmpInsiderPayload?.trades]
  );

  const insiderChartDates = useMemo(
    () => new Set(historicalChartVisibleData.map((d) => d.date)),
    [historicalChartVisibleData]
  );

  function notableOptionTradeMarkerKey(trade: NotableOptionTradeRow): string {
    return `${trade.date}|${trade.time}|${trade.symbol}|${trade.premium}`;
  }

  const notableOptionTradesForVisibleChart = useMemo(() => {
    if (!layers.notableOptionTrades.enabled || notableOptionTradesPerDay.length === 0) return [];
    const dates = new Set(historicalChartVisibleData.map((d) => d.date));
    return notableOptionTradesPerDay.filter((t) => dates.has(t.date));
  }, [
    layers.notableOptionTrades.enabled,
    notableOptionTradesPerDay,
    historicalChartVisibleData,
  ]);

  const notableOptionTradeDiamondRadiusByDate = useMemo(() => {
    const rows = notableOptionTradesForVisibleChart.map((t) => ({
      key: t.date,
      premium: Math.max(Number(t.premium) || 0, 0),
    }));
    return notableOptionTradeDiamondRadiusByKey(rows);
  }, [notableOptionTradesForVisibleChart]);

  const notableOptionDiamondMarkersForVisibleChart = useMemo(() => {
    if (!notableOptionTradesForVisibleChart.length) return [];
    type Bar = (typeof historicalChartVisibleData)[number];
    type Marker = Bar & { trade: NotableOptionTradeRow; yPrice: number };
    const byDate = new Map(notableOptionTradesForVisibleChart.map((t) => [t.date, t]));
    const out: Marker[] = [];
    for (const row of historicalChartVisibleData) {
      const trade = byDate.get(row.date);
      if (!trade) continue;
      out.push({
        ...row,
        trade,
        yPrice: Number(row.close),
      });
    }
    return out;
  }, [notableOptionTradesForVisibleChart, historicalChartVisibleData]);

  const insiderDiamondMarkersForVisibleChart = useMemo(() => {
    if (!layers.insiderTrading.enabled || insiderTradesByTransactionDate.size === 0) return [];
    type Bar = (typeof historicalChartVisibleData)[number];
    type Marker = Bar & {
      txnCode: InsiderTransactionCode;
      trades: FmpInsiderTradeRow[];
      trade: FmpInsiderTradeRow;
      yPrice: number;
      categoryIndex: number;
      categoryCount: number;
    };
    const out: Marker[] = [];
    for (const row of historicalChartVisibleData) {
      const trades = insiderTradesByTransactionDate.get(row.date);
      if (!trades?.length) continue;
      const byCode = new Map<InsiderTransactionCode, FmpInsiderTradeRow[]>();
      for (const trade of trades) {
        const code = insiderTransactionCode(trade);
        const arr = byCode.get(code) ?? [];
        arr.push(trade);
        byCode.set(code, arr);
      }
      const codes = [...byCode.keys()].sort();
      codes.forEach((txnCode, categoryIndex) => {
        const groupTrades = byCode.get(txnCode)!;
        out.push({
          ...row,
          txnCode,
          trades: groupTrades,
          trade: pickRepresentativeInsiderTrade(groupTrades),
          yPrice: Number(row.close),
          categoryIndex,
          categoryCount: codes.length,
        });
      });
    }
    return out;
  }, [layers.insiderTrading.enabled, insiderTradesByTransactionDate, historicalChartVisibleData]);

  const handleInsiderSearchSelect = useCallback(
    (result: InsiderTradeSearchResult) => {
      setInsiderTradingSearchQuery(result.reportingName);
      setInsiderTradingSearchHighlight({
        date: result.transactionDate,
        txnCode: result.txnCode,
      });
      const full = historicalChartData;
      const idx = full.findIndex((d) => d.date === result.transactionDate);
      if (idx >= 0 && full.length > 0) {
        const pad = 30;
        const lo = Math.max(0, idx - pad);
        const hi = Math.min(full.length - 1, idx + pad);
        if (lo === 0 && hi >= full.length - 1) {
          setHistoricalZoomRange(null);
        } else {
          setHistoricalZoomRange({ start: lo, end: hi });
        }
      }
    },
    [historicalChartData]
  );

  const congressTradeCategoryCounts = useMemo(
    () => countCongressTradesByCategory(fmpCongressPayload?.congress_trades),
    [fmpCongressPayload?.congress_trades]
  );

  const congressTradeChamberCounts = useMemo(
    () => countCongressTradesByChamber(fmpCongressPayload?.congress_trades),
    [fmpCongressPayload?.congress_trades]
  );

  const congressChartDates = useMemo(
    () => new Set(historicalChartVisibleData.map((d) => d.date)),
    [historicalChartVisibleData]
  );

  const congressTradesByTransactionDate = useMemo(() => {
    const rows = fmpCongressPayload?.congress_trades;
    if (!rows?.length) return new Map<string, FmpCongressTradeRow[]>();
    const m = new Map<string, FmpCongressTradeRow[]>();
    for (const r of rows) {
      const d = (r.transactionDate ?? '').trim();
      if (!d) continue;
      if (
        !tradeMatchesCongressLayerFilters(
          r,
          congressTradingEnabledCategories,
          congressTradingEnabledChambers,
          congressTradingSearchQuery
        )
      ) {
        continue;
      }
      const arr = m.get(d) ?? [];
      arr.push(r);
      m.set(d, arr);
    }
    return m;
  }, [
    fmpCongressPayload?.congress_trades,
    congressTradingEnabledCategories,
    congressTradingEnabledChambers,
    congressTradingSearchQuery,
  ]);

  const handleCongressSearchSelect = useCallback(
    (result: CongressTradeSearchResult) => {
      setCongressTradingSearchQuery(result.memberName);
      setCongressTradingSearchHighlight({ tradeIdentityKey: result.tradeIdentityKey });
      const full = historicalChartData;
      const idx = full.findIndex((d) => d.date === result.transactionDate);
      if (idx >= 0 && full.length > 0) {
        const pad = 30;
        const lo = Math.max(0, idx - pad);
        const hi = Math.min(full.length - 1, idx + pad);
        if (lo === 0 && hi >= full.length - 1) {
          setHistoricalZoomRange(null);
        } else {
          setHistoricalZoomRange({ start: lo, end: hi });
        }
      }
    },
    [historicalChartData]
  );

  const congressDiamondMarkersForVisibleChart = useMemo(() => {
    if (!layers.senateTrading.enabled || congressTradesByTransactionDate.size === 0) return [];
    type Bar = (typeof historicalChartVisibleData)[number];
    type Marker = Bar & {
      trade: FmpCongressTradeRow;
      yPrice: number;
      stackIndex: number;
      stackCount: number;
    };
    const out: Marker[] = [];
    for (const row of historicalChartVisibleData) {
      const trades = congressTradesByTransactionDate.get(row.date);
      if (!trades?.length) continue;
      const n = trades.length;
      trades.forEach((trade, i) => {
        out.push({
          ...row,
          trade,
          yPrice: Number(row.close),
          stackIndex: i,
          stackCount: n,
        });
      });
    }
    return out;
  }, [layers.senateTrading.enabled, congressTradesByTransactionDate, historicalChartVisibleData]);

  historicalChartFullLengthRef.current = historicalChartData.length;
  historicalChartVisibleDataRef.current = historicalChartVisibleData;
  historicalZoomRangeRef.current = historicalZoomRange;

  /** Shared hover + brush-zoom: map client point to crosshair / axis pills. Brush uses clampToPlot so trackers stay visible while dragging past edges. */
  const syncHistoricalCrosshairFromClientPoint = useCallback(
    (clientX: number, clientY: number, clampToPlot: boolean) => {
      const wrap = historicalChartWrapRef.current;
      const cp = historicalChartMetricsRef.current;
      const visibleData = historicalChartVisibleDataRef.current;
      if (!wrap || !cp?.offset || visibleData.length === 0) return;
      const svg = wrap.querySelector('svg');
      if (!svg) return;
      const res = clientPointToHistoricalPlotLxLy(clientX, clientY, wrap, cp);
      if (!res) {
        setHistoricalPlotMouse(null);
        setHistoricalPriceTrackerOverlay(null);
        setHistoricalOhlcTracker(null);
        return;
      }
      const { width, height } = cp.offset;
      let lx = res.lx;
      let ly = res.ly;
      if (clampToPlot) {
        lx = Math.max(0, Math.min(width, lx));
        ly = Math.max(0, Math.min(height, ly));
      } else if (lx < 0 || lx > width || ly < 0 || ly > height) {
        setHistoricalPlotMouse(null);
        setHistoricalPriceTrackerOverlay(null);
        setHistoricalOhlcTracker(null);
        return;
      }
      setHistoricalPlotMouse({ lx, ly });
      const snap = snapHistoricalCrosshair(cp, visibleData, lx, ly);
      if (!snap) {
        setHistoricalPriceTrackerOverlay(null);
        setHistoricalOhlcTracker(null);
        return;
      }
      const ohlc = historicalOhlcFromRow(snap.snappedRow);
      const { change, changePct } = historicalSessionChangeFromRow(
        visibleData,
        snap.snappedIndex,
        snap.snappedRow,
      );
      if (ohlc) {
        const d = snap.snappedRow.date;
        setHistoricalOhlcTracker({
          o: ohlc.o,
          h: ohlc.h,
          l: ohlc.l,
          c: ohlc.c,
          change,
          changePct,
          date: d,
          fullDate:
            typeof snap.snappedRow.fullDate === 'string' && snap.snappedRow.fullDate.trim()
              ? snap.snappedRow.fullDate
              : undefined,
        });
      } else {
        setHistoricalOhlcTracker(null);
      }
      const label =
        snap.cursorPrice != null && Number.isFinite(snap.cursorPrice)
          ? `$${snap.cursorPrice.toFixed(2)}`
          : '—';
      const pillH = 22;
      const pos = historicalPriceTrackerOverlayPosition(
        svg as SVGSVGElement,
        wrap,
        snap.plotRight,
        snap.horizontalY,
        cp.offset.top,
        snap.plotBottom,
        pillH,
      );
      if (pos) {
        setHistoricalPriceTrackerOverlay({ left: pos.left, top: pos.top, label });
      } else {
        setHistoricalPriceTrackerOverlay(null);
      }
    },
    [],
  );

  const syncIntradayCrosshairFromClientPoint = useCallback((clientX: number, clientY: number) => {
    const wrap = intradayPriceChartWrapRef.current;
    const cp = intradayChartMetricsRef.current;
    const chartData = intradayChartDataForCrosshairRef.current;
    if (!wrap || !cp?.offset || chartData.length === 0) return;
    const svg = wrap.querySelector('svg');
    if (!svg) return;
    const res = clientPointToHistoricalPlotLxLy(clientX, clientY, wrap, cp);
    if (!res) {
      setIntradayPlotMouse(null);
      setIntradayPriceTrackerOverlay(null);
      return;
    }
    const { width, height } = cp.offset;
    const lx = res.lx;
    const ly = res.ly;
    if (lx < 0 || lx > width || ly < 0 || ly > height) {
      setIntradayPlotMouse(null);
      setIntradayPriceTrackerOverlay(null);
      return;
    }
    setIntradayPlotMouse({ lx, ly });
    const snap = snapIntradayCrosshair(cp, chartData, lx, ly);
    if (!snap) {
      setIntradayPriceTrackerOverlay(null);
      return;
    }
    const label =
      snap.cursorPrice != null && Number.isFinite(snap.cursorPrice)
        ? `$${snap.cursorPrice.toFixed(2)}`
        : '—';
    const pillH = 22;
    const pos = historicalPriceTrackerOverlayPosition(
      svg as SVGSVGElement,
      wrap,
      snap.plotRight,
      snap.horizontalY,
      cp.offset.top,
      snap.plotBottom,
      pillH,
    );
    if (pos) {
      setIntradayPriceTrackerOverlay({ left: pos.left, top: pos.top, label });
    } else {
      setIntradayPriceTrackerOverlay(null);
    }
  }, []);

  const handleIntradayPriceChartMouseMove = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      syncIntradayCrosshairFromClientPoint(e.clientX, e.clientY);
    },
    [syncIntradayCrosshairFromClientPoint],
  );

  const handleIntradayPriceChartMouseLeave = useCallback(() => {
    setIntradayPlotMouse(null);
    setIntradayPriceTrackerOverlay(null);
  }, []);

  const handleHistoricalChartMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (historicalBrushSelecting) return;
      syncHistoricalCrosshairFromClientPoint(e.clientX, e.clientY, false);
    },
    [historicalBrushSelecting, syncHistoricalCrosshairFromClientPoint],
  );

  const handleHistoricalChartMouseLeave = useCallback(() => {
    if (historicalBrushSelecting) return;
    setHistoricalPlotMouse(null);
    setHistoricalPriceTrackerOverlay(null);
    setHistoricalOhlcTracker(null);
  }, [historicalBrushSelecting]);

  const handleHistoricalBrushMouseMove = useCallback(
    (e: MouseEvent) => {
      const plotEl = historicalChartPlotRef.current;
      if (!plotEl) return;
      const rect = plotEl.getBoundingClientRect();
      const x2 = e.clientX - rect.left;
      const y2 = e.clientY - rect.top;
      setHistoricalBrushBox((prev) => (prev ? { ...prev, x2, y2 } : null));
      syncHistoricalCrosshairFromClientPoint(e.clientX, e.clientY, true);
    },
    [syncHistoricalCrosshairFromClientPoint],
  );

  const finalizeHistoricalBrush = useCallback((e: MouseEvent) => {
    const pair = historicalBrushListenersRef.current;
    if (pair) {
      window.removeEventListener('mousemove', pair.move);
      window.removeEventListener('mouseup', pair.up);
      historicalBrushListenersRef.current = null;
    }

    const wrap = historicalChartWrapRef.current;
    const cp = historicalChartMetricsRef.current;
    const startCli = historicalBrushStartClientRef.current;
    historicalBrushStartClientRef.current = null;
    setHistoricalBrushSelecting(false);
    setHistoricalBrushBox(null);

    if (!wrap || !cp?.offset || !startCli) return;

    const visible = historicalChartVisibleDataRef.current;
    if (visible.length === 0) return;

    const a0 = clientPointToHistoricalPlotLxLy(startCli.x, startCli.y, wrap, cp);
    const a1 = clientPointToHistoricalPlotLxLy(e.clientX, e.clientY, wrap, cp);
    if (!a0 || !a1) return;

    const lxSpan = Math.abs(a1.lx - a0.lx);
    if (lxSpan < HISTORICAL_BRUSH_MIN_PLOT_WIDTH_PX) return;

    const lyMid = (Math.min(a0.ly, a1.ly) + Math.max(a0.ly, a1.ly)) / 2;
    const i0 = historicalPlotLxToBarIndex(cp, visible, a0.lx, lyMid);
    const i1 = historicalPlotLxToBarIndex(cp, visible, a1.lx, lyMid);
    if (i0 == null || i1 == null) return;

    let lo = Math.min(i0, i1);
    let hi = Math.max(i0, i1);
    if (hi - lo + 1 < HISTORICAL_ZOOM_MIN_VISIBLE_BARS) {
      const pad = HISTORICAL_ZOOM_MIN_VISIBLE_BARS - (hi - lo + 1);
      const leftPad = Math.floor(pad / 2);
      lo = Math.max(0, lo - leftPad);
      hi = Math.min(visible.length - 1, hi + (pad - leftPad));
    }

    const base = historicalZoomRangeRef.current?.start ?? 0;
    const fullLen = historicalChartFullLengthRef.current;
    if (fullLen <= 0) return;

    const absLo = Math.max(0, Math.min(base + lo, fullLen - 1));
    const absHi = Math.max(absLo, Math.min(base + hi, fullLen - 1));

    if (absLo === 0 && absHi >= fullLen - 1) {
      setHistoricalZoomRange(null);
      return;
    }

    setHistoricalZoomRange({ start: absLo, end: absHi });
    historicalChartSuppressClickRef.current = true;
  }, []);

  useEffect(() => {
    return () => {
      const pair = historicalBrushListenersRef.current;
      if (pair) {
        window.removeEventListener('mousemove', pair.move);
        window.removeEventListener('mouseup', pair.up);
        historicalBrushListenersRef.current = null;
      }
    };
  }, []);

  const handleHistoricalBrushMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (loadingHistoricalPrices || e.button !== 0) return;
      const el = e.target as HTMLElement | null;
      if (el?.closest?.('[data-chart-interactive-marker="true"]')) return;

      const plotEl = historicalChartPlotRef.current;
      const wrap = historicalChartWrapRef.current;
      const cp = historicalChartMetricsRef.current;
      if (!plotEl || !wrap || !cp?.offset) return;

      const o = cp.offset;
      const res = clientPointToHistoricalPlotLxLy(e.clientX, e.clientY, wrap, cp);
      if (!res) return;
      if (res.lx < 0 || res.lx > o.width || res.ly < 0 || res.ly > o.height) return;

      const pair = historicalBrushListenersRef.current;
      if (pair) {
        window.removeEventListener('mousemove', pair.move);
        window.removeEventListener('mouseup', pair.up);
        historicalBrushListenersRef.current = null;
      }

      const rect = plotEl.getBoundingClientRect();
      historicalBrushStartClientRef.current = { x: e.clientX, y: e.clientY };
      setHistoricalBrushSelecting(true);
      const x1 = e.clientX - rect.left;
      const y1 = e.clientY - rect.top;
      setHistoricalBrushBox({ x1, y1, x2: x1, y2: y1 });

      const move = (ev: MouseEvent) => {
        handleHistoricalBrushMouseMove(ev);
      };
      const up = (ev: MouseEvent) => {
        finalizeHistoricalBrush(ev);
      };
      historicalBrushListenersRef.current = { move, up };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);

      syncHistoricalCrosshairFromClientPoint(e.clientX, e.clientY, true);
      // Do not call preventDefault() here: it suppresses the synthetic click in many browsers,
      // so Recharts' onClick never runs for in-plot targets (e.g. candle bodies). Axis / margin
      // clicks often skip this handler (bounds check) and still received a click — use CSS
      // select-none on the plot container instead to avoid drag-selecting text.
    },
    [
      loadingHistoricalPrices,
      handleHistoricalBrushMouseMove,
      finalizeHistoricalBrush,
      syncHistoricalCrosshairFromClientPoint,
    ],
  );

  // Format tick labels based on timeframe (full-range view; brush-zoom uses formatHistoricalXTickLabel)
  const formatTickLabel = useCallback((dateString: string) => {
    try {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const monthIndex = date.getMonth();

      if (timeframe === '5Y') {
        return date.getFullYear().toString();
      }
      if (timeframe === '3Y') {
        if (monthIndex === 0) {
          return date.getFullYear().toString();
        }
        return date.toLocaleDateString('en-US', { month: 'short' });
      }
      if (timeframe === '7D' || timeframe === '30D') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      if (monthIndex === 0) {
        return date.getFullYear().toString();
      }
      return date.toLocaleDateString('en-US', { month: 'short' });
    } catch {
      return dateString;
    }
  }, [timeframe]);

  const formatHistoricalXTickLabel = useCallback(
    (dateString: string) => {
      if (!historicalZoomRange) {
        return formatTickLabel(dateString);
      }
      try {
        const [y, m, d] = dateString.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } catch {
        return dateString;
      }
    },
    [historicalZoomRange, formatTickLabel],
  );

  const monthTicks = useMemo(() => {
    const chartData = historicalChartVisibleData;
    if (chartData.length === 0) return [];

    if (timeframe === '7D' || timeframe === '30D') {
      const n = chartData.length;
      const targetTicks = timeframe === '7D' ? 5 : 6;
      const step = Math.max(1, Math.floor((n - 1) / Math.max(1, targetTicks - 1)));
      const out: string[] = [];
      for (let i = 0; i < n; i += step) {
        out.push(chartData[i].date);
      }
      const last = chartData[n - 1].date;
      if (out[out.length - 1] !== last) out.push(last);
      return out;
    }

    const ticks: string[] = [];
    const seen = new Set<string>();

    chartData.forEach((point) => {
      if (!point.dateObj) return;

      const date = point.dateObj;
      const monthIndex = date.getMonth();
      const year = date.getFullYear();

      if (timeframe === '5Y') {
        if (monthIndex === 0) {
          const key = `${year}`;
          if (!seen.has(key)) {
            seen.add(key);
            ticks.push(point.date);
          }
        }
      } else if (timeframe === '3Y') {
        if (monthIndex % 3 === 0) {
          const key = `${year}-${monthIndex}`;
          if (!seen.has(key)) {
            seen.add(key);
            ticks.push(point.date);
          }
        }
      } else {
        const key = `${year}-${monthIndex}`;
        if (!seen.has(key)) {
          seen.add(key);
          ticks.push(point.date);
        }
      }
    });

    return ticks;
  }, [historicalChartVisibleData, timeframe]);

  /** Full-range: month/quarter/year ticks. Brush-zoom: dense per-bar labels (cap before striding). */
  const historicalXTicks = useMemo(() => {
    if (!historicalZoomRange) return monthTicks;
    return historicalZoomBarTicksDense(historicalChartVisibleData, HISTORICAL_ZOOM_X_AXIS_MAX_TICKS);
  }, [historicalZoomRange, historicalChartVisibleData, monthTicks]);

  /**
   * Y-axis domain + ticks from visible OHLC/close (selected timeframe / brush), with vertical padding.
   * `domainMin` is clamped at 0 so the axis never implies negative prices for typical equities.
   */
  const { yAxisDomain, historicalYAxisTicks, historicalYAxisTickStep } = useMemo(() => {
    const chartData = historicalChartVisibleData;
    const empty = (): {
      yAxisDomain: [number, number];
      historicalYAxisTicks: number[];
      historicalYAxisTickStep: number;
    } => {
      const b = buildWholeDollarYAxisTicks(0, 100);
      return { yAxisDomain: [0, 100], historicalYAxisTicks: b.ticks, historicalYAxisTickStep: b.step };
    };

    if (chartData.length === 0) return empty();

    const toFinite = (v: unknown, fallback: number): number => {
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      if (typeof v === 'string' && v.trim() !== '') {
        const n = parseFloat(v);
        if (Number.isFinite(n)) return n;
      }
      return fallback;
    };

    let dataMin: number;
    let dataMax: number;

    if (historicalChartStyle === 'candle') {
      const lows: number[] = [];
      const highs: number[] = [];
      for (const d of chartData as any[]) {
        const c = toFinite(d.close, toFinite(d.price, NaN));
        if (!Number.isFinite(c)) continue;
        const o = toFinite(d.open, c);
        const lowRaw = toFinite(d.low, NaN);
        const sessionLow = Number.isFinite(lowRaw) ? lowRaw : Math.min(o, c);
        const highRaw = toFinite(d.high, NaN);
        const sessionHigh = Number.isFinite(highRaw) ? highRaw : Math.max(o, c);
        lows.push(Math.min(sessionLow, o, c));
        highs.push(Math.max(sessionHigh, o, c));
      }
      if (lows.length === 0 || highs.length === 0) return empty();
      dataMin = Math.min(...lows);
      dataMax = Math.max(...highs);
    } else {
      const prices = chartData.map((d: any) => d.price).filter((p: number) => typeof p === 'number' && !isNaN(p));
      if (prices.length === 0) return empty();
      dataMin = Math.min(...prices);
      dataMax = Math.max(...prices);
    }

    const span = dataMax - dataMin;
    const pad =
      span > 0
        ? span * 0.06
        : Math.abs(dataMax) * 0.03 || Math.abs(dataMin) * 0.03 || 0.02;

    const domainMin = Math.max(0, dataMin - pad);
    const domainMax = Math.max(dataMax + pad, domainMin + 1e-3);

    const built = buildWholeDollarYAxisTicks(domainMin, domainMax);
    return {
      yAxisDomain: [domainMin, domainMax] as [number, number],
      historicalYAxisTicks: built.ticks,
      historicalYAxisTickStep: built.step,
    };
  }, [historicalChartVisibleData, historicalChartStyle]);

  const formatHistoricalYAxisTick = useCallback(
    (v: unknown) => formatPriceAxisTick(v, historicalYAxisTickStep),
    [historicalYAxisTickStep],
  );

  /**
   * Draw OHLC candles in `Customized` using the same `xAxisMap` / `yAxisMap` scales as ticks, grid,
   * and `ReferenceDot`. Recharts' `Bar` custom `shape` receives coordinates that can diverge from
   * those scales; this path stays aligned with the Y-axis.
   */
  const renderHistoricalCandlesOverlay = useCallback(
    (chartProps: HistoricalChartMetrics & Record<string, unknown>) => {
      if (historicalChartStyle !== 'candle') return null;
      const chartData = historicalChartVisibleData as any[];
      if (chartData.length === 0) return null;

      const xAxisMap = chartProps.xAxisMap as Record<string, any> | undefined;
      const yAxisMap = chartProps.yAxisMap as Record<string, any> | undefined;
      const offset = chartProps.offset as { left: number; top: number; width: number; height: number } | undefined;
      if (!xAxisMap || !yAxisMap || !offset) return null;

      const xAxis = xAxisMap[0] ?? Object.values(xAxisMap)[0];
      const yAxis = yAxisMap['price'] ?? yAxisMap[0] ?? Object.values(yAxisMap)[0];
      const xScale = xAxis?.scale;
      const yScale = yAxis?.scale;
      if (typeof xScale !== 'function' || typeof yScale !== 'function') return null;

      const bw = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
      const slotWFallback =
        chartData.length > 1
          ? Math.abs(Number(xScale(chartData[1].date)) - Number(xScale(chartData[0].date))) ||
            offset.width / chartData.length
          : offset.width / Math.max(chartData.length, 1);
      const slotW = bw > 0 ? bw : slotWFallback;
      let candleBodyW = Math.max(slotW * 0.62, 2);
      if (slotW > 4) {
        candleBodyW = Math.min(candleBodyW, slotW - 1);
      }

      const priceToY = (price: number) => Number(yScale(price));

      return (
        <g className="recharts-layer historical-price-candles-overlay" style={{ shapeRendering: 'crispEdges' }}>
          {chartData.map((payload: any, barIndex: number) => {
            if (!payload || typeof payload.open !== 'number' || typeof payload.close !== 'number') return null;
            const { open, high, low, close, isSignificant } = payload;
            if (typeof high !== 'number' || typeof low !== 'number') return null;

            const x0 = xScale(payload.date);
            if (x0 == null || Number.isNaN(Number(x0))) return null;
            const centerX = Number(x0) + (bw > 0 ? bw / 2 : 0);

            const highY = priceToY(high);
            const lowY = priceToY(low);
            const openY = priceToY(open);
            const closeY = priceToY(close);

            const bodyTop = Math.min(openY, closeY);
            const bodyHeight = Math.max(Math.abs(closeY - openY), 1);
            const strokeFill = close >= open ? '#10b981' : '#ef4444';

            return (
              <g key={`${payload.date}-${barIndex}`}>
                <line
                  x1={centerX}
                  y1={highY}
                  x2={centerX}
                  y2={lowY}
                  stroke={strokeFill}
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <rect
                  x={centerX - candleBodyW / 2}
                  y={bodyTop}
                  width={candleBodyW}
                  height={bodyHeight}
                  fill={strokeFill}
                  stroke={strokeFill}
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                {isSignificant ? (
                  <CustomDot
                    cx={centerX}
                    cy={closeY}
                    payload={payload}
                    index={barIndex}
                    onDotClick={handleDotClick}
                    selectedPoint={selectedPoint}
                    timeframe={timeframe}
                    notableEventLabels={notableEventLabels}
                    notableHoverStore={notableEventHoverStore}
                  />
                ) : null}
              </g>
            );
          })}
        </g>
      );
    },
    [historicalChartStyle, historicalChartVisibleData, handleDotClick, selectedPoint, timeframe, notableEventLabels, notableEventHoverStore],
  );

  /**
   * Volume histogram overlaid on the price panel. Uses the same X scale as candles/lines but maps
   * bar height in screen space (Recharts' secondary `Bar` + `yAxisId` is easy to mis-resolve against
   * string axis ids, which shrinks bars to the price domain).
   */
  const renderHistoricalVolumeOverlay = useCallback(
    (chartProps: HistoricalChartMetrics & Record<string, unknown>) => {
      if (!layers.volume.enabled) return null;
      const chartData = historicalChartVisibleData as Array<{
        date: string;
        open: number;
        close: number;
        volume?: number;
      }>;
      if (chartData.length === 0) return null;

      const xAxisMap = chartProps.xAxisMap as Record<string, any> | undefined;
      const offset = chartProps.offset as { left: number; top: number; width: number; height: number } | undefined;
      if (!xAxisMap || !offset) return null;

      const xAxis = xAxisMap[0] ?? Object.values(xAxisMap)[0];
      const xScale = xAxis?.scale;
      if (typeof xScale !== 'function') return null;

      const bw = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
      const slotWFallback =
        chartData.length > 1
          ? Math.abs(Number(xScale(chartData[1].date)) - Number(xScale(chartData[0].date))) ||
            offset.width / chartData.length
          : offset.width / Math.max(chartData.length, 1);
      const slotW = bw > 0 ? bw : slotWFallback;
      let barW = Math.max(slotW * 0.58, 2);
      if (slotW > 4) barW = Math.min(barW, slotW - 1);

      const volumes = chartData.map((d) =>
        typeof d.volume === 'number' && d.volume > 0 ? d.volume : 0,
      );
      const sqrtMax = Math.sqrt(Math.max(...volumes, 1));
      const plotBottom = offset.top + offset.height;
      const maxBarH = offset.height * HISTORICAL_VOLUME_OVERLAY_MAX_HEIGHT_FRAC;

      return (
        <g
          className="recharts-layer historical-volume-overlay"
          pointerEvents="none"
          style={{ shapeRendering: 'crispEdges' }}
        >
          {chartData.map((payload) => {
            const v = typeof payload.volume === 'number' ? payload.volume : 0;
            const x0 = xScale(payload.date);
            if (x0 == null || Number.isNaN(Number(x0))) return null;
            const centerX = Number(x0) + (bw > 0 ? bw / 2 : 0);
            const t = v > 0 && sqrtMax > 0 ? Math.sqrt(v) / sqrtMax : 0;
            let barH = t * maxBarH;
            if (v > 0 && barH < 2.5) barH = 2.5;
            barH = Math.min(barH, maxBarH);
            const x = centerX - barW / 2;
            const y = plotBottom - barH;
            const isUp = payload.close >= payload.open;
            const fill = isUp ? '#22c55e' : '#ef4444';
            return (
              <rect
                key={`vol-${payload.date}`}
                x={x}
                y={y}
                width={barW}
                height={barH}
                fill={fill}
                fillOpacity={0.48}
              />
            );
          })}
        </g>
      );
    },
    [layers.volume.enabled, historicalChartVisibleData],
  );

  /**
   * Headlines + vertical leader lines for origin-event dates (above the price line).
   */
  const renderHistoricalNotableEventLabels = useCallback(
    (chartProps: HistoricalChartMetrics & Record<string, unknown>) => {
      if (!layers.notableEvents.enabled) return null;
      const chartData = historicalChartVisibleData as any[];
      if (chartData.length === 0) return null;

      const xAxisMap = chartProps.xAxisMap as Record<string, any> | undefined;
      const yAxisMap = chartProps.yAxisMap as Record<string, any> | undefined;
      const offset = chartProps.offset as
        | { left: number; top: number; width: number; height: number }
        | undefined;
      if (!xAxisMap || !yAxisMap || !offset) return null;

      const xAxis = xAxisMap[0] ?? Object.values(xAxisMap)[0];
      const yAxis = yAxisMap['price'] ?? yAxisMap[0] ?? Object.values(yAxisMap)[0];
      const xScale = xAxis?.scale;
      const yScale = yAxis?.scale;
      if (typeof xScale !== 'function' || typeof yScale !== 'function') return null;

      const bw = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
      const FONT_SIZE = 11;
      const LINE_H = 14;
      const CHAR_W = 6.5;
      const H_PAD = 16;
      const BUF_X = 8;
      const BUF_Y = 5;
      const GAP = 6;
      const PRICE_GAP = 10;
      const CLAMP_Y = 4;
      const plotW = offset.width;
      const plotH = offset.height;
      // Min leader length keeps the leader visible; max is intentionally generous so
      // dense clusters can lift labels into upper lanes without being dropped.
      const MIN_LEADER_LEN = 8;
      // How far horizontally a label may slide from its anchor before we try a
      // different lane. Allows reasonable shifts in dense clusters without flying off.
      const MAX_HORIZONTAL_SHIFT = 90;
      // Vertical spacing between lanes.
      const LANE_GAP_Y = 6;
      // Horizontal spacing between adjacent labels in the same lane.
      const LANE_INTRA_X_GAP = 6;

      type EventEntry = {
        date: string;
        cx: number;
        cy: number;
        rawLabel: string;
        line1: string;
        line2: string | null;
        halfW: number;
        textH: number;
      };

      type PendingEvent = { date: string; cx: number; cy: number; rawLabel: string };
      const pending: PendingEvent[] = [];
      for (const d of chartData) {
        if (!d.isNotableOriginEvent) continue;
        const x0 = xScale(d.date);
        if (x0 == null || Number.isNaN(Number(x0))) continue;
        const cx = Number(x0) + (bw > 0 ? bw / 2 : 0);
        const priceVal =
          typeof d.close === 'number' ? d.close : typeof d.price === 'number' ? d.price : 0;
        const cy = Number(yScale(priceVal));
        const rawLabel = notableEventLabels[d.date];
        if (rawLabel === undefined) continue;
        pending.push({ date: d.date, cx, cy, rawLabel });
      }
      if (pending.length === 0) return null;

      const compactLabels = pending.length >= 4;
      const events: EventEntry[] = pending.map((p) => {
        const { line1, line2 } = splitChartEventLabel(p.rawLabel, compactLabels);
        const longestChars = Math.max(line1.length, line2?.length ?? 0);
        const halfW = (longestChars * CHAR_W + H_PAD) / 2;
        const textH = line2 ? LINE_H * 2 : LINE_H;
        return { date: p.date, cx: p.cx, cy: p.cy, rawLabel: p.rawLabel, line1, line2, halfW, textH };
      });
      const clampLabelCx = (cx: number, halfW: number) =>
        Math.max(halfW + 10, Math.min(plotW - halfW - 10, cx));

      events.sort((a, b) => a.cx - b.cx);

      // Precompute (x, y) for the highest price of every visible bar so we can
      // efficiently route leader lines above the price line between any two x
      // positions.
      const PRICE_CLEAR = 6;
      const pricePeaks: { x: number; y: number }[] = [];
      for (const r of chartData as Array<{
        date?: string;
        high?: number;
        close?: number;
        price?: number;
      }>) {
        if (!r || !r.date) continue;
        const x0 = xScale(r.date);
        if (x0 == null || Number.isNaN(Number(x0))) continue;
        const x = Number(x0) + (bw > 0 ? bw / 2 : 0);
        const peakVal =
          typeof r.high === 'number'
            ? r.high
            : typeof r.close === 'number'
              ? r.close
              : typeof r.price === 'number'
                ? r.price
                : null;
        if (peakVal == null) continue;
        const y = Number(yScale(peakVal));
        if (!Number.isFinite(y)) continue;
        pricePeaks.push({ x, y });
      }

      /**
       * Returns the SVG y of the highest price between x1 and x2 (inclusive).
       * Smaller y = higher on screen. Used to route leader lines so they hug
       * the top of the price line instead of cutting through it.
       */
      const pathCeilingY = (x1: number, x2: number, fallback: number): number => {
        const xLo = Math.min(x1, x2) - 2;
        const xHi = Math.max(x1, x2) + 2;
        let best: number | null = null;
        for (const p of pricePeaks) {
          if (p.x < xLo || p.x > xHi) continue;
          if (best == null || p.y < best) best = p.y;
        }
        return best ?? fallback;
      };

      /** Local high for this event day (SVG y of the top of the bar). */
      const anchorCeilingY = (ev: EventEntry): number => {
        const row = chartData.find((d: { date?: string }) => d.date === ev.date);
        const high =
          row && typeof row.high === 'number'
            ? row.high
            : row && typeof row.price === 'number'
              ? row.price
              : row && typeof row.close === 'number'
                ? row.close
                : null;
        if (high != null) {
          const y = Number(yScale(high));
          if (Number.isFinite(y)) return y;
        }
        return ev.cy;
      };

      type PlacedLabel = EventEntry & {
        labelCx: number;
        textTopY: number;
        textBottomY: number;
        leaderEndY: number;
        bx1: number;
        by1: number;
        bx2: number;
        by2: number;
      };

      // ── Lane-packing layout ────────────────────────────────────────────────
      // Build a small stack of horizontal "lanes" above the price line. For each
      // event (sorted left → right) we try the lane closest to the anchor first
      // and fall back to higher lanes only when it would horizontally collide
      // with another label already placed in that lane. Within a lane we push
      // the label rightward (away from earlier labels) before giving up. This
      // produces a clean stair-step layout instead of stacked overlap.
      const maxTextH = events.reduce((m, e) => Math.max(m, e.textH), LINE_H);
      const laneStride = maxTextH + LANE_GAP_Y;
      // Available headroom from the top of the plot down to where labels begin
      // to crowd the price line. We still cap to a reasonable number of lanes.
      const HARD_LANE_CAP = 6;
      const headroom = Math.max(plotH * 0.55, laneStride * 4);
      const laneCount = Math.max(
        1,
        Math.min(HARD_LANE_CAP, Math.floor((headroom - CLAMP_Y) / laneStride)),
      );
      // Lane 0 = topmost (farthest from price). Higher index = closer to price.
      const laneTops: number[] = [];
      for (let i = 0; i < laneCount; i++) {
        laneTops.push(CLAMP_Y + i * laneStride);
      }
      const lanePlacements: PlacedLabel[][] = laneTops.map(() => []);

      const tryFitInLane = (
        ev: EventEntry,
        laneIdx: number,
      ): PlacedLabel | null => {
        const textTopY = laneTops[laneIdx];
        const textBottomY = textTopY + ev.textH;
        const leaderEndY = textBottomY + GAP;
        // Must leave a visible leader between text and anchor.
        if (leaderEndY > ev.cy - MIN_LEADER_LEN) return null;
        // Don't overlap the local price high — keeps labels visually "above" the
        // line they describe.
        const ceilingY = anchorCeilingY(ev);
        if (textBottomY + GAP + PRICE_GAP > ceilingY) return null;

        // Ideal horizontal position is centered on the anchor.
        let labelCx = clampLabelCx(ev.cx, ev.halfW);
        // Push past any previously placed label in this lane that would overlap.
        for (const p of lanePlacements[laneIdx]) {
          const minDist = ev.halfW + p.halfW + LANE_INTRA_X_GAP;
          if (Math.abs(labelCx - p.labelCx) < minDist) {
            // Since events are sorted by cx ascending, prior labels are to the
            // left; shift right of them.
            labelCx = Math.max(labelCx, p.labelCx + minDist);
          }
        }
        labelCx = clampLabelCx(labelCx, ev.halfW);
        // Re-verify after clamp.
        for (const p of lanePlacements[laneIdx]) {
          const minDist = ev.halfW + p.halfW + LANE_INTRA_X_GAP;
          if (Math.abs(labelCx - p.labelCx) < minDist) return null;
        }
        // Don't allow huge horizontal drift from the anchor — that produces
        // confusingly long bent leader lines.
        if (Math.abs(labelCx - ev.cx) > MAX_HORIZONTAL_SHIFT) return null;

        return {
          ...ev,
          labelCx,
          textTopY,
          textBottomY,
          leaderEndY,
          bx1: labelCx - ev.halfW - BUF_X,
          bx2: labelCx + ev.halfW + BUF_X,
          by1: textTopY - BUF_Y,
          by2: textBottomY + BUF_Y,
        };
      };

      const placed: PlacedLabel[] = [];
      for (const ev of events) {
        let found: PlacedLabel | null = null;
        // Prefer the lane closest to the anchor (highest index) and ascend.
        for (let li = laneCount - 1; li >= 0; li--) {
          const hit = tryFitInLane(ev, li);
          if (hit) {
            lanePlacements[li].push(hit);
            found = hit;
            break;
          }
        }
        if (!found) {
          // Forced placement in the topmost lane, sliding right past existing
          // neighbors regardless of MAX_HORIZONTAL_SHIFT so we never drop a
          // headline. The leader may bend further but nothing is lost.
          const laneIdx = 0;
          const textTopY = laneTops[laneIdx];
          const textBottomY = textTopY + ev.textH;
          let labelCx = clampLabelCx(ev.cx, ev.halfW);
          for (const p of lanePlacements[laneIdx]) {
            const minDist = ev.halfW + p.halfW + LANE_INTRA_X_GAP;
            if (Math.abs(labelCx - p.labelCx) < minDist) {
              labelCx = Math.max(labelCx, p.labelCx + minDist);
            }
          }
          labelCx = clampLabelCx(labelCx, ev.halfW);
          const leaderEndY = Math.min(textBottomY + GAP, ev.cy - 2);
          found = {
            ...ev,
            labelCx,
            textTopY,
            textBottomY,
            leaderEndY,
            bx1: labelCx - ev.halfW - BUF_X,
            bx2: labelCx + ev.halfW + BUF_X,
            by1: textTopY - BUF_Y,
            by2: textBottomY + BUF_Y,
          };
          lanePlacements[laneIdx].push(found);
        }
        placed.push(found);
      }

      const maxFullLabelChars = Math.max(
        28,
        Math.min(52, Math.floor((plotW * 0.42) / CHAR_W)),
      );

      // Render-prop body for one label. `isHighlighted` toggles compact/expanded;
      // the surrounding subscriber re-renders just this label on hover, so the
      // rest of the chart stays untouched.
      const renderNotableEventCallout = (p: PlacedLabel, isHighlighted: boolean) => {
        if (p.leaderEndY >= p.cy - 2) return null;
        const staggered = Math.abs(p.labelCx - p.cx) > 2;
        const ceilingBetween = pathCeilingY(p.cx, p.labelCx, p.cy);
        const shoulderY = Math.min(ceilingBetween - PRICE_CLEAR, p.leaderEndY);
        const strokeW = isHighlighted ? 1.75 : 1;
        const strokeOp = isHighlighted ? 0.9 : 0.5;

        const compactLines = [p.line1, ...(p.line2 ? [p.line2] : [])];
        const displayLines = isHighlighted
          ? wrapFullNotableEventLabel(p.rawLabel, maxFullLabelChars)
          : compactLines;
        const longestChars = Math.max(...displayLines.map((line) => line.length), 1);
        const displayHalfW = (longestChars * CHAR_W + H_PAD) / 2;
        const displayTextH = displayLines.length * LINE_H;
        const displayBottomY = p.textBottomY;
        let displayTopY = displayBottomY - displayTextH;
        if (displayTopY < CLAMP_Y) displayTopY = CLAMP_Y;
        const displayCx = clampLabelCx(p.labelCx, displayHalfW);
        const handleEnter = () => notableEventHoverStore.set(p.date);
        const handleLeave = () => notableEventHoverStore.set(null);
        const handleHeadlineClick = (e: ReactMouseEvent) => {
          e.stopPropagation();
          const payload = chartData.find((d: { date?: string }) => d.date === p.date);
          if (!payload) return;
          void handleDotClick(payload, { scrollToExplanation: true });
        };

        return (
          <g className={isHighlighted ? 'notable-event-highlight' : undefined}>
            <g
              pointerEvents="none"
              stroke="#111827"
              strokeWidth={strokeW}
              strokeOpacity={strokeOp}
              fill="none"
            >
              {staggered ? (
                <>
                  <line x1={p.cx} y1={p.cy} x2={p.cx} y2={shoulderY} />
                  <line x1={p.cx} y1={shoulderY} x2={displayCx} y2={shoulderY} />
                  <line x1={displayCx} y1={shoulderY} x2={displayCx} y2={p.leaderEndY} />
                </>
              ) : (
                <line x1={p.cx} y1={p.cy} x2={p.cx} y2={p.leaderEndY} />
              )}
            </g>
            <g
              data-chart-interactive-marker="true"
              onMouseEnter={handleEnter}
              onMouseLeave={handleLeave}
              onClick={handleHeadlineClick}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ cursor: 'pointer' }}
              pointerEvents="all"
            >
              <rect
                x={displayCx - displayHalfW - 4}
                y={displayTopY - 4}
                width={(displayHalfW + 4) * 2}
                height={displayTextH + 8}
                fill="white"
                fillOpacity={isHighlighted ? 1 : 0.92}
                rx={3}
                stroke={isHighlighted ? '#e5e7eb' : 'none'}
                strokeWidth={isHighlighted ? 1 : 0}
              />
              <text
                x={displayCx}
                y={displayTopY + FONT_SIZE - 1}
                textAnchor="middle"
                fill="#111827"
                fontSize={FONT_SIZE}
                fontWeight={isHighlighted ? 600 : 400}
                fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              >
                {displayLines.map((line, idx) => (
                  <tspan key={`${p.date}-${idx}`} x={displayCx} dy={idx === 0 ? 0 : LINE_H}>
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          </g>
        );
      };

      return (
        <g className="recharts-layer notable-event-labels">
          {placed.map((p) => (
            <NotableLabelSubscriber
              key={p.date}
              hoverStore={notableEventHoverStore}
              date={p.date}
              render={(isHighlighted) => renderNotableEventCallout(p, isHighlighted)}
            />
          ))}
        </g>
      );
    },
    [layers.notableEvents.enabled, historicalChartVisibleData, notableEventLabels, notableEventHoverStore, handleDotClick],
  );

  const renderIntradayPriceMovementBlock = (): ReactNode => {
    if (
      !selectedPoint ||
      !(
        (selectedPoint.price !== undefined && selectedPoint.percentChange !== undefined) ||
        selectedPoint.blockTradeDetail
      )
    ) {
      return null;
    }
    const priceChartH = 176;
    return (
      <motion.div layout="position" className="relative mt-5 mb-4 overflow-visible">
        <div className="flex flex-wrap items-center justify-end gap-2 mb-2">
          {SHOW_INTRADAY_PRICE_SOURCE &&
          (tradeAnalytics?.venue_volume?.length ?? 0) > 0 && (
            <div className="flex flex-wrap items-center gap-2 min-w-0 mr-auto">
              <label className="flex items-center gap-1.5 text-xs text-gray-600 min-w-0">
                <span className="text-gray-400 shrink-0 hidden sm:inline">Price source</span>
                <select
                  value={intradayPricePublisherId === null ? '' : String(intradayPricePublisherId)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setIntradayPricePublisherId(v === '' ? null : Number(v));
                  }}
                  className="max-w-[min(100%,14rem)] truncate rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  aria-label="Intraday price source (exchange / publisher)"
                >
                  <option value="">Consolidated (Polygon)</option>
                  {(tradeAnalytics?.venue_volume ?? []).map((row, idx) => (
                    <option key={row.publisher_id} value={String(row.publisher_id)}>
                      {showPublisherIds
                        ? row.label
                          ? `${row.label} · pub ${row.publisher_id}`
                          : `Publisher ${row.publisher_id}`
                        : row.label
                          ? row.label
                          : `Venue ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </label>
              {intradayPricePublisherId !== null && (
                <label className="inline-flex items-center gap-1.5 cursor-pointer select-none text-xs text-gray-600 shrink-0">
                  <input
                    type="checkbox"
                    checked={intradayOverlayVenueOnPolygon}
                    onChange={(e) => setIntradayOverlayVenueOnPolygon(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    aria-label="Overlay selected venue on Polygon consolidated chart"
                  />
                  <span className="hidden sm:inline">Overlay on Polygon</span>
                  <span className="sm:hidden">Overlay</span>
                </label>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none text-xs text-gray-600">
              <span>Block trades</span>
              <button
                type="button"
                role="switch"
                aria-checked={showIntradayBlockTrades}
                onClick={() => setShowIntradayBlockTrades((v) => !v)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border px-0.5 transition-colors ${
                  showIntradayBlockTrades
                    ? 'bg-purple-600 border-purple-600'
                    : 'bg-gray-200 border-gray-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-1 ring-black/5 transition-transform ${
                    showIntradayBlockTrades ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </label>
          </div>
        </div>
        {/* Volume & Trades Stats - from intraday bars or fallback to historical daily volume */}
        {(() => {
          const volTrades = intradayVolumeAndTrades ?? (() => {
            if (!selectedPoint?.date || !dynamicHistoricalPrices?.length) return null;
            const day = dynamicHistoricalPrices.find((d) => d.date === selectedPoint.date);
            if (!day?.volume) return null;
            return { volume: day.volume, trades: Math.round(day.volume / 350) };
          })();
          const sessionDay = fullTradingDaySessionFromStats(intradaySessionStats);
          const useSessionStats = sessionDay != null && (sessionDay.total_trades ?? 0) > 0;
          const s = tradeAnalytics?.summary;
          const pubVenueRow =
            !useSessionStats &&
            intradayPricePublisherId != null &&
            tradeAnalytics?.venue_volume
              ? tradeAnalytics.venue_volume.find(
                  (r) => r.publisher_id === intradayPricePublisherId,
                )
              : undefined;
          const tradeCountVal = useSessionStats
            ? (sessionDay!.total_trades ?? null)
            : pubVenueRow != null
              ? (pubVenueRow.trade_count ?? null)
              : (s?.trade_count ?? volTrades?.trades);
          const shareVolVal = useSessionStats
            ? (sessionDay!.total_volume ?? null)
            : pubVenueRow != null
              ? pubVenueRow.volume
              : (s?.total_volume ?? volTrades?.volume);
          const sessionNotionalUsd = useSessionStats
            ? (sessionDay!.total_notional_usd ?? null)
            : pubVenueRow != null
              ? (pubVenueRow.notional_usd ?? null)
              : s?.session_notional_usd;

          const tapeBuySell = useSessionStats
            ? tapeBuySellFromRegularHours(sessionDay)
            : s != null
              ? intradayTapeBuySellFromSummary(s)
              : null;
          const showBuySellTotals =
            tapeBuySell != null &&
            (tapeBuySell.buyShares > 0 ||
              tapeBuySell.sellShares > 0 ||
              tapeBuySell.neutralShares > 0);

          const showTradesLine =
            shareVolVal != null &&
            (pubVenueRow != null
              ? shareVolVal > 0
              : tradeCountVal != null &&
                (tradeCountVal > 0 || shareVolVal > 0));

          const showBlockTrades =
            showIntradayBlockTrades &&
            tradeAnalytics != null &&
            tradeAnalytics.summary.block_trade_count > 0;
          if (!showTradesLine && !showBlockTrades && !showBuySellTotals) return null;
          return (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3 text-xs">
              {showBuySellTotals && tapeBuySell && (
                <div
                  title={
                    useSessionStats
                      ? 'Buy-aggressor (B) vs sell-aggressor (A) share volume for the full trading day (pre-market + regular session + after-hours).'
                      : 'Buy-aggressor (B) vs sell-aggressor (A) share volume for the session window used by tick analytics.'
                  }
                >
                  <span className="text-gray-400">Buy / sell (shares):</span>
                  <span className="text-emerald-600 ml-1.5 font-medium tabular-nums">
                    {formatShareVolCompact(tapeBuySell.buyShares)}
                  </span>
                  <span className="text-gray-400 mx-1">/</span>
                  <span className="text-red-500 font-medium tabular-nums">
                    {formatShareVolCompact(tapeBuySell.sellShares)}
                  </span>
                  {tapeBuySell.neutralShares > 0 ? (
                    <span className="text-gray-400 font-normal ml-1 tabular-nums">
                      · {formatShareVolCompact(tapeBuySell.neutralShares)} uncls.
                    </span>
                  ) : null}
                  {pubVenueRow != null ? (
                    <span className="text-gray-400 font-normal ml-1">(session tape)</span>
                  ) : null}
                </div>
              )}
              {showTradesLine && (
                <div>
                  <span className="text-gray-400">Trades:</span>
                  <span className="text-gray-700 ml-1.5 font-medium">
                    {tradeCountVal != null
                      ? tradeCountVal.toLocaleString()
                      : '—'}
                    <span className="text-gray-400 font-normal ml-1">
                      (
                      {formatShareVolCompact(shareVolVal!)} shares
                      {sessionNotionalUsd != null && sessionNotionalUsd > 0 ? (
                        <>
                          <span className="mx-1">·</span>
                          {formatUsdCompact(sessionNotionalUsd)}
                        </>
                      ) : null}
                      )
                    </span>
                  </span>
                </div>
              )}
              {showBlockTrades && SHOW_INTRADAY_BLOCK_TRADE_STATS && (
                <div>
                  <span className="text-gray-400">Block trades:</span>
                  <span className="text-gray-700 ml-1.5 font-medium">
                    {tradeAnalytics.summary.block_trade_count}
                    <span className="text-gray-400 font-normal ml-1">
                      (
                      {(tradeAnalytics.summary.block_trade_volume / 1000).toFixed(1)}K shares
                      <span className="mx-1">·</span>
                      {formatUsdCompact(tradeAnalytics.summary.block_trade_notional_usd ?? 0)}
                      )
                    </span>
                  </span>
                </div>
              )}
            </div>
          );
        })()}
        {loadingIntraday ? (
        <div className="flex items-center justify-center bg-gray-50/50 rounded-lg" style={{ minHeight: priceChartH }}>
            <div className="flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
              <span className="text-sm text-gray-500">Loading 1-minute data...</span>
            </div>
          </div>
        ) : intradayData && intradayData.length > 0 ? (
          (() => {
            // Merge session VWAP (full tape) when primary series is Polygon (consolidated or overlay)
            const showTapeVwap =
              intradayPricePublisherId === null ||
              (intradayOverlayVenueOnPolygon && intradayPricePublisherId !== null);
            const vwapByMinute = new Map(
              (tradeAnalytics?.per_minute ?? []).map((m) => [m.time, m.vwap]),
            );
            const chartWithVwap = intradayData.map((d) => ({
              ...d,
              vwap: showTapeVwap ? (vwapByMinute.get(d.time) ?? null) : null,
            }));
            const minuteLargestCollapsed =
              intradayMinuteLargestTrades && intradayMinuteLargestTrades.length > 0
                ? collapseToLargestTradePerEtMinute(intradayMinuteLargestTrades)
                : [];
            const useMinuteLargestDiamonds = minuteLargestCollapsed.length > 0;
            const tapeForMerge = useMinuteLargestDiamonds
              ? minuteLargestCollapsed
              : tradeAnalytics?.block_trades;
            const mergedForBlocks = mergeIntradayChartPointsForBlockTradeMinutes(
              chartWithVwap,
              tapeForMerge,
            );
            const mergedForDisplay = mergedForBlocks.map((d) => ({
              ...d,
              vwap: showTapeVwap ? (vwapByMinute.get(d.time) ?? null) : null,
            }));
            const rthChartRows = intradayRegularSessionOnly(mergedForDisplay);
            const chartForDisplay =
              rthChartRows.length > 0 ? rthChartRows : mergedForDisplay;
            intradayChartDataForCrosshairRef.current = chartForDisplay;

            /** Line / gradient color: session close vs open (stable; never driven by block-trade side). */
            const dayOhlc = (dynamicHistoricalPrices ?? historicalPrices)?.find(
              (d) => d.date === selectedPoint.date,
            );
            let closeGeOpen: boolean | null = null;
            if (dayOhlc) {
              const c = finiteHistoricalPrice(dayOhlc.close, NaN);
              const o = finiteHistoricalPrice(dayOhlc.open, c);
              if (Number.isFinite(o) && Number.isFinite(c)) closeGeOpen = c >= o;
            }
            if (closeGeOpen === null && chartForDisplay.length > 0) {
              const first = chartForDisplay[0];
              const last = chartForDisplay[chartForDisplay.length - 1];
              const o =
                first &&
                typeof first.percentFromOpen === 'number' &&
                Number.isFinite(first.percentFromOpen)
                  ? first.price / (1 + first.percentFromOpen / 100)
                  : Number(first?.price);
              const c = Number(last?.price);
              if (Number.isFinite(o) && Number.isFinite(c)) closeGeOpen = c >= o;
            }
            const priceColor =
              closeGeOpen != null
                ? closeGeOpen
                  ? '#10b981'
                  : '#ef4444'
                : selectedPoint.percentChange > 0
                  ? '#10b981'
                  : '#ef4444';

            const venueVolRow = tradeAnalytics?.venue_volume?.find(
              (r) => r.publisher_id === intradayPricePublisherId,
            );
            const venueSeriesName = venueVolRow?.label
              ? `Venue · ${venueVolRow.label}`
              : showPublisherIds
                ? `Venue (pub ${intradayPricePublisherId ?? '—'})`
                : 'Venue';
              const showVenueOverlayLine =
              intradayOverlayVenueOnPolygon &&
              intradayPricePublisherId != null &&
              chartForDisplay.some(
                (d) =>
                  d.venuePrice != null && Number.isFinite(Number(d.venuePrice)),
              );

            /** Largest prints for markers: largest print / minute (DuckDB or trades.json), thinned by adaptive window. */
            // blockList is the full per-minute list (used for selection matching).
            // displayBlockTrades thins it to one diamond per adaptive window so the chart isn't overwhelmed.
            const blockList = useMinuteLargestDiamonds
              ? minuteLargestCollapsed
              : tradeAnalytics?.block_trades;
            const spacedMinuteLargest =
              useMinuteLargestDiamonds && minuteLargestCollapsed.length > 0
                ? ensureSignificantMoveDiamonds(
                    selectSpacedDiamonds(minuteLargestCollapsed),
                    minuteLargestCollapsed,
                    chartForDisplay,
                  )
                : [];
            const displayBlockTrades =
              showIntradayBlockTrades && spacedMinuteLargest.length > 0
                ? spacedMinuteLargest
                : showIntradayBlockTrades &&
                    !useMinuteLargestDiamonds &&
                    blockList &&
                    blockList.length > 0
                  ? [...blockList]
                      .map((b) => ({
                        ...b,
                        _notional: (Number(b.size) || 0) * (Number(b.price) || 0),
                      }))
                      .sort((a, b) => b._notional - a._notional)
                      .slice(0, 5)
                      .map(({ _notional: _n, ...rest }) => rest)
                  : [];
            const blockRowKey = (b: { time: string; size: number; price: number }) =>
              `${b.time}|${Number(b.size)}|${Number(b.price)}`;
            // selectedTapeRow uses the full blockList so a clicked trade is always findable
            // even if it was thinned out of the spaced display set.
            const selectedTapeRow =
              showIntradayBlockTrades &&
              blockList &&
              blockList.length > 0 &&
              selectedPoint?.blockTradeDetail
                ? findIntradayBlockTradeTapeRow(blockList, selectedPoint.blockTradeDetail)
                : undefined;
            const drilledBlockTradeRow =
              showIntradayBlockTrades && selectedPoint?.blockTradeDetail?.time
                ? {
                    time: selectedPoint.blockTradeDetail.time,
                    size: selectedPoint.blockTradeDetail.size,
                    price: selectedPoint.blockTradeDetail.price,
                    side: selectedPoint.blockTradeDetail.side ?? 'N',
                  }
                : undefined;
            const effectiveSelectedTapeRow = selectedTapeRow ?? drilledBlockTradeRow;
            const popoverTapeRow =
              showIntradayBlockTrades &&
              blockList &&
              blockList.length > 0 &&
              intradayBlockTradePopover?.trade
                ? findIntradayBlockTradeTapeRow(blockList, {
                    time: intradayBlockTradePopover.trade.time,
                    size: intradayBlockTradePopover.trade.size,
                    price: intradayBlockTradePopover.trade.price,
                    dollar_value: intradayBlockTradePopover.trade.dollar_value,
                  })
                : undefined;
            const displayKeys = new Set(displayBlockTrades.map(blockRowKey));
            let intradayBlockMarkers =
              !effectiveSelectedTapeRow || displayKeys.has(blockRowKey(effectiveSelectedTapeRow))
                ? displayBlockTrades
                : [...displayBlockTrades, effectiveSelectedTapeRow];
            const markerKeys = new Set(intradayBlockMarkers.map(blockRowKey));
            if (popoverTapeRow && !markerKeys.has(blockRowKey(popoverTapeRow))) {
              intradayBlockMarkers = [...intradayBlockMarkers, popoverTapeRow];
            }

            // Resolve + deduplicate marker positions here (outside Customized) so the
            // stable IntradayDiamondsCustomized component receives plain data props.
            const bd = selectedPoint.blockTradeDetail;
            const pop = intradayBlockTradePopover?.trade;
            const resolvedMarkersRaw = intradayBlockMarkers.map((bt) => {
              const dollarValue = (Number(bt.size) || 0) * (Number(bt.price) || 0);
              const matchBd =
                bd != null &&
                bd.time === bt.time &&
                Number(bd.size) === Number(bt.size) &&
                Math.abs(Number(bd.price) - Number(bt.price)) <= 0.005;
              const matchPop =
                pop != null &&
                pop.time === bt.time &&
                Number(pop.size) === Number(bt.size) &&
                Math.abs(Number(pop.price) - Number(bt.price)) <= 0.005;
              const pulsate = matchBd || matchPop;
              return {
                time: resolveIntradayChartCategoryTime(bt.time, chartForDisplay),
                price: bt.price,
                size: bt.size,
                side: bt.side,
                dollar_value: dollarValue,
                date: selectedPoint.date,
                fullDate: selectedPoint.fullDate,
                pulsate,
              };
            });
            const resolvedMarkersDeduped = new Map<string, typeof resolvedMarkersRaw[0]>();
            for (const t of resolvedMarkersRaw) {
              const prev = resolvedMarkersDeduped.get(t.time);
              if (!prev || t.pulsate || t.dollar_value > prev.dollar_value) {
                resolvedMarkersDeduped.set(t.time, t);
              }
            }
            const resolvedMarkers = [...resolvedMarkersDeduped.values()].sort(
              (a, b) => a.time.localeCompare(b.time),
            );
            const intradayBlockRadii = blockTradeDiamondRadiusByKey(
              resolvedMarkers.map((m) => ({
                key: m.time,
                notional: blockTradeNotionalUsd(m),
              })),
            );
            const resolvedMarkersSized = resolvedMarkers.map((m) => ({
              ...m,
              radiusPx: intradayBlockRadii.get(m.time),
            }));

            // Cumulative delta series (standalone, aligned by time)
            const deltaData = (tradeAnalytics?.per_minute ?? []).map((m) => ({
              time: m.time,
              delta: m.cumulative_delta,
            }));
            const maxAbsDelta = deltaData.length
              ? Math.max(...deltaData.map((d) => Math.abs(d.delta)), 1)
              : 1;

            return (
              <div className="space-y-1 px-1 sm:px-2">
                <p className="mb-1 text-[11px] text-gray-400">
                  Regular session (9:30 AM–4:00 PM ET).
                </p>
                {/* ── Price + VWAP chart ── */}
                <div
                  ref={intradayPriceChartWrapRef}
                  className="relative min-h-0 w-full cursor-crosshair"
                  style={{ height: priceChartH }}
                  onMouseMove={handleIntradayPriceChartMouseMove}
                  onMouseLeave={handleIntradayPriceChartMouseLeave}
                >
                  <div ref={intradayPriceChartPlotRef} className="relative h-full w-full min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={chartForDisplay}
                      margin={{
                        ...INTRADAY_COMPOSED_CHART_MARGIN,
                        right:
                          layers.peakVolumePrice.enabled && intradayPeakVolumeLevel
                            ? 96
                            : INTRADAY_COMPOSED_CHART_MARGIN.right,
                      }}
                    >
                      <defs>
                        <linearGradient id="intradayPriceGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={priceColor} stopOpacity={0.18} />
                          <stop offset="100%" stopColor={priceColor} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} />
                      {/* Session boundaries: 9:30 / 16:00 — Customized so 16:00 shows when no bar at exactly 16:00 (Recharts ReferenceLine drops missing categories). */}
                      <Customized
                        component={(chartProps: {
                          xAxisMap?: Record<string, { scale: { (v: string): number | undefined; domain?: () => string[]; bandwidth?: () => number } }>;
                          offset?: { top: number; height: number };
                        }) => (
                          <IntradaySessionBoundaryLines
                            xAxisMap={chartProps.xAxisMap}
                            offset={chartProps.offset}
                            showPremarketEnd
                            showAfterHoursStart
                            timeDomainFallback={chartForDisplay.map((d) => d.time)}
                          />
                        )}
                      />
                      <XAxis
                        dataKey="time"
                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        interval="preserveStartEnd"
                        tickMargin={8}
                      />
                      <YAxis
                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => `$${typeof v === 'number' ? v.toFixed(2) : v}`}
                        domain={['auto', 'auto']}
                        width={52}
                        tickMargin={4}
                      />
                      <Customized component={renderIntradayVolumeByPrice} />
                      <Area
                        type="monotone"
                        dataKey="price"
                        name="Polygon"
                        stroke={priceColor}
                        strokeWidth={2}
                        fill="url(#intradayPriceGradient)"
                        animationDuration={800}
                        dot={false}
                        activeDot={false}
                      />
                      {showVenueOverlayLine && (
                        <Line
                          type="monotone"
                          dataKey="venuePrice"
                          name={venueSeriesName}
                          stroke="#9333ea"
                          strokeWidth={1.75}
                          strokeDasharray="6 4"
                          dot={false}
                          activeDot={{ r: 3 }}
                          connectNulls
                          animationDuration={600}
                        />
                      )}
                      {/* VWAP overlay */}
                      {SHOW_VWAP_AND_DELTA && tradeAnalytics && (
                        <Line
                          type="monotone"
                          dataKey="vwap"
                          stroke="#6366f1"
                          strokeWidth={1.5}
                          strokeDasharray="4 3"
                          dot={false}
                          activeDot={false}
                          animationDuration={600}
                          connectNulls
                        />
                      )}
                      {/* Block trade diamonds — stable named component so React preserves
                          instance identity across re-renders (hover state on each diamond). */}
                      <Customized
                        // Recharts types Customized.component too narrowly for forwarded props.
                        component={IntradayDiamondsCustomized as never}
                        markers={resolvedMarkersSized}
                        onDotClick={(bt: IntradayBlockDiamondPayload, cx: number, cy: number) => {
                          const wrap = intradayPriceChartWrapRef.current;
                          if (!wrap) return;
                          setIntradayBlockTradePopover({
                            trade: {
                              date: bt.date,
                              time: bt.time,
                              price: bt.price,
                              size: bt.size,
                              dollar_value: bt.dollar_value,
                              side: bt.side,
                              fullDate: bt.fullDate,
                            },
                            ...clampBlockTradePopoverBesideDiamond(
                              wrap,
                              intradayPriceChartPlotRef.current,
                              cx,
                              cy,
                              0,
                            ),
                          });
                        }}
                      />
                      {layers.peakVolumePrice.enabled && intradayPeakVolumeLevel ? (
                        <Customized
                          component={(chartProps: HistoricalChartMetrics & Record<string, unknown>) => {
                            queueMicrotask(() =>
                              syncIntradayPeakVolumeGuideOverlay(intradayPeakVolumeLevel.price),
                            );
                            return (
                              <PeakVolumePriceGuideSvg
                                price={intradayPeakVolumeLevel.price}
                                chartData={chartForDisplay}
                                categoryKey="time"
                                chartProps={chartProps}
                              />
                            );
                          }}
                        />
                      ) : null}
                      <Customized
                        component={(chartProps: HistoricalChartMetrics & Record<string, unknown>) => {
                          intradayChartMetricsRef.current = chartProps;
                          return (
                            <IntradayChartCrosshairGraphics
                              chartProps={chartProps}
                              chartData={chartForDisplay}
                              plotMouse={intradayPlotMouse}
                            />
                          );
                        }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                  </div>
                  {intradayBlockTradePopover && (
                    <div
                      ref={intradayBlockTradePopoverRef}
                      role="tooltip"
                      aria-label="Block trade: time, price, shares, total"
                      className="absolute z-[35] rounded-2xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
                      style={{
                        left: intradayBlockTradePopover.left,
                        top: intradayBlockTradePopover.top,
                        width: HISTORICAL_BLOCK_TRADE_POPOVER_W,
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIntradayBlockTradePopover(null);
                        }}
                        className="absolute right-1.5 top-1.5 z-10 rounded-md p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        aria-label="Close"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      <BlockTradeTooltipContent
                        trade={intradayBlockTradePopover.trade}
                        sessionVolume={historicalSessionVolumeByDate.get(
                          intradayBlockTradePopover.trade.date,
                        )}
                      />
                    </div>
                  )}
                  {intradayPriceTrackerOverlay && (
                    <div
                      className="pointer-events-none absolute z-20 flex h-[22px] min-w-[4.75rem] items-center justify-center rounded-sm bg-gray-900 px-2 text-[11px] font-medium text-white shadow-sm tabular-nums"
                      style={{
                        left: intradayPriceTrackerOverlay.left,
                        top: intradayPriceTrackerOverlay.top,
                      }}
                    >
                      {intradayPriceTrackerOverlay.label}
                    </div>
                  )}
                  {intradayPeakVolumeGuideOverlay && (
                    <PeakVolumePriceGuidePill
                      label={intradayPeakVolumeGuideOverlay.label}
                      left={intradayPeakVolumeGuideOverlay.left}
                      top={intradayPeakVolumeGuideOverlay.top}
                    />
                  )}
                </div>

                {showVenueOverlayLine && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-2 pt-1 text-[11px] text-gray-500">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="h-0.5 w-7 shrink-0 rounded-full"
                        style={{ backgroundColor: priceColor }}
                      />
                      Polygon (consolidated)
                    </span>
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <svg width={28} height={10} aria-hidden className="shrink-0">
                        <line
                          x1={0}
                          y1={5}
                          x2={28}
                          y2={5}
                          stroke="#9333ea"
                          strokeWidth={2}
                          strokeDasharray="6 4"
                        />
                      </svg>
                      <span className="truncate">{venueSeriesName}</span>
                    </span>
                  </div>
                )}

                {/* VWAP legend pill */}
                {SHOW_VWAP_AND_DELTA && tradeAnalytics && (
                  <div className="flex items-center gap-3 px-2 pb-1">
                    <div className="flex items-center gap-1.5">
                      <svg width="16" height="8">
                        <line x1="0" y1="4" x2="16" y2="4" stroke="#6366f1" strokeWidth="1.5" strokeDasharray="4 3" />
                      </svg>
                      <span className="text-xs text-gray-400">VWAP</span>
                      {tradeAnalytics.summary.session_vwap != null && (
                        <span className="text-xs font-medium text-gray-600">
                          ${tradeAnalytics.summary.session_vwap.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Cumulative Delta chart ── */}
                {SHOW_VWAP_AND_DELTA && tradeAnalytics && deltaData.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide px-2 mb-1 mt-3">
                      Cumulative Delta
                    </p>
                    <div className="h-24">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={deltaData} margin={{ top: 2, right: 5, left: 5, bottom: 2 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.2} />
                          <XAxis
                            dataKey="time"
                            tick={{ fill: '#9ca3af', fontSize: 10 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                            interval="preserveStartEnd"
                          />
                          <YAxis
                            tick={{ fill: '#9ca3af', fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) =>
                              Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v)
                            }
                            domain={[-maxAbsDelta * 1.1, maxAbsDelta * 1.1]}
                            width={40}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              padding: '6px 10px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            }}
                            labelStyle={{ color: '#6b7280', fontSize: '11px' }}
                            formatter={(value: number) => [
                              value >= 0 ? `+${value.toLocaleString()}` : value.toLocaleString(),
                              'Cum. Delta',
                            ]}
                          />
                          <ReferenceLine y={0} stroke="#d1d5db" strokeWidth={1} />
                          <Bar
                            dataKey="delta"
                            radius={[1, 1, 0, 0]}
                            animationDuration={600}
                            isAnimationActive
                          >
                            {deltaData.map((entry, index) => (
                              <Cell
                                key={index}
                                fill={entry.delta >= 0 ? '#10b981' : '#ef4444'}
                              />
                            ))}
                          </Bar>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* ── Buy / Sell / Delta summary stats (block trades shown above chart with Volume/Trades) ── */}
                {tradeAnalytics && SHOW_TRADE_ANALYTICS_STATS && (
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-2 pt-3 pb-1 border-t border-gray-100 mt-2">
                    {/* Buy pressure */}
                    {tradeAnalytics.summary.buy_pct != null && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-gray-400">Buy pressure</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-semibold text-emerald-600">
                            {tradeAnalytics.summary.buy_pct.toFixed(1)}%
                          </span>
                          <span className="text-xs text-gray-300">/</span>
                          <span className="text-xs font-semibold text-red-500">
                            {tradeAnalytics.summary.sell_pct?.toFixed(1)}%
                          </span>
                          <span className="text-xs text-gray-400 ml-0.5">sell</span>
                        </div>
                      </div>
                    )}

                    {/* Net delta */}
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-gray-400">Net delta</span>
                      <span
                        className={`text-xs font-semibold ${
                          tradeAnalytics.summary.net_delta >= 0 ? 'text-emerald-600' : 'text-red-500'
                        }`}
                      >
                        {tradeAnalytics.summary.net_delta >= 0 ? '+' : ''}
                        {tradeAnalytics.summary.net_delta.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          <p className="text-sm text-gray-400 italic py-8">
            No 1-minute intraday data available for this date
          </p>
        )}
        {showPublisherVolumeDashboard &&
        tradeAnalytics?.venue_volume &&
        tradeAnalytics.venue_volume.length > 0 ? (
          <IntradayPublisherVolumeDashboard rows={tradeAnalytics.venue_volume} className="mt-4" />
        ) : null}
        {SHOW_INTRADAY_BLOCK_TRADE_DETAIL && selectedIntradayPoint?.blockDetail && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    selectedIntradayPoint.blockDetail.side === 'B'
                      ? 'bg-emerald-50 text-emerald-700'
                      : selectedIntradayPoint.blockDetail.side === 'A'
                      ? 'bg-red-50 text-red-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {selectedIntradayPoint.blockDetail.side === 'B'
                    ? 'Buy block'
                    : selectedIntradayPoint.blockDetail.side === 'A'
                    ? 'Sell block'
                    : 'Block trade'}
                </span>
                <p className="text-xs text-gray-500">
                  {selectedIntradayPoint.time}
                </p>
              </div>
              <button
                onClick={() => setSelectedIntradayPoint(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <span>
                <span className="text-gray-400 mr-1">Size</span>
                <span className="font-medium">{selectedIntradayPoint.blockDetail.size.toLocaleString()} shares</span>
              </span>
              <span>
                <span className="text-gray-400 mr-1">Price</span>
                <span className="font-medium">${selectedIntradayPoint.blockDetail.price.toFixed(2)}</span>
              </span>
              <span>
                <span className="text-gray-400 mr-1">Notional</span>
                <span className="font-medium">
                  ${(selectedIntradayPoint.blockDetail.size * selectedIntradayPoint.blockDetail.price).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </span>
            </div>
          </div>
        )}
        <TradeSessionProfile
          ticker={stockData.ticker}
          date={selectedPoint.date}
          className="mt-4"
        />
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="bg-white"
      >
        <div className="max-w-6xl mx-auto px-8 py-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-gray-700 transition-colors mb-12"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Stock Info */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="relative">
                <span className="px-4 py-1.5 bg-gray-50 rounded-xl text-gray-500">
                  {stockData.ticker}
                </span>
                {betaNoticeOpen && (
                  <BetaStockRestrictionNotice
                    demoTicker={BETA_DEMO_TICKER}
                    onDismiss={() => setBetaNoticeOpen(false)}
                  />
                )}
              </div>
              <span className={isPositiveChange ? 'text-emerald-600' : 'text-rose-600'}>
                {stockData.changePercent}
              </span>
            </div>
            <h1 className="mb-1 text-5xl tracking-tight text-gray-700">
              {stockData.company}
            </h1>
            <p className="text-gray-400 text-2xl">{stockData.currentPrice}</p>
            {sectorLabel ? (
              <p className="mt-2 text-gray-500">{sectorLabel}</p>
            ) : null}
          </div>

          {useStorylineTabs && headerChartPrices.length > 0 ? (
            <div className="mt-6 space-y-4">
              <StockHeaderMiniChart prices={headerChartPrices} className="mt-0" />
              {todayMoveSummaryCard}
              {todayAnalogPanel}
            </div>
          ) : null}
        </div>
      </motion.header>

      {/* Main Content */}
      <main className={`max-w-6xl mx-auto px-8 ${useStorylineTabs ? 'py-8' : 'py-16'}`}>
        <div className="space-y-6">
          {useStorylineTabs ? (
            <StockStorylineTabsPanel
              ticker={stockData.ticker}
              companyName={stockData.company}
              storylines={tickerStorylines}
            />
          ) : (
            <StockAnalogAnalysisPanel
              ticker={stockData.ticker}
              companyName={stockData.company}
              referenceDate={todayIsoDate}
              catalystSummary={todayMoveStory?.executiveSummary}
              omitHeaderSummary={Boolean(todayMoveStory?.executiveSummary)}
              dayReturnPct={todayMovePercentChange}
              close={todayMoveClose}
            />
          )}

          {stockData.ticker === BETA_DEMO_TICKER && (
            <StockAnalogueTeaserCard
              onClick={() => navigate(`/stock/${stockData.ticker}/analogue-analysis`)}
            />
          )}

          {!useStorylineTabs && (STOCK_CHART_UI_ENABLED || PRICE_INTELLIGENCE_FEED_ENABLED) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative overflow-visible bg-white rounded-3xl p-10"
          >
            <PriceIntelligenceFeed
              ticker={stockData.ticker}
              date={priceIntelligenceContext.date}
              ohlc={priceIntelligenceContext.ohlc}
              recentBars={priceIntelligenceContext.recentBars}
              yearlyPriceVolume={yearlyPriceVolume}
            />
            {STOCK_CHART_UI_ENABLED ? (
            <>
            <div className="mb-8 flex min-w-0 max-w-full flex-col gap-3">
                {/* Layers — same strip in historical and intraday */}
                <div className="relative shrink-0">
                  <motion.div
                    className="flex items-start gap-2 bg-white rounded-xl px-3 py-2 border border-gray-200 shadow-sm"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div className="flex items-center gap-1.5 mr-1 pt-1.5 shrink-0">
                      <Layers className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500">Layers</span>
                    </motion.div>
                    <div className="flex min-w-0 flex-1 flex-nowrap items-start gap-2 overflow-x-auto">
                      <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-0.5 gap-y-1">
                        <motion.button
                          type="button"
                          onClick={() => toggleLayer('notableEvents')}
                          className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            layers.notableEvents.enabled
                              ? 'bg-gray-900 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <motion.div
                            className={`w-2 h-2 shrink-0 rounded-full ${layers.notableEvents.enabled ? 'bg-white' : 'bg-gray-900'}`}
                            animate={{ scale: layers.notableEvents.enabled ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          Notable Events
                        </motion.button>
                        <div className="col-span-2" aria-hidden="true" />
                        <motion.button
                          type="button"
                          onClick={() => toggleLayer('institutionalHolders')}
                          disabled={institutionalHoldersLoading}
                          className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            layers.institutionalHolders.enabled
                              ? 'bg-emerald-700 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          } ${institutionalHoldersLoading ? 'opacity-60 cursor-wait' : ''}`}
                          whileHover={{ scale: institutionalHoldersLoading ? 1 : 1.02 }}
                          whileTap={{ scale: institutionalHoldersLoading ? 1 : 0.98 }}
                        >
                          <motion.div
                            className={`w-2 h-2 shrink-0 rounded-full ${layers.institutionalHolders.enabled ? 'bg-white' : 'bg-emerald-600'}`}
                            animate={{ scale: layers.institutionalHolders.enabled ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          Institutional Holders
                        </motion.button>
                        <InstitutionalHoldersLayerSearch
                          layerActive={layers.institutionalHolders.enabled}
                          payload={institutionalHoldersPayload}
                          displayMode={institutionalHoldersChartDisplayMode}
                          chartQuarterKeys={institutionalChartQuarterKeys}
                          searchQuery={institutionalHoldersSearchQuery}
                          onSearchQueryChange={setInstitutionalHoldersSearchQuery}
                          onSelectResult={handleInstitutionalSearchSelect}
                        />
                        <InstitutionalHoldersLayerSettings
                          layerActive={layers.institutionalHolders.enabled}
                          displayMode={institutionalHoldersChartDisplayMode}
                          onDisplayModeChange={(mode) => {
                            setInstitutionalHoldersChartDisplayMode(mode);
                            persistInstitutionalHoldersChartDisplayMode(mode);
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <motion.button
                          type="button"
                          onClick={() => toggleLayer('largestTrades')}
                          className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            layers.largestTrades.enabled
                              ? 'bg-purple-600 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <motion.div
                            className={`w-2 h-2 shrink-0 rounded-full ${layers.largestTrades.enabled ? 'bg-white' : 'bg-purple-600'}`}
                            animate={{ scale: layers.largestTrades.enabled ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          Notable Trades
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => toggleLayer('optionsPositioning')}
                          disabled={optionsChartLoading}
                          className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            layers.optionsPositioning.enabled
                              ? 'bg-indigo-600 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          } ${optionsChartLoading ? 'opacity-60 cursor-wait' : ''}`}
                          whileHover={{ scale: optionsChartLoading ? 1 : 1.02 }}
                          whileTap={{ scale: optionsChartLoading ? 1 : 0.98 }}
                        >
                          <motion.div
                            className={`w-2 h-2 shrink-0 rounded-full ${layers.optionsPositioning.enabled ? 'bg-white' : 'bg-indigo-500'}`}
                            animate={{ scale: layers.optionsPositioning.enabled ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          Options Positioning
                        </motion.button>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <motion.button
                          type="button"
                          onClick={() => toggleLayer('imbalance')}
                          className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            layers.imbalance.enabled
                              ? 'bg-teal-700 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <motion.div
                            className={`w-2 h-2 shrink-0 rounded-full ${layers.imbalance.enabled ? 'bg-white' : 'bg-teal-500'}`}
                            animate={{ scale: layers.imbalance.enabled ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          Imbalance
                        </motion.button>
                        <motion.button
                          type="button"
                          title={optionImbalanceLoadError ?? undefined}
                          onClick={() => toggleLayer('optionImbalance')}
                          disabled={optionImbalanceLoading}
                          className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            layers.optionImbalance.enabled
                              ? 'bg-rose-600 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          } ${optionImbalanceLoading ? 'opacity-60 cursor-wait' : ''}`}
                          whileHover={{ scale: optionImbalanceLoading ? 1 : 1.02 }}
                          whileTap={{ scale: optionImbalanceLoading ? 1 : 0.98 }}
                        >
                          <motion.div
                            className={`w-2 h-2 shrink-0 rounded-full ${layers.optionImbalance.enabled ? 'bg-white' : 'bg-rose-500'}`}
                            animate={{ scale: layers.optionImbalance.enabled ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          Option Imbalance
                        </motion.button>
                        {SHOW_LAYER_VOLUME && (
                          <motion.button
                            type="button"
                            onClick={() => toggleLayer('volume')}
                            className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                              layers.volume.enabled
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <motion.div
                              className={`w-2 h-2 shrink-0 rounded-full ${layers.volume.enabled ? 'bg-white' : 'bg-blue-500'}`}
                              animate={{ scale: layers.volume.enabled ? [1, 1.2, 1] : 1 }}
                              transition={{ duration: 0.3 }}
                            />
                            Volume
                          </motion.button>
                        )}
                        {SHOW_LAYER_VOLUME_BY_PRICE && (
                          <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-0.5">
                            <motion.button
                              type="button"
                              onClick={() => toggleLayer('volumeByPrice')}
                              title={
                                volumeByPriceProfile
                                  ? volumeByPriceRangeLabel(
                                      volumeByPriceProfile.symbol,
                                      volumeByPriceProfile.startDate,
                                      volumeByPriceProfile.endDate,
                                    )
                                  : layers.volumeByPrice.enabled && yearlyPriceVolumeLoading
                                    ? 'Loading yearly volume profile…'
                                    : layers.volumeByPrice.enabled && volumeByPriceVisibleRange
                                      ? `No volume profile for ${volumeByPriceVisibleRange.start} – ${volumeByPriceVisibleRange.end}`
                                      : 'Yearly volume profile on the right edge of the chart'
                              }
                              className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                                layers.volumeByPrice.enabled
                                  ? 'bg-sky-600 text-white shadow-sm'
                                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                              }`}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <motion.div
                                className={`w-2 h-2 shrink-0 rounded-full ${layers.volumeByPrice.enabled ? 'bg-white' : 'bg-sky-500'}`}
                                animate={{
                                  scale: layers.volumeByPrice.enabled ? [1, 1.2, 1] : 1,
                                }}
                                transition={{ duration: 0.3 }}
                              />
                              Volume by Price
                            </motion.button>
                            <VolumeByPriceLayerSettings
                              layerActive={layers.volumeByPrice.enabled}
                              displayMode={volumeByPriceDisplayMode}
                              hasSideAttribution={volumeByPriceProfile?.hasSideAttribution ?? false}
                              hasDateAttribution={volumeByPriceProfile?.hasDateAttribution ?? false}
                              dateLegend={volumeByPriceProfile?.dateOrder}
                              onDisplayModeChange={setVolumeByPriceDisplayMode}
                            />
                          </div>
                        )}
                      </div>
                      <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-0.5 gap-y-1">
                        <motion.button
                          type="button"
                          onClick={() => toggleLayer('insiderTrading')}
                          disabled={insiderTradingLoading}
                          className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            layers.insiderTrading.enabled
                              ? 'bg-amber-600 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          } ${insiderTradingLoading ? 'opacity-60 cursor-wait' : ''}`}
                          whileHover={{ scale: insiderTradingLoading ? 1 : 1.02 }}
                          whileTap={{ scale: insiderTradingLoading ? 1 : 0.98 }}
                        >
                          <motion.div
                            className={`w-2 h-2 shrink-0 rounded-full ${layers.insiderTrading.enabled ? 'bg-white' : 'bg-amber-500'}`}
                            animate={{ scale: layers.insiderTrading.enabled ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          Insider Trading
                        </motion.button>
                        <InsiderTradingLayerSearch
                          layerActive={layers.insiderTrading.enabled}
                          payload={fmpInsiderPayload}
                          enabledCodes={insiderTradingEnabledTypeCodes}
                          chartDates={insiderChartDates}
                          searchQuery={insiderTradingSearchQuery}
                          onSearchQueryChange={setInsiderTradingSearchQuery}
                          onSelectResult={handleInsiderSearchSelect}
                        />
                        <InsiderTradingLayerSettings
                          layerActive={layers.insiderTrading.enabled}
                          enabledCodes={insiderTradingEnabledTypeCodes}
                          typeCounts={insiderTradeTypeCounts}
                          onEnabledCodesChange={(codes) => {
                            setInsiderTradingEnabledTypeCodes(codes);
                            persistInsiderTradingEnabledTypeCodes(codes);
                          }}
                        />
                        <motion.button
                          type="button"
                          title={notableOptionTradesLoadError ?? undefined}
                          onClick={() => toggleLayer('notableOptionTrades')}
                          disabled={notableOptionTradesLoading}
                          className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            layers.notableOptionTrades.enabled
                              ? 'bg-fuchsia-600 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          } ${notableOptionTradesLoading ? 'opacity-60 cursor-wait' : ''}`}
                          whileHover={{ scale: notableOptionTradesLoading ? 1 : 1.02 }}
                          whileTap={{ scale: notableOptionTradesLoading ? 1 : 0.98 }}
                        >
                          <motion.div
                            className={`w-2 h-2 shrink-0 rounded-full ${layers.notableOptionTrades.enabled ? 'bg-white' : 'bg-fuchsia-500'}`}
                            animate={{ scale: layers.notableOptionTrades.enabled ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          Notable Option Trades
                        </motion.button>
                        <NotableOptionTradesLayerSettings
                          layerActive={layers.notableOptionTrades.enabled}
                          minPremiumUsd={notableOptionMinPremiumUsd}
                          onMinPremiumChange={(usd) => {
                            setNotableOptionMinPremiumUsd(usd);
                            persistNotableOptionMinPremiumUsd(usd);
                          }}
                        />
                        <div aria-hidden="true" />
                      </div>
                      <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-0.5 gap-y-1">
                        <motion.button
                          type="button"
                          onClick={() => toggleLayer('senateTrading')}
                          disabled={congressTradingLoading}
                          className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            layers.senateTrading.enabled
                              ? 'bg-sky-600 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          } ${congressTradingLoading ? 'opacity-60 cursor-wait' : ''}`}
                          whileHover={{ scale: congressTradingLoading ? 1 : 1.02 }}
                          whileTap={{ scale: congressTradingLoading ? 1 : 0.98 }}
                        >
                          <motion.div
                            className={`w-2 h-2 shrink-0 rounded-full ${layers.senateTrading.enabled ? 'bg-white' : 'bg-sky-500'}`}
                            animate={{ scale: layers.senateTrading.enabled ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          />
                          Congress Trading
                        </motion.button>
                        <CongressTradingLayerSearch
                          layerActive={layers.senateTrading.enabled}
                          payload={fmpCongressPayload}
                          enabledCategories={congressTradingEnabledCategories}
                          enabledChambers={congressTradingEnabledChambers}
                          chartDates={congressChartDates}
                          searchQuery={congressTradingSearchQuery}
                          onSearchQueryChange={setCongressTradingSearchQuery}
                          onSelectResult={handleCongressSearchSelect}
                        />
                        <CongressTradingLayerSettings
                          layerActive={layers.senateTrading.enabled}
                          enabledCategories={congressTradingEnabledCategories}
                          enabledChambers={congressTradingEnabledChambers}
                          categoryCounts={congressTradeCategoryCounts}
                          chamberCounts={congressTradeChamberCounts}
                          onEnabledCategoriesChange={(categories) => {
                            setCongressTradingEnabledCategories(categories);
                            persistCongressTradingEnabledCategories(categories);
                          }}
                          onEnabledChambersChange={(chambers) => {
                            setCongressTradingEnabledChambers(chambers);
                            persistCongressTradingEnabledChambers(chambers);
                          }}
                        />
                        <div className="col-span-3 h-[30px]" aria-hidden="true" />
                      </div>
                      {(SHOW_LAYER_PEAK_VOLUME || SHOW_LAYER_PREDICTION) && (
                        <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-0.5 gap-y-1">
                          {SHOW_LAYER_PEAK_VOLUME && (
                            <motion.button
                              type="button"
                              onClick={() => toggleLayer('peakVolumePrice')}
                              className={`col-span-3 flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                                layers.peakVolumePrice.enabled
                                  ? 'bg-rose-600 text-white shadow-sm'
                                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                              }`}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <motion.div
                                className={`w-2 h-2 shrink-0 rounded-full ${layers.peakVolumePrice.enabled ? 'bg-white' : 'bg-rose-500'}`}
                                animate={{ scale: layers.peakVolumePrice.enabled ? [1, 1.2, 1] : 1 }}
                                transition={{ duration: 0.3 }}
                              />
                              Peak Volume
                            </motion.button>
                          )}
                          {SHOW_LAYER_PREDICTION && (
                            <motion.button
                              type="button"
                              data-prediction-dismiss-skip="true"
                              onClick={() => toggleLayer('prediction')}
                              disabled={fedRateLoading}
                              className={`col-span-3 flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                                layers.prediction.enabled
                                  ? 'bg-violet-600 text-white shadow-sm'
                                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                              } ${fedRateLoading ? 'opacity-60 cursor-wait' : ''}`}
                              whileHover={{ scale: fedRateLoading ? 1 : 1.02 }}
                              whileTap={{ scale: fedRateLoading ? 1 : 0.98 }}
                            >
                              <motion.div
                                className={`w-2 h-2 shrink-0 rounded-full ${layers.prediction.enabled ? 'bg-white' : 'bg-violet-500'}`}
                                animate={{ scale: layers.prediction.enabled ? [1, 1.2, 1] : 1 }}
                                transition={{ duration: 0.3 }}
                              />
                              Prediction
                            </motion.button>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                  {layers.insiderTrading.enabled && insiderTradingLoadError ? (
                    <p className="absolute left-0 top-full z-10 mt-1 max-w-[min(100%,20rem)] text-[10px] text-amber-700">
                      {insiderTradingLoadError}
                    </p>
                  ) : null}
                  {layers.senateTrading.enabled && congressTradingLoadError ? (
                    <p className="absolute left-0 top-full z-10 mt-1 max-w-[min(100%,20rem)] text-[10px] text-sky-700">
                      {congressTradingLoadError}
                    </p>
                  ) : null}
                  {layers.prediction.enabled && fedRateError ? (
                    <p className="absolute left-0 top-full z-10 mt-1 max-w-[min(100%,20rem)] text-[10px] text-violet-700">
                      {fedRateError}
                    </p>
                  ) : null}
                  {layers.optionsPositioning.enabled &&
                  !optionsChartLoading &&
                  !optionsChartOverlaySummary ? (
                    <p className="absolute left-0 top-full z-10 mt-1 max-w-[min(100%,20rem)] text-[10px] text-indigo-700">
                      No options data for this ticker. Run fetch_options_positioning.py first.
                    </p>
                  ) : null}
                  {layers.institutionalHolders.enabled && institutionalHoldersLoadError ? (
                    <p className="absolute left-0 top-full z-10 mt-1 max-w-[min(100%,20rem)] text-[10px] text-emerald-800">
                      {institutionalHoldersLoadError}
                    </p>
                  ) : null}
                  {layers.institutionalHolders.enabled &&
                  !institutionalHoldersLoading &&
                  !institutionalHoldersLoadError &&
                  institutionalFilingPeriodsForChart.length === 0 &&
                  institutionalHoldersPayload ? (
                    <p className="absolute left-0 top-full z-10 mt-1 max-w-[min(100%,20rem)] text-[10px] text-emerald-800">
                      No 13F filings in this chart range.
                    </p>
                  ) : null}
                  {layers.institutionalHolders.enabled &&
                  institutionalHoldersDenseHint &&
                  !historicalZoomRange &&
                  !institutionalHoldersLoading &&
                  !institutionalHoldersLoadError ? (
                    <p className="absolute left-0 top-full z-10 mt-1 max-w-[min(100%,22rem)] text-[10px] text-emerald-800">
                      Quarters are condensed — click a quarter for the full list, or drag the chart to
                      zoom in for holder cards.
                    </p>
                  ) : null}
                  {layers.institutionalHolders.enabled &&
                  institutionalHoldersSearchActive &&
                  !filingPeriodsHaveSearchMatch(institutionalFilingPeriodsFiltered) &&
                  !institutionalHoldersLoading &&
                  !institutionalHoldersLoadError &&
                  institutionalFilingPeriodsForChart.length > 0 ? (
                    <p className="absolute left-0 top-full z-10 mt-1 max-w-[min(100%,22rem)] text-[10px] text-emerald-800">
                      No matches on chart for this range — check search results for other quarters.
                    </p>
                  ) : null}
                </div>
                <div className="flex min-w-0 flex-wrap items-center justify-start gap-x-3 gap-y-2">
                {!HISTORICAL_CHART_DEMO_LOCK_1Y ? (
                  <div className="flex shrink-0 items-center gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setTimeframe('7D')}
                      disabled={loadingHistoricalPrices}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        timeframe === '7D'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      } ${loadingHistoricalPrices ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      7D
                    </button>
                    <button
                      onClick={() => setTimeframe('30D')}
                      disabled={loadingHistoricalPrices}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        timeframe === '30D'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      } ${loadingHistoricalPrices ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      30D
                    </button>
                    <button
                      onClick={() => setTimeframe('12M')}
                      disabled={loadingHistoricalPrices}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        timeframe === '12M'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      } ${loadingHistoricalPrices ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      1Y
                    </button>
                    <button
                      onClick={() => setTimeframe('3Y')}
                      disabled={loadingHistoricalPrices}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        timeframe === '3Y'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      } ${loadingHistoricalPrices ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      3Y
                    </button>
                    <button
                      onClick={() => setTimeframe('5Y')}
                      disabled={loadingHistoricalPrices}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        timeframe === '5Y'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      } ${loadingHistoricalPrices ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      5Y
                    </button>
                  </div>
                ) : null}
                <div className="flex shrink-0 items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setHistoricalChartStyle('line')}
                    disabled={loadingHistoricalPrices}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      historicalChartStyle === 'line'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    } ${loadingHistoricalPrices ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Line
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistoricalChartStyle('candle')}
                    disabled={loadingHistoricalPrices}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      historicalChartStyle === 'candle'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    } ${loadingHistoricalPrices ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Candles
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setHistoricalZoomRange(null)}
                  disabled={!historicalZoomRange}
                  style={{ visibility: historicalZoomRange ? 'visible' : 'hidden' }}
                  aria-hidden={!historicalZoomRange}
                  tabIndex={historicalZoomRange ? 0 : -1}
                  className="shrink-0 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 px-3 py-1.5 rounded-md transition-colors disabled:pointer-events-none"
                >
                  Reset zoom
                </button>
                {loadingHistoricalPrices && (
                  <span className="shrink-0 text-gray-400 text-xs">Loading...</span>
                )}
                </div>
            </div>

            {/* Caption strip above the plot + chart SVG (OHLC clears Y-axis / grid). */}
            <div
              ref={historicalChartWrapRef}
              className="relative flex flex-col overflow-visible"
              style={{
                minHeight:
                  HISTORICAL_CHART_VIEW_HEIGHT_PX + HISTORICAL_CHART_TOP_CAPTION_PX,
              }}
              onMouseMove={handleHistoricalChartMouseMove}
              onMouseLeave={handleHistoricalChartMouseLeave}
            >
              <div
                className="pointer-events-none flex shrink-0 items-center pr-2 text-[13px] leading-none tabular-nums tracking-tight text-gray-500"
                style={{
                  height: HISTORICAL_CHART_TOP_CAPTION_PX,
                  paddingLeft: historicalOhlcAlignPadPx,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  transform: `translateY(${HISTORICAL_OHLC_CAPTION_TRANSLATE_Y_PX}px)`,
                }}
              >
                {historicalOhlcTracker ? (
                  <span
                    className="inline-flex max-w-full flex-wrap items-baseline gap-y-0.5"
                    style={{ columnGap: HISTORICAL_OHLC_SEGMENT_GAP_PX }}
                  >
                    {(
                      [
                        ['O', 'o'],
                        ['L', 'l'],
                        ['H', 'h'],
                        ['C', 'c'],
                      ] as const
                    ).map(([label, key]) => (
                      <span key={label} className="inline-flex items-baseline gap-px">
                        <span className="text-gray-500">{label}</span>
                        <span>{historicalOhlcTracker[key].toFixed(2)}</span>
                      </span>
                    ))}
                    <span
                      className={`font-medium ${
                        historicalOhlcTracker.change > 0
                          ? 'text-emerald-600'
                          : historicalOhlcTracker.change < 0
                            ? 'text-rose-600'
                            : 'text-gray-600'
                      }`}
                    >
                      {historicalOhlcTracker.change > 0 ? '+' : ''}
                      {historicalOhlcTracker.change.toFixed(2)} (
                      {historicalOhlcTracker.changePct > 0 ? '+' : ''}
                      {historicalOhlcTracker.changePct.toFixed(2)}%)
                    </span>
                  </span>
                ) : null}
                {layers.volumeByPrice.enabled && volumeByPriceVisibleRange ? (
                  <span
                    className={`ml-4 text-[11px] tabular-nums ${
                      volumeByPriceProfile ? 'text-sky-700' : 'text-gray-400'
                    }`}
                  >
                    {volumeByPriceProfile
                      ? volumeByPriceRangeLabel(
                          volumeByPriceProfile.symbol,
                          volumeByPriceProfile.startDate,
                          volumeByPriceProfile.endDate,
                        )
                      : yearlyPriceVolumeLoading
                        ? 'Loading volume profile…'
                        : `No volume profile for ${volumeByPriceVisibleRange.start} – ${volumeByPriceVisibleRange.end}`}
                  </span>
                ) : null}
              </div>
              <div
                ref={historicalChartPlotRef}
                className={`relative min-h-0 flex-1 select-none cursor-crosshair ${
                  layers.peakVolumePrice.enabled || layers.volumeByPrice.enabled
                    ? 'peak-volume-chart-layer overflow-x-visible overflow-y-hidden'
                    : 'overflow-hidden'
                }`}
                onMouseDown={handleHistoricalBrushMouseDown}
                onClickCapture={handleHistoricalPlotDateClickCapture}
              >
              {loadingHistoricalPrices && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                    <span className="text-sm text-gray-600">Loading historical data...</span>
                  </div>
                </div>
              )}
              <div className="relative min-w-full w-full">
              <ResponsiveContainer width="100%" height={HISTORICAL_CHART_VIEW_HEIGHT_PX}>
                <ComposedChart
                  data={historicalChartVisibleDataWithProb}
                  margin={{
                    top: historicalChartPlotTopMargin,
                    right:
                      layers.peakVolumePrice.enabled
                        ? 108
                        : layers.volumeByPrice.enabled
                          ? VOLUME_BY_PRICE_CHART_RIGHT_MARGIN_PX + 44
                          : selectedEventGroup.length > 0 && Object.keys(fedProbHistories).length > 0
                            ? 52
                            : 5,
                    left: historicalChartLeftMargin,
                    bottom:
                      HISTORICAL_CHART_BOTTOM_AXIS_PX +
                      (historicalChartStyle === 'candle' ? 12 : 0),
                  }}
                >
                  {/* Subtle grid — syncWithTicks avoids an extra line at domain top/bottom with no Y tick */}
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f3f4f6"
                    vertical={false}
                    syncWithTicks
                  />

                  {/* X Axis - minimal styling */}
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    dy={8}
                    ticks={historicalXTicks}
                    tickFormatter={formatHistoricalXTickLabel}
                  />

                  {/* Y Axis - minimal styling */}
                  <YAxis
                    yAxisId="price"
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    ticks={historicalYAxisTicks}
                    tickFormatter={formatHistoricalYAxisTick}
                    dx={-8}
                    padding={
                      historicalChartStyle === 'candle'
                        ? { top: 6, bottom: 0 }
                        : { top: 0, bottom: 0 }
                    }
                    domain={yAxisDomain}
                    allowDataOverflow={false}
                  />
                  {/* Right Y axis: prediction market probability (0–100%) */}
                  {selectedEventGroup.length > 0 && Object.keys(fedProbHistories).length > 0 && (
                    <YAxis
                      yAxisId="prob"
                      orientation="right"
                      domain={[0, 100]}
                      ticks={[0, 25, 50, 75, 100]}
                      tickFormatter={(v: number) => `${v}%`}
                      tick={{ fill: '#7c3aed', fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                      dx={6}
                      width={40}
                    />
                  )}
                  <Customized component={renderHistoricalVolumeOverlay} />
                  <Customized component={renderHistoricalPeakVolumeGuide} />
                  <Customized component={renderHistoricalNotableEventLabels} />
                  <Customized component={renderInstitutionalHoldersChartOverlay} />
                  {/* Probability overlay lines — one per selected outcome leg */}
                  {selectedEventGroup.length > 0 &&
                    selectedEventGroup.map((m) => {
                      if (!fedProbHistories[m.id]) return null;
                      const eventNorm = (m.event_title ?? '').trim().toLowerCase();
                      const siblings = fedPredictionMarketsOrdered.filter(
                        (x) => (x.event_title ?? '').trim().toLowerCase() === eventNorm,
                      );
                      const stableIdx = siblings.findIndex((x) => x.id === m.id);
                      const colorIdx = stableIdx >= 0 ? stableIdx : 0;
                      return (
                        <Line
                          key={m.id}
                          yAxisId="prob"
                          type="monotone"
                          dataKey={`fedProb_${m.id}`}
                          name={fedOutcomeShortLabel(m.question)}
                          stroke={fedOutcomeColor(colorIdx)}
                          strokeWidth={1.5}
                          strokeDasharray="5 3"
                          dot={false}
                          activeDot={false}
                          connectNulls
                        />
                      );
                    })}
                  {historicalChartStyle === 'line' ? (
                    <Line
                      yAxisId="price"
                      type="monotone"
                      dataKey="price"
                      stroke="#374151"
                      strokeWidth={2}
                      dot={(props: any) => (
                        <CustomDot
                          {...props}
                          onDotClick={handleDotClick}
                          timeframe={timeframe}
                          selectedPoint={selectedPoint}
                          notableEventLabels={notableEventLabels}
                          notableHoverStore={notableEventHoverStore}
                        />
                      )}
                      activeDot={{ r: 0, fill: 'transparent', strokeWidth: 0 }}
                    />
                  ) : (
                    <>
                      {/* Invisible bar keeps band/domain merge; candles render in Customized using axis scales. */}
                      <Bar
                        yAxisId="price"
                        layout="horizontal"
                        dataKey="close"
                        shape={() => <g />}
                        isAnimationActive={false}
                      />
                      <Customized component={renderHistoricalCandlesOverlay} />
                      <Customized component={renderHistoricalNotableEventLabels} />
                    </>
                  )}
                  <Customized component={renderHistoricalVolumeByPrice} />
                  {blockTradesForVisibleChart.map((bt, i) => {
                    const bd = selectedPoint?.blockTradeDetail;
                    const pop = historicalBlockTradePopover?.trade;
                    const matchBd =
                      bd != null &&
                      selectedPoint?.date === bt.date &&
                      bd.time === bt.time &&
                      Number(bd.size) === Number(bt.size) &&
                      Math.abs(Number(bd.price) - Number(bt.price)) <= 0.005;
                    const matchPop =
                      pop != null &&
                      pop.date === bt.date &&
                      pop.time === bt.time &&
                      Number(pop.size) === Number(bt.size) &&
                      Math.abs(Number(pop.price) - Number(bt.price)) <= 0.005;
                    const pulsateHistoricalBlock = matchBd || matchPop;
                    return (
                    <ReferenceDot
                      key={`${bt.date}-${bt.time}-${i}`}
                      yAxisId="price"
                      x={bt.date}
                      y={bt.price}
                      r={0}
                      fill="transparent"
                      stroke="none"
                      shape={(props: any) => {
                        const cx = props.cx ?? 0;
                        const cy = props.cy ?? 0;
                        return (
                        <HistoricalBlockTradeDot
                          cx={cx}
                          cy={cy}
                          payload={bt}
                          pulsate={pulsateHistoricalBlock}
                          sizeScale={0.82}
                          radiusPx={blockTradeDiamondRadiusByDate.get(bt.date)}
                          onClick={() => {
                            const wrap = historicalChartWrapRef.current;
                            if (!wrap) return;
                            setInsiderTradingPopover(null);
                            setCongressTradingPopover(null);
                            setNotableOptionTradePopover(null);
                            setTapeImbalancePopover(null);
                            setOptionImbalancePopover(null);
                            const key = historicalBlockTradeInteractionKey(bt);
                            if (historicalBlockTradeAwaitingDrillKeyRef.current === key) {
                              historicalBlockTradeAwaitingDrillKeyRef.current = null;
                              setHistoricalBlockTradePopover(null);
                              const row = historicalChartData.find((d) => d.date === bt.date);
                              const blockTradeDetail = {
                                time: bt.time,
                                size: bt.size,
                                price: bt.price,
                                dollar_value: bt.dollar_value,
                                side: bt.side,
                              };
                              const payload = row
                                ? { ...row, blockTradeDetail }
                                : {
                                    date: bt.date,
                                    fullDate: bt.fullDate,
                                    price: bt.price,
                                    percentChange:
                                      typeof (bt as { percentChange?: number }).percentChange === 'number'
                                        ? (bt as { percentChange?: number }).percentChange
                                        : undefined,
                                    blockTradeDetail,
                                  };
                              void handleDotClick(payload);
                              return;
                            }
                            historicalBlockTradeAwaitingDrillKeyRef.current = null;
                            setHistoricalBlockTradePopover({
                              trade: bt,
                              ...clampHistoricalBlockTradePopoverBesideDiamond(
                                wrap,
                                historicalChartPlotRef.current,
                                cx,
                                cy,
                              ),
                            });
                          }}
                        />
                        );
                      }}
                    />
                    );
                  })}
                  {imbalanceDotsForVisibleChart.map((row) => {
                    const signed = tapeImbalanceSignedRatio(row.buy_volume, row.sell_volume);
                    const fill = tapeImbalanceDotFill(signed);
                    const totalTapeVol = tapeImbalanceTotalVolume(
                      row.buy_volume,
                      row.sell_volume,
                      row.neutral_volume,
                    );
                    const dotR = imbalanceDotRadiusByDate.get(row.date) ?? 4;
                    const strokeW = Math.min(1.65, Math.max(0.85, dotR * 0.24));
                    const tipParts = [
                      `Tape ${row.date}: ${formatShareVolCompact(row.buy_volume)} buy / ${formatShareVolCompact(row.sell_volume)} sell${
                        row.neutral_volume > 0
                          ? ` · ${formatShareVolCompact(row.neutral_volume)} unclassified`
                          : ''
                      } · total ${formatShareVolCompact(totalTapeVol)}`,
                    ];
                    if (row.sessionChange) {
                      const { change: chg, changePct: chgPct } = row.sessionChange;
                      const dollarStr = chg >= 0 ? `+$${chg.toFixed(2)}` : `-$${Math.abs(chg).toFixed(2)}`;
                      const pctStr = `${chgPct >= 0 ? '+' : ''}${chgPct.toFixed(2)}%`;
                      tipParts.push(`Session ${dollarStr} (${pctStr})`);
                    }
                    const tip = tipParts.join(' · ');
                    return (
                      <ReferenceDot
                        key={`imb-${row.date}`}
                        yAxisId="price"
                        x={row.date}
                        y={row.close}
                        r={0}
                        fill="transparent"
                        stroke="none"
                        shape={(props: any) => {
                          const cx = props.cx ?? 0;
                          const cy = props.cy ?? 0;
                          return (
                            <TapeImbalanceChartDot
                              cx={cx}
                              cy={cy}
                              baseR={dotR}
                              fill={fill}
                              strokeWidth={strokeW}
                              title={tip}
                              onClick={() => {
                                const wrap = historicalChartWrapRef.current;
                                if (!wrap) return;
                                let toggledOff = false;
                                setTapeImbalancePopover((prev) => {
                                  if (prev?.date === row.date) {
                                    toggledOff = true;
                                    return null;
                                  }
                                  return {
                                    date: row.date,
                                    fullDate: row.fullDate,
                                    buy_volume: row.buy_volume,
                                    sell_volume: row.sell_volume,
                                    neutral_volume: row.neutral_volume,
                                    sessionChange: row.sessionChange,
                                    ...clampHistoricalBlockTradePopoverBesideDiamond(
                                      wrap,
                                      historicalChartPlotRef.current,
                                      cx,
                                      cy,
                                      {
                                        popW: TAPE_IMBALANCE_POPOVER_W,
                                        popH: TAPE_IMBALANCE_POPOVER_EST_H,
                                        outwardPx: Math.max(
                                          HISTORICAL_BLOCK_TRADE_MARKER_RADIUS_LAYOUT_PX,
                                          dotR * 1.35,
                                        ),
                                      },
                                    ),
                                  };
                                });
                                if (!toggledOff) {
                                  setHistoricalBlockTradePopover(null);
                                  historicalBlockTradeAwaitingDrillKeyRef.current = null;
                                  setInsiderTradingPopover(null);
                                  setCongressTradingPopover(null);
                                  setNotableOptionTradePopover(null);
                                  setOptionImbalancePopover(null);
                                }
                              }}
                            />
                          );
                        }}
                      />
                    );
                  })}
                  {optionImbalanceDotsForVisibleChart.map((row) => {
                    const signed = tapeImbalanceSignedRatio(row.call_premium, row.put_premium);
                    const fill = tapeImbalanceDotFill(signed);
                    const totalPremium = tapeImbalanceTotalVolume(
                      row.call_premium,
                      row.put_premium,
                      row.unknown_premium,
                    );
                    const dotR = optionImbalanceDotRadiusByDate.get(row.date) ?? 4;
                    const strokeW = Math.min(1.65, Math.max(0.85, dotR * 0.24));
                    const tipParts = [
                      `Options ${row.date}: ${formatUsdCompact(row.call_premium)} call / ${formatUsdCompact(row.put_premium)} put${
                        row.unknown_premium > 0
                          ? ` · ${formatUsdCompact(row.unknown_premium)} unclassified`
                          : ''
                      } · total ${formatUsdCompact(totalPremium)}`,
                    ];
                    if (row.sessionChange) {
                      const { change: chg, changePct: chgPct } = row.sessionChange;
                      const dollarStr = chg >= 0 ? `+$${chg.toFixed(2)}` : `-$${Math.abs(chg).toFixed(2)}`;
                      const pctStr = `${chgPct >= 0 ? '+' : ''}${chgPct.toFixed(2)}%`;
                      tipParts.push(`Session ${dollarStr} (${pctStr})`);
                    }
                    const tip = tipParts.join(' · ');
                    return (
                      <ReferenceDot
                        key={`opt-imb-${row.date}`}
                        yAxisId="price"
                        x={row.date}
                        y={row.close}
                        r={0}
                        fill="transparent"
                        stroke="none"
                        shape={(props: any) => {
                          const cx = props.cx ?? 0;
                          const cy = props.cy ?? 0;
                          return (
                            <TapeImbalanceChartDot
                              cx={cx}
                              cy={cy}
                              baseR={dotR}
                              fill={fill}
                              strokeWidth={strokeW}
                              title={tip}
                              onClick={() => {
                                const wrap = historicalChartWrapRef.current;
                                if (!wrap) return;
                                let toggledOff = false;
                                setOptionImbalancePopover((prev) => {
                                  if (prev?.date === row.date) {
                                    toggledOff = true;
                                    return null;
                                  }
                                  return {
                                    date: row.date,
                                    fullDate: row.fullDate,
                                    call_premium: row.call_premium,
                                    put_premium: row.put_premium,
                                    unknown_premium: row.unknown_premium,
                                    sessionChange: row.sessionChange,
                                    ...clampHistoricalBlockTradePopoverBesideDiamond(
                                      wrap,
                                      historicalChartPlotRef.current,
                                      cx,
                                      cy,
                                      {
                                        popW: OPTION_IMBALANCE_POPOVER_W,
                                        popH: OPTION_IMBALANCE_POPOVER_EST_H,
                                        outwardPx: Math.max(
                                          HISTORICAL_BLOCK_TRADE_MARKER_RADIUS_LAYOUT_PX,
                                          dotR * 1.35,
                                        ),
                                      },
                                    ),
                                  };
                                });
                                if (!toggledOff) {
                                  setHistoricalBlockTradePopover(null);
                                  historicalBlockTradeAwaitingDrillKeyRef.current = null;
                                  setInsiderTradingPopover(null);
                                  setCongressTradingPopover(null);
                                  setNotableOptionTradePopover(null);
                                  setTapeImbalancePopover(null);
                                }
                              }}
                            />
                          );
                        }}
                      />
                    );
                  })}
                  {congressDiamondMarkersForVisibleChart.map((m) => {
                    const pop = congressTradingPopover;
                    const markerKey = fmpCongressTradeMarkerKey(m.date, m.trade, m.stackIndex);
                    const searchPulsate =
                      congressTradingSearchHighlight != null &&
                      congressTradingSearchHighlight.tradeIdentityKey ===
                        congressTradeIdentityKey(m.trade);
                    const pulsate =
                      searchPulsate || (pop != null && pop.highlightKey === markerKey);
                    const stackDy =
                      m.stackCount > 1
                        ? (m.stackIndex - (m.stackCount - 1) / 2) * 12
                        : 0;
                    return (
                      <ReferenceDot
                        key={`congress-${markerKey}`}
                        yAxisId="price"
                        x={m.date}
                        y={m.yPrice}
                        r={0}
                        fill="transparent"
                        stroke="none"
                        shape={(props: any) => {
                          const cx = props.cx ?? 0;
                          const cy = props.cy ?? 0;
                          return (
                            <HistoricalCongressTradeDiamond
                              cx={cx}
                              cy={cy}
                              trade={m.trade}
                              pulsate={pulsate}
                              stackDyPx={stackDy}
                              stackCount={m.stackCount}
                              onClick={() => {
                                const wrap = historicalChartWrapRef.current;
                                if (!wrap) return;
                                setHistoricalBlockTradePopover(null);
                                historicalBlockTradeAwaitingDrillKeyRef.current = null;
                                setInsiderTradingPopover(null);
                                setTapeImbalancePopover(null);
                                setOptionImbalancePopover(null);
                                setCongressTradingPopover({
                                  date: m.date,
                                  fullDate: m.fullDate,
                                  trades: [m.trade],
                                  highlightKey: markerKey,
                                  sameDayOrdinal: m.stackCount > 1 ? m.stackIndex + 1 : undefined,
                                  sameDayTotal: m.stackCount > 1 ? m.stackCount : undefined,
                                  ...clampHistoricalBlockTradePopoverBesideDiamond(
                                    wrap,
                                    historicalChartPlotRef.current,
                                    cx,
                                    cy,
                                    {
                                      popW: SENATE_TRADING_POPOVER_W,
                                      popH: SENATE_TRADING_POPOVER_EST_H,
                                    },
                                  ),
                                });
                              }}
                            />
                          );
                        }}
                      />
                    );
                  })}
                  {notableOptionDiamondMarkersForVisibleChart.map((m) => {
                    const pop = notableOptionTradePopover;
                    const markerKey = notableOptionTradeMarkerKey(m.trade);
                    const pulsate =
                      pop != null &&
                      pop.trade.date === m.trade.date &&
                      notableOptionTradeMarkerKey(pop.trade) === markerKey;
                    return (
                      <ReferenceDot
                        key={`option-${markerKey}`}
                        yAxisId="price"
                        x={m.date}
                        y={m.yPrice}
                        r={0}
                        fill="transparent"
                        stroke="none"
                        shape={(props: any) => {
                          const cx = props.cx ?? 0;
                          const cy = props.cy ?? 0;
                          return (
                            <NotableOptionTradeDiamond
                              cx={cx}
                              cy={cy}
                              trade={m.trade}
                              pulsate={pulsate}
                              sizeScale={0.82}
                              radiusPx={notableOptionTradeDiamondRadiusByDate.get(m.date)}
                              onClick={() => {
                                const wrap = historicalChartWrapRef.current;
                                if (!wrap) return;
                                setHistoricalBlockTradePopover(null);
                                historicalBlockTradeAwaitingDrillKeyRef.current = null;
                                setTapeImbalancePopover(null);
                                setOptionImbalancePopover(null);
                                setInsiderTradingPopover(null);
                                setCongressTradingPopover(null);
                                setNotableOptionTradePopover({
                                  trade: m.trade,
                                  fullDate: m.fullDate,
                                  ...clampHistoricalBlockTradePopoverBesideDiamond(
                                    wrap,
                                    historicalChartPlotRef.current,
                                    cx,
                                    cy,
                                    {
                                      popW: NOTABLE_OPTION_TRADE_POPOVER_W,
                                      popH: NOTABLE_OPTION_TRADE_POPOVER_EST_H,
                                    },
                                  ),
                                });
                              }}
                            />
                          );
                        }}
                      />
                    );
                  })}
                  {insiderDiamondMarkersForVisibleChart.map((m) => {
                    const pop = insiderTradingPopover;
                    const markerKey = fmpInsiderTradeMarkerKey(m.date, m.txnCode);
                    const searchPulsate =
                      insiderTradingSearchHighlight != null &&
                      insiderTradingSearchHighlight.date === m.date &&
                      insiderTradingSearchHighlight.txnCode === m.txnCode;
                    const pulsate =
                      searchPulsate || (pop != null && pop.highlightKey === markerKey);
                    const stackDx =
                      m.categoryCount > 1
                        ? (m.categoryIndex - (m.categoryCount - 1) / 2) * 14
                        : 0;
                    return (
                      <ReferenceDot
                        key={`insider-${markerKey}`}
                        yAxisId="price"
                        x={m.date}
                        y={m.yPrice}
                        r={0}
                        fill="transparent"
                        stroke="none"
                        shape={(props: any) => {
                          const cx = (props.cx ?? 0) + stackDx;
                          const cy = props.cy ?? 0;
                          return (
                            <HistoricalInsiderTradeDiamond
                              cx={cx}
                              cy={cy}
                              trade={m.trade}
                              pulsate={pulsate}
                              onClick={() => {
                                const wrap = historicalChartWrapRef.current;
                                if (!wrap) return;
                                setHistoricalBlockTradePopover(null);
                                historicalBlockTradeAwaitingDrillKeyRef.current = null;
                                setTapeImbalancePopover(null);
                                setOptionImbalancePopover(null);
                                setInsiderTradingPopover({
                                  date: m.date,
                                  fullDate: m.fullDate,
                                  trades: m.trades,
                                  highlightKey: markerKey,
                                  ...clampHistoricalBlockTradePopoverBesideDiamond(
                                    wrap,
                                    historicalChartPlotRef.current,
                                    cx,
                                    cy,
                                    {
                                      popW: INSIDER_TRADING_POPOVER_W,
                                      popH: INSIDER_TRADING_POPOVER_EST_H,
                                    },
                                  ),
                                });
                              }}
                            />
                          );
                        }}
                      />
                    );
                  })}
                  <Customized
                    component={(chartProps: HistoricalChartMetrics & Record<string, unknown>) => {
                      historicalChartMetricsRef.current = chartProps;
                      queueMicrotask(() => {
                        const wrap = historicalChartWrapRef.current;
                        if (!wrap) return;
                        const pad = measureHistoricalYAxisTickLeftPad(wrap);
                        if (pad == null) return;
                        setHistoricalOhlcAlignPadPx((prev) =>
                          Math.abs(prev - pad) < 0.25 ? prev : pad,
                        );
                      });
                      return (
                        <HistoricalChartCrosshairGraphics
                          chartProps={chartProps}
                          chartData={historicalChartVisibleData}
                          plotMouse={historicalPlotMouse}
                        />
                      );
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              </div>
              {historicalBrushBox && (
                <div
                  className="pointer-events-none absolute z-[15] border-2 border-gray-900 bg-gray-900/10"
                  style={{
                    left: `${Math.min(historicalBrushBox.x1, historicalBrushBox.x2)}px`,
                    top: `${Math.min(historicalBrushBox.y1, historicalBrushBox.y2)}px`,
                    width: `${Math.abs(historicalBrushBox.x2 - historicalBrushBox.x1)}px`,
                    height: `${Math.abs(historicalBrushBox.y2 - historicalBrushBox.y1)}px`,
                  }}
                />
              )}
              </div>
              {layers.optionsPositioning.enabled &&
              !optionsLegendDismissed &&
              optionsChartOverlaySummary ? (
                <OptionsPositioningChartLegend
                  summary={optionsChartOverlaySummary}
                  rows={optionsChartLegendRows}
                  ladderRows={optionsChartLadderRows}
                  position={optionsLegendPosition}
                  loading={optionsChartLoading}
                  onClose={() => setOptionsLegendDismissed(true)}
                />
              ) : null}
              {historicalPriceTrackerOverlay && (
                <div
                  className="pointer-events-none absolute z-20 flex h-[22px] min-w-[4.75rem] items-center justify-center rounded-sm bg-gray-900 px-2 text-[11px] font-medium text-white shadow-sm tabular-nums"
                  style={{
                    left: historicalPriceTrackerOverlay.left,
                    top: historicalPriceTrackerOverlay.top,
                  }}
                >
                  {historicalPriceTrackerOverlay.label}
                </div>
              )}
              {historicalPeakVolumeGuideOverlay && (
                <PeakVolumePriceGuidePill
                  label={historicalPeakVolumeGuideOverlay.label}
                  left={historicalPeakVolumeGuideOverlay.left}
                  top={historicalPeakVolumeGuideOverlay.top}
                />
              )}
              {historicalBlockTradePopover && (
                <div
                  ref={historicalBlockTradePopoverRef}
                  role="tooltip"
                  aria-label="Trade: date, price, shares, total"
                  className="absolute z-[35] rounded-2xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
                  style={{
                    left: historicalBlockTradePopover.left,
                    top: historicalBlockTradePopover.top,
                    width: HISTORICAL_BLOCK_TRADE_POPOVER_W,
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setHistoricalBlockTradePopover(null);
                    }}
                    className="absolute right-1.5 top-1.5 z-10 rounded-md p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  <BlockTradeTooltipContent
                    trade={historicalBlockTradePopover.trade}
                    sessionVolume={historicalSessionVolumeByDate.get(
                      historicalBlockTradePopover.trade.date,
                    )}
                  />
                </div>
              )}
              {tapeImbalancePopover && (
                <div
                  ref={tapeImbalancePopoverRef}
                  role="tooltip"
                  aria-label="Tape buy and sell aggressor volumes"
                  className="absolute z-[35] rounded-2xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
                  style={{
                    left: tapeImbalancePopover.left,
                    top: tapeImbalancePopover.top,
                    width: TAPE_IMBALANCE_POPOVER_W,
                  }}
                >
                  <TapeImbalanceTooltipContent
                    date={tapeImbalancePopover.date}
                    fullDate={tapeImbalancePopover.fullDate}
                    buy_volume={tapeImbalancePopover.buy_volume}
                    sell_volume={tapeImbalancePopover.sell_volume}
                    neutral_volume={tapeImbalancePopover.neutral_volume}
                    sessionChange={tapeImbalancePopover.sessionChange}
                  />
                </div>
              )}
              {optionImbalancePopover && (
                <div
                  ref={optionImbalancePopoverRef}
                  role="tooltip"
                  aria-label="Option call and put premium imbalance"
                  className="absolute z-[35] rounded-2xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
                  style={{
                    left: optionImbalancePopover.left,
                    top: optionImbalancePopover.top,
                    width: OPTION_IMBALANCE_POPOVER_W,
                  }}
                >
                  <OptionImbalanceTooltipContent
                    date={optionImbalancePopover.date}
                    fullDate={optionImbalancePopover.fullDate}
                    call_premium={optionImbalancePopover.call_premium}
                    put_premium={optionImbalancePopover.put_premium}
                    unknown_premium={optionImbalancePopover.unknown_premium}
                    sessionChange={optionImbalancePopover.sessionChange}
                  />
                </div>
              )}
              {insiderTradingPopover && (
                <div
                  ref={insiderTradingPopoverRef}
                  role="tooltip"
                  aria-label="Insider trading details"
                  className="absolute z-[35] max-h-[min(72vh,420px)] overflow-hidden rounded-2xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
                  style={{
                    left: insiderTradingPopover.left,
                    top: insiderTradingPopover.top,
                    width: INSIDER_TRADING_POPOVER_W,
                  }}
                >
                  <InsiderTradeTooltipContent
                    date={insiderTradingPopover.date}
                    fullDate={insiderTradingPopover.fullDate}
                    trades={insiderTradingPopover.trades}
                  />
                </div>
              )}
              {notableOptionTradePopover && (
                <div
                  ref={notableOptionTradePopoverRef}
                  role="tooltip"
                  aria-label="Notable option trade details"
                  className="absolute z-[35] rounded-2xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
                  style={{
                    left: notableOptionTradePopover.left,
                    top: notableOptionTradePopover.top,
                    width: NOTABLE_OPTION_TRADE_POPOVER_W,
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNotableOptionTradePopover(null);
                    }}
                    className="absolute right-1.5 top-1.5 z-10 rounded-md p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  <NotableOptionTradesTooltipContent
                    trade={notableOptionTradePopover.trade}
                    fullDate={notableOptionTradePopover.fullDate}
                  />
                </div>
              )}
              {congressTradingPopover && (
                <div
                  ref={congressTradingPopoverRef}
                  role="tooltip"
                  aria-label="Senate/House trading details"
                  className="absolute z-[35] max-h-[min(72vh,420px)] overflow-hidden rounded-2xl border border-sky-100 bg-white px-3 py-2.5 shadow-sm"
                  style={{
                    left: congressTradingPopover.left,
                    top: congressTradingPopover.top,
                    width: SENATE_TRADING_POPOVER_W,
                  }}
                >
                  <CongressTradeTooltipContent
                    date={congressTradingPopover.date}
                    fullDate={congressTradingPopover.fullDate}
                    trades={congressTradingPopover.trades}
                    sameDayOrdinal={congressTradingPopover.sameDayOrdinal}
                    sameDayTotal={congressTradingPopover.sameDayTotal}
                  />
                </div>
              )}
              <InstitutionalHoldersTopHoldersWidget
                layerActive={layers.institutionalHolders.enabled}
                payload={institutionalHoldersPayload}
                loading={institutionalHoldersLoading}
                loadError={institutionalHoldersLoadError}
                selectedPositionKey={institutionalHoldersSearchHighlight?.positionKey ?? null}
                onSelectHolder={handleInstitutionalTopHolderSelect}
              />
              {/* Fed Rate Decision: compact reopen chip when panel dismissed */}
              {layers.prediction.enabled && !predictionPanelOpen && (
                <motion.button
                  type="button"
                  data-prediction-dismiss-skip="true"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setPredictionPanelOpen(true)}
                  className="absolute left-3 bottom-12 z-[36] flex items-center gap-1.5 rounded-full border border-violet-200 bg-white px-3 py-1.5 text-[11px] font-medium text-violet-700 shadow-md hover:bg-violet-50"
                >
                  Polymarket
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />
                </motion.button>
              )}
              {/* Fed Rate Decision Prediction Overlay */}
              {layers.prediction.enabled && predictionPanelOpen && (
                <motion.div
                  ref={predictionPanelRef}
                  initial={{ opacity: 0, y: 8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.97 }}
                  transition={{ duration: 0.25 }}
                  className="absolute left-3 bottom-12 z-[36] w-72 rounded-2xl border border-violet-100 bg-white shadow-lg overflow-hidden"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-violet-50 bg-violet-50/60">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-600">Polymarket</span>
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-semibold">
                        <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                        Live
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-[11px] font-medium text-gray-700">Fed & SPY (May)</span>
                      <span className="text-[9px] text-gray-400 max-w-[11rem] text-right leading-tight">
                        Tap "Show on chart" on each outcome to layer odds
                      </span>
                      {selectedEventGroup.length > 0 && (
                        <span className="text-[9px] text-violet-500 font-medium">
                          {fedProbHistoryLoading
                            ? 'Loading…'
                            : Object.keys(fedProbHistories).length > 0
                              ? `✓ ${Object.keys(fedProbHistories).length} lines plotted`
                              : '—'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="px-4 py-3 space-y-3">
                    {fedRateLoading && (
                      <div className="flex items-center justify-center py-4">
                        <div className="w-4 h-4 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
                        <span className="ml-2 text-xs text-gray-400">Loading markets…</span>
                      </div>
                    )}
                    {!fedRateLoading && fedRateError && (
                      <p className="text-xs text-rose-500 py-2">{fedRateError}</p>
                    )}
                    {!fedRateLoading && fedRateData && fedRateData.markets.length === 0 && (
                      <p className="text-xs text-gray-400 py-2">No active Fed markets found on Polymarket.</p>
                    )}
                    {!fedRateLoading && fedRateData && fedPredictionMarketsOrdered.length > 0 && (
                    <div className="max-h-[min(52vh,380px)] overflow-y-auto space-y-3 pr-0.5 -mr-0.5">
                    {fedPredictionMarketsOrdered.map((market: FedRateMarket, marketIdx: number) => {
                      const yesPct = market.yes_probability != null ? Math.round(market.yes_probability * 100) : null;
                      const noPct = yesPct != null ? 100 - yesPct : null;
                      const vol24k = market.volume_24h >= 1000
                        ? `$${(market.volume_24h / 1000).toFixed(1)}k`
                        : market.volume_24h > 0 ? `$${Math.round(market.volume_24h)}` : null;

                      // Generic grouping: all markets sharing the same event_title form a group.
                      const eventTitleNorm = (market.event_title ?? '').trim().toLowerCase();
                      const groupMarkets = fedPredictionMarketsOrdered.filter(
                        (m) => (m.event_title ?? '').trim().toLowerCase() === eventTitleNorm,
                      );
                      const isGroupMarket = groupMarkets.length > 1;
                      const groupIdx = isGroupMarket ? groupMarkets.findIndex((m) => m.id === market.id) : 0;
                      const lineColor = fedOutcomeColor(groupIdx);
                      const isSelected = selectedEventGroup.some((m) => m.id === market.id);
                      const historyTok = (market.clob_yes_token_id ?? '').trim();
                      const canPlot = historyTok.length > 0;
                      const shortLabel = isGroupMarket ? fedOutcomeShortLabel(market.question) : null;
                      // Render a section divider before the first market of each new event_title group.
                      const prevMarket = marketIdx > 0 ? fedPredictionMarketsOrdered[marketIdx - 1] : null;
                      const prevEventTitleNorm = prevMarket ? (prevMarket.event_title ?? '').trim().toLowerCase() : null;
                      const showGroupHeader = isGroupMarket && eventTitleNorm !== prevEventTitleNorm;

                      return (
                        <div key={market.id}>
                          {showGroupHeader && (
                            <p className="text-[10px] font-semibold text-violet-800/90 px-0.5 pb-1 mb-1 border-b border-violet-100">
                              {(market.event_title ?? '').trim()}
                            </p>
                          )}
                          <div
                            className={`space-y-1.5 rounded-lg border p-2 ${isSelected ? 'border-violet-200 bg-violet-50/40' : 'border-gray-100 bg-gray-50/40'}`}
                          >
                          <div className="flex items-start gap-1.5 px-0.5">
                            {isGroupMarket && (
                              <span
                                className="mt-0.5 shrink-0 w-2 h-2 rounded-full"
                                style={{ backgroundColor: lineColor }}
                              />
                            )}
                            <p className="text-[11px] leading-snug text-gray-800 font-medium line-clamp-3">
                              {shortLabel ?? market.question}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 px-0.5">
                            <button
                              type="button"
                              onClick={() => {
                                if (!canPlot) return;
                                if (isGroupMarket) {
                                  if (isSelected) {
                                    setSelectedEventGroup((prev) => prev.filter((x) => x.id !== market.id));
                                  } else {
                                    setSelectedEventGroup((prev) => [...prev, market]);
                                  }
                                } else if (isSelected) {
                                  setSelectedEventGroup([]);
                                  setFedProbHistories({});
                                } else {
                                  setSelectedEventGroup([market]);
                                }
                              }}
                              disabled={!canPlot}
                              className={`shrink-0 rounded-lg px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                                !canPlot
                                  ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                                  : isSelected
                                    ? 'bg-violet-600 text-white ring-1 ring-violet-700'
                                    : 'bg-white text-violet-700 ring-1 ring-violet-200 hover:bg-violet-50'
                              }`}
                            >
                              {isSelected
                                ? (fedProbHistoryLoading ? 'Loading…' : 'On chart')
                                : 'Show on chart'}
                            </button>
                            {!canPlot && (
                              <span className="text-[9px] text-amber-600">No price history id</span>
                            )}
                            <a
                              href={market.polymarket_url || 'https://polymarket.com'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-auto text-[10px] font-medium text-violet-500 hover:text-violet-700"
                            >
                              View ↗
                            </a>
                          </div>
                          {yesPct != null && (
                            <div className="space-y-1 px-0.5 pt-0.5">
                              <div>
                                <div className="flex justify-between items-center mb-0.5">
                                  <span className="text-[9px] font-semibold text-emerald-600 uppercase tracking-wider">Yes</span>
                                  <span className="text-[11px] font-semibold text-gray-800">{yesPct}%</span>
                                </div>
                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <motion.div
                                    className="h-full rounded-full"
                                    style={{ backgroundColor: isSelected ? lineColor : '#10b981' }}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${yesPct}%` }}
                                    transition={{ duration: 0.7, ease: 'easeOut' }}
                                  />
                                </div>
                              </div>
                              <div>
                                <div className="flex justify-between items-center mb-0.5">
                                  <span className="text-[9px] font-semibold text-rose-500 uppercase tracking-wider">No</span>
                                  <span className="text-[11px] font-semibold text-gray-800">{noPct}%</span>
                                </div>
                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <motion.div
                                    className="h-full bg-rose-400 rounded-full"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${noPct}%` }}
                                    transition={{ duration: 0.7, ease: 'easeOut' }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          {vol24k && (
                            <p className="text-[9px] text-gray-400 px-0.5">24h vol {vol24k}</p>
                          )}
                          </div>
                        </div>
                      );
                    })}
                    </div>
                    )}
                    {!fedRateLoading && fedRateData && (
                      <div className="pt-1 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-[9px] text-gray-400">
                          {fedRateData.from_cache ? 'Cached' : 'Live'} · {fedRateData.markets.length} market{fedRateData.markets.length !== 1 ? 's' : ''}
                        </span>
                        <a
                          href="https://polymarket.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[9px] text-violet-500 hover:text-violet-700 transition-colors"
                        >
                          polymarket.com ↗
                        </a>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            {todayMoveSummaryCard ? <div className="mt-8">{todayMoveSummaryCard}</div> : null}

            {/* Event Explanation Panel - Anti-Jank: layout prop + fixed height scroll container */}
            {selectedPoint && (
              <motion.div
                ref={eventExplanationPanelRef}
                layout
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-8 scroll-mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200"
              >
                <motion.div layout="position">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      {loadingStory ? (
                        <div className="animate-pulse mb-1">
                          <div className="h-3 bg-gray-200 rounded w-24"></div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 mb-1">{selectedPoint.fullDate}</p>
                      )}
                      {loadingStory ? (
                        <div className="animate-pulse">
                          <div className="h-6 bg-gray-200 rounded w-48"></div>
                        </div>
                      ) : (
                        <h4 className="text-gray-700 text-lg font-medium">
                          {selectedPoint.explanationTitle?.trim() ||
                            (decisionAttribution &&
                            decisionAttribution.date === selectedPoint.date &&
                            decisionAttribution.primary_driver
                              ? labelDecisionDriver(decisionAttribution.primary_driver)
                              : null) ||
                            'Significant Price Move'}
                        </h4>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedPoint(null);
                        setHistoricalBlockTradePopover(null);
                        setInsiderTradingPopover(null);
                        setIntradayBlockTradePopover(null);
                        setTapeImbalancePopover(null);
                        historicalBlockTradeAwaitingDrillKeyRef.current = null;
                        setExecutiveSummary(null);
                        setTapeStoryline(null);
                        setHighSignificanceThemes(null);
                        setStoryError(null);
                        setOriginEventCatalysts(null);
                        setDecisionAttribution(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    {loadingStory ? (
                      <>
                        <div className="animate-pulse">
                          <div className="h-6 bg-gray-200 rounded-lg w-16"></div>
                        </div>
                        <div className="animate-pulse">
                          <div className="h-5 bg-gray-200 rounded w-12"></div>
                        </div>
                        <div className="animate-pulse">
                          <div className="h-5 bg-gray-200 rounded w-20"></div>
                        </div>
                      </>
                    ) : (
                      <>
                        {selectedPoint.percentChange !== undefined && (
                          <>
                            <span className={`px-3 py-1 rounded-lg text-sm ${
                              (selectedPoint.percentChange > 0)
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-rose-100 text-rose-700'
                            }`}>
                              {selectedPoint.percentChange > 0 ? 'Positive' : 'Negative'}
                            </span>
                            <span className="text-gray-500 text-sm font-medium">
                              {selectedPoint.percentChange > 0 ? '+' : ''}{selectedPoint.percentChange?.toFixed(2)}%
                            </span>
                          </>
                        )}
                        <span className="text-gray-300 text-sm">
                          Price: ${selectedPoint.price?.toFixed(2) || 'N/A'}
                        </span>
                      </>
                    )}
                  </div>

                  {renderIntradayPriceMovementBlock()}

                  {!loadingStory && tapeStoryline && (
                      <div className="mt-5 mb-4 pt-4 border-t border-gray-100">
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                          Tape narrative
                        </p>
                        <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                          {tapeStoryline.overall_arc.trim()}
                        </p>
                      </div>
                    )}

                  {loadingStory ? (
                    // Loading skeleton - same height as real content to prevent layout shift
                    <div className="space-y-2 animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                      <div className="h-4 bg-gray-200 rounded w-10/12"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-9/12"></div>
                    </div>
                  ) : originEventCatalysts && originEventCatalysts.length > 0 ? (
                    <ol className="space-y-2 list-none text-sm">
                      {originEventCatalysts.map((item) => (
                        <li key={`${item.rank}-${item.catalyst.slice(0, 48)}`} className="space-y-1">
                          <span className="text-gray-800 font-medium leading-snug">
                            {item.rank}. {item.catalyst}
                          </span>
                          <p className="text-gray-600 pl-0 leading-relaxed">{item.mechanism}</p>
                        </li>
                      ))}
                    </ol>
                  ) : executiveSummary ? (
                    <div className="space-y-4 text-sm">
                      <p className="text-gray-600 leading-relaxed">
                        {executiveSummary}
                      </p>
                      {highSignificanceThemes && highSignificanceThemes.length > 0 && (
                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-gray-800 font-semibold mb-2">Key Reasons for the Move</p>
                          <ol className="space-y-2 list-none">
                            {highSignificanceThemes.map((item, index) => (
                              <li key={index} className="space-y-1">
                                <span className="text-gray-800 font-medium">
                                  {index + 1}. {item.theme}
                                </span>
                                <p className="text-gray-600 pl-0 leading-relaxed">{item.key_evidence}</p>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  ) : storyError ? (
                    <p className="text-gray-500 text-sm italic">
                      {storyError}
                    </p>
                  ) : (
                    <p className="text-gray-500 text-sm italic">
                      No analysis available for this date.
                    </p>
                  )}

                  {decisionAttribution && (
                    <div className="mt-6 pt-5 border-t border-gray-200 space-y-3">
                      <p className="text-xs text-gray-400 uppercase tracking-wide">
                        Move driver attribution
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        {decisionAttribution.primary_driver && (
                          <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-violet-100 text-violet-800">
                            {labelDecisionDriver(decisionAttribution.primary_driver)}
                          </span>
                        )}
                        {decisionAttribution.confidence && (
                          <span className="text-xs text-gray-500">
                            Confidence: <span className="font-medium text-gray-700">{decisionAttribution.confidence}</span>
                          </span>
                        )}
                        {decisionAttribution.price_change_pct != null && !Number.isNaN(decisionAttribution.price_change_pct) && (
                          <span className="text-xs text-gray-500">
                            Model day move:{' '}
                            <span
                              className={`font-medium ${
                                decisionAttribution.price_change_pct >= 0 ? 'text-emerald-700' : 'text-rose-700'
                              }`}
                            >
                              {decisionAttribution.price_change_pct >= 0 ? '+' : ''}
                              {decisionAttribution.price_change_pct.toFixed(2)}%
                            </span>
                          </span>
                        )}
                      </div>
                      {decisionAttribution.driver_scores &&
                        DECISION_DRIVER_SCORE_KEYS.some((k) => decisionAttribution.driver_scores![k] != null) && (
                          <div className="space-y-2">
                            <p className="text-xs text-gray-500">Driver weights</p>
                            <div className="space-y-1.5">
                              {DECISION_DRIVER_SCORE_KEYS.map((key) => {
                                const v = decisionAttribution.driver_scores![key];
                                if (v == null || Number.isNaN(v)) return null;
                                const pct = Math.round(v * 100);
                                return (
                                  <div key={key} className="flex items-center gap-2 text-xs">
                                    <span className="w-36 shrink-0 text-gray-600">{labelDecisionDriver(key)}</span>
                                    <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden min-w-[4rem]">
                                      <div
                                        className="h-full rounded-full bg-violet-500/80"
                                        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                                      />
                                    </div>
                                    <span className="w-10 text-right tabular-nums text-gray-700">{pct}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      {decisionAttribution.signals_available &&
                        Object.keys(decisionAttribution.signals_available).length > 0 && (
                          <p className="text-xs text-gray-500">
                            Signals used:{' '}
                            {Object.entries(decisionAttribution.signals_available)
                              .filter(([, ok]) => ok)
                              .map(([k]) => k.replace(/_/g, ' '))
                              .join(' · ') || '—'}
                          </p>
                        )}
                      {decisionAttribution.reasoning && (
                        <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                          {decisionAttribution.reasoning}
                        </p>
                      )}
                    </div>
                  )}
                </motion.div>

                {/* Related Coverage Section - Anti-Jank: Always show skeleton/content during/after loading */}
                <motion.div layout="position" className="mt-6 pt-5 border-t border-gray-200">
                  {loadingStory ? (
                    <div className="animate-pulse mb-3">
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">Related Coverage</p>
                  )}
                  {loadingStory ? (
                    // Loading skeleton for articles - prevents layout shift
                    <div className="space-y-2.5 max-h-64 overflow-y-auto pr-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse p-2">
                          <div className="flex items-start gap-2">
                            <div className="w-4 h-4 bg-gray-200 rounded mt-0.5 flex-shrink-0"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                            </div>
                            <div className="w-3.5 h-3.5 bg-gray-200 rounded mt-1 flex-shrink-0"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (executiveSummary || (originEventCatalysts && originEventCatalysts.length > 0)) && relatedArticles.length > 0 ? (
                    <div className="space-y-2.5 max-h-64 overflow-y-auto overflow-x-hidden pr-2">
                      {relatedArticles.map((article, index) => (
                        <motion.a
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block group cursor-pointer hover:bg-gray-100/50 rounded-lg p-2.5 -mx-2.5 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <svg 
                              className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                              />
                            </svg>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors leading-snug line-clamp-2">
                                {article.title}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {article.source}
                              </p>
                            </div>
                            <svg 
                              className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors mt-1 flex-shrink-0" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                              />
                            </svg>
                          </div>
                        </motion.a>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 italic">
                      No related articles available
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
            </>
            ) : null}
          </motion.div>
          )}

          {/* Time & Sales feed — defaults to today, updates when a chart day is selected */}
          {SHOW_TIME_AND_SALES && (
            <TimeAndSalesFeed
              ticker={stockData.ticker}
              date={selectedPoint?.date ?? localTodayIsoDate()}
              sessionVolume={
                (dynamicHistoricalPrices ?? historicalPrices)?.find(
                  (d) => d.date === (selectedPoint?.date ?? localTodayIsoDate()),
                )?.volume ?? null
              }
            />
          )}

          {/* Options Positioning & Market-Implied Price Direction */}
          {SHOW_OPTIONS_POSITIONING && (
            <OptionsPositioningSection ticker={stockData.ticker} />
          )}

          {/* Polymarket Prediction Market Odds */}
          {SHOW_PREDICTION_MARKETS && (
            <PolymarketSection
              ticker={stockData.ticker}
              companyName={stockData.company}
              sector={financialHealth?.metadata?.sector_name ?? 'unknown'}
              industry={financialHealth?.metadata?.industry_name ?? 'unknown'}
            />
          )}

          {/* Metrics - set SHOW_METRICS to true to display */}
          {SHOW_METRICS && (
            <>
              <ScoreCard
                title="Fundamentals"
                score={fundamentalsScore}
                metrics={fundamentalsMetrics}
                referenceMetrics={referenceMetrics}
                delay={0.3}
                isOpen={openSections.fundamentals}
                onToggle={() => toggleSection('fundamentals')}
              />
              <ScoreCard
                title="Analysts"
                score={sentimentScore}
                metrics={sentimentMetrics}
                referenceMetrics={sentimentReferenceMetrics}
                delay={0.4}
                isOpen={openSections.sentiment}
                onToggle={() => toggleSection('sentiment')}
              />
              <ScoreCard
                title="Momentum"
                score={momentumScore}
                metrics={momentumMetrics}
                referenceMetrics={momentumReferenceMetrics}
                delay={0.5}
                isOpen={openSections.momentum}
                onToggle={() => toggleSection('momentum')}
              />
            </>
          )}
        </div>
      </main>
      {institutionalSearchDetailPanel && institutionalHoldersSearchDetail ? (
        <InstitutionalHolderChartTooltip
          detail={institutionalSearchDetailPanel}
          anchorRect={
            new DOMRect(
              Math.max(8, (typeof window !== 'undefined' ? window.innerWidth : 800) / 2 - 140),
              Math.max(8, (typeof window !== 'undefined' ? window.innerHeight : 600) * 0.28),
              280,
              1
            )
          }
          onClose={() => {
            setInstitutionalHoldersSearchDetail(null);
            setInstitutionalHoldersSearchHighlight(null);
          }}
        />
      ) : null}
    </div>
  );
}

